/**
 * HR view — Phase 2.2.d (full HR cluster extraction).
 *
 * Owns the entire HR view: shift templates / active shifts (the "time"
 * section), the people directory + employee detail panels (departments,
 * positions, employees, recruitment), the performance / talent / learning
 * / finance / compliance / ess sub-sections, and every dialog they spawn
 * (leave, goals, reviews, probation, training, certs, loans, profile
 * changes, doc uploads, onboarding templates + tasks, vacancies,
 * applicants, skill requirements). Boot-time top-level listener wiring
 * that originally lived at admin.js lines 13196-13251 + 16346-16866 was
 * consolidated next to `initShiftTemplatesView` by the 2.2.d-prep PR;
 * this module strips that single contiguous block.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `hasRole`, `openDialog`, `closeDialog` — generic.
 *
 * Boot contract: `admin.html` loads this script after the other view
 * modules. The IIFE registers `window.adminViews.hr`. The view-router
 * calls `init()` when the user opens the HR tab; the orphan top-level
 * listeners are wired by `mount()` at admin.js boot so they fire whether
 * or not the user navigates to HR (preserves the original behavior).
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
import { escapeHtml, formatDateTimeShort, formatCurrency } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as hrui from '../../src/lib/hr-ui.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

// ---- Compat aliases (so lifted function bodies need zero changes) ----
const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- State (verbatim from admin.js) -----------------------------------
    // Labels for the ShiftStaffGroup enum (kept in sync with
    // inet_portal/modules/hr/domain/enums.py:ShiftStaffGroup and the
    // identical table in views/shift-management.js).
    const STAFF_GROUP_LABELS = {
      customer_care: 'Customer care',
      field_tech: 'Field tech',
      call_center: 'Call center',
      noc: 'NOC',
      security: 'Security',
      regular: 'Regular',
    };
    const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    let shiftTemplatesState = { items: [], loading: false, initialized: false };
    let shiftActiveState = { items: [], loading: false, loaded: false };
    // Biometric weekly attendance import (preview → confirm). `preview` holds
    // the last preview response; `file` the File we previewed (re-sent on commit).
    let hrImportState = { file: null, preview: null, busy: false };
    // Attendance grid (period matrix). Lazy: only fetched when the tab is
    // opened and whenever the view/period changes.
    let hrGridState = { loading: false, loaded: false, data: null };
    let hrActiveTab = 'templates';
    let hrSection = 'time';
    let hrPeopleTab = 'employees';
    const hrSectionPanels = {};
    const hrDataState = {
      people: false,
      departments: false,
      positions: false,
      performance: false,
      talent: false,
      learning: false,
      finance: false,
      compliance: false,
      ess: false,
    };
    let hrDepartmentsList = [];
    let hrPositionsList = [];
    let hrGradesList = [];
    let hrPositionSearch, hrPositionDeptFilter, hrPositionVacancyFilter, hrPositionCount;
    let _hrPositionEditId = null;
    let hrEmployeeDetail = null;
    let hrEmployeeAdminUser = null;
    let hrDepartmentsMap = new Map();
    // Helper for the silent-dead-button class of bug: previously every
    // openHr*Dialog and load* function did `if (!ref) return;`, which is
    // invisible when the ref is null. This wraps that pattern with a
    // deduplicated console.warn so the next missing-element bug is
    // diagnosable instead of leaving a stuck UI.
    const _hrSilentWarnSeen = new Set();
    function _hrWarnMissing(label) {
      if (_hrSilentWarnSeen.has(label)) return;
      _hrSilentWarnSeen.add(label);
      // eslint-disable-next-line no-console
      console.warn(`hr.js: ${label} missing — feature will not load.`);
    }

    let hrPeopleList = [];
    let hrLoansList = [];
    let hrDeptTreeSelection = '';
    let hrPeoplePage = 1;
    const HR_PEOPLE_PAGE_SIZE = 15;
    let hrDepartmentGoalsCache = [];
    const hrPeopleMap = new Map();
    let hrGoalsList = [];
    let hrReviewsList = [];
    let hrTrainingCourses = [];
    const hrTrainingCourseMap = new Map();
    const hrTrainingSessionMap = new Map();
    let hrSkillsList = [];
    let hrSkillRequirements = [];
    let hrOnboardingTemplates = [];
    let hrOnboardingTemplateItems = [];
    let hrSelectedOnboardingTemplateId = '';
    const hrOnboardingTasksCache = new Map();
    let hrOnboardingTasksOnboardingId = '';
    let hrRecruitmentList = [];
    let hrOnboardingList = [];
    let hrProbationList = [];
    let hrApplicantList = [];
    let hrVacancyList = [];
    const hrEmployeeSectionState = {
      leave: { loaded: false, loading: false },
      status_audit: { loaded: false, loading: false },
      attendance: { loaded: false, loading: false },
      performance: { loaded: false, loading: false },
      talent: { loaded: false, loading: false },
      learning: { loaded: false, loading: false },
      finance: { loaded: false, loading: false },
      ess: { loaded: false, loading: false },
      compliance: { loaded: false, loading: false },
      documents: { loaded: false, loading: false },
      shifts: { loaded: false, loading: false },
      leave_balances: { loaded: false, loading: false },
    };

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let shiftTemplatesTbody, shiftTemplateAddBtn, shiftRefreshBtn, shiftTemplateDlg, shiftTemplateForm;
  let hrStaffPanel, hrStaffOn, hrStaffSearch, hrStaffFilter, hrStaffRefreshBtn, hrStaffSummary, hrStaffTbody;
  let hrStaffShiftDlg, hrStaffShiftForm, hrStaffShiftName, hrStaffShiftSelfNote, hrStaffShiftTemplate;
  let hrStaffShiftFrom, hrStaffShiftNote, hrStaffShiftHistoryTbody, hrStaffShiftSubmit;
  let hrStaffShiftKind, hrStaffShiftTo, hrStaffShiftToField, hrStaffShiftFromLabel;
  let shiftTemplateDlgTitle, shiftTemplateIdInput, shiftTemplateNameInput, shiftTemplateGroupInput, shiftTemplateTimezoneInput;
  let shiftTemplateStartInput, shiftTemplateEndInput, shiftTemplateDaysContainer, shiftTemplateEffectiveFromInput, shiftTemplateEffectiveToInput;
  let shiftTemplateStatusInput, shiftTemplateSubmit, hrTabBar, hrTemplatesPanel, hrActivePanel;
  let shiftTemplateGraceInput, shiftTemplateIsDefaultInput;
  let hrImportPanel, hrImportFile, hrImportPreviewBtn, hrImportResetBtn, hrImportSummary, hrImportHint;
  let hrImportPreviewWrap, hrImportPreviewTbody, hrImportCommitBtn;
  let hrGridPanel, hrGridView, hrGridMonth, hrGridWeek, hrGridMonthField, hrGridWeekField;
  let hrGridRefreshBtn, hrGridRecomputeBtn, hrGridPrintBtn, hrGridSummary, hrGridDetail, hrGridHead, hrGridBody;
  let hrRosterPanel, hrRosterYear, hrRosterDept, hrRosterRefreshBtn, hrRosterSummary, hrRosterCalendar;
  const hrRosterState = { loaded: false };
  // Staff tab: standing shift per employee. `editing` is the row open in the dialog.
  const hrStaffState = { loaded: false, data: null, editing: null };
  let hrView;
  let hrTimeContainer, hrTimeToolbar, hrSectionTitle, hrPeoplePanel, hrPerformancePanel;
  let hrTalentPanel, hrLearningPanel, hrFinancePanel, hrLoanStatusFilter, hrPerformanceYear;
  let hrPerformancePeriod, hrPerformanceDept, hrPerformanceRefresh, hrPerformanceGapGoals, hrPerformanceGapReviews;
  let hrAiScoresSection, hrAiScoresCohortTabs, hrAiScoresTbody, hrAiScoresFlagCol;
  let hrAiScoresKpiAvg, hrAiScoresKpiCoverage, hrAiScoresKpiLowCount, hrAiScoresRecompute;
  let hrAiScoresYear, hrAiScoresMonth;
  let hrAiScoresYtdTbody, hrAiScoresYtdFlagCol, hrAiScoresYtdMeta;
  let hrAiScoresCohort = 'field_tech';
  let hrAiScoresRecomputing = false;
  let hrPerformanceGoalsMet, hrRecruitmentAddBtn, hrRecruitmentEditBtn, hrRecruitmentStageFilter, hrRecruitmentAddApplicantBtn;
  let hrOnboardingAddBtn, hrOnboardingCompleteBtn, hrOnboardingStatusFilter, hrProbationAddBtn, hrProbationStatusFilter;
  let hrLoanPendingCount, hrLoanOpenCount, hrLoanOutstanding, hrLoanStaffChart, hrLoanDetailDlg;
  let hrLoanDetailTitle, hrLoanDetailMeta, hrLoanDetailFields, hrLoanRepayInput, hrLoanApproveBtn;
  let hrLoanRejectBtn, hrLoanRepayBtn, hrLoanSaveBtn, hrLoanPrincipalInput, hrLoanTermInput;
  let hrLoanCollateralInput, hrLoanInterestInput, hrLoanIssueInput, hrLoanCurrencyInput, hrLoanFxInput;
  let hrLoanBaseCurrencyInput, hrCompliancePanel, hrEssPanel, hrPeopleSearch, hrPeopleDeptFilter;
  let hrPeopleStatusFilter, hrPeopleTbody;
  let hrPeoplePositionFilter, hrPeopleEmploymentFilter, hrPeoplePrev, hrPeopleNext;
  let hrPeopleCount, hrPeopleFiltersBtn, hrPeopleFiltersPanel, hrPeopleFiltersCount, hrPeopleFiltersClear;
  let hrPeopleSortState = { key: 'name', dir: 'asc' };
  let hrPeoplePageInfo, hrPeopleTabBar, hrEmployeesContainer, hrEmployeeDetailContainer, hrEmployeeBackBtn;
  let hrEmployeeDetailName, hrEmployeeDetailStatus, hrEmployeeDetailMeta, hrEmployeeDetailDept, hrEmployeeDeactivateBtn;
  let hrEmployeeTerminateBtn, hrEmployeeReactivateBtn, hrEmployeeJobForm, hrEmployeeJobSubmit, hrDetailFirstNameInput;
  let hrDetailLastNameInput, hrDetailDepartmentInput, hrDetailPositionInput, hrDetailManagerInput, hrDetailDeputyInput;
  let hrDetailStatusInput, hrDetailStatusReasonInput, hrDetailEmploymentTypeInput, hrDetailEmploymentStatusIdInput, hrDetailLocationInput;
  let hrDetailHireDateInput, hrDetailTerminationDateInput, hrDetailMonthlyPayInput, hrEmployeePositionHistoryTbody, hrAdminAccountSummary, hrAdminLockBtn;
  let hrAdminUnlockBtn, hrAdminResetBtn, hrEmployeeContactForm, hrEmployeeContactSubmit, hrDetailEmailInput;
  let hrLinkAccountBtn, hrUnlinkAccountBtn, hrLinkAccountDlg, hrLinkAccountForm, hrLinkAccountSearch, hrLinkAccountResults;
  let hrPeopleAccountFilter;
  let hrLinkAccountSearchTimer = null;
  let hrDetailPhoneInput, hrEmployeeLeaveTbody, hrLeaveDlg, hrLeaveForm, hrLeaveTypeInput;
  let hrLeaveStartInput, hrLeaveEndInput, hrLeaveReasonInput, hrLeaveSubmitBtn, hrLeaveDaysTbody;
  let hrLeaveDaysSummary, hrEmployeeAttendanceTbody, hrAttendanceOverview, hrEmployeeReviewsTbody, hrEmployeeGoalsTbody;
  let hrPerformanceOverview, hrEmployeeOnboardingTbody, hrEmployeeProbationTbody, hrTalentOverview, hrEmployeeTrainingTbody;
  let hrEmployeeCertsTbody, hrEmployeeSkillsTbody, hrLearningOverview, hrEmployeeLoansTbody, hrFinanceOverview;
  let hrEmployeeProfileChangesTbody, hrEmployeeDocsTbody, hrEmployeeComplianceSummary, hrEmployeePoliciesTbody, hrEmployeeAnnouncementsTbody;
  let hrEmployeeStatusAuditTbody, hrEmployeeLeaveBalancesTbody, hrEmployeeShiftsTbody, hrEmployeeLeaveAddBtn, hrEmployeeLeaveRefreshBtn;
  let hrEmployeeShiftSummary, hrEmployeeSetShiftBtn;
  let hrEmployeeAddGoalBtn, hrEmployeeAddReviewBtn, hrEmployeeGoalCheckinBtn, hrEmployeeEnrollTrainingBtn, hrEmployeeAddOnboardingTaskBtn;
  let hrEmployeeAddProbationBtn, hrGoalDlg, hrGoalForm, hrGoalModeSelect, hrGoalExistingRow;
  let hrGoalExistingSelect, hrGoalTitleInput, hrGoalPeriodInput, hrGoalDescriptionInput, hrGoalStartInput;
  let hrGoalEndInput, hrGoalSubmitBtn, hrGoalCheckinDlg, hrGoalCheckinForm, hrGoalCheckinSelect;
  let hrGoalCheckinProgress, hrGoalCheckinNotes, hrGoalCheckinSubmit, hrReviewDlg, hrReviewForm;
  let hrReviewPeriodInput, hrReviewReviewerInput, hrReviewSubmitBtn, hrReviewReviewerOptions, hrReviewScoreInput;
  let hrReviewStrengthsInput, hrReviewImprovementsInput, hrReviewDetailDlg, hrReviewDetailTitle, hrReviewDetailMeta;
  let hrReviewDetailScore, hrReviewDetailStrengths, hrReviewDetailImprovements, hrOnboardingDlg, hrOnboardingForm;
  let hrOnboardingSelect, hrOnboardingNotes, hrOnboardingEvidenceFile, hrOnboardingEvidence, hrOnboardingSubmit, hrProbationDlg;
  let hrProbationForm, hrProbationSelect, hrProbationDateInput, hrProbationImprovementInput, hrProbationNotesInput;
  let hrProbationSubmit, hrEmployeeOptions, hrRecruitmentDlg, hrRecruitmentForm, hrRecruitmentApplicantInput;
  let hrRecruitmentVacancyInput, hrRecruitmentStageInput, hrRecruitmentNotesInput, hrRecruitmentSubmitBtn, hrRecruitmentUpdateDlg;
  let hrRecruitmentUpdateForm, hrRecruitmentUpdateIdInput, hrRecruitmentUpdateStageInput, hrRecruitmentUpdateNotesInput, hrRecruitmentUpdateSubmitBtn;
  let hrApplicantDlg, hrApplicantForm, hrApplicantNameInput, hrApplicantEmailInput, hrApplicantPhoneInput;
  let hrApplicantSourceInput, hrApplicantDegreeInput, hrApplicantExpertiseInput, hrApplicantSkillsInput, hrApplicantCompetenciesInput;
  let hrApplicantWorkExpInput, hrApplicantBioInput, hrApplicantDobInput, hrApplicantSubmitBtn, hrApplicantDetailDlg;
  let hrApplicantDetailTitle, hrApplicantDetailMeta, hrApplicantDetailTags, hrApplicantDetailBio, hrApplicantDocsTbody;
  let hrApplicantDocDlg, hrApplicantDocForm, hrApplicantDocApplicantInput, hrApplicantDocFileInput, hrApplicantDocSubmitBtn;
  let hrOnboardingCreateDlg, hrOnboardingCreateForm, hrOnboardingEmployeeInput, hrOnboardingTemplateInput, hrOnboardingStartInput;
  let hrOnboardingStatusInput, hrOnboardingCreateSubmitBtn, hrOnboardingTaskDlg, hrOnboardingTaskForm, hrOnboardingTaskOnboardingInput;
  let hrOnboardingTaskTemplateItemInput, hrOnboardingTaskAssigneeInput, hrOnboardingTaskDueInput, hrOnboardingTaskStatusInput, hrOnboardingTaskNotesInput;
  let hrOnboardingTaskSubmitBtn, hrProbationCreateDlg, hrProbationCreateForm, hrProbationEmployeeInput, hrProbationStartInput;
  let hrProbationEndInput, hrProbationStatusInput, hrProbationFrequencyInput, hrProbationCreateSubmitBtn, hrTrainingDlg;
  let hrTrainingForm, hrTrainingSessionInput, hrTrainingStatusInput, hrTrainingSubmitBtn, hrAddTrainingSessionBtn;
  let hrTrainingSessionDlg, hrTrainingSessionForm, hrTrainingCourseInput, hrTrainingCourseOptions, hrTrainingCourseNewToggle;
  let hrTrainingCourseExtra, hrTrainingCourseProviderInput, hrTrainingCourseCategoryInput, hrTrainingCourseInternalInput, hrTrainingSessionStartInput;
  let hrTrainingSessionEndInput, hrTrainingSessionLocationInput, hrTrainingSessionStatusInput, hrTrainingSessionCapacityInput, hrTrainingAttendeesSelect;
  let hrTrainingSessionSubmitBtn, hrTrainingSkillInput, hrTrainingSkillCategoryInput;
  let hrAddSkillBtn, hrAddCertBtn, hrAddSkillReqBtn;
  let hrSkillDlg, hrSkillForm, hrSkillNameInput, hrSkillCategoryInput, hrSkillScaleInput;
  let hrSkillSubmitBtn, hrEmployeeCertDlg, hrEmployeeCertForm;
  let hrEmployeeCertEmployeeInput;
  let hrEmployeeCertSubmitBtn, hrSkillReqDlg, hrSkillReqForm, hrSkillReqPositionInput;
  let hrSkillReqSkillInput, hrSkillReqLevelInput, hrSkillReqCriticalityInput, hrSkillReqSubmitBtn, hrSkillOptions;
  let hrOnboardingTemplateOptions, hrOnboardingTemplatesTbody, hrOnboardingTemplateItemsTbody, hrOnboardingTemplateItemsTitle, hrAddOnboardingTemplateBtn;
  let hrAddOnboardingTemplateItemBtn, hrOnboardingTemplateDlg, hrOnboardingTemplateForm, hrOnboardingTemplateNameInput, hrOnboardingTemplateRoleInput;
  let hrOnboardingTemplateTypeInput, hrOnboardingTemplateLocationInput, hrOnboardingTemplateDueInput, hrOnboardingTemplateStatusInput, hrOnboardingTemplateSubmitBtn;
  let hrOnboardingTemplateItemDlg, hrOnboardingTemplateItemForm, hrOnboardingTemplateItemTemplateInput, hrOnboardingTemplateItemNameInput, hrOnboardingTemplateItemDescInput;
  let hrOnboardingTemplateItemOwnerInput, hrOnboardingTemplateItemSeqInput, hrOnboardingTemplateItemOffsetInput, hrOnboardingTemplateItemEvidenceInput, hrOnboardingTemplateItemReminderInput;
  let hrOnboardingTemplateItemSubmitBtn, hrOnboardingTasksDlg, hrOnboardingTasksTitle, hrOnboardingTasksTbody, hrLoanDlg;
  let hrLoanForm, hrLoanCreatePrincipalInput, hrLoanCreateTermInput, hrLoanCreateCollateralInput, hrLoanCreateCurrencySelect, hrLoanCreateReasonInput;
  let hrLoanCreateFxInput, hrLoanCreateBaseCurrencySelect, hrLoanSubmitBtn, hrProfileChangeDlg, hrProfileChangeForm;
  let hrProfileFieldInput, hrProfileOldValueInput, hrProfileNewValueInput, hrProfileChangeSubmitBtn, hrDocUploadDlg;
  let hrDocUploadForm, hrDocFileInput, hrDocCategoryInput, hrDocUploadSubmit, hrEmployeeAddCertBtn;
  let hrEmployeeRequiredDocsTbody;
  let hrEmployeeUpdateSkillBtn, hrEmployeeAddLoanBtn;
  let hrEmployeeAddProfileChangeBtn, hrEmployeeApproveProfileChangeBtn, hrEmployeeAddDocBtn, hrDepartmentsContainer, hrPositionsContainer;
  let hrDepartmentSearch, hrDepartmentStatusFilter, hrDepartmentCount;
  let hrAddEmployeeBtn, hrEmployeeDlg, hrEmployeeForm, hrEmployeeFirstNameInput, hrEmployeeLastNameInput;
  let hrEmployeeEmailInput, hrEmployeePhoneInput, hrEmployeeDepartmentInput, hrEmployeePositionInput;
  let hrEmployeeStatusInput, hrEmployeeEmploymentTypeInput, hrEmployeeSubmitBtn, hrDepartmentOptions;
  let hrPositionOptions, hrDepartmentsTbody, hrAddDepartmentBtn, hrDepartmentDlg, hrDepartmentForm;
  let hrDepartmentNameInput, hrDepartmentCodeInput, hrDepartmentParentInput, hrDepartmentManagerInput, hrDepartmentDeputyInput;
  let hrDepartmentStatusInput, hrDepartmentSubmitBtn, hrPositionsTbody, hrAddPositionBtn, hrPositionDlg;
  let hrPositionForm, hrPositionNameInput, hrPositionCodeInput, hrPositionDeptInput, hrPositionStatusInput;
  let hrPositionEstablishedInput, hrPositionGradeInput, hrPositionDlgTitle;
  let hrGradesContainer, hrGradesTbody, hrAddGradeBtn, hrGradeDlg, hrGradeForm, hrGradeDlgTitle;
  let hrGradeCodeInput, hrGradeNameInput, hrGradeRankInput, hrGradeMinSalaryInput, hrGradeMaxSalaryInput, hrGradeStatusInput, hrGradeSubmitBtn;
  let _hrGradeEditId = null;
  let hrPositionSubmitBtn, hrGoalsTbody, hrReviewsTbody, hrRecruitmentTbody, hrOnboardingTbody;
  let hrProbationTbody, hrTrainingTbody, hrCertsTbody, hrSkillsTbody, hrSkillReqTbody;
  let hrLoansTbody, hrComplianceTbody, hrComplianceRecords, hrComplianceTraining, hrComplianceRecruitment;
  let hrApplicantOptions, hrVacancyOptions, hrApplicantsTbody, hrApplicantSearch, hrApplicantUploadBtn;
  let hrAddVacancyBtn, hrVacanciesTbody, hrVacancyDlg, hrVacancyForm, hrVacancyTitleInput;
  let hrVacancyDeptInput, hrVacancyPositionInput, hrVacancyOpeningsInput, hrVacancyStatusInput, hrVacancyDescriptionInput;
  let hrVacancySubmitBtn, hrPoliciesTbody, hrAnnouncementsTbody, hrProfileChangesTbody, activeShiftsTbody;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    hrView = document.getElementById('hrView');
    shiftTemplatesTbody = document.getElementById('shiftTemplatesTbody');
    shiftTemplateAddBtn = document.getElementById('shiftTemplateAddBtn');
    shiftRefreshBtn = document.getElementById('shiftRefreshBtn');
    shiftTemplateDlg = document.getElementById('shiftTemplateDlg');
    shiftTemplateForm = document.getElementById('shiftTemplateForm');
    shiftTemplateDlgTitle = document.getElementById('shiftTemplateDlgTitle');
    shiftTemplateIdInput = document.getElementById('shiftTemplateId');
    shiftTemplateNameInput = document.getElementById('shiftTemplateName');
    shiftTemplateGroupInput = document.getElementById('shiftTemplateGroup');
    shiftTemplateTimezoneInput = document.getElementById('shiftTemplateTimezone');
    shiftTemplateStartInput = document.getElementById('shiftTemplateStart');
    shiftTemplateEndInput = document.getElementById('shiftTemplateEnd');
    shiftTemplateDaysContainer = document.getElementById('shiftTemplateDays');
    shiftTemplateEffectiveFromInput = document.getElementById('shiftTemplateEffectiveFrom');
    shiftTemplateEffectiveToInput = document.getElementById('shiftTemplateEffectiveTo');
    shiftTemplateStatusInput = document.getElementById('shiftTemplateStatus');
    shiftTemplateGraceInput = document.getElementById('shiftTemplateGrace');
    shiftTemplateIsDefaultInput = document.getElementById('shiftTemplateIsDefault');
    shiftTemplateSubmit = document.getElementById('shiftTemplateSubmit');
    hrTabBar = document.getElementById('hrTabBar');
    hrTemplatesPanel = document.getElementById('hrTemplatesPanel');
    hrActivePanel = document.getElementById('hrActivePanel');
    hrImportPanel = document.getElementById('hrImportPanel');
    hrImportFile = document.getElementById('hrImportFile');
    hrImportPreviewBtn = document.getElementById('hrImportPreviewBtn');
    hrImportResetBtn = document.getElementById('hrImportResetBtn');
    hrImportSummary = document.getElementById('hrImportSummary');
    hrImportHint = document.getElementById('hrImportHint');
    hrImportPreviewWrap = document.getElementById('hrImportPreviewWrap');
    hrImportPreviewTbody = document.getElementById('hrImportPreviewTbody');
    hrImportCommitBtn = document.getElementById('hrImportCommitBtn');
    hrGridPanel = document.getElementById('hrGridPanel');
    hrRosterPanel = document.getElementById('hrRosterPanel');
    hrStaffPanel = document.getElementById('hrStaffPanel');
    hrStaffOn = document.getElementById('hrStaffOn');
    hrStaffSearch = document.getElementById('hrStaffSearch');
    hrStaffFilter = document.getElementById('hrStaffFilter');
    hrStaffRefreshBtn = document.getElementById('hrStaffRefreshBtn');
    hrStaffSummary = document.getElementById('hrStaffSummary');
    hrStaffTbody = document.getElementById('hrStaffTbody');
    hrStaffShiftDlg = document.getElementById('hrStaffShiftDlg');
    hrStaffShiftForm = document.getElementById('hrStaffShiftForm');
    hrStaffShiftName = document.getElementById('hrStaffShiftName');
    hrStaffShiftSelfNote = document.getElementById('hrStaffShiftSelfNote');
    hrStaffShiftTemplate = document.getElementById('hrStaffShiftTemplate');
    hrStaffShiftFrom = document.getElementById('hrStaffShiftFrom');
    hrStaffShiftNote = document.getElementById('hrStaffShiftNote');
    hrStaffShiftHistoryTbody = document.getElementById('hrStaffShiftHistoryTbody');
    hrStaffShiftSubmit = document.getElementById('hrStaffShiftSubmit');
    hrStaffShiftKind = document.getElementById('hrStaffShiftKind');
    hrStaffShiftTo = document.getElementById('hrStaffShiftTo');
    hrStaffShiftToField = document.getElementById('hrStaffShiftToField');
    hrStaffShiftFromLabel = document.getElementById('hrStaffShiftFromLabel');
    if (hrStaffShiftKind) hrStaffShiftKind.addEventListener('change', syncStaffShiftKindFields);
    hrRosterYear = document.getElementById('hrRosterYear');
    hrRosterDept = document.getElementById('hrRosterDept');
    hrRosterRefreshBtn = document.getElementById('hrRosterRefreshBtn');
    hrRosterSummary = document.getElementById('hrRosterSummary');
    hrRosterCalendar = document.getElementById('hrRosterCalendar');
    hrGridView = document.getElementById('hrGridView');
    hrGridMonth = document.getElementById('hrGridMonth');
    hrGridWeek = document.getElementById('hrGridWeek');
    hrGridMonthField = document.getElementById('hrGridMonthField');
    hrGridWeekField = document.getElementById('hrGridWeekField');
    hrGridRefreshBtn = document.getElementById('hrGridRefreshBtn');
    hrGridRecomputeBtn = document.getElementById('hrGridRecomputeBtn');
    hrGridPrintBtn = document.getElementById('hrGridPrintBtn');
    hrGridSummary = document.getElementById('hrGridSummary');
    hrGridDetail = document.getElementById('hrGridDetail');
    hrGridHead = document.getElementById('hrGridHead');
    hrGridBody = document.getElementById('hrGridBody');
    hrTimeContainer = document.getElementById('hrTimeContainer');
    hrTimeToolbar = document.getElementById('hrTimeToolbar');
    hrSectionTitle = document.getElementById('hrSectionTitle');
    hrPeoplePanel = document.getElementById('hrPeoplePanel');
    hrPerformancePanel = document.getElementById('hrPerformancePanel');
    hrTalentPanel = document.getElementById('hrTalentPanel');
    hrLearningPanel = document.getElementById('hrLearningPanel');
    hrFinancePanel = document.getElementById('hrFinancePanel');
    hrLoanStatusFilter = document.getElementById('hrLoanStatusFilter');
    hrPerformanceYear = document.getElementById('hrPerformanceYear');
    hrPerformancePeriod = document.getElementById('hrPerformancePeriod');
    hrPerformanceDept = document.getElementById('hrPerformanceDept');
    hrPerformanceRefresh = document.getElementById('hrPerformanceRefresh');
    hrPerformanceGapGoals = document.getElementById('hrPerformanceGapGoals');
    hrPerformanceGapReviews = document.getElementById('hrPerformanceGapReviews');
    hrPerformanceGoalsMet = document.getElementById('hrPerformanceGoalsMet');
    hrRecruitmentAddBtn = document.getElementById('hrRecruitmentAddBtn');
    hrRecruitmentEditBtn = document.getElementById('hrRecruitmentEditBtn');
    hrRecruitmentStageFilter = document.getElementById('hrRecruitmentStageFilter');
    hrRecruitmentAddApplicantBtn = document.getElementById('hrRecruitmentAddApplicantBtn');
    hrOnboardingAddBtn = document.getElementById('hrOnboardingAddBtn');
    hrOnboardingCompleteBtn = document.getElementById('hrOnboardingCompleteBtn');
    hrOnboardingStatusFilter = document.getElementById('hrOnboardingStatusFilter');
    hrProbationAddBtn = document.getElementById('hrProbationAddBtn');
    hrProbationStatusFilter = document.getElementById('hrProbationStatusFilter');
    hrLoanPendingCount = document.getElementById('hrLoanPendingCount');
    hrLoanOpenCount = document.getElementById('hrLoanOpenCount');
    hrLoanOutstanding = document.getElementById('hrLoanOutstanding');
    hrLoanStaffChart = document.getElementById('hrLoanStaffChart');
    hrLoanDetailDlg = document.getElementById('hrLoanDetailDlg');
    hrLoanDetailTitle = document.getElementById('hrLoanDetailTitle');
    hrLoanDetailMeta = document.getElementById('hrLoanDetailMeta');
    hrLoanDetailFields = document.getElementById('hrLoanDetailFields');
    hrLoanRepayInput = document.getElementById('hrLoanRepayInput');
    hrLoanApproveBtn = document.getElementById('hrLoanApproveBtn');
    hrLoanRejectBtn = document.getElementById('hrLoanRejectBtn');
    hrLoanRepayBtn = document.getElementById('hrLoanRepayBtn');
    hrLoanSaveBtn = document.getElementById('hrLoanSaveBtn');
    hrLoanPrincipalInput = document.getElementById('hrLoanPrincipalInput');
    hrLoanTermInput = document.getElementById('hrLoanTermInput');
    hrLoanCollateralInput = document.getElementById('hrLoanCollateralInput');
    hrLoanInterestInput = document.getElementById('hrLoanInterestInput');
    hrLoanIssueInput = document.getElementById('hrLoanIssueInput');
    hrLoanCurrencyInput = document.getElementById('hrLoanCurrencyInput');
    hrLoanFxInput = document.getElementById('hrLoanFxInput');
    hrLoanBaseCurrencyInput = document.getElementById('hrLoanBaseCurrencyInput');
    hrCompliancePanel = document.getElementById('hrCompliancePanel');
    hrEssPanel = document.getElementById('hrEssPanel');
    hrPeopleSearch = document.getElementById('hrPeopleSearch');
    hrPeopleDeptFilter = document.getElementById('hrPeopleDeptFilter');
    hrPeopleStatusFilter = document.getElementById('hrPeopleStatusFilter');
    hrPeopleTbody = document.getElementById('hrPeopleTbody');
    hrPeoplePositionFilter = document.getElementById('hrPeoplePositionFilter');
    hrPeopleEmploymentFilter = document.getElementById('hrPeopleEmploymentFilter');
    hrPeopleCount = document.getElementById('hrPeopleCount');
    hrPeopleFiltersBtn = document.getElementById('hrPeopleFiltersBtn');
    hrPeopleFiltersPanel = document.getElementById('hrPeopleFiltersPanel');
    hrPeopleFiltersCount = document.getElementById('hrPeopleFiltersCount');
    hrPeopleFiltersClear = document.getElementById('hrPeopleFiltersClear');
    hrPeoplePrev = document.getElementById('hrPeoplePrev');
    hrPeopleNext = document.getElementById('hrPeopleNext');
    hrPeoplePageInfo = document.getElementById('hrPeoplePageInfo');
    hrPeopleTabBar = document.getElementById('hrPeopleTabBar');
    hrEmployeesContainer = document.getElementById('hrEmployeesContainer');
    hrEmployeeDetailContainer = document.getElementById('hrEmployeeDetailContainer');
    hrEmployeeBackBtn = document.getElementById('hrEmployeeBackBtn');
    hrEmployeeDetailName = document.getElementById('hrEmployeeDetailName');
    hrEmployeeDetailStatus = document.getElementById('hrEmployeeDetailStatus');
    hrEmployeeDetailMeta = document.getElementById('hrEmployeeDetailMeta');
    hrEmployeeDetailDept = document.getElementById('hrEmployeeDetailDept');
    hrEmployeeDeactivateBtn = document.getElementById('hrEmployeeDeactivateBtn');
    hrEmployeeTerminateBtn = document.getElementById('hrEmployeeTerminateBtn');
    hrEmployeeReactivateBtn = document.getElementById('hrEmployeeReactivateBtn');
    hrEmployeeJobForm = document.getElementById('hrEmployeeJobForm');
    hrEmployeeJobSubmit = document.getElementById('hrEmployeeJobSubmit');
    hrDetailFirstNameInput = document.getElementById('hrDetailFirstNameInput');
    hrDetailLastNameInput = document.getElementById('hrDetailLastNameInput');
    hrDetailDepartmentInput = document.getElementById('hrDetailDepartmentInput');
    hrDetailPositionInput = document.getElementById('hrDetailPositionInput');
    hrDetailManagerInput = document.getElementById('hrDetailManagerInput');
    hrDetailDeputyInput = document.getElementById('hrDetailDeputyInput');
    hrDetailStatusInput = document.getElementById('hrDetailStatusInput');
    hrDetailStatusReasonInput = document.getElementById('hrDetailStatusReasonInput');
    hrDetailEmploymentTypeInput = document.getElementById('hrDetailEmploymentTypeInput');
    hrDetailEmploymentStatusIdInput = document.getElementById('hrDetailEmploymentStatusIdInput');
    hrDetailLocationInput = document.getElementById('hrDetailLocationInput');
    hrDetailHireDateInput = document.getElementById('hrDetailHireDateInput');
    hrDetailTerminationDateInput = document.getElementById('hrDetailTerminationDateInput');
    hrDetailMonthlyPayInput = document.getElementById('hrDetailMonthlyPayInput');
    hrEmployeePositionHistoryTbody = document.getElementById('hrEmployeePositionHistoryTbody');
    hrAdminAccountSummary = document.getElementById('hrAdminAccountSummary');
    hrAdminLockBtn = document.getElementById('hrAdminLockBtn');
    hrAdminUnlockBtn = document.getElementById('hrAdminUnlockBtn');
    hrAdminResetBtn = document.getElementById('hrAdminResetBtn');
    hrLinkAccountBtn = document.getElementById('hrLinkAccountBtn');
    hrUnlinkAccountBtn = document.getElementById('hrUnlinkAccountBtn');
    hrLinkAccountDlg = document.getElementById('hrLinkAccountDlg');
    hrLinkAccountForm = document.getElementById('hrLinkAccountForm');
    hrLinkAccountSearch = document.getElementById('hrLinkAccountSearch');
    hrLinkAccountResults = document.getElementById('hrLinkAccountResults');
    hrPeopleAccountFilter = document.getElementById('hrPeopleAccountFilter');
    hrEmployeeContactForm = document.getElementById('hrEmployeeContactForm');
    hrEmployeeContactSubmit = document.getElementById('hrEmployeeContactSubmit');
    hrDetailEmailInput = document.getElementById('hrDetailEmailInput');
    hrDetailPhoneInput = document.getElementById('hrDetailPhoneInput');
    hrEmployeeLeaveTbody = document.getElementById('hrEmployeeLeaveTbody');
    hrLeaveDlg = document.getElementById('hrLeaveDlg');
    hrLeaveForm = document.getElementById('hrLeaveForm');
    hrLeaveTypeInput = document.getElementById('hrLeaveTypeInput');
    hrLeaveStartInput = document.getElementById('hrLeaveStartInput');
    hrLeaveEndInput = document.getElementById('hrLeaveEndInput');
    hrLeaveReasonInput = document.getElementById('hrLeaveReasonInput');
    hrLeaveSubmitBtn = document.getElementById('hrLeaveSubmitBtn');
    hrLeaveDaysTbody = document.getElementById('hrLeaveDaysTbody');
    hrLeaveDaysSummary = document.getElementById('hrLeaveDaysSummary');
    hrEmployeeAttendanceTbody = document.getElementById('hrEmployeeAttendanceTbody');
    hrAttendanceOverview = document.getElementById('hrAttendanceOverview');
    hrEmployeeReviewsTbody = document.getElementById('hrEmployeeReviewsTbody');
    hrEmployeeGoalsTbody = document.getElementById('hrEmployeeGoalsTbody');
    hrPerformanceOverview = document.getElementById('hrPerformanceOverview');
    hrEmployeeOnboardingTbody = document.getElementById('hrEmployeeOnboardingTbody');
    hrEmployeeProbationTbody = document.getElementById('hrEmployeeProbationTbody');
    hrTalentOverview = document.getElementById('hrTalentOverview');
    hrEmployeeTrainingTbody = document.getElementById('hrEmployeeTrainingTbody');
    hrEmployeeCertsTbody = document.getElementById('hrEmployeeCertsTbody');
    hrEmployeeSkillsTbody = document.getElementById('hrEmployeeSkillsTbody');
    hrLearningOverview = document.getElementById('hrLearningOverview');
    hrEmployeeLoansTbody = document.getElementById('hrEmployeeLoansTbody');
    hrFinanceOverview = document.getElementById('hrFinanceOverview');
    hrEmployeeProfileChangesTbody = document.getElementById('hrEmployeeProfileChangesTbody');
    hrEmployeeDocsTbody = document.getElementById('hrEmployeeDocsTbody');
    hrEmployeeComplianceSummary = document.getElementById('hrEmployeeComplianceSummary');
    hrEmployeePoliciesTbody = document.getElementById('hrEmployeePoliciesTbody');
    hrEmployeeAnnouncementsTbody = document.getElementById('hrEmployeeAnnouncementsTbody');
    hrEmployeeStatusAuditTbody = document.getElementById('hrEmployeeStatusAuditTbody');
    hrEmployeeLeaveBalancesTbody = document.getElementById('hrEmployeeLeaveBalancesTbody');
    hrEmployeeShiftsTbody = document.getElementById('hrEmployeeShiftsTbody');
    hrEmployeeShiftSummary = document.getElementById('hrEmployeeShiftSummary');
    hrEmployeeSetShiftBtn = document.getElementById('hrEmployeeSetShiftBtn');
    hrEmployeeLeaveAddBtn = document.getElementById('hrEmployeeLeaveAddBtn');
    hrEmployeeLeaveRefreshBtn = document.getElementById('hrEmployeeLeaveRefreshBtn');
    hrEmployeeAddGoalBtn = document.getElementById('hrEmployeeAddGoalBtn');
    hrEmployeeAddReviewBtn = document.getElementById('hrEmployeeAddReviewBtn');
    hrEmployeeGoalCheckinBtn = document.getElementById('hrEmployeeGoalCheckinBtn');
    hrEmployeeEnrollTrainingBtn = document.getElementById('hrEmployeeEnrollTrainingBtn');
    hrEmployeeAddOnboardingTaskBtn = document.getElementById('hrEmployeeAddOnboardingTaskBtn');
    hrEmployeeAddProbationBtn = document.getElementById('hrEmployeeAddProbationBtn');
    hrGoalDlg = document.getElementById('hrGoalDlg');
    hrGoalForm = document.getElementById('hrGoalForm');
    hrGoalModeSelect = document.getElementById('hrGoalModeSelect');
    hrGoalExistingRow = document.getElementById('hrGoalExistingRow');
    hrGoalExistingSelect = document.getElementById('hrGoalExistingSelect');
    hrGoalTitleInput = document.getElementById('hrGoalTitleInput');
    hrGoalPeriodInput = document.getElementById('hrGoalPeriodInput');
    hrGoalDescriptionInput = document.getElementById('hrGoalDescriptionInput');
    hrGoalStartInput = document.getElementById('hrGoalStartInput');
    hrGoalEndInput = document.getElementById('hrGoalEndInput');
    hrGoalSubmitBtn = document.getElementById('hrGoalSubmitBtn');
    hrGoalCheckinDlg = document.getElementById('hrGoalCheckinDlg');
    hrGoalCheckinForm = document.getElementById('hrGoalCheckinForm');
    hrGoalCheckinSelect = document.getElementById('hrGoalCheckinSelect');
    hrGoalCheckinProgress = document.getElementById('hrGoalCheckinProgress');
    hrGoalCheckinNotes = document.getElementById('hrGoalCheckinNotes');
    hrGoalCheckinSubmit = document.getElementById('hrGoalCheckinSubmit');
    hrReviewDlg = document.getElementById('hrReviewDlg');
    hrReviewForm = document.getElementById('hrReviewForm');
    hrReviewPeriodInput = document.getElementById('hrReviewPeriodInput');
    hrReviewReviewerInput = document.getElementById('hrReviewReviewerInput');
    hrReviewSubmitBtn = document.getElementById('hrReviewSubmitBtn');
    hrReviewReviewerOptions = document.getElementById('hrReviewReviewerOptions');
    hrReviewScoreInput = document.getElementById('hrReviewScoreInput');
    hrReviewStrengthsInput = document.getElementById('hrReviewStrengthsInput');
    hrReviewImprovementsInput = document.getElementById('hrReviewImprovementsInput');
    hrReviewDetailDlg = document.getElementById('hrReviewDetailDlg');
    hrReviewDetailTitle = document.getElementById('hrReviewDetailTitle');
    hrReviewDetailMeta = document.getElementById('hrReviewDetailMeta');
    hrReviewDetailScore = document.getElementById('hrReviewDetailScore');
    hrReviewDetailStrengths = document.getElementById('hrReviewDetailStrengths');
    hrReviewDetailImprovements = document.getElementById('hrReviewDetailImprovements');
    hrOnboardingDlg = document.getElementById('hrOnboardingDlg');
    hrOnboardingForm = document.getElementById('hrOnboardingForm');
    hrOnboardingSelect = document.getElementById('hrOnboardingSelect');
    hrOnboardingNotes = document.getElementById('hrOnboardingNotes');
    hrOnboardingEvidenceFile = document.getElementById('hrOnboardingEvidenceFile');
    hrOnboardingEvidence = hrOnboardingEvidenceFile; // alias for legacy references
    hrOnboardingSubmit = document.getElementById('hrOnboardingSubmit');
    hrProbationDlg = document.getElementById('hrProbationDlg');
    hrProbationForm = document.getElementById('hrProbationForm');
    hrProbationSelect = document.getElementById('hrProbationSelect');
    hrProbationDateInput = document.getElementById('hrProbationDateInput');
    hrProbationImprovementInput = document.getElementById('hrProbationImprovementInput');
    hrProbationNotesInput = document.getElementById('hrProbationNotesInput');
    hrProbationSubmit = document.getElementById('hrProbationSubmit');
    hrEmployeeOptions = document.getElementById('hrEmployeeOptions');
    hrRecruitmentDlg = document.getElementById('hrRecruitmentDlg');
    hrRecruitmentForm = document.getElementById('hrRecruitmentForm');
    hrRecruitmentApplicantInput = document.getElementById('hrRecruitmentApplicantInput');
    hrRecruitmentVacancyInput = document.getElementById('hrRecruitmentVacancyInput');
    hrRecruitmentStageInput = document.getElementById('hrRecruitmentStageInput');
    hrRecruitmentNotesInput = document.getElementById('hrRecruitmentNotesInput');
    hrRecruitmentSubmitBtn = document.getElementById('hrRecruitmentSubmitBtn');
    hrRecruitmentUpdateDlg = document.getElementById('hrRecruitmentUpdateDlg');
    hrRecruitmentUpdateForm = document.getElementById('hrRecruitmentUpdateForm');
    hrRecruitmentUpdateIdInput = document.getElementById('hrRecruitmentUpdateIdInput');
    hrRecruitmentUpdateStageInput = document.getElementById('hrRecruitmentUpdateStageInput');
    hrRecruitmentUpdateNotesInput = document.getElementById('hrRecruitmentUpdateNotesInput');
    hrRecruitmentUpdateSubmitBtn = document.getElementById('hrRecruitmentUpdateSubmitBtn');
    hrApplicantDlg = document.getElementById('hrApplicantDlg');
    hrApplicantForm = document.getElementById('hrApplicantForm');
    hrApplicantNameInput = document.getElementById('hrApplicantNameInput');
    hrApplicantEmailInput = document.getElementById('hrApplicantEmailInput');
    hrApplicantPhoneInput = document.getElementById('hrApplicantPhoneInput');
    hrApplicantSourceInput = document.getElementById('hrApplicantSourceInput');
    hrApplicantDegreeInput = document.getElementById('hrApplicantDegreeInput');
    hrApplicantExpertiseInput = document.getElementById('hrApplicantExpertiseInput');
    hrApplicantSkillsInput = document.getElementById('hrApplicantSkillsInput');
    hrApplicantCompetenciesInput = document.getElementById('hrApplicantCompetenciesInput');
    hrApplicantWorkExpInput = document.getElementById('hrApplicantWorkExpInput');
    hrApplicantBioInput = document.getElementById('hrApplicantBioInput');
    hrApplicantDobInput = document.getElementById('hrApplicantDobInput');
    hrApplicantSubmitBtn = document.getElementById('hrApplicantSubmitBtn');
    hrApplicantDetailDlg = document.getElementById('hrApplicantDetailDlg');
    hrApplicantDetailTitle = document.getElementById('hrApplicantDetailTitle');
    hrApplicantDetailMeta = document.getElementById('hrApplicantDetailMeta');
    hrApplicantDetailTags = document.getElementById('hrApplicantDetailTags');
    hrApplicantDetailBio = document.getElementById('hrApplicantDetailBio');
    hrApplicantDocsTbody = document.getElementById('hrApplicantDocsTbody');
    hrApplicantDocDlg = document.getElementById('hrApplicantDocDlg');
    hrApplicantDocForm = document.getElementById('hrApplicantDocForm');
    hrApplicantDocApplicantInput = document.getElementById('hrApplicantDocApplicantInput');
    hrApplicantDocFileInput = document.getElementById('hrApplicantDocFileInput');
    hrApplicantDocSubmitBtn = document.getElementById('hrApplicantDocSubmitBtn');
    hrOnboardingCreateDlg = document.getElementById('hrOnboardingCreateDlg');
    hrOnboardingCreateForm = document.getElementById('hrOnboardingCreateForm');
    hrOnboardingEmployeeInput = document.getElementById('hrOnboardingEmployeeInput');
    hrOnboardingTemplateInput = document.getElementById('hrOnboardingTemplateInput');
    hrOnboardingStartInput = document.getElementById('hrOnboardingStartInput');
    hrOnboardingStatusInput = document.getElementById('hrOnboardingStatusInput');
    hrOnboardingCreateSubmitBtn = document.getElementById('hrOnboardingCreateSubmitBtn');
    hrOnboardingTaskDlg = document.getElementById('hrOnboardingTaskDlg');
    hrOnboardingTaskForm = document.getElementById('hrOnboardingTaskForm');
    hrOnboardingTaskOnboardingInput = document.getElementById('hrOnboardingTaskOnboardingInput');
    hrOnboardingTaskTemplateItemInput = document.getElementById('hrOnboardingTaskTemplateItemInput');
    hrOnboardingTaskAssigneeInput = document.getElementById('hrOnboardingTaskAssigneeInput');
    hrOnboardingTaskDueInput = document.getElementById('hrOnboardingTaskDueInput');
    hrOnboardingTaskStatusInput = document.getElementById('hrOnboardingTaskStatusInput');
    hrOnboardingTaskNotesInput = document.getElementById('hrOnboardingTaskNotesInput');
    hrOnboardingTaskSubmitBtn = document.getElementById('hrOnboardingTaskSubmitBtn');
    hrProbationCreateDlg = document.getElementById('hrProbationCreateDlg');
    hrProbationCreateForm = document.getElementById('hrProbationCreateForm');
    hrProbationEmployeeInput = document.getElementById('hrProbationEmployeeInput');
    hrProbationStartInput = document.getElementById('hrProbationStartInput');
    hrProbationEndInput = document.getElementById('hrProbationEndInput');
    hrProbationStatusInput = document.getElementById('hrProbationStatusInput');
    hrProbationFrequencyInput = document.getElementById('hrProbationFrequencyInput');
    hrProbationCreateSubmitBtn = document.getElementById('hrProbationCreateSubmitBtn');
    hrTrainingDlg = document.getElementById('hrTrainingDlg');
    hrTrainingForm = document.getElementById('hrTrainingForm');
    hrTrainingSessionInput = document.getElementById('hrTrainingSessionInput');
    hrTrainingStatusInput = document.getElementById('hrTrainingStatusInput');
    hrTrainingSubmitBtn = document.getElementById('hrTrainingSubmitBtn');
    hrAddTrainingSessionBtn = document.getElementById('hrAddTrainingSessionBtn');
    hrTrainingSessionDlg = document.getElementById('hrTrainingSessionDlg');
    hrTrainingSessionForm = document.getElementById('hrTrainingSessionForm');
    hrTrainingCourseInput = document.getElementById('hrTrainingCourseInput');
    hrTrainingCourseOptions = document.getElementById('hrTrainingCourseOptions');
    hrTrainingCourseNewToggle = document.getElementById('hrTrainingCourseNewToggle');
    hrTrainingCourseExtra = document.getElementById('hrTrainingCourseExtra');
    hrTrainingCourseProviderInput = document.getElementById('hrTrainingCourseProviderInput');
    hrTrainingCourseCategoryInput = document.getElementById('hrTrainingCourseCategoryInput');
    hrTrainingCourseInternalInput = document.getElementById('hrTrainingCourseInternalInput');
    hrTrainingSessionStartInput = document.getElementById('hrTrainingSessionStartInput');
    hrTrainingSessionEndInput = document.getElementById('hrTrainingSessionEndInput');
    hrTrainingSessionLocationInput = document.getElementById('hrTrainingSessionLocationInput');
    hrTrainingSessionStatusInput = document.getElementById('hrTrainingSessionStatusInput');
    hrTrainingSessionCapacityInput = document.getElementById('hrTrainingSessionCapacityInput');
    hrTrainingAttendeesSelect = document.getElementById('hrTrainingAttendeesSelect');
    hrTrainingSessionSubmitBtn = document.getElementById('hrTrainingSessionSubmitBtn');
    hrTrainingSkillInput = document.getElementById('hrTrainingSkillInput');
    hrTrainingSkillCategoryInput = document.getElementById('hrTrainingSkillCategoryInput');
    hrAddSkillBtn = document.getElementById('hrAddSkillBtn');
    hrAddCertBtn = document.getElementById('hrAddCertBtn');
    hrAddSkillReqBtn = document.getElementById('hrAddSkillReqBtn');
    hrSkillDlg = document.getElementById('hrSkillDlg');
    hrSkillForm = document.getElementById('hrSkillForm');
    hrSkillNameInput = document.getElementById('hrSkillNameInput');
    hrSkillCategoryInput = document.getElementById('hrSkillCategoryInput');
    hrSkillScaleInput = document.getElementById('hrSkillScaleInput');
    hrSkillSubmitBtn = document.getElementById('hrSkillSubmitBtn');
    hrEmployeeCertDlg = document.getElementById('hrEmployeeCertDlg');
    hrEmployeeCertForm = document.getElementById('hrEmployeeCertForm');
    hrEmployeeCertEmployeeInput = document.getElementById('hrEmployeeCertEmployeeInput');
    hrEmployeeCertSubmitBtn = document.getElementById('hrEmployeeCertSubmitBtn');
    hrSkillReqDlg = document.getElementById('hrSkillReqDlg');
    hrSkillReqForm = document.getElementById('hrSkillReqForm');
    hrSkillReqPositionInput = document.getElementById('hrSkillReqPositionInput');
    hrSkillReqSkillInput = document.getElementById('hrSkillReqSkillInput');
    hrSkillReqLevelInput = document.getElementById('hrSkillReqLevelInput');
    hrSkillReqCriticalityInput = document.getElementById('hrSkillReqCriticalityInput');
    hrSkillReqSubmitBtn = document.getElementById('hrSkillReqSubmitBtn');
    hrSkillOptions = document.getElementById('hrSkillOptions');
    hrOnboardingTemplateOptions = document.getElementById('hrOnboardingTemplateOptions');
    hrOnboardingTemplatesTbody = document.getElementById('hrOnboardingTemplatesTbody');
    hrOnboardingTemplateItemsTbody = document.getElementById('hrOnboardingTemplateItemsTbody');
    hrOnboardingTemplateItemsTitle = document.getElementById('hrOnboardingTemplateItemsTitle');
    hrAddOnboardingTemplateBtn = document.getElementById('hrAddOnboardingTemplateBtn');
    hrAddOnboardingTemplateItemBtn = document.getElementById('hrAddOnboardingTemplateItemBtn');
    hrOnboardingTemplateDlg = document.getElementById('hrOnboardingTemplateDlg');
    hrOnboardingTemplateForm = document.getElementById('hrOnboardingTemplateForm');
    hrOnboardingTemplateNameInput = document.getElementById('hrOnboardingTemplateNameInput');
    hrOnboardingTemplateRoleInput = document.getElementById('hrOnboardingTemplateRoleInput');
    hrOnboardingTemplateTypeInput = document.getElementById('hrOnboardingTemplateTypeInput');
    hrOnboardingTemplateLocationInput = document.getElementById('hrOnboardingTemplateLocationInput');
    hrOnboardingTemplateDueInput = document.getElementById('hrOnboardingTemplateDueInput');
    hrOnboardingTemplateStatusInput = document.getElementById('hrOnboardingTemplateStatusInput');
    hrOnboardingTemplateSubmitBtn = document.getElementById('hrOnboardingTemplateSubmitBtn');
    hrOnboardingTemplateItemDlg = document.getElementById('hrOnboardingTemplateItemDlg');
    hrOnboardingTemplateItemForm = document.getElementById('hrOnboardingTemplateItemForm');
    hrOnboardingTemplateItemTemplateInput = document.getElementById('hrOnboardingTemplateItemTemplateInput');
    hrOnboardingTemplateItemNameInput = document.getElementById('hrOnboardingTemplateItemNameInput');
    hrOnboardingTemplateItemDescInput = document.getElementById('hrOnboardingTemplateItemDescInput');
    hrOnboardingTemplateItemOwnerInput = document.getElementById('hrOnboardingTemplateItemOwnerInput');
    hrOnboardingTemplateItemSeqInput = document.getElementById('hrOnboardingTemplateItemSeqInput');
    hrOnboardingTemplateItemOffsetInput = document.getElementById('hrOnboardingTemplateItemOffsetInput');
    hrOnboardingTemplateItemEvidenceInput = document.getElementById('hrOnboardingTemplateItemEvidenceInput');
    hrOnboardingTemplateItemReminderInput = document.getElementById('hrOnboardingTemplateItemReminderInput');
    hrOnboardingTemplateItemSubmitBtn = document.getElementById('hrOnboardingTemplateItemSubmitBtn');
    hrOnboardingTasksDlg = document.getElementById('hrOnboardingTasksDlg');
    hrOnboardingTasksTitle = document.getElementById('hrOnboardingTasksTitle');
    hrOnboardingTasksTbody = document.getElementById('hrOnboardingTasksTbody');
    hrLoanDlg = document.getElementById('hrLoanDlg');
    hrLoanForm = document.getElementById('hrLoanForm');
    hrLoanCreatePrincipalInput = document.getElementById('hrLoanCreatePrincipalInput');
    hrLoanCreateTermInput = document.getElementById('hrLoanCreateTermInput');
    hrLoanCreateCollateralInput = document.getElementById('hrLoanCreateCollateralInput');
    hrLoanCreateReasonInput = document.getElementById('hrLoanCreateReasonInput');
    hrLoanCreateCurrencySelect = document.getElementById('hrLoanCreateCurrencySelect');
    hrLoanCreateFxInput = document.getElementById('hrLoanCreateFxInput');
    hrLoanCreateBaseCurrencySelect = document.getElementById('hrLoanCreateBaseCurrencySelect');
    hrLoanSubmitBtn = document.getElementById('hrLoanSubmitBtn');
    hrProfileChangeDlg = document.getElementById('hrProfileChangeDlg');
    hrProfileChangeForm = document.getElementById('hrProfileChangeForm');
    hrProfileFieldInput = document.getElementById('hrProfileFieldInput');
    hrProfileOldValueInput = document.getElementById('hrProfileOldValueInput');
    hrProfileNewValueInput = document.getElementById('hrProfileNewValueInput');
    hrProfileChangeSubmitBtn = document.getElementById('hrProfileChangeSubmitBtn');
    hrDocUploadDlg = document.getElementById('hrDocUploadDlg');
    hrDocUploadForm = document.getElementById('hrDocUploadForm');
    hrDocFileInput = document.getElementById('hrDocFileInput');
    hrDocCategoryInput = document.getElementById('hrDocCategoryInput');
    hrDocUploadSubmit = document.getElementById('hrDocUploadSubmit');
    hrEmployeeRequiredDocsTbody = document.getElementById('hrEmployeeRequiredDocsTbody');
    hrEmployeeAddCertBtn = document.getElementById('hrEmployeeAddCertBtn');
    hrEmployeeUpdateSkillBtn = document.getElementById('hrEmployeeUpdateSkillBtn');
    hrEmployeeAddLoanBtn = document.getElementById('hrEmployeeAddLoanBtn');
    hrEmployeeAddProfileChangeBtn = document.getElementById('hrEmployeeAddProfileChangeBtn');
    hrEmployeeApproveProfileChangeBtn = document.getElementById('hrEmployeeApproveProfileChangeBtn');
    hrEmployeeAddDocBtn = document.getElementById('hrEmployeeAddDocBtn');
    hrDepartmentsContainer = document.getElementById('hrDepartmentsContainer');
    hrDepartmentSearch = document.getElementById('hrDepartmentSearch');
    hrDepartmentStatusFilter = document.getElementById('hrDepartmentStatusFilter');
    hrDepartmentCount = document.getElementById('hrDepartmentCount');
    hrPositionsContainer = document.getElementById('hrPositionsContainer');
    hrAddEmployeeBtn = document.getElementById('hrAddEmployeeBtn');
    hrEmployeeDlg = document.getElementById('hrEmployeeDlg');
    hrEmployeeForm = document.getElementById('hrEmployeeForm');
    hrEmployeeFirstNameInput = document.getElementById('hrEmployeeFirstName');
    hrEmployeeLastNameInput = document.getElementById('hrEmployeeLastName');
    hrEmployeeEmailInput = document.getElementById('hrEmployeeEmail');
    hrEmployeePhoneInput = document.getElementById('hrEmployeePhone');
    hrEmployeeDepartmentInput = document.getElementById('hrEmployeeDepartment');
    hrEmployeePositionInput = document.getElementById('hrEmployeePosition');
    hrEmployeeStatusInput = document.getElementById('hrEmployeeStatus');
    hrEmployeeEmploymentTypeInput = document.getElementById('hrEmployeeEmploymentType');
    hrEmployeeSubmitBtn = document.getElementById('hrEmployeeSubmit');
    hrDepartmentOptions = document.getElementById('hrDepartmentOptions');
    hrPositionOptions = document.getElementById('hrPositionOptions');
    hrDepartmentsTbody = document.getElementById('hrDepartmentsTbody');
    hrAddDepartmentBtn = document.getElementById('hrAddDepartmentBtn');
    hrDepartmentDlg = document.getElementById('hrDepartmentDlg');
    hrDepartmentForm = document.getElementById('hrDepartmentForm');
    hrDepartmentNameInput = document.getElementById('hrDepartmentNameInput');
    hrDepartmentCodeInput = document.getElementById('hrDepartmentCodeInput');
    hrDepartmentParentInput = document.getElementById('hrDepartmentParentInput');
    hrDepartmentManagerInput = document.getElementById('hrDepartmentManagerInput');
    hrDepartmentDeputyInput = document.getElementById('hrDepartmentDeputyInput');
    hrDepartmentStatusInput = document.getElementById('hrDepartmentStatusInput');
    hrDepartmentSubmitBtn = document.getElementById('hrDepartmentSubmit');
    hrPositionsTbody = document.getElementById('hrPositionsTbody');
    hrPositionSearch = document.getElementById('hrPositionSearch');
    hrPositionDeptFilter = document.getElementById('hrPositionDeptFilter');
    hrPositionVacancyFilter = document.getElementById('hrPositionVacancyFilter');
    hrPositionCount = document.getElementById('hrPositionCount');
    hrAddPositionBtn = document.getElementById('hrAddPositionBtn');
    hrPositionDlg = document.getElementById('hrPositionDlg');
    hrPositionForm = document.getElementById('hrPositionForm');
    hrPositionNameInput = document.getElementById('hrPositionNameInput');
    hrPositionCodeInput = document.getElementById('hrPositionCodeInput');
    hrPositionDeptInput = document.getElementById('hrPositionDeptInput');
    hrPositionStatusInput = document.getElementById('hrPositionStatusInput');
    hrPositionEstablishedInput = document.getElementById('hrPositionEstablishedInput');
    hrPositionGradeInput = document.getElementById('hrPositionGradeInput');
    hrPositionDlgTitle = document.getElementById('hrPositionDlgTitle');
    hrPositionSubmitBtn = document.getElementById('hrPositionSubmit');
    hrGradesContainer = document.getElementById('hrGradesContainer');
    hrGradesTbody = document.getElementById('hrGradesTbody');
    hrAddGradeBtn = document.getElementById('hrAddGradeBtn');
    hrGradeDlg = document.getElementById('hrGradeDlg');
    hrGradeForm = document.getElementById('hrGradeForm');
    hrGradeDlgTitle = document.getElementById('hrGradeDlgTitle');
    hrGradeCodeInput = document.getElementById('hrGradeCodeInput');
    hrGradeNameInput = document.getElementById('hrGradeNameInput');
    hrGradeRankInput = document.getElementById('hrGradeRankInput');
    hrGradeMinSalaryInput = document.getElementById('hrGradeMinSalaryInput');
    hrGradeMaxSalaryInput = document.getElementById('hrGradeMaxSalaryInput');
    hrGradeStatusInput = document.getElementById('hrGradeStatusInput');
    hrGradeSubmitBtn = document.getElementById('hrGradeSubmit');
    hrGoalsTbody = document.getElementById('hrGoalsTbody');
    hrReviewsTbody = document.getElementById('hrReviewsTbody');
    hrAiScoresSection = document.getElementById('hrAiScoresSection');
    hrAiScoresCohortTabs = document.getElementById('hrAiScoresCohortTabs');
    hrAiScoresTbody = document.getElementById('hrAiScoresTbody');
    hrAiScoresFlagCol = document.getElementById('hrAiScoresFlagCol');
    hrAiScoresKpiAvg = document.getElementById('hrAiScoresKpiAvg');
    hrAiScoresKpiCoverage = document.getElementById('hrAiScoresKpiCoverage');
    hrAiScoresKpiLowCount = document.getElementById('hrAiScoresKpiLowCount');
    hrAiScoresRecompute = document.getElementById('hrAiScoresRecompute');
    hrAiScoresYear = document.getElementById('hrAiScoresYear');
    hrAiScoresMonth = document.getElementById('hrAiScoresMonth');
    hrAiScoresYtdTbody = document.getElementById('hrAiScoresYtdTbody');
    hrAiScoresYtdFlagCol = document.getElementById('hrAiScoresYtdFlagCol');
    hrAiScoresYtdMeta = document.getElementById('hrAiScoresYtdMeta');
    hrRecruitmentTbody = document.getElementById('hrRecruitmentTbody');
    hrOnboardingTbody = document.getElementById('hrOnboardingTbody');
    hrProbationTbody = document.getElementById('hrProbationTbody');
    hrTrainingTbody = document.getElementById('hrTrainingTbody');
    hrCertsTbody = document.getElementById('hrCertsTbody');
    hrSkillsTbody = document.getElementById('hrSkillsTbody');
    hrSkillReqTbody = document.getElementById('hrSkillReqTbody');
    hrLoansTbody = document.getElementById('hrLoansTbody');
    hrComplianceTbody = document.getElementById('hrComplianceTbody');
    hrComplianceRecords = document.getElementById('hrComplianceRecords');
    hrComplianceTraining = document.getElementById('hrComplianceTraining');
    hrComplianceRecruitment = document.getElementById('hrComplianceRecruitment');
    hrApplicantOptions = document.getElementById('hrApplicantOptions');
    hrVacancyOptions = document.getElementById('hrVacancyOptions');
    hrApplicantsTbody = document.getElementById('hrApplicantsTbody');
    hrApplicantSearch = document.getElementById('hrApplicantSearch');
    hrApplicantUploadBtn = document.getElementById('hrApplicantUploadBtn');
    hrAddVacancyBtn = document.getElementById('hrAddVacancyBtn');
    hrVacanciesTbody = document.getElementById('hrVacanciesTbody');
    hrVacancyDlg = document.getElementById('hrVacancyDlg');
    hrVacancyForm = document.getElementById('hrVacancyForm');
    hrVacancyTitleInput = document.getElementById('hrVacancyTitleInput');
    hrVacancyDeptInput = document.getElementById('hrVacancyDeptInput');
    hrVacancyPositionInput = document.getElementById('hrVacancyPositionInput');
    hrVacancyOpeningsInput = document.getElementById('hrVacancyOpeningsInput');
    hrVacancyStatusInput = document.getElementById('hrVacancyStatusInput');
    hrVacancyDescriptionInput = document.getElementById('hrVacancyDescriptionInput');
    hrVacancySubmitBtn = document.getElementById('hrVacancySubmitBtn');
    hrPoliciesTbody = document.getElementById('hrPoliciesTbody');
    hrAnnouncementsTbody = document.getElementById('hrAnnouncementsTbody');
    hrProfileChangesTbody = document.getElementById('hrProfileChangesTbody');
    activeShiftsTbody = document.getElementById('activeShiftsTbody');
  }

  // ---- Functions (verbatim from admin.js lines 7399-12104) ------------
    function detectDefaultTimezone() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz || 'UTC';
      } catch (err) {
        return 'UTC';
      }
    }

    function normalizeTimeInput(value, fallback) {
      const raw = (value == null ? '' : String(value)).trim();
      if (!raw) return fallback;
      const match = raw.match(/^(\d{1,2}):(\d{2})/);
      if (!match) return fallback;
      const hours = match[1].padStart(2, '0');
      const minutes = match[2].padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    function normalizeDateValue(value) {
      if (!value && value !== 0) return '';
      const raw = String(value).trim();
      if (!raw) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : '';
    }

    function formatTimeDisplay(value) {
      const normalized = normalizeTimeInput(value, '');
      return normalized || '—';
    }

    function formatShiftTemplateDays(days) {
      if (!Array.isArray(days) || !days.length) return '—';
      const unique = [...new Set(days.map((day) => Number(day)))].filter((day) => Number.isFinite(day));
      if (unique.length >= 7) return 'Daily';
      const labels = unique
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_LABELS[day] || `Day ${day + 1}`);
      return labels.join(' · ');
    }

    function getShiftTemplateDayCheckboxes() {
      if (!shiftTemplateDaysContainer) return [];
      return Array.from(shiftTemplateDaysContainer.querySelectorAll('input[type="checkbox"]'));
    }

    function setShiftTemplateDaySelections(selectedDays) {
      const checkboxes = getShiftTemplateDayCheckboxes();
      const selected = new Set((selectedDays || []).map((day) => Number(day)));
      checkboxes.forEach((checkbox) => {
        const day = Number(checkbox.value);
        checkbox.checked = selected.has(day);
      });
    }

    function collectShiftTemplateDaySelections() {
      const checkboxes = getShiftTemplateDayCheckboxes();
      return checkboxes
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => Number(checkbox.value))
        .filter((day) => Number.isFinite(day));
    }

    function renderShiftTemplates(items = []) {
      if (!shiftTemplatesTbody) return;
      const list = Array.isArray(items) ? items : [];
      shiftTemplatesState.items = list;
      shiftTemplatesTbody.innerHTML = '';
      if (!list.length) {
        shiftTemplatesTbody.innerHTML = '<tr><td colspan="11" style="color:var(--sub);">No staff shifts configured</td></tr>';
        return;
      }
      list.forEach((template, index) => {
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td>${index + 1}</td>
          <td>${escapeHtml(template?.name || '')}</td>
          <td>${escapeHtml(STAFF_GROUP_LABELS[template?.staff_group] || template?.staff_group || '')}</td>
          <td>${escapeHtml(template?.timezone || '—')}</td>
          <td>${escapeHtml(formatTimeDisplay(template?.start_time))}</td>
          <td>${escapeHtml(formatTimeDisplay(template?.end_time))}</td>
          <td>${escapeHtml(formatShiftTemplateDays(template?.days_of_week || []))}</td>
          <td>${template?.is_default ? '<span class="badge">Default</span>' : '—'}</td>
          <td>${escapeHtml(String(template?.grace_minutes ?? 0))} min</td>
          <td>${escapeHtml(template?.is_active === false ? 'Inactive' : 'Active')}</td>
          <td>
            <div class="table-actions">
              <button class="btn" data-shift-template-action="edit" data-id="${template?.id}">Edit</button>
              <button class="btn danger" data-shift-template-action="remove" data-id="${template?.id}">Delete</button>
            </div>
          </td>
        `;
        shiftTemplatesTbody.appendChild(tr);
      });
    }

    async function loadShiftTemplates(options = {}) {
      if (!shiftTemplatesTbody) return;
      const { silent = false } = options;
      shiftTemplatesState.loading = true;
      if (!silent) {
        shiftTemplatesTbody.innerHTML = '<tr><td colspan="11" style="color:var(--sub);">Loading…</td></tr>';
      }
      try {
        const response = await adminAPI.fetch(API.adminShiftTemplates, { method: 'GET' });
        const items = Array.isArray(response?.items) ? response.items : (Array.isArray(response) ? response : []);
        renderShiftTemplates(items);
      } catch (err) {
        renderShiftTemplates([]);
        const message = err && err.message ? err.message : 'Failed to load staff shifts.';
        showFlash(message, 'error');
      } finally {
        shiftTemplatesState.loading = false;
      }
    }

    // ---- Biometric weekly attendance import ----------------------------

    function resetAttendanceImport() {
      hrImportState = { file: null, preview: null, busy: false };
      if (hrImportFile) hrImportFile.value = '';
      if (hrImportSummary) { hrImportSummary.style.display = 'none'; hrImportSummary.innerHTML = ''; }
      if (hrImportPreviewWrap) hrImportPreviewWrap.style.display = 'none';
      if (hrImportPreviewTbody) {
        hrImportPreviewTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No file previewed yet.</td></tr>';
      }
    }

    function ensureImportEmployees() {
      if (hrDataState.people && Array.isArray(hrPeopleList) && hrPeopleList.length) return Promise.resolve();
      return loadHrPeople().catch(() => {});
    }

    function attendanceMappingSelect(userId, selectedId) {
      const list = Array.isArray(hrPeopleList) ? hrPeopleList : [];
      const opts = ['<option value="">— Skip (do not import) —</option>'];
      list.forEach((p) => {
        const label = `${p.first_name || ''} ${p.last_name || ''}`.trim() || String(p.id);
        const sel = String(p.id) === String(selectedId || '') ? ' selected' : '';
        opts.push(`<option value="${escapeHtml(String(p.id))}"${sel}>${escapeHtml(label)}</option>`);
      });
      return `<select class="hr-import-map" data-uid="${escapeHtml(String(userId))}">${opts.join('')}</select>`;
    }

    function attendanceDayBadges(days) {
      return (days || []).map((d) => {
        let txt = d.status;
        if (d.early_departure) txt = `left early (${d.early_minutes || 0}m)`;
        else if (d.status === 'present' && d.late) txt = `late ${d.lateness_minutes || 0}m`;
        const inOut = `${d.check_in ? ' in ' + d.check_in : ''}${d.check_out ? ' out ' + d.check_out : ''}`;
        const title = `${d.date}: ${d.status}${inOut}`;
        const label = `${String(d.date || '').slice(5)}: ${txt}`;
        return `<span class="badge" title="${escapeHtml(title)}" style="margin:1px;">${escapeHtml(label)}</span>`;
      }).join(' ');
    }

    function renderAttendancePreview(preview) {
      if (!hrImportPreviewTbody) return;
      const employees = Array.isArray(preview?.employees) ? preview.employees : [];
      if (!employees.length) {
        hrImportPreviewTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No employees found in the file.</td></tr>';
      } else {
        hrImportPreviewTbody.innerHTML = employees.map((e) => {
          const matchNote = e.match_type === 'biometric_id' ? 'linked by ID'
            : e.match_type === 'name_suggestion' ? 'suggested by name'
            : 'no match — pick one';
          const drift = e.name_drift ? ' · <span style="color:var(--warn,#c93);">name differs from file</span>' : '';
          return `
            <tr>
              <td>${escapeHtml(String(e.biometric_id || ''))}</td>
              <td>${escapeHtml(e.name_in_file || '')}<div class="table-meta">${escapeHtml(matchNote)}${drift}</div></td>
              <td>${attendanceMappingSelect(e.biometric_id, e.matched_employee_id)}</td>
              <td>${escapeHtml(String(e.present_days || 0))}</td>
              <td>${escapeHtml(String(e.absent_days || 0))}</td>
              <td>${escapeHtml(String(e.late_days || 0))}</td>
              <td style="line-height:1.9;">${attendanceDayBadges(e.days)}</td>
            </tr>`;
        }).join('');
      }
      if (hrImportSummary) {
        hrImportSummary.style.display = '';
        hrImportSummary.innerHTML = `Week <strong>${escapeHtml(preview.week_start || '')}</strong> → <strong>${escapeHtml(preview.week_end || '')}</strong>`
          + (preview.as_of ? ` · data through ${escapeHtml(preview.as_of)}` : '')
          + ` · default shift: ${escapeHtml(preview.default_shift_name || '—')}`
          + ` · scanned ${preview.scanned || 0}, matched ${preview.matched || 0}, unmatched ${preview.unmatched || 0}`;
      }
      if (hrImportPreviewWrap) hrImportPreviewWrap.style.display = '';
    }

    async function previewAttendanceImport() {
      if (!hrImportFile) return;
      const file = hrImportFile.files && hrImportFile.files[0];
      if (!file) { showFlash('Choose a weekly export file first.', 'error'); return; }
      if (hrImportState.busy) return;
      await ensureImportEmployees();
      hrImportState.busy = true;
      if (hrImportPreviewBtn) hrImportPreviewBtn.disabled = true;
      try {
        const fd = new FormData();
        fd.append('file', file, file.name);
        const preview = await adminAPI.fetch(API.adminHrAttendanceImportPreview, { method: 'POST', body: fd });
        hrImportState.file = file;
        hrImportState.preview = preview;
        renderAttendancePreview(preview);
      } catch (err) {
        const detail = err && err.body && err.body.detail ? String(err.body.detail) : '';
        if (detail === 'default_shift_required') {
          showFlash('Create a default shift first: Staff shifts → add/edit a shift → tick "Default shift".', 'error');
        } else {
          showFlash(detail || (err && err.message) || 'Preview failed.', 'error');
        }
      } finally {
        hrImportState.busy = false;
        if (hrImportPreviewBtn) hrImportPreviewBtn.disabled = false;
      }
    }

    async function commitAttendanceImport() {
      if (hrImportState.busy) return;
      if (!hrImportState.file || !hrImportState.preview) { showFlash('Preview a file before saving.', 'error'); return; }
      const selects = hrImportPreviewTbody ? Array.from(hrImportPreviewTbody.querySelectorAll('select.hr-import-map')) : [];
      const mappings = [];
      const usedEmployees = new Set();
      let conflict = false;
      selects.forEach((sel) => {
        const uid = sel.dataset.uid || '';
        const employeeId = sel.value || '';
        if (!uid || !employeeId) return;
        if (usedEmployees.has(employeeId)) conflict = true;
        usedEmployees.add(employeeId);
        mappings.push({ biometric_id: uid, employee_id: employeeId });
      });
      if (conflict) { showFlash('Two biometric IDs map to the same employee. Fix the duplicate before saving.', 'error'); return; }
      if (!mappings.length) { showFlash('Map at least one biometric ID to an employee.', 'error'); return; }
      if (!window.confirm(`Save attendance for ${mappings.length} employee(s)? This updates their records for the week.`)) return;
      hrImportState.busy = true;
      if (hrImportCommitBtn) hrImportCommitBtn.disabled = true;
      try {
        const fd = new FormData();
        fd.append('file', hrImportState.file, hrImportState.file.name);
        fd.append('mappings', JSON.stringify(mappings));
        const res = await adminAPI.fetch(API.adminHrAttendanceImportCommit, { method: 'POST', body: fd });
        showFlash(`Import saved — ${res.created || 0} created, ${res.updated || 0} updated, ${res.employees_linked || 0} linked, ${res.skipped || 0} skipped.`);
        resetAttendanceImport();
      } catch (err) {
        const detail = err && err.body && err.body.detail ? String(err.body.detail) : (err && err.message) || 'Import failed.';
        showFlash(detail, 'error');
      } finally {
        hrImportState.busy = false;
        if (hrImportCommitBtn) hrImportCommitBtn.disabled = false;
      }
    }

    // ---- Attendance grid (period matrix) ------------------------------

    function pad2(n) { return String(n).padStart(2, '0'); }

    function isoWeekMonday(year, week) {
      // Monday of the given ISO week, as a YYYY-MM-DD string.
      const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
      const dow = simple.getUTCDay() || 7; // 1=Mon..7=Sun
      simple.setUTCDate(simple.getUTCDate() - dow + 1);
      return `${simple.getUTCFullYear()}-${pad2(simple.getUTCMonth() + 1)}-${pad2(simple.getUTCDate())}`;
    }

    function ensureGridPickerDefaults() {
      const now = new Date();
      if (hrGridMonth && !hrGridMonth.value) {
        hrGridMonth.value = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
      }
      if (hrGridWeek && !hrGridWeek.value) {
        // ISO week number for today.
        const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
        const day = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - day);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        hrGridWeek.value = `${d.getUTCFullYear()}-W${pad2(week)}`;
      }
    }

    function gridDateParam() {
      const view = hrGridView ? hrGridView.value : 'month';
      if (view === 'week') {
        const v = hrGridWeek ? hrGridWeek.value : '';
        const m = /^(\d{4})-W(\d{2})$/.exec(v || '');
        return m ? isoWeekMonday(Number(m[1]), Number(m[2])) : '';
      }
      const v = hrGridMonth ? hrGridMonth.value : '';
      return /^\d{4}-\d{2}$/.test(v || '') ? `${v}-01` : '';
    }

    function syncGridViewFields() {
      const week = hrGridView && hrGridView.value === 'week';
      if (hrGridMonthField) hrGridMonthField.style.display = week ? 'none' : '';
      if (hrGridWeekField) hrGridWeekField.style.display = week ? '' : 'none';
    }

    function weekdayMon0(isoDate) {
      const [y, m, d] = isoDate.split('-').map(Number);
      return (new Date(y, m - 1, d).getDay() + 6) % 7; // Mon=0 … Sun=6
    }

    function renderAttendanceGrid(data) {
      if (!hrGridHead || !hrGridBody) return;
      const days = Array.isArray(data?.days) ? data.days : [];
      const workdays = new Set(Array.isArray(data?.workday_weekdays) ? data.workday_weekdays : []);
      const today = data?.today;

      const headCells = ['<th class="hr-grid-name">Staff</th>'];
      days.forEach((iso) => {
        const wd = weekdayMon0(iso);
        const dayNum = Number(iso.split('-')[2]);
        const cls = ['hr-grid-colhead'];
        if (!workdays.has(wd)) cls.push('hr-grid-weekend');
        if (iso === today) cls.push('hr-grid-today');
        headCells.push(`<th class="${cls.join(' ')}">${WEEKDAY_LABELS[wd]}<small>${dayNum}</small></th>`);
      });
      hrGridHead.innerHTML = `<tr>${headCells.join('')}</tr>`;

      const employees = Array.isArray(data?.employees) ? data.employees : [];
      if (!employees.length) {
        hrGridBody.innerHTML = `<tr><td class="hr-grid-name">No staff found.</td></tr>`;
        return;
      }
      const rows = employees.map((emp) => {
        const cellsHtml = days.map((iso) => {
          const wd = weekdayMon0(iso);
          const weekendCls = workdays.has(wd) ? '' : ' hr-grid-weekend';
          const cell = emp.cells ? emp.cells[iso] : null;
          if (cell && cell.status === 'present') {
            const lateCls = cell.late ? ' is-late' : '';
            const attrs = `data-date="${iso}" data-in="${escapeHtml(cell.check_in || '')}" data-out="${escapeHtml(cell.check_out || '')}" data-late="${cell.lateness_minutes || 0}" data-name="${escapeHtml(emp.name)}"`;
            return `<td class="${weekendCls}"><span class="hr-grid-cell is-present${lateCls}" ${attrs}>✓</span></td>`;
          }
          if (cell && cell.status === 'absent') {
            const attrs = `data-date="${iso}" data-status="absent" data-name="${escapeHtml(emp.name)}"`;
            return `<td class="${weekendCls}"><span class="hr-grid-cell is-absent" ${attrs}>✗</span></td>`;
          }
          if (!workdays.has(wd)) return `<td class="${weekendCls}"><span class="hr-grid-cell is-off">–</span></td>`;
          return `<td class="${weekendCls}"><span class="hr-grid-cell is-none">·</span></td>`;
        }).join('');
        const meta = emp.biometric_id ? `<div class="table-meta">ID ${escapeHtml(emp.biometric_id)}</div>` : '';
        return `<tr><td class="hr-grid-name">${escapeHtml(emp.name)}${meta}</td>${cellsHtml}</tr>`;
      }).join('');
      hrGridBody.innerHTML = rows;

      if (hrGridSummary) {
        const label = data.view === 'week' ? 'Week' : 'Month';
        hrGridSummary.innerHTML = `${label}: <strong>${escapeHtml(data.start || '')}</strong> → <strong>${escapeHtml(data.end || '')}</strong> · ${employees.length} staff · click a ✓ for clock-in/out.`;
      }
      if (hrGridDetail) hrGridDetail.textContent = '';
    }

    async function loadAttendanceGrid() {
      if (!hrGridBody || hrGridState.loading) return;
      ensureGridPickerDefaults();
      syncGridViewFields();
      const view = hrGridView ? hrGridView.value : 'month';
      const dateParam = gridDateParam();
      hrGridState.loading = true;
      hrGridBody.innerHTML = `<tr><td class="hr-grid-name" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrAttendanceGrid(view, dateParam), { method: 'GET' });
        hrGridState.data = data;
        hrGridState.loaded = true;
        renderAttendanceGrid(data);
      } catch (err) {
        hrGridBody.innerHTML = `<tr><td class="hr-grid-name" style="color:var(--sub);">${escapeHtml(err && err.message ? err.message : 'Failed to load attendance grid.')}</td></tr>`;
      } finally {
        hrGridState.loading = false;
      }
    }

    // Re-grade the visible period against the shifts now set, then reload the grid.
    async function recomputeAttendanceGrid() {
      if (!hrGridRecomputeBtn || hrGridState.loading) return;
      const view = hrGridView ? hrGridView.value : 'month';
      const dateParam = gridDateParam();
      const original = hrGridRecomputeBtn.textContent;
      hrGridRecomputeBtn.disabled = true;
      hrGridRecomputeBtn.textContent = 'Recomputing…';
      try {
        const res = await adminAPI.fetch(API.adminHrAttendanceRecompute(view, dateParam), { method: 'POST' });
        const changed = res && typeof res.changed === 'number' ? res.changed : 0;
        const examined = res && typeof res.examined === 'number' ? res.examined : 0;
        showFlash(changed
          ? `Recomputed ${examined} day(s); ${changed} updated against the current shifts.`
          : `Recomputed ${examined} day(s); nothing changed.`, 'success');
        await loadAttendanceGrid();
      } catch (err) {
        showFlash((err && err.message) ? err.message : 'Recompute failed.', 'error');
      } finally {
        hrGridRecomputeBtn.disabled = false;
        hrGridRecomputeBtn.textContent = original;
      }
    }

    function onGridCellClick(event) {
      const cell = event.target.closest('.hr-grid-cell');
      if (!cell || !hrGridDetail) return;
      const date = cell.dataset.date;
      if (!date) return;
      if (cell.dataset.status === 'absent') {
        hrGridDetail.textContent = `${cell.dataset.name} · ${date} · Absent`;
        return;
      }
      const cin = cell.dataset.in || '—';
      const cout = cell.dataset.out || '—';
      const late = Number(cell.dataset.late || 0);
      const lateTxt = late > 0 ? ` · late ${late} min` : '';
      hrGridDetail.textContent = `${cell.dataset.name} · ${date} · in ${cin} · out ${cout}${lateTxt}`;
    }

    function renderActiveShifts() {
      if (!activeShiftsTbody) return;
      if (shiftActiveState.loading) {
        activeShiftsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>';
        return;
      }
      const items = Array.isArray(shiftActiveState.items) ? shiftActiveState.items : [];
      if (!items.length) {
        activeShiftsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No active shifts right now.</td></tr>';
        return;
      }
      const rows = items.map((item, index) => {
        const user = item?.admin_user || null;
        const staffLabel = user ? escapeHtml(user.username || user.email || `User ${user.id}`) : escapeHtml(`User ${item?.user_id ?? '—'}`);
        const staffMeta = user && user.email && user.email !== user.username ? `<div class="table-meta">${escapeHtml(user.email)}</div>` : '';
        const templateGroup = item?.shift_template?.staff_group;
        const staffGroup = templateGroup ? (STAFF_GROUP_LABELS[templateGroup] || templateGroup) : '—';
        const assigned = Array.isArray(item?.assigned_lines) && item.assigned_lines.length ? escapeHtml(item.assigned_lines.join(', ')) : '—';
        const trunks = Array.isArray(item?.pbx_trunks) && item.pbx_trunks.length ? escapeHtml(item.pbx_trunks.join(', ')) : '—';
        const startedAt = escapeHtml(formatDateTimeShort(item?.login_at));
        const expectedEnd = escapeHtml(formatDateTimeShort(item?.expected_logout_at));
        const statusParts = [];
        if (item?.logout_requested_at && !item?.logout_at) statusParts.push('Logout requested');
        if (item?.logout_at) statusParts.push('Ended');
        if (item?.handover_completed) statusParts.push('Handover done');
        if (Array.isArray(item?.unresolved_ticket_ids) && item.unresolved_ticket_ids.length) {
          statusParts.push(`${item.unresolved_ticket_ids.length} unresolved`);
        }
        if (!statusParts.length) statusParts.push('Active');
        const statusText = escapeHtml(statusParts.join(' • '));
        return `
          <tr>
            <td>${index + 1}</td>
            <td>${staffLabel}${staffMeta}</td>
            <td>${escapeHtml(staffGroup)}</td>
            <td>${assigned}</td>
            <td>${trunks}</td>
            <td>${startedAt}</td>
            <td>${expectedEnd}</td>
            <td>${statusText}</td>
          </tr>
        `;
      }).join('');
      activeShiftsTbody.innerHTML = rows;
    }

    async function loadActiveShifts(options = {}) {
      if (!activeShiftsTbody) return;
      const { silent = false } = options;
      if (shiftActiveState.loading) return;
      shiftActiveState.loading = true;
      if (!silent && hrActiveTab === 'active') {
        activeShiftsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>';
      }
      try {
        const response = await adminAPI.fetch(API.adminShiftActive, { method: 'GET' });
        const items = Array.isArray(response?.items) ? response.items : (Array.isArray(response) ? response : []);
        shiftActiveState.items = items;
        shiftActiveState.loaded = true;
      } catch (err) {
        shiftActiveState.items = [];
        shiftActiveState.loaded = true;
        const message = err && err.message ? err.message : 'Failed to load active shifts.';
        showFlash(message, 'error');
      } finally {
        shiftActiveState.loading = false;
        if (hrActiveTab === 'active') renderActiveShifts();
      }
    }

    function setHrSection(section) {
      hrSection = section || 'time';
      hrSectionPanels.time = hrTimeContainer;
      hrSectionPanels.people = hrPeoplePanel;
      hrSectionPanels.performance = hrPerformancePanel;
      hrSectionPanels.talent = hrTalentPanel;
      hrSectionPanels.learning = hrLearningPanel;
      hrSectionPanels.finance = hrFinancePanel;
      hrSectionPanels.compliance = hrCompliancePanel;
      hrSectionPanels.ess = hrEssPanel;
      if (hrSectionTitle) {
        const labelMap = {
          people: 'HR — People',
          time: 'HR — Attendance',
          performance: 'HR — Performance',
          talent: 'HR — Talent',
          learning: 'HR — Learning',
          finance: 'HR — Finance',
          compliance: 'HR — Compliance',
          ess: 'HR — Policies & Quizzes',
        };
        hrSectionTitle.textContent = labelMap[hrSection] || 'HR';
      }
      // Hide all panels first
      Object.values(hrSectionPanels).forEach((panel) => {
        if (panel) panel.style.display = 'none';
      });
      if (hrTimeToolbar) hrTimeToolbar.style.display = hrSection === 'time' ? '' : 'none';
      const panelToShow = hrSectionPanels[hrSection] || null;
      if (panelToShow) {
        panelToShow.style.display = '';
      }
      if (hrSection === 'time') {
        setHrTab(hrActiveTab);
      }
      if (hrSection === 'people') {
        setHrPeopleTab(hrPeopleTab);
      }
      loadHrSectionData(hrSection);
    }

    function setHrTab(nextTab) {
      const target = ['staff', 'active', 'import', 'grid', 'roster'].includes(nextTab) ? nextTab : 'templates';
      hrActiveTab = target;
      if (hrTabBar) {
        Array.from(hrTabBar.querySelectorAll('[data-hr-tab]')).forEach((btn) => {
          const key = btn.dataset.hrTab || 'templates';
          btn.classList.toggle('active', key === target);
        });
      }
      if (hrTemplatesPanel) hrTemplatesPanel.style.display = target === 'templates' ? '' : 'none';
      if (hrActivePanel) hrActivePanel.style.display = target === 'active' ? '' : 'none';
      if (hrImportPanel) hrImportPanel.style.display = target === 'import' ? '' : 'none';
      if (hrGridPanel) hrGridPanel.style.display = target === 'grid' ? '' : 'none';
      if (hrRosterPanel) hrRosterPanel.style.display = target === 'roster' ? '' : 'none';
      if (hrStaffPanel) hrStaffPanel.style.display = target === 'staff' ? '' : 'none';
      // The "Add staff shift" toolbar button only makes sense on the templates tab.
      if (shiftTemplateAddBtn) shiftTemplateAddBtn.style.display = target === 'templates' ? '' : 'none';
      if (target === 'active') {
        renderActiveShifts();
        const silent = shiftActiveState.loaded;
        loadActiveShifts({ silent }).catch(() => {});
      }
      if (target === 'grid' && !hrGridState.loaded) {
        loadAttendanceGrid().catch(() => {});
      }
      if (target === 'roster' && !hrRosterState.loaded) {
        loadLeaveRoster().catch(() => {});
      }
      if (target === 'staff' && !hrStaffState.loaded) {
        loadStaffShifts().catch(() => {});
      }
    }

    // ---- Staff: standing shift per employee ------------------------------
    // HR sets the shift each employee works from a date onward. Anyone with none
    // is on the default shift. A customer care agent's own sign-on pick for a day
    // still applies first when attendance is graded, so those rows say so.

    function localIsoDate(d = new Date()) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    function staffShiftLabel(shift) {
      if (!shift) return '';
      return `${shift.name} · ${formatTimeDisplay(shift.start_time)}–${formatTimeDisplay(shift.end_time)}`;
    }

    function staffAssignmentLabel(assignment, defaultShift) {
      if (!assignment) return '';
      if (!assignment.shift) {
        return defaultShift ? `Default (${defaultShift.name})` : 'Default shift';
      }
      return assignment.shift.name;
    }

    function shiftDayBefore(iso) {
      const [y, m, d] = String(iso).split('-').map(Number);
      return localIsoDate(new Date(y, m - 1, d - 1));
    }

    // Each change applies from its date until the day before the next one, so
    // the "until" is derived rather than stored. Status is relative to the
    // date the tab is viewing, which is what HR is checking corrections against.
    function staffTimeline(assignments, onDate) {
      const ordered = (Array.isArray(assignments) ? assignments : [])
        .slice()
        .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from)));
      return ordered.map((row, index) => {
        const next = ordered[index + 1];
        const until = next ? shiftDayBefore(next.effective_from) : '';
        let status = 'current';
        if (row.effective_from > onDate) status = 'upcoming';
        else if (next && next.effective_from <= onDate) status = 'past';
        return { row, until, status };
      });
    }

    const STAFF_STATUS_LABEL = { past: 'Past', current: 'Current', upcoming: 'Upcoming' };

    function staffTimelineShiftLabel(row, defaultShift) {
      const label = staffAssignmentLabel(row, defaultShift);
      return row.shift_inactive ? `${label} (inactive, default applies)` : label;
    }

    async function loadStaffShifts(options = {}) {
      if (!hrStaffTbody) return;
      const { silent = false } = options;
      if (hrStaffOn && !hrStaffOn.value) hrStaffOn.value = localIsoDate();
      if (!silent) hrStaffTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
      try {
        const data = await adminAPI.fetch(API.adminHrStaffShifts(hrStaffOn ? hrStaffOn.value : ''), { method: 'GET', cache: 'no-store' });
        hrStaffState.data = data;
        hrStaffState.loaded = true;
        renderStaffShifts();
      } catch (err) {
        hrStaffTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">${escapeHtml(err && err.message ? err.message : 'Failed to load staff shifts.')}</td></tr>`;
        if (hrStaffSummary) hrStaffSummary.textContent = '';
      }
    }

    function staffRowMatches(item, query, filter) {
      if (filter === 'assigned' && item.source !== 'assigned') return false;
      if (filter === 'default' && item.source !== 'default') return false;
      if (filter === 'self' && !item.selects_own_shift) return false;
      if (filter === 'upcoming' && !item.upcoming) return false;
      if (!query) return true;
      return [item.name, item.department, item.position]
        .some((v) => (v || '').toLowerCase().includes(query));
    }

    function renderStaffTimelineCell(item, onDate, defaultShift) {
      const rows = (Array.isArray(item.assignments) ? item.assignments.slice() : [])
        .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from)));
      if (!rows.length) return '<span style="color:var(--sub);">None set</span>';
      const day = onDate || localIsoDate();
      return rows.map((row) => {
        const status = shiftRowStatus(row, day);
        const isTemp = (row.kind || 'standing') === 'temporary';
        const end = isTemp ? (row.effective_to || '—') : 'onward';
        const range = `${escapeHtml(row.effective_from)} – ${escapeHtml(String(end))}`;
        const muted = status === 'past' ? ' style="color:var(--sub);"' : '';
        const tag = status === 'current'
          ? ' <span class="badge">Active</span>'
          : ` <span class="table-meta">${STAFF_STATUS_LABEL[status]}</span>`;
        const kindTag = isTemp ? ' <span class="badge">Temp</span>' : '';
        const note = row.note ? ` <span class="table-meta">· ${escapeHtml(row.note)}</span>` : '';
        return `<div${muted}>${range} · ${escapeHtml(staffTimelineShiftLabel(row, defaultShift))}${kindTag}${tag}${note}</div>`;
      }).join('');
    }

    function renderStaffShifts() {
      const data = hrStaffState.data;
      if (!hrStaffTbody || !data) return;
      const items = Array.isArray(data.items) ? data.items : [];
      const defaultShift = data.default_shift || null;
      const onDate = data.on_date || localIsoDate();
      const query = (hrStaffSearch && hrStaffSearch.value || '').trim().toLowerCase();
      const filter = (hrStaffFilter && hrStaffFilter.value) || '';
      const visible = items.filter((item) => staffRowMatches(item, query, filter));

      if (hrStaffSummary) {
        const assigned = items.filter((i) => i.source === 'assigned').length;
        const onDefault = items.length - assigned;
        const parts = [`${items.length} staff`, `${assigned} set by HR`];
        if (defaultShift) {
          parts.push(`${onDefault} on the default shift (${staffShiftLabel(defaultShift)})`);
          hrStaffSummary.textContent = parts.join(' · ');
        } else {
          // Without a default, staff with no shift have nothing to be graded against.
          parts.push(`${onDefault} with no shift`);
          hrStaffSummary.innerHTML = `${escapeHtml(parts.join(' · '))} · <strong>No default shift is set, so lateness isn't computed for staff without a shift. Mark one under Staff shifts.</strong>`;
        }
      }

      if (!visible.length) {
        hrStaffTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">${items.length ? 'No staff match.' : 'No active employees.'}</td></tr>`;
        return;
      }

      hrStaffTbody.innerHTML = visible.map((item) => {
        const selfChip = item.selects_own_shift
          ? ' <span class="badge" title="Picks their own shift when signing on. That pick applies first for the day.">Customer care</span>'
          : '';
        let shiftCell;
        if (item.current && item.current.shift_inactive) {
          shiftCell = `${escapeHtml(staffShiftLabel(item.effective_shift) || 'No shift')}`
            + `<div class="table-meta">“${escapeHtml(item.current.shift.name)}” is inactive, so the default applies</div>`;
        } else if (item.source === 'assigned') {
          shiftCell = escapeHtml(staffShiftLabel(item.effective_shift));
        } else if (item.source === 'off') {
          const n = Array.isArray(item.active) ? item.active.length : 0;
          shiftCell = `<span style="color:var(--sub);">Off today${n ? ` · ${n} shift${n === 1 ? '' : 's'} set` : ''}</span>`;
        } else if (item.effective_shift) {
          shiftCell = `<span style="color:var(--sub);">Default · ${escapeHtml(staffShiftLabel(item.effective_shift))}</span>`;
        } else {
          shiftCell = '<span style="color:var(--sub);">No shift</span>';
        }
        return `<tr>
          <td style="vertical-align:top;">${escapeHtml(item.name || '—')}${selfChip}</td>
          <td style="vertical-align:top;">${escapeHtml(item.department || '—')}</td>
          <td style="vertical-align:top;">${escapeHtml(item.position || '—')}</td>
          <td style="vertical-align:top;">${shiftCell}</td>
          <td style="vertical-align:top; line-height:1.7;">${renderStaffTimelineCell(item, onDate, defaultShift)}</td>
          <td style="vertical-align:top;"><button class="btn" type="button" data-staff-shift-edit="${escapeHtml(item.employee_id)}">Set shift</button></td>
        </tr>`;
      }).join('');
    }

    // Each row owns its own window now (standing = open-ended; temporary = a range),
    // so status is per row rather than derived from the next one starting.
    function shiftRowStatus(row, onDate) {
      const from = String(row.effective_from);
      const to = row.effective_to ? String(row.effective_to) : null;
      if (from > onDate) return 'upcoming';
      if (to && to < onDate) return 'past';
      return 'current';
    }

    function renderStaffShiftHistory(rows, defaultShift, onDate) {
      if (!hrStaffShiftHistoryTbody) return;
      const list = (Array.isArray(rows) ? rows.slice() : [])
        .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from)));
      if (!list.length) {
        hrStaffShiftHistoryTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">No shifts set. On the default shift.</td></tr>';
        return;
      }
      const day = onDate || localIsoDate();
      hrStaffShiftHistoryTbody.innerHTML = list.map((row) => {
        const status = shiftRowStatus(row, day);
        const isTemp = (row.kind || 'standing') === 'temporary';
        const endLabel = isTemp ? (row.effective_to ? escapeHtml(row.effective_to) : '—') : 'onward';
        const kindTag = isTemp ? ' <span class="badge" title="Overrides standing shifts within its window">Temporary</span>' : '';
        const statusTag = status === 'current'
          ? '<span class="badge">Active</span>'
          : `<span class="table-meta">${STAFF_STATUS_LABEL[status]}</span>`;
        return `<tr${status === 'past' ? ' style="color:var(--sub);"' : ''}>
          <td style="white-space:nowrap; vertical-align:middle;">${escapeHtml(row.effective_from)}</td>
          <td style="white-space:nowrap; vertical-align:middle;">${endLabel}</td>
          <td style="vertical-align:middle; overflow-wrap:anywhere;">${escapeHtml(staffTimelineShiftLabel(row, defaultShift))}${kindTag}</td>
          <td style="white-space:nowrap; vertical-align:middle;">${statusTag}</td>
          <td style="vertical-align:middle; overflow-wrap:anywhere;">${escapeHtml(row.note || '')}</td>
          <td style="text-align:right; vertical-align:middle;"><button class="btn ghost" type="button" data-staff-shift-delete="${escapeHtml(row.id)}" title="Remove this shift" aria-label="Remove this shift" style="padding:2px 8px;">✕</button></td>
        </tr>`;
      }).join('');
    }

    // The dialog loads the one employee it is editing, so it works the same from
    // Attendance → Staff and from an employee's detail view, and for employees the
    // Staff list does not show (e.g. inactive).
    async function refreshStaffShiftDialog() {
      const editing = hrStaffState.editing;
      if (!editing) return null;
      const detail = await adminAPI.fetch(API.adminHrStaffShiftDetail(editing.employeeId, editing.onDate), { method: 'GET', cache: 'no-store' });
      editing.detail = detail;
      renderStaffShiftHistory(detail.assignments || [], detail.default_shift || null, detail.on_date);
      return detail;
    }

    // Tell whichever views show this employee's shifts that they changed.
    function notifyStaffShiftChanged() {
      const editing = hrStaffState.editing;
      if (hrStaffState.loaded) loadStaffShifts({ silent: true }).catch(() => {});
      if (editing && typeof editing.onChange === 'function') {
        try { editing.onChange(); } catch { /* the dialog stays usable either way */ }
      }
    }

    // A temporary shift needs an end date; a standing one is open-ended and scoped
    // to its template's weekdays. Toggle the "Ends on" field to match.
    function syncStaffShiftKindFields() {
      const temp = hrStaffShiftKind && hrStaffShiftKind.value === 'temporary';
      if (hrStaffShiftToField) hrStaffShiftToField.style.display = temp ? '' : 'none';
      if (hrStaffShiftTo) hrStaffShiftTo.required = !!temp;
      if (hrStaffShiftFromLabel) hrStaffShiftFromLabel.textContent = temp ? 'From' : 'Starts on';
    }

    async function openStaffShiftDialog(employeeId, options = {}) {
      if (!employeeId || !hrStaffShiftDlg) return;
      const onDate = options.onDate || localIsoDate();
      hrStaffState.editing = { employeeId, onDate, onChange: options.onChange || null, detail: null };
      if (hrStaffShiftName) hrStaffShiftName.textContent = options.name || '…';
      if (hrStaffShiftSelfNote) hrStaffShiftSelfNote.hidden = true;
      if (hrStaffShiftNote) hrStaffShiftNote.value = '';
      if (hrStaffShiftKind) hrStaffShiftKind.value = 'standing';
      if (hrStaffShiftTo) hrStaffShiftTo.value = '';
      syncStaffShiftKindFields();
      if (hrStaffShiftFrom) hrStaffShiftFrom.value = onDate;
      if (hrStaffShiftTemplate) hrStaffShiftTemplate.innerHTML = '<option value="">Loading…</option>';
      if (hrStaffShiftHistoryTbody) hrStaffShiftHistoryTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
      openDialog(hrStaffShiftDlg);

      try {
        const [templatesRes, detail] = await Promise.all([
          adminAPI.fetch(API.adminShiftTemplates, { method: 'GET', cache: 'no-store' }),
          refreshStaffShiftDialog(),
        ]);
        const defaultShift = detail.default_shift || null;
        if (hrStaffShiftName) hrStaffShiftName.textContent = detail.name || '—';
        if (hrStaffShiftSelfNote) hrStaffShiftSelfNote.hidden = !detail.selects_own_shift;
        const templates = (Array.isArray(templatesRes?.items) ? templatesRes.items : (Array.isArray(templatesRes) ? templatesRes : []))
          .filter((t) => t && t.is_active !== false)
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        const defaultLabel = defaultShift ? `Default shift (${staffShiftLabel(defaultShift)})` : 'Default shift (none set)';
        const options_ = [`<option value="">${escapeHtml(defaultLabel)}</option>`].concat(
          templates.map((t) => `<option value="${escapeHtml(t.id)}">${escapeHtml(staffShiftLabel(t))}${t.is_default ? ' — current default' : ''}</option>`),
        );
        hrStaffShiftTemplate.innerHTML = options_.join('');
        const current = detail.current;
        hrStaffShiftTemplate.value = current && current.shift && !current.shift_inactive ? current.shift.id : '';
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to load shifts.', 'error');
      }
    }

    async function submitStaffShift(event) {
      event.preventDefault();
      const editing = hrStaffState.editing;
      if (!editing || !hrStaffShiftForm || hrStaffShiftForm.dataset.pending === '1') return;
      const name = editing.detail?.name || 'This employee';
      const from = hrStaffShiftFrom ? hrStaffShiftFrom.value : '';
      if (!from) { showFlash('Choose the date the shift applies from.', 'error'); return; }
      const kind = hrStaffShiftKind && hrStaffShiftKind.value === 'temporary' ? 'temporary' : 'standing';
      const to = hrStaffShiftTo ? hrStaffShiftTo.value : '';
      if (kind === 'temporary') {
        if (!to) { showFlash('A temporary shift needs an end date.', 'error'); return; }
        if (to < from) { showFlash('The end date cannot be before the start date.', 'error'); return; }
      }
      const payload = {
        shift_template_id: hrStaffShiftTemplate && hrStaffShiftTemplate.value ? hrStaffShiftTemplate.value : null,
        effective_from: from,
        effective_to: kind === 'temporary' ? to : null,
        kind,
        note: hrStaffShiftNote && hrStaffShiftNote.value.trim() ? hrStaffShiftNote.value.trim() : null,
      };
      hrStaffShiftForm.dataset.pending = '1';
      if (hrStaffShiftSubmit) hrStaffShiftSubmit.disabled = true;
      try {
        await adminAPI.fetch(API.adminHrStaffShift(editing.employeeId), { method: 'PUT', body: JSON.stringify(payload) });
        const what = payload.shift_template_id
          ? (hrStaffShiftTemplate.selectedOptions[0]?.textContent || 'the new shift')
          : 'the default shift';
        showFlash(`Added: ${name} on ${what} from ${from}.`);
        // Stay open for the next shift. Its date and note belong to the one just
        // added, so clear them; keep the shift picked, it is often the same one.
        if (hrStaffShiftNote) hrStaffShiftNote.value = '';
        if (hrStaffShiftFrom) { hrStaffShiftFrom.value = ''; hrStaffShiftFrom.focus(); }
        await refreshStaffShiftDialog();
        notifyStaffShiftChanged();
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to add the shift.', 'error');
      } finally {
        hrStaffShiftForm.dataset.pending = '0';
        if (hrStaffShiftSubmit) hrStaffShiftSubmit.disabled = false;
      }
    }

    async function deleteStaffShiftChange(assignmentId) {
      const editing = hrStaffState.editing;
      if (!editing) return;
      if (!window.confirm('Remove this shift? The shift before it carries on applying.')) return;
      try {
        await adminAPI.fetch(API.adminHrStaffShiftAssignment(editing.employeeId, assignmentId), { method: 'DELETE' });
        showFlash('Shift removed.');
        await refreshStaffShiftDialog();
        notifyStaffShiftChanged();
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to remove the shift.', 'error');
      }
    }

    const ROSTER_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    async function ensureRosterDepartments() {
      if (hrRosterState.deptsLoaded || !hrRosterDept) return;
      try {
        const res = await adminAPI.fetch(API.adminHrDepartments, { method: 'GET', cache: 'no-store' });
        const opts = ['<option value="">All departments</option>'].concat(
          (res.items || []).map((d) => `<option value="${escapeHtml(d.id)}">${escapeHtml(d.name)}</option>`),
        );
        hrRosterDept.innerHTML = opts.join('');
        hrRosterState.deptsLoaded = true;
      } catch { /* leave the default "All departments" option */ }
    }

    async function loadLeaveRoster() {
      if (!hrRosterCalendar) return;
      await ensureRosterDepartments();
      if (hrRosterYear && !hrRosterYear.value) hrRosterYear.value = String(new Date().getFullYear());
      const year = (hrRosterYear && parseInt(hrRosterYear.value, 10)) || new Date().getFullYear();
      const deptId = (hrRosterDept && hrRosterDept.value) || '';
      hrRosterCalendar.innerHTML = '<div class="table-meta">Loading…</div>';
      try {
        let url = `${API.adminHrLeaveRoster}?year=${year}`;
        if (deptId) url += `&department_id=${encodeURIComponent(deptId)}`;
        const data = await adminAPI.fetch(url, { method: 'GET', cache: 'no-store' });
        hrRosterState.loaded = true;
        renderLeaveRoster(data);
      } catch (err) {
        const msg = adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Failed to load roster';
        hrRosterCalendar.innerHTML = `<div class="error" style="padding:8px;">${escapeHtml(msg)}</div>`;
      }
    }

    function renderLeaveRoster(data) {
      if (!hrRosterCalendar) return;
      const year = data.year;
      const holidays = new Set(data.holidays || []);
      const weekend = new Set(data.weekend_days || [5, 6]);
      const overMax = new Set(data.over_max_days || []);
      const employees = data.employees || [];

      // Column model: months, each with its day cells (ISO + Python weekday).
      const months = ROSTER_MONTHS.map((name, m) => {
        const dim = new Date(year, m + 1, 0).getDate();
        const days = [];
        for (let d = 1; d <= dim; d += 1) {
          const iso = `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const pyDow = (new Date(year, m, d).getDay() + 6) % 7; // JS Sun=0 → Python Mon=0
          days.push({ iso, d, off: weekend.has(pyDow) || holidays.has(iso) });
        }
        return { name, days };
      });

      const monthHead = months.map((mo) => `<th class="roster-month" colspan="${mo.days.length}">${mo.name}</th>`).join('');
      const dayHead = months.map((mo) => mo.days.map((day) => `<th title="${day.iso}">${day.d}</th>`).join('')).join('');

      const body = employees.map((emp) => {
        const cells = months.map((mo) => mo.days.map((day) => {
          const bucket = (emp.days || {})[day.iso];
          let cls = 'roster-day';
          if (bucket === 'taken') cls += ' d-taken';
          else if (bucket === 'upcoming') cls += ' d-upcoming';
          else if (day.off) cls += ' d-off';
          if (bucket && overMax.has(day.iso)) cls += ' d-over';
          const title = bucket ? `${emp.name} · ${day.iso} · ${bucket}` : day.iso;
          return `<td class="${cls}" title="${escapeHtml(title)}"></td>`;
        }).join('')).join('');
        return `<tr><td class="roster-emp" title="${escapeHtml(emp.department_name || '')}">${escapeHtml(emp.name || '—')}</td>${cells}</tr>`;
      }).join('');

      hrRosterCalendar.innerHTML = employees.length
        ? `<table class="roster-table">
             <thead>
               <tr><th class="roster-emp" rowspan="2">Employee (${employees.length})</th>${monthHead}</tr>
               <tr>${dayHead}</tr>
             </thead>
             <tbody>${body}</tbody>
           </table>`
        : '<div class="table-meta">No active employees for this filter.</div>';

      if (hrRosterSummary) {
        const withLeave = employees.filter((e) => Object.keys(e.days || {}).length).length;
        const bits = [`${year}`, `${employees.length} employee(s)`, `${withLeave} with approved leave`];
        if (data.max_concurrent != null) {
          bits.push(`cap ${data.max_concurrent}/day`);
          bits.push(`${(data.over_max_days || []).length} over-capacity day(s)`);
        }
        hrRosterSummary.textContent = bits.join(' · ');
      }
    }

    function setHrPeopleTab(tab) {
      const target = ['departments', 'positions', 'grades'].includes(tab) ? tab : 'employees';
      hrPeopleTab = target;
      if (hrPeopleTabBar) {
        Array.from(hrPeopleTabBar.querySelectorAll('[data-hr-people-tab]')).forEach((btn) => {
          const key = btn.dataset.hrPeopleTab || 'employees';
          btn.classList.toggle('active', key === target);
        });
      }
      showEmployeeList();
      if (hrEmployeesContainer) hrEmployeesContainer.style.display = target === 'employees' ? '' : 'none';
      if (hrDepartmentsContainer) hrDepartmentsContainer.style.display = target === 'departments' ? '' : 'none';
      if (hrPositionsContainer) hrPositionsContainer.style.display = target === 'positions' ? '' : 'none';
      if (hrGradesContainer) hrGradesContainer.style.display = target === 'grades' ? '' : 'none';
      if (target === 'employees' && !hrDataState.people) {
        loadHrPeople().catch(() => {});
      }
      if (target === 'departments' && !hrDataState.departments) {
        loadHrDepartments().catch(() => {});
      }
      if (target === 'positions' && !hrDataState.positions) {
        loadHrPositions().catch(() => {});
      }
      if (target === 'grades') {
        loadHrGradesTab().catch(() => {});
      }
    }

    function setTableRows(tbody, rowsHtml, emptyText) {
      if (!tbody) return;
      const colCount = tbody.closest('table')?.querySelectorAll('th').length || 1;
      if (!rowsHtml || !rowsHtml.length) {
        tbody.innerHTML = `<tr><td colspan="${colCount}" style="color:var(--sub);">${emptyText || 'No data.'}</td></tr>`;
      } else {
        tbody.innerHTML = rowsHtml;
      }
    }

    function formatDateRange(start, end) {
      const startVal = start ? escapeHtml(start) : '';
      const endVal = end ? escapeHtml(end) : '';
      if (startVal && endVal) return `${startVal} → ${endVal}`;
      return startVal || endVal || '';
    }

    function normalizeOptionalInput(el) {
      if (!el) return null;
      const value = String(el.value || '').trim();
      return value ? value : null;
    }

    async function openRecruitmentDialog() {
      if (!hrRecruitmentDlg || !hrRecruitmentForm) { _hrWarnMissing('#hrRecruitmentDlg'); return; }
      // Make sure applicant/vacancy lists are populated
      try {
        if (!hrApplicantList.length || !hrVacancyList.length) {
          await loadHrTalent();
        }
      } catch {}
      populateApplicantOptions();
      populateVacancyOptions();
      hrRecruitmentForm.reset();
      hrRecruitmentForm.dataset.pending = '0';
      openDialog(hrRecruitmentDlg);
    }

    function openRecruitmentUpdateDialog(id = '') {
      if (!hrRecruitmentUpdateDlg || !hrRecruitmentUpdateForm) { _hrWarnMissing('#hrRecruitmentUpdateDlg'); return; }
      hrRecruitmentUpdateForm.reset();
      hrRecruitmentUpdateForm.dataset.pending = '0';
      if (hrRecruitmentUpdateIdInput) hrRecruitmentUpdateIdInput.value = id || '';
      openDialog(hrRecruitmentUpdateDlg);
    }

    function openOnboardingCreateDialog(employeeId = '') {
      if (!hrOnboardingCreateDlg || !hrOnboardingCreateForm) { _hrWarnMissing('#hrOnboardingCreateDlg'); return; }
      hrOnboardingCreateForm.reset();
      hrOnboardingCreateForm.dataset.pending = '0';
      if (employeeId && hrOnboardingEmployeeInput) hrOnboardingEmployeeInput.value = getEmployeeNameById(employeeId) || employeeId;
      if (hrOnboardingStartInput) hrOnboardingStartInput.value = new Date().toISOString().slice(0, 10);
      populateOnboardingTemplateOptions();
      if (hrSelectedOnboardingTemplateId && hrOnboardingTemplateInput) {
        hrOnboardingTemplateInput.value = getOnboardingTemplateNameById(hrSelectedOnboardingTemplateId) || '';
      }
      openDialog(hrOnboardingCreateDlg);
    }

    function openOnboardingTaskDialog(onboardingId = '', assigneeId = '') {
      if (!hrOnboardingTaskDlg || !hrOnboardingTaskForm) { _hrWarnMissing('#hrOnboardingTaskDlg'); return; }
      hrOnboardingTaskForm.reset();
      hrOnboardingTaskForm.dataset.pending = '0';
      if (onboardingId && hrOnboardingTaskOnboardingInput) hrOnboardingTaskOnboardingInput.value = onboardingId;
      if (assigneeId && hrOnboardingTaskAssigneeInput) hrOnboardingTaskAssigneeInput.value = getEmployeeNameById(assigneeId) || assigneeId;
      openDialog(hrOnboardingTaskDlg);
    }

    async function loadOnboardingTasks(onboardingId) {
      if (!onboardingId) return [];
      if (hrOnboardingTasksCache.has(onboardingId)) return hrOnboardingTasksCache.get(onboardingId);
      try {
        const data = await adminAPI.fetch(API.adminHrOnboardingTasks(onboardingId), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        hrOnboardingTasksCache.set(onboardingId, items);
        return items;
      } catch {
        return [];
      }
    }

    function renderOnboardingTasksDialog(onboardingId, tasks) {
      if (!hrOnboardingTasksTbody) { _hrWarnMissing('#hrOnboardingTasksTbody'); return; }
      const rows = tasks.map((task) => {
        const name = task.template_item_name || getTemplateItemNameById(task.template_item_id) || 'Task';
        const status = task.status || 'not_started';
        const due = task.due_date || '';
        const checked = status === 'completed';
        return `
          <tr>
            <td><input type="checkbox" data-onboarding-task-id="${escapeHtml(task.id || '')}" ${checked ? 'checked' : ''} /></td>
            <td>${escapeHtml(name)}</td>
            <td>${escapeHtml(due)}</td>
            <td>${escapeHtml(status)}</td>
          </tr>
        `;
      }).join('');
      setTableRows(hrOnboardingTasksTbody, rows, 'No tasks.');
      const title = onboardingId ? `Onboarding tasks — ${escapeHtml(onboardingId)}` : 'Onboarding tasks';
      if (hrOnboardingTasksTitle) hrOnboardingTasksTitle.textContent = title;
      hrOnboardingTasksOnboardingId = onboardingId || '';
      if (hrOnboardingTasksDlg) {
        openDialog(hrOnboardingTasksDlg);
      }
    }

    function openProbationCreateDialog(employeeId = '') {
      if (!hrProbationCreateDlg || !hrProbationCreateForm) { _hrWarnMissing('#hrProbationCreateDlg'); return; }
      hrProbationCreateForm.reset();
      hrProbationCreateForm.dataset.pending = '0';
      if (employeeId && hrProbationEmployeeInput) hrProbationEmployeeInput.value = getEmployeeNameById(employeeId) || employeeId;
      const today = new Date();
      if (hrProbationStartInput) hrProbationStartInput.value = today.toISOString().slice(0, 10);
      const end = new Date(today); end.setMonth(end.getMonth() + 3);
      if (hrProbationEndInput) hrProbationEndInput.value = end.toISOString().slice(0, 10);
      openDialog(hrProbationCreateDlg);
    }

    function deriveYear(value) {
      if (!value) return null;
      try {
        const d = new Date(value);
        if (!Number.isNaN(d.getTime())) return d.getFullYear();
      } catch { /* ignore */ }
      return null;
    }

    function getItemYear(item) {
      return deriveYear(item?.period_start) || deriveYear(item?.period_end) || deriveYear(item?.created_at) || null;
    }

    function populateHrDepartmentOptions() {
      if (!hrDepartmentOptions) return;
      const options = hrDepartmentsList.map((dept) => {
        const labelParts = [dept.name || 'Department'];
        if (dept.code) labelParts.push(`(${dept.code})`);
        const label = `${labelParts.join(' ')} — ${dept.id || ''}`;
        return `<option value="${escapeHtml(dept.name || '')}" data-id="${escapeHtml(dept.id || '')}">${escapeHtml(label)}</option>`;
      }).join('');
      hrDepartmentOptions.innerHTML = options;
    }

    function populatePerformanceDeptOptions() {
      if (!hrPerformanceDept) return;
      const options = ['<option value=\"\">All departments</option>'];
      hrDepartmentsList.forEach((dept) => {
        options.push(`<option value="${escapeHtml(dept.id || '')}">${escapeHtml(dept.name || dept.id || '')}</option>`);
      });
      hrPerformanceDept.innerHTML = options.join('');
    }

    function populateTrainingCourseOptions() {
      if (!hrTrainingCourseOptions) return;
      const opts = hrTrainingCourses.map((c) => `<option value="${escapeHtml(c.title || '')}" data-id="${escapeHtml(c.id || '')}">${escapeHtml(c.title || '')}</option>`).join('');
      hrTrainingCourseOptions.innerHTML = opts;
    }

    function populateTrainingAttendeesSelect() {
      if (!hrTrainingAttendeesSelect) return;
      const opts = hrPeopleList.map((p) => {
        const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id || '';
        return `<option value="${escapeHtml(p.id || '')}">${escapeHtml(name)}</option>`;
      }).join('');
      hrTrainingAttendeesSelect.innerHTML = opts;
    }

    function populateSkillOptions() {
      if (!hrSkillOptions) return;
      const opts = hrSkillsList.map((s) => `<option value="${escapeHtml(s.name || '')}" data-id="${escapeHtml(s.id || '')}">${escapeHtml(s.name || '')}</option>`).join('');
      hrSkillOptions.innerHTML = opts;
    }

    function populateApplicantOptions() {
      if (!hrApplicantOptions) return;
      const opts = hrApplicantList.map((a) => `<option value="${escapeHtml(a.full_name || '')}" data-id="${escapeHtml(a.id || '')}">${escapeHtml(a.full_name || '')}</option>`).join('');
      hrApplicantOptions.innerHTML = opts;
    }

    function populateVacancyOptions() {
      if (!hrVacancyOptions) return;
      const options = [];
      const openVacancies = hrVacancyList.filter((v) => String(v.status || '').toLowerCase() === 'open' || !v.status);
      openVacancies.forEach((v) => {
        const label = v.title || getPositionNameById(v.position_id) || v.id || '';
        options.push(`<option value="${escapeHtml(label)}" data-id="${escapeHtml(v.id || '')}">${escapeHtml(label)}</option>`);
      });
      hrVacancyOptions.innerHTML = options.join('');
    }

    // One-step certification entry + assignment. When opened from an employee's
    // detail the employee is fixed (its field is hidden); from the Learning page
    // the employee is picked in the dialog.
    function openEmployeeCertDialog(employeeId = '') {
      if (!hrEmployeeCertDlg || !hrEmployeeCertForm) { _hrWarnMissing('#hrEmployeeCertDlg'); return; }
      hrEmployeeCertForm.reset();
      hrEmployeeCertForm.dataset.pending = '0';
      hrEmployeeCertForm.dataset.employeeId = employeeId || '';
      populateEmployeeOptions();
      const empField = document.getElementById('hrEmployeeCertEmployeeField');
      if (employeeId) {
        if (hrEmployeeCertEmployeeInput) hrEmployeeCertEmployeeInput.value = getEmployeeNameById(employeeId) || employeeId;
        if (empField) empField.style.display = 'none';
      } else if (empField) {
        empField.style.display = '';
      }
      openDialog(hrEmployeeCertDlg);
    }

    function formatBytes(bytes) {
      if (!bytes || Number.isNaN(Number(bytes))) return '';
      const b = Number(bytes);
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${Math.round((b / 1024) * 10) / 10} KB`;
      return `${Math.round((b / (1024 * 1024)) * 10) / 10} MB`;
    }

    async function openApplicantDetailDialog(applicantId) {
      const applicant = hrApplicantList.find((a) => String(a.id) === String(applicantId));
      if (!applicant || !hrApplicantDetailDlg) {
        showFlash('Applicant not found', 'error');
        return;
      }
      if (hrApplicantDetailTitle) hrApplicantDetailTitle.textContent = applicant.full_name || 'Applicant';
      if (hrApplicantDetailMeta) {
        const parts = [];
        if (applicant.email) parts.push(applicant.email);
        if (applicant.phone) parts.push(applicant.phone);
        if (applicant.source) parts.push(`Source: ${applicant.source}`);
        if (applicant.date_of_birth) parts.push(`DOB: ${applicant.date_of_birth}`);
        hrApplicantDetailMeta.textContent = parts.join(' • ') || '—';
      }
      if (hrApplicantDetailTags) {
        const tags = [];
        if (applicant.highest_degree) tags.push(`<span class="tag">${escapeHtml(applicant.highest_degree)}</span>`);
        if (applicant.expertise) tags.push(`<span class="tag">${escapeHtml(applicant.expertise)}</span>`);
        if (applicant.skills) tags.push(`<span class="tag">${escapeHtml(applicant.skills)}</span>`);
        if (applicant.competencies) tags.push(`<span class="tag">${escapeHtml(applicant.competencies)}</span>`);
        hrApplicantDetailTags.innerHTML = tags.join('') || '<span style="color:var(--sub);">No tags</span>';
      }
      if (hrApplicantDetailBio) hrApplicantDetailBio.textContent = applicant.biography || '—';
      if (hrApplicantDocApplicantInput) {
        hrApplicantDocApplicantInput.value = applicant.full_name || '';
      }
      if (hrApplicantDocsTbody) {
        hrApplicantDocsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      }
      try {
        const params = new URLSearchParams({ owner_type: 'applicant', owner_id: String(applicant.id) });
        const data = await adminAPI.fetch(`${API.adminHrDocuments}?${params.toString()}`, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        if (hrApplicantDocsTbody) {
          const rows = items.map((d, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(d.filename || '')}</td>
              <td>${escapeHtml(d.mime_type || '')}</td>
              <td>${formatBytes(d.size)}</td>
              <td>
                <div class="row" style="gap:6px;">
                  <a class="btn ghost small" href="${API.adminHrDocumentDownload(d.id || '')}" target="_blank" rel="noopener">Download</a>
                </div>
              </td>
            </tr>
          `).join('');
          setTableRows(hrApplicantDocsTbody, rows, 'No documents.');
        }
      } catch (err) {
        if (hrApplicantDocsTbody) setTableRows(hrApplicantDocsTbody, '', err && err.message ? err.message : 'Failed to load documents.');
      }
      openDialog(hrApplicantDetailDlg);
    }

    function populateOnboardingTemplateOptions() {
      if (!hrOnboardingTemplateOptions) return;
      const opts = hrOnboardingTemplates.map((t) => `<option value="${escapeHtml(t.name || '')}" data-id="${escapeHtml(t.id || '')}">${escapeHtml(t.name || '')}</option>`).join('');
      hrOnboardingTemplateOptions.innerHTML = opts;
    }

    function populateHrPositionOptions() {
      if (!hrPositionOptions) return;
      const options = hrPositionsList.map((pos) => {
        const labelParts = [pos.name || 'Position'];
        if (pos.code) labelParts.push(`(${pos.code})`);
        const label = `${labelParts.join(' ')} — ${pos.id || ''}`;
        return `<option value="${escapeHtml(pos.name || '')}" data-id="${escapeHtml(pos.id || '')}">${escapeHtml(label)}</option>`;
      }).join('');
      hrPositionOptions.innerHTML = options;
    }

    function findOptionId(optionsEl, value) {
      if (!optionsEl || !value) return null;
      const option = optionsEl.querySelector(`option[value="${CSS.escape(value)}"]`);
      if (option && option.dataset && option.dataset.id) return option.dataset.id;
      return null;
    }

    function getDepartmentNameById(id) {
      if (!id) return '';
      const dept = hrDepartmentsMap.get(String(id)) || hrDepartmentsList.find((item) => String(item.id) === String(id));
      if (!dept) return String(id);
      if (dept.code) return `${dept.name || id} (${dept.code})`;
      return dept.name || String(id);
    }

    function getPositionNameById(id) {
      if (!id) return '';
      const pos = hrPositionsList.find((item) => String(item.id) === String(id));
      if (!pos) return String(id);
      if (pos.code) return `${pos.name || id} (${pos.code})`;
      return pos.name || String(id);
    }

    function getSkillNameById(id) {
      if (!id) return '';
      const skill = hrSkillsList.find((s) => String(s.id) === String(id));
      return skill ? skill.name || String(id) : String(id);
    }

    function getTemplateItemNameById(id) {
      if (!id) return '';
      const item = hrOnboardingTemplateItems.find((t) => String(t.id) === String(id));
      return item ? item.name || String(id) : String(id);
    }

    function getOnboardingTemplateNameById(id) {
      if (!id) return '';
      const tpl = hrOnboardingTemplates.find((t) => String(t.id) === String(id));
      return tpl ? tpl.name || String(id) : String(id);
    }

    function getApplicantNameById(id) {
      if (!id) return '';
      const app = hrApplicantList.find((a) => String(a.id) === String(id));
      return app ? app.full_name || String(id) : String(id);
    }

    function getVacancyTitleById(id) {
      if (!id) return '';
      const vac = hrVacancyList.find((v) => String(v.id) === String(id));
      return vac ? vac.title || String(id) : String(id);
    }

    function formatLevelWithLabel(level) {
      const num = Number(level);
      if (!Number.isFinite(num)) return '';
      const labels = { 2: 'Awareness', 5: 'Understanding', 7: 'Expert', 10: 'Master' };
      const label = labels[num];
      return label ? `${num} — ${label}` : String(num);
    }

    function getEmployeeNameById(id) {
      if (!id) return '';
      const person = hrPeopleMap.get(String(id)) || hrPeopleList.find((p) => String(p.id) === String(id));
      if (!person) return String(id);
      return `${person.first_name || ''} ${person.last_name || ''}`.trim() || String(id);
    }

    function resolveEmployeeId(raw) {
      const value = (raw || '').trim();
      if (!value) return null;
      const byId = hrPeopleMap.get(String(value));
      if (byId) return byId.id;
      const lower = value.toLowerCase();
      const found = hrPeopleList.find((p) => `${p.first_name || ''} ${p.last_name || ''}`.trim().toLowerCase() === lower);
      return found ? found.id : null;
    }

    function populateEmployeeOptions() {
      if (!hrEmployeeOptions) return;
      const opts = hrPeopleList.map((p) => {
        const label = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id || '';
        return `<option value="${escapeHtml(label)}" data-id="${escapeHtml(p.id || '')}">${escapeHtml(label)} (${escapeHtml(p.id || '')})</option>`;
      }).join('');
      hrEmployeeOptions.innerHTML = opts;
    }

    // Every department beneath `rootId` (its whole subtree). Used to keep a
    // department from being parented to itself or one of its own descendants,
    // which would create a cycle in the tree.
    function _departmentDescendantIds(rootId) {
      const out = new Set();
      if (!rootId) return out;
      const byParent = new Map();
      (hrDepartmentsList || []).forEach((d) => {
        const p = d.parent_department_id ? String(d.parent_department_id) : '';
        if (!byParent.has(p)) byParent.set(p, []);
        byParent.get(p).push(String(d.id));
      });
      const stack = [String(rootId)];
      while (stack.length) {
        (byParent.get(stack.pop()) || []).forEach((cid) => {
          if (!out.has(cid)) { out.add(cid); stack.push(cid); }
        });
      }
      return out;
    }

    // Fill a <select> with the departments that may be a parent (value = id) plus
    // "— None —", preselecting `selectedId`. `excludeId` (the department being
    // edited) and its descendants are omitted to prevent cycles.
    // Render a picker of {value,label} entries: "— None —" first, then the entries
    // sorted by the label the user actually reads (the API returns them in
    // insertion order, which looks random in a dropdown). `nameFor` resolves a
    // `current` value missing from the list so it stays selectable.
    function renderIdSelect(sel, entries, current, nameFor) {
      const sorted = entries.slice().sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }));
      sel.innerHTML = ['<option value="">— None —</option>'].concat(
        sorted.map((e) => `<option value="${escapeHtml(e.value)}">${escapeHtml(e.label)}</option>`),
      ).join('');
      sel.value = current;
      if (current && sel.value !== current) {
        const name = nameFor(current) || current;
        sel.insertAdjacentHTML('beforeend', `<option value="${escapeHtml(current)}">${escapeHtml(name)}</option>`);
        sel.value = current;
      }
    }

    function populateDepartmentSelect(sel, selectedId, excludeId) {
      if (!sel) return;
      const banned = _departmentDescendantIds(excludeId);
      if (excludeId) banned.add(String(excludeId));
      const entries = (Array.isArray(hrDepartmentsList) ? hrDepartmentsList : [])
        .filter((d) => !banned.has(String(d.id)))
        .map((d) => ({
          value: String(d.id || ''),
          label: d.code ? `${d.name || d.id} (${d.code})` : (d.name || String(d.id || '')),
        }));
      renderIdSelect(sel, entries, selectedId ? String(selectedId) : '', getDepartmentNameById);
    }

    // Fill a <select> with every employee (value = employee id) plus a "— None —"
    // option, preselecting `selectedId`. Used for the department manager/deputy
    // pickers so the full list is always visible and can be cleared.
    function populateEmployeeSelect(sel, selectedId) {
      if (!sel) return;
      const entries = (Array.isArray(hrPeopleList) ? hrPeopleList : []).map((p) => ({
        value: String(p.id || ''),
        label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || String(p.id || ''),
      }));
      renderIdSelect(sel, entries, selectedId ? String(selectedId) : '', getEmployeeNameById);
    }

    function resolveReviewerEmployeeId(raw) {
      const value = (raw || '').trim();
      if (!value) return null;
      if (hrReviewReviewerOptions) {
        const opt = hrReviewReviewerOptions.querySelector(`option[value="${CSS.escape(value)}"]`);
        if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
      }
      const lower = value.toLowerCase();
      const person = hrPeopleList.find((p) => {
        const name = `${p.first_name || ''} ${p.last_name || ''}`.trim().toLowerCase();
        return name === lower || String(p.id || '').toLowerCase() === lower;
      });
      return person ? person.id : null;
    }

    function getDepartmentPathLabel(id) {
      if (!id) return '';
      const visited = new Set();
      const parts = [];
      let currentId = String(id);
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const dept = hrDepartmentsMap.get(currentId);
        if (!dept) break;
        parts.unshift(dept.name || dept.code || currentId);
        currentId = dept.parent_department_id ? String(dept.parent_department_id) : null;
      }
      return parts.join(' / ');
    }

    function getDepartmentDescendants(rootId) {
      const ids = new Set();
      if (!rootId) return ids;
      const queue = [String(rootId)];
      while (queue.length) {
        const current = queue.shift();
        ids.add(current);
        hrDepartmentsList.forEach((dept) => {
          if (dept.parent_department_id && String(dept.parent_department_id) === current && !ids.has(String(dept.id))) {
            queue.push(String(dept.id));
          }
        });
      }
      return ids;
    }

    function getEmployeeRoleBadges(employeeId) {
      if (!employeeId) return [];
      const roles = [];
      hrDepartmentsList.forEach((dept) => {
        if (String(dept.manager_employee_id) === String(employeeId)) {
          roles.push({ label: `Manager — ${dept.name || 'Dept'}`, className: 'badge ok' });
        }
        if (String(dept.deputy_employee_id) === String(employeeId)) {
          roles.push({ label: `Deputy — ${dept.name || 'Dept'}`, className: 'badge warn' });
        }
      });
      return roles;
    }

    function populateHrPeopleDeptFilter() {
      if (!hrPeopleDeptFilter) return;
      const prev = hrPeopleDeptFilter.value;
      const options = ['<option value="">All departments</option>'].concat(
        hrDepartmentsList
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          .map((dept) => {
            const label = escapeHtml(getDepartmentPathLabel(dept.id) || dept.name || dept.code || dept.id);
            return `<option value="${escapeHtml(dept.id || '')}">${label}</option>`;
          }),
      );
      hrPeopleDeptFilter.innerHTML = options.join('');
      if (prev) hrPeopleDeptFilter.value = prev;
    }

    function populateHrPeoplePositionFilter() {
      if (!hrPeoplePositionFilter) return;
      const prev = hrPeoplePositionFilter.value;
      const options = ['<option value="">All positions</option>'].concat(
        hrPositionsList
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          .map((pos) => `<option value="${escapeHtml(pos.id || '')}">${escapeHtml(pos.name || pos.code || pos.id || '')}</option>`),
      );
      hrPeoplePositionFilter.innerHTML = options.join('');
      if (prev) hrPeoplePositionFilter.value = prev;
    }

    // renderHrDepartmentTree removed: the tree UI never landed in admin.html
    // (3 missing ids — hrDeptTree, hrDeptTreeClear, hrDeptSelectionLabel),
    // so the function was dead in practice. The `hrDeptTreeSelection` state
    // variable stays in place — it's always '' now, so the people-table
    // filter (`hrDeptTreeSelection || selectDeptFilter`) just falls through
    // to the existing dept-filter dropdown.

    function formatBadgeClass(status) {
      const normalized = (status || '').toLowerCase();
      if (normalized === 'terminated') return 'badge err';
      if (normalized === 'on_leave') return 'badge warn';
      return 'badge ok';
    }

    function resolveDepartmentId(raw, optionsEl = hrDepartmentOptions) {
      const value = (raw || '').trim();
      if (!value) return null;
      const matchFromOption = findOptionId(optionsEl, value);
      if (matchFromOption) return matchFromOption;
      const lower = value.toLowerCase();
      const byId = hrDepartmentsList.find((dept) => String(dept.id || '').toLowerCase() === lower);
      if (byId) return byId.id;
      const byCode = hrDepartmentsList.find((dept) => (dept.code || '').toLowerCase() === lower);
      if (byCode) return byCode.id;
      const byName = hrDepartmentsList.find((dept) => (dept.name || '').toLowerCase() === lower);
      return byName ? byName.id : null;
    }

    function resolvePositionId(raw) {
      const value = (raw || '').trim();
      if (!value) return null;
      const matchFromOption = findOptionId(hrPositionOptions, value);
      if (matchFromOption) return matchFromOption;
      const lower = value.toLowerCase();
      const byId = hrPositionsList.find((pos) => String(pos.id || '').toLowerCase() === lower);
      if (byId) return byId.id;
      const byCode = hrPositionsList.find((pos) => (pos.code || '').toLowerCase() === lower);
      if (byCode) return byCode.id;
      const byName = hrPositionsList.find((pos) => (pos.name || '').toLowerCase() === lower);
      return byName ? byName.id : null;
    }

    function resolveCourseId(raw) {
      const value = (raw || '').trim();
      if (!value) return null;
      if (hrTrainingCourseOptions) {
        const opt = hrTrainingCourseOptions.querySelector(`option[value="${CSS.escape(value)}"]`);
        if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
      }
      const lower = value.toLowerCase();
      const byId = hrTrainingCourses.find((c) => String(c.id || '').toLowerCase() === lower);
      if (byId) return byId.id;
      const byTitle = hrTrainingCourses.find((c) => (c.title || '').toLowerCase() === lower);
      return byTitle ? byTitle.id : null;
    }

    async function openHrEmployeeDialog() {
      if (!hrEmployeeDlg || !hrEmployeeForm) { _hrWarnMissing('#hrEmployeeDlg'); return; }
      hrEmployeeForm.reset();
      hrEmployeeForm.dataset.pending = '0';
      if (hrEmployeeStatusInput) hrEmployeeStatusInput.value = 'active';
      if (hrEmployeeEmploymentTypeInput) hrEmployeeEmploymentTypeInput.value = 'full_time';
      if (!hrDataState.departments) {
        try { await loadHrDepartments(); } catch {}
      }
      if (!hrDataState.positions) {
        try { await loadHrPositions(); } catch {}
      }
      populateHrDepartmentOptions();
      populateHrPositionOptions();
      openDialog(hrEmployeeDlg);
      setTimeout(() => {
        if (hrEmployeeFirstNameInput) {
          try { hrEmployeeFirstNameInput.focus(); } catch {}
        }
      }, 30);
    }

    function closeHrEmployeeDialog() {
      if (!hrEmployeeDlg) return;
      try { hrEmployeeDlg.close(); }
      catch { hrEmployeeDlg.open = false; }
    }

    function openHrLeaveDialog() {
      if (!hrLeaveDlg || !hrLeaveForm) { _hrWarnMissing('#hrLeaveDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrLeaveForm.reset();
      hrLeaveForm.dataset.pending = '0';
      const todayStr = new Date().toISOString().slice(0, 10);
      if (hrLeaveStartInput) hrLeaveStartInput.value = todayStr;
      if (hrLeaveEndInput) hrLeaveEndInput.value = todayStr;
      if (hrLeaveDaysSummary) hrLeaveDaysSummary.textContent = 'Select start/end dates to preview.';
      if (hrLeaveDaysTbody) hrLeaveDaysTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Select start/end dates.</td></tr>`;
      openDialog(hrLeaveDlg);
      setTimeout(() => {
        if (hrLeaveTypeInput) {
          try { hrLeaveTypeInput.focus(); } catch {}
        }
      }, 20);
    }

    function closeHrLeaveDialog() {
      if (!hrLeaveDlg) return;
      try { hrLeaveDlg.close(); }
      catch { hrLeaveDlg.open = false; }
    }

    function populateGoalExistingOptions() {
      if (!hrGoalExistingSelect) return;
      const deptId = hrEmployeeDetail ? hrEmployeeDetail.department_id : null;
      const options = ['<option value="">Select department goal…</option>'];
      const filtered = hrDepartmentGoalsCache
        .filter((g) => deptId && String(g.department_id || '') === String(deptId));
      filtered.forEach((g) => {
        options.push(`<option value="${escapeHtml(g.id || '')}" data-period="${escapeHtml(g.period || '')}" data-description="${escapeHtml(g.description || '')}">${escapeHtml(g.title || g.id || '')}</option>`);
      });
      hrGoalExistingSelect.innerHTML = options.join('');
    }

    function setGoalMode(mode) {
      const isCustom = mode === 'custom';
      if (hrGoalModeSelect) hrGoalModeSelect.value = mode;
      if (hrGoalExistingRow) hrGoalExistingRow.style.display = isCustom ? 'none' : '';
      if (isCustom) {
        if (hrGoalTitleInput) hrGoalTitleInput.value = '';
        if (hrGoalDescriptionInput) hrGoalDescriptionInput.value = '';
      } else if (hrGoalExistingSelect && hrGoalExistingSelect.value) {
        const option = hrGoalExistingSelect.selectedOptions[0];
        if (hrGoalTitleInput) hrGoalTitleInput.value = option.textContent || '';
        if (hrGoalDescriptionInput) hrGoalDescriptionInput.value = option.dataset.description || '';
      }
    }

    function isGoalCheckinAllowed(goal, now = new Date()) {
      const status = (goal.status || '').toLowerCase();
      if (status === 'done' || status === 'completed') return false;
      const end = goal.period_end ? new Date(goal.period_end) : null;
      if (!end || Number.isNaN(end.getTime())) return true;
      const deadline = new Date(end);
      deadline.setDate(deadline.getDate() + 30);
      return now <= deadline;
    }

    function populateGoalCheckinOptions() {
      if (!hrGoalCheckinSelect) return;
      const deptId = hrEmployeeDetail ? hrEmployeeDetail.department_id : null;
      const options = ['<option value="">Select goal…</option>'];
      const now = new Date();
      const filtered = hrDepartmentGoalsCache.filter((g) => deptId && String(g.department_id || '') === String(deptId) && isGoalCheckinAllowed(g, now));
      filtered.forEach((g) => options.push(`<option value="${escapeHtml(g.id || '')}">${escapeHtml(g.title || g.id || '')}</option>`));
      hrGoalCheckinSelect.innerHTML = options.join('');
    }

    async function populateOnboardingTaskOptions(onboardingId) {
      if (!hrOnboardingSelect) return;
      if (!onboardingId) {
        hrOnboardingSelect.innerHTML = '<option value=\"\">Select an onboarding from the list first</option>';
        return;
      }
      hrOnboardingSelect.innerHTML = '<option value=\"\">Loading tasks…</option>';
      try {
        const tasks = await loadOnboardingTasks(onboardingId);
        const opts = (tasks || []).map((t) => {
          const label = t.template_item_name || getTemplateItemNameById(t.template_item_id) || t.id || '';
          return `<option value="${escapeHtml(t.id || '')}">${escapeHtml(label)} — ${escapeHtml(t.status || '')}</option>`;
        });
        hrOnboardingSelect.innerHTML = ['<option value=\"\">Select task…</option>', ...opts].join('');
      } catch (err) {
        hrOnboardingSelect.innerHTML = `<option value=\"\">${escapeHtml(err && err.message ? err.message : 'Failed to load tasks')}</option>`;
      }
    }

    function populateProbationOptions(employeeId) {
      if (!hrProbationSelect) return;
      const options = ['<option value="">Select probation period…</option>'];
      const items = hrEmployeeSectionState?.talent?.probationItems || [];
      const probationItems = items.filter((item) => String(item.employee_id || '') === String(employeeId));
      probationItems.forEach((item) => {
        options.push(`<option value="${escapeHtml(item.id || '')}">${escapeHtml(item.end_date || '')} — ${escapeHtml(item.status || '')}</option>`);
      });
      hrProbationSelect.innerHTML = options.join('');
    }

    function openHrGoalDialog() {
      if (!hrGoalDlg || !hrGoalForm) { _hrWarnMissing('#hrGoalDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.department_id) {
        showFlash('Department required to add a goal.', 'error');
        return;
      }
      hrGoalForm.reset();
      hrGoalForm.dataset.pending = '0';
      const yearStart = new Date();
      yearStart.setMonth(0, 1);
      const yearEnd = new Date();
      yearEnd.setMonth(11, 31);
      if (hrGoalPeriodInput) hrGoalPeriodInput.value = 'year';
      if (hrGoalStartInput) hrGoalStartInput.value = yearStart.toISOString().slice(0, 10);
      if (hrGoalEndInput) hrGoalEndInput.value = yearEnd.toISOString().slice(0, 10);
      populateGoalExistingOptions();
      setGoalMode('dept');
      openDialog(hrGoalDlg);
      setTimeout(() => {
        if (hrGoalTitleInput) {
          try { hrGoalTitleInput.focus(); } catch {}
        }
      }, 20);
    }

    function openHrGoalCheckinDialog() {
      if (!hrGoalCheckinDlg || !hrGoalCheckinForm) { _hrWarnMissing('#hrGoalCheckinDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.department_id) {
        showFlash('Select an employee with a department first.', 'error');
        return;
      }
      populateGoalCheckinOptions();
      hrGoalCheckinForm.reset();
      hrGoalCheckinForm.dataset.pending = '0';
      if (hrGoalCheckinProgress) hrGoalCheckinProgress.value = '0';
      openDialog(hrGoalCheckinDlg);
    }
    function openHrGoalCheckinDialogWithGoal(goalId) {
      openHrGoalCheckinDialog();
      if (hrGoalCheckinSelect && goalId) {
        hrGoalCheckinSelect.value = goalId;
      }
    }

    function openHrReviewDialog() {
      if (!hrReviewDlg || !hrReviewForm) { _hrWarnMissing('#hrReviewDlg'); return; }
      if (!hrEmployeeDetail) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      populateReviewerOptions();
      hrReviewForm.reset();
      hrReviewForm.dataset.pending = '0';
      if (hrReviewPeriodInput) hrReviewPeriodInput.value = 'annual';
      openDialog(hrReviewDlg);
    }

    async function openHrOnboardingDialog(onboardingId = '') {
      if (!hrOnboardingDlg || !hrOnboardingForm) { _hrWarnMissing('#hrOnboardingDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrOnboardingForm.reset();
      hrOnboardingForm.dataset.pending = '0';
      await populateOnboardingTaskOptions(onboardingId);
      openDialog(hrOnboardingDlg);
    }

    function openHrProbationDialog(probationId = '', employeeId = '') {
      if (!hrProbationDlg || !hrProbationForm) { _hrWarnMissing('#hrProbationDlg'); return; }
      hrProbationForm.reset();
      hrProbationForm.dataset.pending = '0';
      const empId = employeeId || (hrEmployeeDetail && hrEmployeeDetail.id) || '';
      hrProbationForm.dataset.employeeId = empId || '';
      // populate select with probations for this employee if known
      if (hrProbationSelect) {
        let options = ['<option value=\"\">Select probation…</option>'];
        const source = hrProbationList && hrProbationList.length
          ? hrProbationList.filter((p) => !empId || String(p.employee_id || '') === String(empId))
          : [];
        if (source.length) {
          options = ['<option value=\"\">Select probation…</option>', ...source.map((p) => `<option value=\"${escapeHtml(p.id || '')}\">${escapeHtml(p.end_date || p.id || '')}</option>`)];
        } else if (probationId) {
          options = [`<option value=\"${escapeHtml(probationId)}\">${escapeHtml(probationId)}</option>`];
        }
        hrProbationSelect.innerHTML = options.join('');
      }
      const todayStr = new Date().toISOString().slice(0, 10);
      if (hrProbationDateInput) hrProbationDateInput.value = todayStr;
      if (probationId && hrProbationSelect) hrProbationSelect.value = probationId;
      openDialog(hrProbationDlg);
    }

    function openHrTrainingDialog() {
      if (!hrTrainingDlg || !hrTrainingForm) { _hrWarnMissing('#hrTrainingDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrTrainingForm.reset();
      hrTrainingForm.dataset.pending = '0';
      openDialog(hrTrainingDlg);
      setTimeout(() => {
        if (hrTrainingSessionInput) {
          try { hrTrainingSessionInput.focus(); } catch {}
        }
      }, 20);
    }

    async function openHrTrainingSessionDialog() {
      if (!hrTrainingSessionDlg || !hrTrainingSessionForm) { _hrWarnMissing('#hrTrainingSessionDlg'); return; }
      try {
        if (!hrDataState.people) await loadHrPeople();
        if (!hrTrainingCourses.length) {
          try { await adminAPI.fetch(API.adminHrTrainingCourses, { method: 'GET' }).then((data) => {
            hrTrainingCourses = Array.isArray(data?.items) ? data.items : [];
            hrTrainingCourseMap.clear();
            hrTrainingCourses.forEach((c) => hrTrainingCourseMap.set(String(c.id), c));
            populateTrainingCourseOptions();
          }); } catch {}
        }
      } catch {}
      populateTrainingAttendeesSelect();
      hrTrainingSessionForm.reset();
      hrTrainingSessionForm.dataset.pending = '0';
      if (hrTrainingCourseNewToggle) hrTrainingCourseNewToggle.checked = false;
      if (hrTrainingCourseExtra) hrTrainingCourseExtra.style.display = 'none';
      openDialog(hrTrainingSessionDlg);
    }

    function openHrLoanDialog() {
      if (!hrLoanDlg || !hrLoanForm) { _hrWarnMissing('#hrLoanDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrLoanForm.reset();
      hrLoanForm.dataset.pending = '0';
      if (hrLoanCreateTermInput) hrLoanCreateTermInput.value = '6';
      if (hrLoanCreateCollateralInput) hrLoanCreateCollateralInput.value = '0';
      if (hrLoanCreateCurrencySelect) hrLoanCreateCurrencySelect.value = 'GMD';
      if (hrLoanCreateBaseCurrencySelect) hrLoanCreateBaseCurrencySelect.value = 'USD';
      if (hrLoanCreateFxInput) hrLoanCreateFxInput.value = '';
      openDialog(hrLoanDlg);
      setTimeout(() => {
      if (hrLoanCreatePrincipalInput) {
        try { hrLoanCreatePrincipalInput.focus(); } catch {}
      }
      }, 20);
    }

    function openHrProfileChangeDialog() {
      if (!hrProfileChangeDlg || !hrProfileChangeForm) { _hrWarnMissing('#hrProfileChangeDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrProfileChangeForm.reset();
      hrProfileChangeForm.dataset.pending = '0';
      openDialog(hrProfileChangeDlg);
      setTimeout(() => {
        if (hrProfileFieldInput) {
          try { hrProfileFieldInput.focus(); } catch {}
        }
      }, 20);
    }

    function openHrDocUploadDialog(presetCategory) {
      if (!hrDocUploadDlg || !hrDocUploadForm) { _hrWarnMissing('#hrDocUploadDlg'); return; }
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
        showFlash('Select an employee first.', 'error');
        return;
      }
      hrDocUploadForm.reset();
      hrDocUploadForm.dataset.pending = '0';
      // When launched from a missing required-document row, pre-select its category
      // so the upload lands tagged correctly (and satisfies that requirement).
      if (hrDocCategoryInput) {
        hrDocCategoryInput.value = typeof presetCategory === 'string' ? presetCategory : '';
      }
      openDialog(hrDocUploadDlg);
      setTimeout(() => {
        if (hrDocFileInput) {
          try { hrDocFileInput.focus(); } catch {}
        }
      }, 20);
    }

    function enumerateLeaveDays() {
      const startVal = hrLeaveStartInput ? hrLeaveStartInput.value : '';
      const endVal = hrLeaveEndInput ? hrLeaveEndInput.value : '';
      if (!startVal || !endVal) return [];
      const start = new Date(startVal);
      const end = new Date(endVal);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
      const days = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push(d.toISOString().slice(0, 10));
      }
      return days;
    }

    let hrLeaveDayOverrides = [];

    function renderLeaveDays() {
      const days = enumerateLeaveDays();
      if (!hrLeaveDaysTbody) { _hrWarnMissing('#hrLeaveDaysTbody'); return; }
      if (!days.length) {
        hrLeaveDaysTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Select valid dates.</td></tr>`;
        if (hrLeaveDaysSummary) hrLeaveDaysSummary.textContent = 'Select start/end dates to preview.';
        return;
      }
      hrLeaveDayOverrides = hrLeaveDayOverrides.filter((item) => days.includes(item.date));
      const rows = days.map((date) => {
        const existing = hrLeaveDayOverrides.find((d) => d.date === date) || { date, half: false, holiday: false, off: false };
        return `
          <tr>
            <td>${escapeHtml(date)}</td>
            <td><input type="checkbox" data-leave-date="${escapeHtml(date)}" data-leave-toggle="half" ${existing.half ? 'checked' : ''} /></td>
            <td><input type="checkbox" data-leave-date="${escapeHtml(date)}" data-leave-toggle="holiday" ${existing.holiday ? 'checked' : ''} /></td>
            <td><input type="checkbox" data-leave-date="${escapeHtml(date)}" data-leave-toggle="off" ${existing.off ? 'checked' : ''} /></td>
          </tr>
        `;
      }).join('');
      hrLeaveDaysTbody.innerHTML = rows;
      const { chargeable, halfCount } = summarizeLeaveDays();
      if (hrLeaveDaysSummary) hrLeaveDaysSummary.textContent = `${chargeable} day(s) chargeable${halfCount ? ` • ${halfCount} half-day(s)` : ''}`;
    }

    function summarizeLeaveDays() {
      const days = enumerateLeaveDays();
      let chargeable = 0;
      let halfCount = 0;
      days.forEach((date) => {
        const override = hrLeaveDayOverrides.find((d) => d.date === date);
        if (override?.holiday || override?.off) return;
        if (override?.half) {
          chargeable += 0.5;
          halfCount += 1;
        } else {
          chargeable += 1;
        }
      });
      return { chargeable, halfCount };
    }

    function handleLeaveDayToggle(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.dataset.leaveDate || !target.dataset.leaveToggle) return;
      const date = target.dataset.leaveDate;
      const kind = target.dataset.leaveToggle;
      let entry = hrLeaveDayOverrides.find((d) => d.date === date);
      if (!entry) {
        entry = { date, half: false, holiday: false, off: false };
        hrLeaveDayOverrides.push(entry);
      }
      entry[kind] = target.checked;
      // If marked holiday/off, clear half-day
      if ((kind === 'holiday' || kind === 'off') && target.checked) {
        entry.half = false;
        const checkbox = hrLeaveDaysTbody?.querySelector(`input[data-leave-date="${CSS.escape(date)}"][data-leave-toggle="half"]`);
        if (checkbox) checkbox.checked = false;
      }
      const { chargeable, halfCount } = summarizeLeaveDays();
      if (hrLeaveDaysSummary) hrLeaveDaysSummary.textContent = `${chargeable} day(s) chargeable${halfCount ? ` • ${halfCount} half-day(s)` : ''}`;
    }

    async function handleHrEmployeeSubmit(event) {
      event.preventDefault();
      if (!hrEmployeeForm || hrEmployeeForm.dataset.pending === '1') return;
      if (hrEmployeeForm && !hrEmployeeForm.reportValidity()) return;

      const departmentId = resolveDepartmentId(normalizeOptionalInput(hrEmployeeDepartmentInput));
      const positionId = resolvePositionId(normalizeOptionalInput(hrEmployeePositionInput));
      // Manager & deputy are inferred from the department server-side — not sent.
      const payload = {
        first_name: (hrEmployeeFirstNameInput && hrEmployeeFirstNameInput.value.trim()) || '',
        last_name: (hrEmployeeLastNameInput && hrEmployeeLastNameInput.value.trim()) || '',
        email: normalizeOptionalInput(hrEmployeeEmailInput),
        phone: normalizeOptionalInput(hrEmployeePhoneInput),
        department_id: departmentId,
        position_id: positionId,
        status: (hrEmployeeStatusInput && hrEmployeeStatusInput.value) || 'active',
        employment_type: (hrEmployeeEmploymentTypeInput && hrEmployeeEmploymentTypeInput.value) || 'full_time',
      };

      hrEmployeeForm.dataset.pending = '1';
      if (hrEmployeeSubmitBtn) {
        hrEmployeeSubmitBtn.disabled = true;
        hrEmployeeSubmitBtn.textContent = 'Saving…';
      }

      try {
        await adminAPI.fetch(API.adminHrEmployees, { method: 'POST', body: JSON.stringify(payload) });
        showFlash('Employee added');
        closeHrEmployeeDialog();
        await loadHrPeople();
      } catch (err) {
        const message = err && err.message ? err.message : 'Failed to add employee.';
        showFlash(message, 'error');
      } finally {
        hrEmployeeForm.dataset.pending = '0';
        if (hrEmployeeSubmitBtn) {
          hrEmployeeSubmitBtn.disabled = false;
          hrEmployeeSubmitBtn.textContent = 'Save employee';
        }
      }
    }

    function openHrDepartmentDialog(dept = null, { parentId = null } = {}) {
      if (!hrDepartmentDlg || !hrDepartmentForm) { _hrWarnMissing('#hrDepartmentDlg'); return; }
      hrDepartmentForm.reset();
      hrDepartmentForm.dataset.pending = '0';
      hrDepartmentForm.dataset.editId = dept && dept.id ? String(dept.id) : '';
      if (hrDepartmentStatusInput) hrDepartmentStatusInput.value = dept && dept.is_active === false ? 'inactive' : 'active';
      populateHrDepartmentOptions();
      populateEmployeeOptions();     // manager / deputy manager pickers
      if (dept) {
        if (hrDepartmentNameInput) hrDepartmentNameInput.value = dept.name || '';
        if (hrDepartmentCodeInput) hrDepartmentCodeInput.value = dept.code || '';
      }
      // Parent is a real <select> keyed by department id with an explicit
      // "— None —", so an existing parent can be changed or cleared. (It used to
      // be an <input list=datalist>, which filters suggestions by the input's
      // text — pre-filling the current parent's name hid every other option.)
      // "Add sub-department" opens the create form with the parent preselected.
      populateDepartmentSelect(
        hrDepartmentParentInput,
        dept ? dept.parent_department_id : parentId,
        dept ? dept.id : null,
      );
      // Manager/deputy are real <select>s keyed by employee id, with an explicit
      // "— None —" option so a mistaken manager can be cleared. (They used to be
      // <input list=datalist>: the browser filters datalist suggestions by the
      // input's text, so pre-filling the current manager's name made it look like
      // only one employee existed.) Ensure the employee list is loaded even if the
      // People→Employees tab was never opened.
      const _fillManagers = () => {
        populateEmployeeSelect(hrDepartmentManagerInput, dept && dept.manager_employee_id);
        populateEmployeeSelect(hrDepartmentDeputyInput, dept && dept.deputy_employee_id);
      };
      _fillManagers();
      Promise.resolve(ensureImportEmployees()).then(_fillManagers).catch(() => {});
      openDialog(hrDepartmentDlg);
      setTimeout(() => {
        if (hrDepartmentNameInput) {
          try { hrDepartmentNameInput.focus(); } catch {}
        }
      }, 30);
    }

    function closeHrDepartmentDialog() {
      if (!hrDepartmentDlg) return;
      try { hrDepartmentDlg.close(); }
      catch { hrDepartmentDlg.open = false; }
    }

    async function handleHrDepartmentSubmit(event) {
      event.preventDefault();
      if (!hrDepartmentForm || hrDepartmentForm.dataset.pending === '1') return;
      if (!hrDepartmentForm.reportValidity()) return;
      const editId = hrDepartmentForm.dataset.editId || '';
      const isEdit = Boolean(editId);
      const payload = {
        name: (hrDepartmentNameInput && hrDepartmentNameInput.value.trim()) || '',
        code: normalizeOptionalInput(hrDepartmentCodeInput),
        // These are <select>s keyed by id; '' → null clears the parent/manager.
        parent_department_id: (hrDepartmentParentInput && hrDepartmentParentInput.value) || null,
        manager_employee_id: (hrDepartmentManagerInput && hrDepartmentManagerInput.value) || null,
        deputy_employee_id: (hrDepartmentDeputyInput && hrDepartmentDeputyInput.value) || null,
        is_active: isEdit ? undefined : (!hrDepartmentStatusInput || hrDepartmentStatusInput.value !== 'inactive'),
      };
      if (isEdit && hrDepartmentStatusInput) {
        payload.is_active = hrDepartmentStatusInput.value !== 'inactive';
      }
      hrDepartmentForm.dataset.pending = '1';
      if (hrDepartmentSubmitBtn) {
        hrDepartmentSubmitBtn.disabled = true;
        hrDepartmentSubmitBtn.textContent = 'Saving…';
      }
      try {
        const endpoint = isEdit ? API.adminHrDepartment(editId) : API.adminHrDepartments;
        const method = isEdit ? 'PATCH' : 'POST';
        await adminAPI.fetch(endpoint, { method, body: JSON.stringify(payload) });
        showFlash(isEdit ? 'Department updated' : 'Department added');
        closeHrDepartmentDialog();
        hrDataState.departments = false;
        hrDataState.people = false;
        await loadHrDepartments();
        renderHrPeopleTable();
      } catch (err) {
        const message = err && err.message ? err.message : 'Failed to add department.';
        showFlash(message, 'error');
      } finally {
        hrDepartmentForm.dataset.pending = '0';
        if (hrDepartmentSubmitBtn) {
          hrDepartmentSubmitBtn.disabled = false;
          hrDepartmentSubmitBtn.textContent = 'Save department';
        }
      }
    }

    function _populateHrPositionGradeOptions(selectedId) {
      if (!hrPositionGradeInput) return;
      const opts = ['<option value="">— No grade —</option>'];
      hrGradesList.slice().sort((a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0)).forEach((g) => {
        const label = `${g.code}${g.name ? ` — ${g.name}` : ''}`;
        opts.push(`<option value="${escapeHtml(g.id || '')}">${escapeHtml(label)}</option>`);
      });
      hrPositionGradeInput.innerHTML = opts.join('');
      if (selectedId) hrPositionGradeInput.value = String(selectedId);
    }

    function openHrPositionDialog(pos = null) {
      if (!hrPositionDlg || !hrPositionForm) { _hrWarnMissing('#hrPositionDlg'); return; }
      hrPositionForm.reset();
      hrPositionForm.dataset.pending = '0';
      _hrPositionEditId = pos && pos.id ? String(pos.id) : null;
      if (hrPositionDlgTitle) hrPositionDlgTitle.textContent = pos ? 'Edit position' : 'Add position';
      Promise.resolve(ensureGradesLoaded()).then(() => _populateHrPositionGradeOptions(pos ? pos.grade_id : ''));
      if (hrPositionNameInput) hrPositionNameInput.value = pos ? (pos.name || '') : '';
      if (hrPositionCodeInput) hrPositionCodeInput.value = pos ? (pos.code || '') : '';
      if (hrPositionEstablishedInput) hrPositionEstablishedInput.value = pos && pos.established_count != null ? String(pos.established_count) : '1';
      if (hrPositionDeptInput) hrPositionDeptInput.value = pos ? (getDepartmentNameById(pos.department_id) || '') : '';
      if (hrPositionStatusInput) hrPositionStatusInput.value = pos && pos.is_active === false ? 'inactive' : 'active';
      openDialog(hrPositionDlg);
      setTimeout(() => { if (hrPositionNameInput) { try { hrPositionNameInput.focus(); } catch {} } }, 30);
    }

    function closeHrPositionDialog() {
      if (!hrPositionDlg) return;
      try { hrPositionDlg.close(); }
      catch { hrPositionDlg.open = false; }
    }

    async function handleHrPositionSubmit(event) {
      event.preventDefault();
      if (!hrPositionForm || hrPositionForm.dataset.pending === '1') return;
      if (!hrPositionForm.reportValidity()) return;
      const deptId = resolveDepartmentId(normalizeOptionalInput(hrPositionDeptInput));
      if (!deptId) { showFlash('A department is required.', 'error'); return; }
      const estRaw = (hrPositionEstablishedInput && hrPositionEstablishedInput.value || '').trim();
      const established = estRaw === '' ? 1 : Number(estRaw);
      if (!Number.isInteger(established) || established < 0) { showFlash('Established headcount must be a whole number ≥ 0.', 'error'); return; }
      const payload = {
        name: (hrPositionNameInput && hrPositionNameInput.value.trim()) || '',
        code: normalizeOptionalInput(hrPositionCodeInput),
        department_id: deptId,
        grade_id: (hrPositionGradeInput && hrPositionGradeInput.value) || null,
        established_count: established,
        is_active: !hrPositionStatusInput || hrPositionStatusInput.value !== 'inactive',
      };
      hrPositionForm.dataset.pending = '1';
      if (hrPositionSubmitBtn) {
        hrPositionSubmitBtn.disabled = true;
        hrPositionSubmitBtn.textContent = 'Saving…';
      }
      try {
        if (_hrPositionEditId) {
          await adminAPI.fetch(API.adminHrPosition(_hrPositionEditId), { method: 'PATCH', body: JSON.stringify(payload) });
          showFlash('Position updated');
        } else {
          await adminAPI.fetch(API.adminHrPositions, { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Position added');
        }
        closeHrPositionDialog();
        hrDataState.positions = false;
        await loadHrPositions();
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (err && err.status === 409 && d === 'position_name_exists_in_department') {
          showFlash('A position with that name already exists in this department.', 'error');
        } else {
          showFlash(_hrErr(err, 'Failed to save position.'), 'error');
        }
      } finally {
        hrPositionForm.dataset.pending = '0';
        if (hrPositionSubmitBtn) {
          hrPositionSubmitBtn.disabled = false;
          hrPositionSubmitBtn.textContent = 'Save position';
        }
      }
    }

    // Shared HR list-UI primitives live in src/lib/hr-ui.js (single-source status
    // vocabulary, badges, initials avatars, kebab menus). Local aliases keep the
    // existing call sites unchanged.
    const HR_STATUS_META = hrui.HR_STATUS_META;
    const _titleCase = hrui.titleCase;
    const hrStatusPill = hrui.statusPill;
    const hrEmploymentBadge = hrui.employmentBadge;
    const hrInitials = hrui.initials;

    function _hrPeopleActiveExtraFilters() {
      return [hrPeoplePositionFilter, hrPeopleEmploymentFilter, hrPeopleAccountFilter]
        .filter((el) => el && el.value).length;
    }

    function _renderHrPeopleSortArrows() {
      const table = document.getElementById('hrPeopleTable');
      if (!table) return;
      table.querySelectorAll('[data-people-sort-arrow]').forEach((el) => {
        const key = el.dataset.peopleSortArrow;
        el.textContent = hrPeopleSortState.key === key ? (hrPeopleSortState.dir === 'desc' ? '▼' : '▲') : '';
      });
    }

    function renderHrPeopleTable() {
      if (!hrPeopleTbody) { _hrWarnMissing('#hrPeopleTbody'); return; }
      const searchTerm = (hrPeopleSearch && hrPeopleSearch.value ? hrPeopleSearch.value : '').toLowerCase();
      const statusFilter = hrPeopleStatusFilter ? hrPeopleStatusFilter.value : '';
      const selectDeptFilter = hrPeopleDeptFilter ? hrPeopleDeptFilter.value : '';
      const positionFilter = hrPeoplePositionFilter ? hrPeoplePositionFilter.value : '';
      const employmentFilter = hrPeopleEmploymentFilter ? hrPeopleEmploymentFilter.value : '';
      const accountFilter = hrPeopleAccountFilter ? hrPeopleAccountFilter.value : '';
      const effectiveDept = hrDeptTreeSelection || selectDeptFilter;
      const deptScope = effectiveDept ? getDepartmentDescendants(effectiveDept) : null;
      const filtered = hrPeopleList.filter((item) => {
        if (statusFilter && String(item.status || '') !== statusFilter) return false;
        if (deptScope && !deptScope.has(String(item.department_id || ''))) return false;
        if (positionFilter && String(item.position_id || '') !== positionFilter) return false;
        if (employmentFilter && String(item.employment_type || '') !== employmentFilter) return false;
        if (accountFilter) {
          const hasAcct = item.admin_user_id != null && String(item.admin_user_id) !== '' && String(item.admin_user_id) !== 'None';
          if (accountFilter === 'linked' && !hasAcct) return false;
          if (accountFilter === 'unlinked' && hasAcct) return false;
        }
        if (searchTerm) {
          const haystack = [
            item.first_name,
            item.last_name,
            item.email,
            item.phone,
            getDepartmentNameById(item.department_id),
            getPositionNameById(item.position_id),
          ].join(' ').toLowerCase();
          if (!haystack.includes(searchTerm)) return false;
        }
        return true;
      });
      const dir = hrPeopleSortState.dir === 'desc' ? -1 : 1;
      const nameOf = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim().toLowerCase();
      const keyOf = (e) => {
        if (hrPeopleSortState.key === 'department') return (getDepartmentPathLabel(e.department_id) || '').toLowerCase();
        if (hrPeopleSortState.key === 'position') return (getPositionNameById(e.position_id) || '').toLowerCase();
        return nameOf(e);
      };
      const sorted = filtered.slice().sort((a, b) => (keyOf(a).localeCompare(keyOf(b)) * dir) || nameOf(a).localeCompare(nameOf(b)));
      _renderHrPeopleSortArrows();
      const totalPages = Math.max(1, Math.ceil(sorted.length / HR_PEOPLE_PAGE_SIZE));
      if (hrPeoplePage > totalPages) hrPeoplePage = totalPages;
      const startIdx = (hrPeoplePage - 1) * HR_PEOPLE_PAGE_SIZE;
      const pageItems = sorted.slice(startIdx, startIdx + HR_PEOPLE_PAGE_SIZE);
      const rows = pageItems.map((item) => {
        const id = String(item.id || '');
        const fullName = [item.first_name, item.last_name].filter(Boolean).join(' ') || 'Employee';
        const deptLabel = getDepartmentPathLabel(item.department_id) || getDepartmentNameById(item.department_id) || '—';
        const positionLabel = getPositionNameById(item.position_id) || '—';
        // "Manager — <Dept>" role badges under the name (shared .badge component).
        const roleBadges = getEmployeeRoleBadges(item.id)
          .map((role) => `<span class="${role.className}" style="font-size:10px;">${escapeHtml(role.label)}</span>`).join(' ');
        const badgesBlock = roleBadges ? `<div style="margin-top:2px;">${roleBadges}</div>` : '';
        const nameCell = `<span style="display:inline-flex; align-items:center; gap:8px;">
          ${hrui.initialsAvatar(fullName)}
          <span><span>${escapeHtml(fullName)}</span>${badgesBlock}</span></span>`;
        // Manager (inferred from the department) as a link to that manager's profile.
        const mgrId = item.manager_employee_id;
        const mgrName = mgrId ? getEmployeeNameById(mgrId) : '';
        const managerCell = mgrName
          ? `<a href="#" data-people-manager="${escapeHtml(String(mgrId))}" title="Open ${escapeHtml(mgrName)}">${escapeHtml(mgrName)}</a>`
          : '<span class="muted">—</span>';
        return `
          <tr data-employee-id="${escapeHtml(id)}" tabindex="0" role="button" style="cursor:pointer;" aria-label="Open ${escapeHtml(fullName)}">
            <td>${nameCell}</td>
            <td>${escapeHtml(deptLabel)}</td>
            <td>${escapeHtml(positionLabel)}</td>
            <td>${managerCell}</td>
            <td>${hrEmploymentBadge(item.employment_type)}</td>
            <td>${hrStatusPill(item.status)}</td>
          </tr>
        `;
      }).join('');
      setTableRows(hrPeopleTbody, rows, 'No employees loaded.');
      if (hrPeoplePageInfo) hrPeoplePageInfo.textContent = `Page ${hrPeoplePage} of ${totalPages}`;
      if (hrPeoplePrev) hrPeoplePrev.disabled = hrPeoplePage <= 1;
      if (hrPeopleNext) hrPeopleNext.disabled = hrPeoplePage >= totalPages;
      if (hrPeopleCount) {
        const n = filtered.length;
        hrPeopleCount.textContent = `${n} employee${n === 1 ? '' : 's'}`;
      }
      if (hrPeopleFiltersCount) {
        const active = _hrPeopleActiveExtraFilters();
        hrPeopleFiltersCount.style.display = active ? '' : 'none';
        hrPeopleFiltersCount.textContent = String(active);
      }
    }

    async function loadHrPeople() {
      if (!hrPeopleTbody) { _hrWarnMissing('#hrPeopleTbody'); return; }
      hrPeopleTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.departments) {
        try { await loadHrDepartments(); } catch {}
      }
      if (!hrDataState.positions) {
        try { await loadHrPositions(); } catch {}
      }
      const data = await adminAPI.fetch(API.adminHrEmployees, { method: 'GET' });
      hrPeopleList = Array.isArray(data?.items) ? data.items : [];
      hrPeopleMap.clear();
      hrPeopleList.forEach((p) => hrPeopleMap.set(String(p.id), p));
      populateEmployeeOptions();
      hrPeoplePage = 1;
      populateHrPeoplePositionFilter();
      renderHrPeopleTable();
      hrDataState.people = true;
    }

    function showEmployeeList() {
      if (hrEmployeesContainer) hrEmployeesContainer.style.display = '';
      if (hrEmployeeDetailContainer) hrEmployeeDetailContainer.style.display = 'none';
      hrEmployeeDetail = null;
      Object.keys(hrEmployeeSectionState).forEach((key) => {
        hrEmployeeSectionState[key].loaded = false;
        hrEmployeeSectionState[key].loading = false;
      });
    }

    function showEmployeeDetailContainer() {
      if (hrEmployeesContainer) hrEmployeesContainer.style.display = 'none';
      if (hrEmployeeDetailContainer) hrEmployeeDetailContainer.style.display = '';
    }

    function applyEmployeeDetailToUI(detail) {
      if (!detail) return;
      const fullName = [detail.first_name, detail.last_name].filter(Boolean).join(' ') || 'Employee';
      if (hrEmployeeDetailName) hrEmployeeDetailName.textContent = fullName;
      if (hrEmployeeDetailStatus) {
        // Same single-source status vocabulary as the list.
        const meta = HR_STATUS_META[String(detail.status || '').toLowerCase()];
        hrEmployeeDetailStatus.textContent = meta ? meta.label : (_titleCase(detail.status || '') || 'Active');
        hrEmployeeDetailStatus.className = `badge ${meta ? meta.cls : ''}`;
      }
      if (hrEmployeeDetailMeta) {
        const pieces = [];
        if (detail.email) pieces.push(detail.email);
        if (detail.phone) pieces.push(detail.phone);
        hrEmployeeDetailMeta.textContent = pieces.join(' • ');
      }
      const deptLabel = getDepartmentNameById(detail.department_id) || 'No department';
      const positionLabel = getPositionNameById(detail.position_id);
      if (hrEmployeeDetailDept) hrEmployeeDetailDept.textContent = positionLabel ? `${deptLabel} — ${positionLabel}` : deptLabel;

      if (hrDetailDepartmentInput) hrDetailDepartmentInput.value = getDepartmentNameById(detail.department_id) || '';
      if (hrDetailPositionInput) hrDetailPositionInput.value = getPositionNameById(detail.position_id) || '';
      // Inferred from the department — show names, not raw ids.
      if (hrDetailManagerInput) hrDetailManagerInput.value = getEmployeeNameById(detail.manager_employee_id) || '';
      if (hrDetailDeputyInput) hrDetailDeputyInput.value = getEmployeeNameById(detail.deputy_manager_employee_id) || '';
      if (hrDetailStatusInput) hrDetailStatusInput.value = detail.status || 'active';
      if (hrDetailEmploymentTypeInput) hrDetailEmploymentTypeInput.value = detail.employment_type || 'full_time';
      if (hrDetailHireDateInput) hrDetailHireDateInput.value = normalizeDateValue(detail.hire_date);
      if (hrDetailTerminationDateInput) hrDetailTerminationDateInput.value = normalizeDateValue(detail.termination_date);
      if (hrDetailMonthlyPayInput) hrDetailMonthlyPayInput.value = detail.monthly_pay ?? '';
      if (hrDetailFirstNameInput) hrDetailFirstNameInput.value = detail.first_name || '';
      if (hrDetailLastNameInput) hrDetailLastNameInput.value = detail.last_name || '';
      if (hrDetailEmploymentStatusIdInput) hrDetailEmploymentStatusIdInput.value = detail.employment_status_id || '';
      if (hrDetailLocationInput) hrDetailLocationInput.value = detail.location_id || '';
      if (hrDetailEmailInput) hrDetailEmailInput.value = detail.email || '';
      if (hrDetailPhoneInput) hrDetailPhoneInput.value = detail.phone || '';
      if (hrDetailStatusReasonInput) hrDetailStatusReasonInput.value = (detail.meta && detail.meta.status_reason) || '';
      renderPositionHistory(detail);
      Object.keys(hrEmployeeSectionState).forEach((key) => {
        hrEmployeeSectionState[key].loaded = false;
        hrEmployeeSectionState[key].loading = false;
      });
    }

    async function loadEmployeeDetail(id) {
      if (!id) return;
      if (!hrDataState.departments) {
        try { await loadHrDepartments(); } catch {}
      }
      if (!hrDataState.positions) {
        try { await loadHrPositions(); } catch {}
      }
      if (!hrPeopleList.length) {
        try { await loadHrPeople(); } catch {}
      }
      const data = await adminAPI.fetch(API.adminHrEmployee(id), { method: 'GET' });
      hrEmployeeDetail = data;
      hrEmployeeAdminUser = null;
      applyEmployeeDetailToUI(data);
      renderAdminAccountSummary(data, null);
      if (data && data.admin_user_id && hasRole('root','system_admin')) {
        try {
          const adminData = await adminAPI.fetch(API.user(data.admin_user_id), { method: 'GET' });
          hrEmployeeAdminUser = adminData;
          renderAdminAccountSummary(data, adminData);
        } catch (err) {
          console.warn('load_admin_account_failed', err);
        }
      }
      showEmployeeDetailContainer();
      loadEmployeeSections(id).catch(() => {});
    }

    // --- Department hierarchy -------------------------------------------------
    // Collapsed parents persist per browser (no user-preferences store exists).
    const HR_DEPT_COLLAPSED_KEY = 'hr.departments.collapsed';
    let hrDeptCollapsed = null;

    function getDeptCollapsed() {
      if (hrDeptCollapsed) return hrDeptCollapsed;
      try {
        hrDeptCollapsed = new Set(JSON.parse(localStorage.getItem(HR_DEPT_COLLAPSED_KEY) || '[]'));
      } catch { hrDeptCollapsed = new Set(); }
      return hrDeptCollapsed;
    }

    function saveDeptCollapsed() {
      try { localStorage.setItem(HR_DEPT_COLLAPSED_KEY, JSON.stringify(Array.from(getDeptCollapsed()))); } catch { /* quota/private mode */ }
    }

    /**
     * Group departments into a parent→children tree. Returns `{roots, childrenOf}`,
     * each sibling list sorted by name. A department whose parent chain loops back on
     * itself is treated as a root (cycle broken) and warned about, so bad data can't
     * hang the render.
     */
    function buildDeptTree(items, compare) {
      const byId = new Map(items.map((d) => [String(d.id), d]));
      const childrenOf = new Map();
      const roots = [];
      const parentOf = (d) => (d && d.parent_department_id ? String(d.parent_department_id) : null);
      const hasCycle = (dept) => {
        const seen = new Set([String(dept.id)]);
        let cur = parentOf(dept);
        while (cur) {
          if (seen.has(cur)) return true;
          seen.add(cur);
          cur = parentOf(byId.get(cur));
        }
        return false;
      };
      items.forEach((dept) => {
        const pid = parentOf(dept);
        if (pid && byId.has(pid) && !hasCycle(dept)) {
          if (!childrenOf.has(pid)) childrenOf.set(pid, []);
          childrenOf.get(pid).push(dept);
          return;
        }
        if (pid && byId.has(pid)) {
          console.warn(`[hr] circular parent reference for department ${dept.id} (${dept.name || ''}) — rendering at root level`);
        }
        roots.push(dept);
      });
      // Sorting is applied per level, so nesting is always preserved.
      const cmp = compare || ((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      roots.sort(cmp);
      childrenOf.forEach((list) => list.sort(cmp));
      return { roots, childrenOf };
    }

    // Sort state for the department table (name asc by default).
    let hrDeptSort = { key: 'name', dir: 'asc' };

    function hrDeptComparator() {
      const dir = hrDeptSort.dir === 'desc' ? -1 : 1;
      const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
      if (hrDeptSort.key === 'staff') {
        // Ties fall back to name so the order stays stable/readable.
        return (a, b) => (((Number(a.staff_count) || 0) - (Number(b.staff_count) || 0)) * dir) || byName(a, b);
      }
      return (a, b) => byName(a, b) * dir;
    }

    function renderHrDeptSortArrows() {
      const table = document.getElementById('hrDepartmentsTable');
      if (!table) return;
      table.querySelectorAll('[data-dept-sort-arrow]').forEach((el) => {
        const key = el.dataset.deptSortArrow;
        el.textContent = hrDeptSort.key === key ? (hrDeptSort.dir === 'desc' ? '▼' : '▲') : '';
      });
      table.querySelectorAll('th[data-dept-sort]').forEach((th) => {
        const key = th.dataset.deptSort;
        th.setAttribute('aria-sort', hrDeptSort.key === key
          ? (hrDeptSort.dir === 'desc' ? 'descending' : 'ascending')
          : 'none');
      });
    }

    function closeHrDeptMenus() {
      if (!hrDepartmentsTbody) return;
      hrDepartmentsTbody.querySelectorAll('.hr-menu:not([hidden])').forEach((m) => { m.hidden = true; });
      hrDepartmentsTbody.querySelectorAll('button[data-dept-menu][aria-expanded="true"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }

    /**
     * Soft status change (never a delete). Deactivation is refused while the
     * department still has active staff or active sub-departments — we check
     * locally to explain why up-front, and the server enforces the same rule.
     */
    async function toggleHrDepartmentStatus(dept) {
      const id = String(dept.id || '');
      const name = dept.name || 'this department';
      const deactivating = dept.is_active !== false;

      if (deactivating) {
        const staff = Number(dept.staff_count) || 0;
        const activeKids = hrDepartmentsList.filter(
          (d) => String(d.parent_department_id || '') === id && d.is_active !== false,
        ).length;
        const reasons = [];
        if (staff) reasons.push(`${staff} active employee(s) are still assigned to it`);
        if (activeKids) reasons.push(`${activeKids} active sub-department(s) report to it`);
        if (reasons.length) {
          window.alert(`Cannot deactivate “${name}” because:\n\n• ${reasons.join('\n• ')}\n\nReassign or deactivate those first.`);
          return;
        }
        if (!window.confirm(`Deactivate “${name}”?\n\nThis is a status change, not a delete — the department and its history are kept and it can be reactivated later.`)) return;
      }

      try {
        await adminAPI.fetch(API.adminHrDepartment(id), {
          method: 'PATCH',
          body: JSON.stringify({ is_active: !deactivating }),
        });
        showFlash(deactivating ? 'Department deactivated.' : 'Department activated.', 'success');
        hrDataState.departments = false;
        await loadHrDepartments();
      } catch (err) {
        // The server enforces the same rule — surface its reasons if it refused.
        const detail = err && err.body && err.body.detail;
        if (detail && typeof detail === 'object' && detail.error === 'department_deactivation_blocked') {
          window.alert(`Cannot deactivate “${name}” because:\n\n• ${(detail.reasons || []).join('\n• ')}`);
          return;
        }
        const msg = adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : (err && err.message);
        showFlash(msg || 'Failed to update department status', 'error');
      }
    }

    async function loadHrDepartments() {
      if (!hrDepartmentsTbody) { _hrWarnMissing('#hrDepartmentsTbody'); return; }
      hrDepartmentsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      const data = await adminAPI.fetch(API.adminHrDepartments, { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      hrDepartmentsList = items;
      hrDepartmentsMap = new Map(items.map((item) => [String(item.id), item]));
      populateHrDepartmentOptions();
      populateHrPeopleDeptFilter();
      populateHrPeoplePositionFilter();
      if (hrDeptTreeSelection && !hrDepartmentsMap.has(String(hrDeptTreeSelection))) {
        hrDeptTreeSelection = '';
      }
      renderHrDepartmentRows();
      hrDataState.departments = true;
      // Manager/deputy are employee ids — without the people list loaded
      // `getEmployeeNameById` falls back to the raw UUID. Load it (even if the
      // People→Employees tab was never opened) and re-render so names show.
      Promise.resolve(ensureImportEmployees()).then(renderHrDepartmentRows).catch(() => {});
    }

    /**
     * Render the department table as a parent-grouped hierarchy: roots first, each
     * child indented beneath its parent (recursive, so deeper nesting renders too).
     * Collapsed parents hide their whole subtree.
     */
    function renderHrDepartmentRows() {
      if (!hrDepartmentsTbody) return;
      const items = Array.isArray(hrDepartmentsList) ? hrDepartmentsList : [];
      const { roots, childrenOf } = buildDeptTree(items, hrDeptComparator());
      renderHrDeptSortArrows();
      const collapsed = getDeptCollapsed();
      const rows = [];

      // --- Search + status filter -------------------------------------------
      const term = String(hrDepartmentSearch && hrDepartmentSearch.value || '').trim().toLowerCase();
      const statusFilter = String(hrDepartmentStatusFilter && hrDepartmentStatusFilter.value || '');
      const filtering = !!(term || statusFilter);
      const isActive = (d) => d.is_active !== false;
      const matches = (d) => {
        if (statusFilter === 'active' && !isActive(d)) return false;
        if (statusFilter === 'inactive' && isActive(d)) return false;
        if (!term) return true;
        return String(d.name || '').toLowerCase().includes(term)
          || String(d.code || '').toLowerCase().includes(term);
      };
      const matched = new Set(items.filter(matches).map((d) => String(d.id)));
      // A matching child keeps its ancestors on screen so the hierarchy still reads.
      const visible = new Set(matched);
      if (filtering) {
        matched.forEach((id) => {
          const seen = new Set();
          let pid = hrDepartmentsMap.get(id)?.parent_department_id;
          pid = pid ? String(pid) : null;
          while (pid && !seen.has(pid) && hrDepartmentsMap.has(pid)) {
            seen.add(pid);
            visible.add(pid);
            const parent = hrDepartmentsMap.get(pid);
            pid = parent && parent.parent_department_id ? String(parent.parent_department_id) : null;
          }
        });
      }
      if (hrDepartmentCount) {
        const shown = items.filter((d) => matched.has(String(d.id)));
        const activeShown = shown.filter(isActive).length;
        hrDepartmentCount.textContent = `${shown.length} department${shown.length === 1 ? '' : 's'} · ${activeShown} active`;
      }

      // Rolled-up headcount = this department's own active staff plus every
      // descendant's. Memoised so a deep tree stays O(n).
      const rollupCache = new Map();
      const rollupStaff = (id) => {
        if (rollupCache.has(id)) return rollupCache.get(id);
        rollupCache.set(id, 0);   // cycle-safe placeholder
        const own = Number(hrDepartmentsMap.get(id)?.staff_count) || 0;
        const total = (childrenOf.get(id) || [])
          .reduce((sum, kid) => sum + rollupStaff(String(kid.id)), own);
        rollupCache.set(id, total);
        return total;
      };

      // Manager with an initials avatar; a deputy is a compact hint (name in the
      // tooltip). No manager is a data-quality signal, so surface it as a badge
      // rather than an empty cell.
      const managerCell = (dept) => {
        const managerName = dept.manager_employee_id ? getEmployeeNameById(dept.manager_employee_id) : '';
        if (!managerName) {
          return '<span class="badge warn" title="No manager assigned to this department">No manager</span>';
        }
        const deputyName = dept.deputy_employee_id ? getEmployeeNameById(dept.deputy_employee_id) : '';
        const deputyHint = deputyName
          ? ` <span class="muted" style="font-size:11px; white-space:nowrap;" title="Deputy: ${escapeHtml(deputyName)}">+ deputy</span>`
          : '';
        return `<span style="display:inline-flex; align-items:center; gap:6px;">`
          + hrui.initialsAvatar(managerName)
          + `<span>${escapeHtml(managerName)}</span></span>${deputyHint}`;
      };

      const statusPill = (dept) => hrui.activePill(dept.is_active);

      // Own headcount, plus a subtle rolled-up subtree total for parents — "4 (31)".
      // Clicking opens the employee list filtered to that department.
      const staffCell = (dept, id, kidCount) => {
        const own = Number(dept.staff_count) || 0;
        const total = kidCount ? rollupStaff(id) : own;
        const rolled = kidCount && total !== own
          ? ` <span class="muted" style="font-size:11px;" title="Including sub-departments">(${total})</span>`
          : '';
        return `<a href="#" data-dept-staff="${escapeHtml(id)}" title="View staff in ${escapeHtml(dept.name || 'this department')}">${own}</a>${rolled}`;
      };

      const renderNode = (dept, depth) => {
        const id = String(dept.id || '');
        // Prune anything the filter excludes — its subtree goes with it.
        if (filtering && !visible.has(id)) return;
        const kids = childrenOf.get(id) || [];
        // While filtering, ignore collapse so matches are never hidden behind it.
        const isCollapsed = !filtering && collapsed.has(id);
        const chevron = kids.length
          ? `<button type="button" class="btn ghost hr-dept-toggle" data-dept-toggle="${escapeHtml(id)}" aria-expanded="${!isCollapsed}" title="${isCollapsed ? 'Expand' : 'Collapse'}" style="padding:0 4px; font-size:11px; line-height:1;">${isCollapsed ? '▶' : '▼'}</button>`
          : '<span style="display:inline-block; width:16px;"></span>';
        const codeChip = dept.code
          ? ` <span class="badge hr-code-chip" title="Department code">${escapeHtml(dept.code)}</span>`
          : '';
        rows.push(`
          <tr data-dept-id="${escapeHtml(id)}">
            <td><span style="padding-left:${depth * 18}px;">${chevron} ${escapeHtml(dept.name || '')}${codeChip}</span></td>
            <td>${managerCell(dept)}</td>
            <td style="text-align:right;">${staffCell(dept, id, kids.length)}</td>
            <td>${statusPill(dept)}</td>
            ${hrui.kebabMenu({ id, ns: 'dept', actions: [
              { action: 'edit', label: 'Edit' },
              { action: 'toggle-status', label: dept.is_active === false ? 'Activate' : 'Deactivate' },
              { action: 'add-child', label: 'Add sub-department' },
            ] })}
          </tr>
        `);
        if (!isCollapsed) kids.forEach((kid) => renderNode(kid, depth + 1));
      };

      roots.forEach((root) => renderNode(root, 0));
      setTableRows(hrDepartmentsTbody, rows.join(''), 'No departments loaded.');
    }

    async function loadHrPositions() {
      if (!hrPositionsTbody) { _hrWarnMissing('#hrPositionsTbody'); return; }
      hrPositionsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.departments || !hrDepartmentsList.length) {
        try { await loadHrDepartments(); } catch {}
      }
      try { await ensureGradesLoaded(); } catch {}
      const data = await adminAPI.fetch(API.adminHrPositions, { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      hrPositionsList = items;
      populateHrPositionOptions();
      populateHrPeoplePositionFilter();
      populateHrPositionDeptFilter();
      renderHrPositionRows();
      hrDataState.positions = true;
    }

    function populateHrPositionDeptFilter() {
      if (!hrPositionDeptFilter) return;
      const prev = hrPositionDeptFilter.value;
      const opts = ['<option value="">All departments</option>', '<option value="__unassigned">Unassigned</option>'];
      hrDepartmentsList.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach((d) => {
        if (String(d.code || '').toUpperCase() === 'UNASSIGNED') return;   // covered by the Unassigned option
        opts.push(`<option value="${escapeHtml(d.id || '')}">${escapeHtml(d.name || d.code || d.id)}</option>`);
      });
      hrPositionDeptFilter.innerHTML = opts.join('');
      if (prev) hrPositionDeptFilter.value = prev;
    }

    function _positionGradeLabel(pos) {
      if (pos.grade_code) return pos.grade_code;
      const g = hrGradesList.find((x) => String(x.id) === String(pos.grade_id));
      return g ? g.code : '';
    }

    // filled / established with a compact vacancy or over-established badge.
    function _positionFillCell(pos) {
      const filled = Number(pos.filled_count) || 0;
      const est = Number(pos.established_count);
      const estText = Number.isFinite(est) ? est : '—';
      let badge = '';
      if (Number.isFinite(est)) {
        if (filled < est) badge = ` <span class="badge off" title="Open seats">${est - filled} vacant</span>`;
        else if (filled > est) badge = ` <span class="badge warn" title="More incumbents than the approved headcount">over-established</span>`;
      }
      return `<span>${filled} / ${escapeHtml(String(estText))}</span>${badge}`;
    }

    /**
     * Positions grouped by department (subheader + count), alphabetical within a
     * group. Positions on the placeholder department (or, defensively, with no
     * department) are pinned to the top with a warning. Honours the search /
     * department / vacancy filters and updates the live summary count.
     */
    function renderHrPositionRows() {
      if (!hrPositionsTbody) return;
      const items = Array.isArray(hrPositionsList) ? hrPositionsList : [];
      const term = String(hrPositionSearch && hrPositionSearch.value || '').trim().toLowerCase();
      const deptFilter = String(hrPositionDeptFilter && hrPositionDeptFilter.value || '');
      const vacancyOnly = String(hrPositionVacancyFilter && hrPositionVacancyFilter.value || '') === 'vacant';
      const isVacant = (p) => Number.isFinite(Number(p.established_count)) && (Number(p.filled_count) || 0) < Number(p.established_count);
      const matches = (p) => {
        if (deptFilter === '__unassigned') { if (!_positionIsUnassigned(p)) return false; }
        else if (deptFilter && String(p.department_id || '') !== deptFilter) return false;
        if (vacancyOnly && !isVacant(p)) return false;
        if (!term) return true;
        return String(p.name || '').toLowerCase().includes(term) || String(p.code || '').toLowerCase().includes(term);
      };
      const shown = items.filter(matches);

      // Group by department; unassigned bucket first.
      const groups = new Map();   // deptId|'__unassigned' -> [positions]
      shown.forEach((p) => {
        const key = _positionIsUnassigned(p) ? '__unassigned' : String(p.department_id);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(p);
      });
      const deptName = (key) => key === '__unassigned' ? '' : (getDepartmentNameById(key) || 'Unknown department');
      const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
        if (a === '__unassigned') return -1;
        if (b === '__unassigned') return 1;
        return deptName(a).localeCompare(deptName(b));
      });

      const rows = [];
      orderedKeys.forEach((key) => {
        const list = groups.get(key).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        if (key === '__unassigned') {
          rows.push(`<tr class="hr-dept-subhead"><td colspan="5"><span class="badge warn">⚠ Unassigned — ${list.length} position${list.length === 1 ? '' : 's'} need a department</span></td></tr>`);
        } else {
          rows.push(`<tr class="hr-dept-subhead"><td colspan="5"><strong>${escapeHtml(deptName(key))}</strong> <span class="muted" style="font-size:12px;">· ${list.length} position${list.length === 1 ? '' : 's'}</span></td></tr>`);
        }
        list.forEach((pos) => {
          const id = String(pos.id || '');
          const codeChip = pos.code ? ` <span class="badge hr-code-chip" title="Position code">${escapeHtml(pos.code)}</span>` : '';
          const grade = _positionGradeLabel(pos);
          rows.push(`
            <tr data-position-id="${escapeHtml(id)}">
              <td><span style="padding-left:18px;">${escapeHtml(pos.name || '')}${codeChip}</span></td>
              <td>${grade ? escapeHtml(grade) : '<span class="muted">—</span>'}</td>
              <td style="text-align:right;">${_positionFillCell(pos)}</td>
              <td>${hrui.activePill(pos.is_active)}</td>
              ${hrui.kebabMenu({ id, ns: 'position', actions: [
                { action: 'edit', label: 'Edit' },
                { action: 'toggle-status', label: pos.is_active === false ? 'Activate' : 'Deactivate' },
                { action: 'adjust', label: 'Adjust established count' },
                { action: 'holders', label: 'View holders' },
              ] })}
            </tr>
          `);
        });
      });
      setTableRows(hrPositionsTbody, rows.join(''), 'No positions loaded.');

      if (hrPositionCount) {
        const total = items.length;
        const unassigned = items.filter(_positionIsUnassigned).length;
        const vacant = items.filter(isVacant).length;
        hrPositionCount.textContent = `${total} position${total === 1 ? '' : 's'} · ${unassigned} unassigned · ${vacant} vacant`;
      }
    }

    // A position is "unassigned" if it has no department or sits on the migration
    // placeholder department (code UNASSIGNED).
    function _positionIsUnassigned(pos) {
      if (!pos.department_id) return true;
      const dept = hrDepartmentsMap.get(String(pos.department_id));
      return !!dept && String(dept.code || '').toUpperCase() === 'UNASSIGNED';
    }

    async function ensureGradesLoaded() {
      if (hrGradesList.length) return;
      try {
        const data = await adminAPI.fetch(API.adminHrGrades, { method: 'GET', cache: 'no-store' });
        hrGradesList = Array.isArray(data?.items) ? data.items : [];
      } catch { hrGradesList = []; }
    }

    function closeHrPositionMenus() {
      if (!hrPositionsTbody) return;
      hrPositionsTbody.querySelectorAll('.hr-menu:not([hidden])').forEach((m) => { m.hidden = true; });
      hrPositionsTbody.querySelectorAll('button[data-position-menu][aria-expanded="true"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }

    function _hrErr(err, fallback) {
      const msg = adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : (err && err.message);
      return msg || fallback;
    }

    async function toggleHrPositionStatus(pos) {
      const id = String(pos.id || '');
      const deactivating = pos.is_active !== false;
      if (deactivating) {
        const filled = Number(pos.filled_count) || 0;
        if (filled) {
          window.alert(`Cannot deactivate “${pos.name}” — ${filled} active employee(s) still hold this position. Reassign them first.`);
          return;
        }
        if (!window.confirm(`Deactivate “${pos.name}”?\n\nThis is a status change, not a delete.`)) return;
      }
      try {
        await adminAPI.fetch(API.adminHrPosition(id), { method: 'PATCH', body: JSON.stringify({ is_active: !deactivating }) });
        showFlash(deactivating ? 'Position deactivated.' : 'Position activated.', 'success');
        hrDataState.positions = false;
        await loadHrPositions();
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (d && typeof d === 'object' && d.error === 'position_deactivation_blocked') {
          window.alert(`Cannot deactivate “${pos.name}”:\n\n• ${(d.reasons || []).join('\n• ')}`);
          return;
        }
        showFlash(_hrErr(err, 'Failed to update position'), 'error');
      }
    }

    async function adjustHrPositionEstablished(pos) {
      const filled = Number(pos.filled_count) || 0;
      const current = Number(pos.established_count);
      const raw = window.prompt(`Established (approved) headcount for “${pos.name}”\n\nCurrently filled: ${filled}. Value must be 0 or more.`, Number.isFinite(current) ? String(current) : '1');
      if (raw == null) return;
      const next = Number(String(raw).trim());
      if (!Number.isInteger(next) || next < 0) { showFlash('Enter a whole number ≥ 0.', 'error'); return; }
      if (next < filled && !window.confirm(`This sets the headcount (${next}) BELOW the ${filled} current incumbent(s) — the position will show as over-established. Continue?`)) return;
      try {
        await adminAPI.fetch(API.adminHrPosition(String(pos.id)), { method: 'PATCH', body: JSON.stringify({ established_count: next }) });
        showFlash('Established count updated.', 'success');
        hrDataState.positions = false;
        await loadHrPositions();
      } catch (err) { showFlash(_hrErr(err, 'Failed to update established count'), 'error'); }
    }

    function viewHrPositionHolders(pos) {
      // Jump to the employee list filtered to this position.
      hrDeptTreeSelection = '';
      if (hrPeopleDeptFilter) hrPeopleDeptFilter.value = '';
      if (hrPeoplePositionFilter) hrPeoplePositionFilter.value = String(pos.id || '');
      hrPeoplePage = 1;
      setHrPeopleTab('employees');
      Promise.resolve(ensureImportEmployees()).then(renderHrPeopleTable).catch(() => {});
    }

    // ---- Grades admin -----------------------------------------------------
    const _money = (v) => (v == null || v === '' ? '' : formatCurrency(v));

    async function loadHrGradesTab() {
      if (!hrGradesTbody) { _hrWarnMissing('#hrGradesTbody'); return; }
      hrGradesTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrGrades, { method: 'GET', cache: 'no-store' });
        hrGradesList = Array.isArray(data?.items) ? data.items : [];
      } catch (err) {
        hrGradesTbody.innerHTML = `<tr><td colspan="6" class="error">${escapeHtml(_hrErr(err, 'Failed to load grades'))}</td></tr>`;
        return;
      }
      const rows = hrGradesList.map((g) => {
        const band = (g.min_salary != null || g.max_salary != null)
          ? `${_money(g.min_salary) || '—'} – ${_money(g.max_salary) || '—'}`
          : '<span class="muted">not set</span>';
        return `
          <tr data-grade-id="${escapeHtml(g.id || '')}">
            <td><span class="badge">${escapeHtml(g.code || '')}</span></td>
            <td>${escapeHtml(g.name || '')}</td>
            <td style="text-align:right;">${escapeHtml(String(g.rank))}</td>
            <td style="text-align:right;">${band}</td>
            <td>${g.is_active === false ? '<span class="badge warn">Inactive</span>' : '<span class="badge ok">Active</span>'}</td>
            <td><button type="button" class="btn ghost" data-grade-edit="${escapeHtml(g.id || '')}" style="font-size:11px; padding:2px 8px;">✎ Edit</button></td>
          </tr>`;
      }).join('');
      setTableRows(hrGradesTbody, rows, 'No grades yet — add the bands your positions will use.');
    }

    function openHrGradeDialog(grade = null) {
      if (!hrGradeDlg || !hrGradeForm) { _hrWarnMissing('#hrGradeDlg'); return; }
      hrGradeForm.reset();
      hrGradeForm.dataset.pending = '0';
      _hrGradeEditId = grade && grade.id ? String(grade.id) : null;
      if (hrGradeDlgTitle) hrGradeDlgTitle.textContent = grade ? 'Edit grade' : 'Add grade';
      if (hrGradeCodeInput) hrGradeCodeInput.value = grade ? (grade.code || '') : '';
      if (hrGradeNameInput) hrGradeNameInput.value = grade ? (grade.name || '') : '';
      if (hrGradeRankInput) hrGradeRankInput.value = grade && grade.rank != null ? String(grade.rank) : '';
      if (hrGradeMinSalaryInput) hrGradeMinSalaryInput.value = grade && grade.min_salary != null ? String(grade.min_salary) : '';
      if (hrGradeMaxSalaryInput) hrGradeMaxSalaryInput.value = grade && grade.max_salary != null ? String(grade.max_salary) : '';
      if (hrGradeStatusInput) hrGradeStatusInput.value = grade && grade.is_active === false ? 'inactive' : 'active';
      openDialog(hrGradeDlg);
      setTimeout(() => { if (hrGradeCodeInput) { try { hrGradeCodeInput.focus(); } catch {} } }, 30);
    }

    async function handleHrGradeSubmit(event) {
      event.preventDefault();
      if (!hrGradeForm || hrGradeForm.dataset.pending === '1') return;
      if (!hrGradeForm.reportValidity()) return;
      const rank = Number((hrGradeRankInput && hrGradeRankInput.value || '').trim());
      if (!Number.isInteger(rank)) { showFlash('Rank must be a whole number.', 'error'); return; }
      const num = (el) => { const v = (el && el.value || '').trim(); return v === '' ? null : Number(v); };
      const payload = {
        code: (hrGradeCodeInput && hrGradeCodeInput.value.trim()) || '',
        name: (hrGradeNameInput && hrGradeNameInput.value.trim()) || '',
        rank,
        min_salary: num(hrGradeMinSalaryInput),
        max_salary: num(hrGradeMaxSalaryInput),
        is_active: !hrGradeStatusInput || hrGradeStatusInput.value !== 'inactive',
      };
      hrGradeForm.dataset.pending = '1';
      if (hrGradeSubmitBtn) { hrGradeSubmitBtn.disabled = true; hrGradeSubmitBtn.textContent = 'Saving…'; }
      try {
        if (_hrGradeEditId) await adminAPI.fetch(API.adminHrGrade(_hrGradeEditId), { method: 'PATCH', body: JSON.stringify(payload) });
        else await adminAPI.fetch(API.adminHrGrades, { method: 'POST', body: JSON.stringify(payload) });
        showFlash('Grade saved.', 'success');
        try { hrGradeDlg.close(); } catch { hrGradeDlg.open = false; }
        hrGradesList = [];
        await loadHrGradesTab();
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (err && err.status === 409 && d === 'grade_code_exists') showFlash('That grade code is already in use.', 'error');
        else if (err && err.status === 409 && d === 'grade_rank_exists') showFlash('That rank is already in use.', 'error');
        else showFlash(_hrErr(err, 'Failed to save grade'), 'error');
      } finally {
        hrGradeForm.dataset.pending = '0';
        if (hrGradeSubmitBtn) { hrGradeSubmitBtn.disabled = false; hrGradeSubmitBtn.textContent = 'Save grade'; }
      }
    }

    function ensureEmployeeSectionState(section) {
    if (!hrEmployeeSectionState[section]) {
      hrEmployeeSectionState[section] = { loaded: false, loading: false };
    }
  }

    function renderPositionHistory(detail) {
      if (!hrEmployeePositionHistoryTbody) { _hrWarnMissing('#hrEmployeePositionHistoryTbody'); return; }
      const history = Array.isArray(detail?.position_history) ? detail.position_history : [];
      // Collapsed by default (a <details> widget) — show the count on the summary.
      const summary = hrEmployeePositionHistoryTbody.closest('details')?.querySelector('summary');
      if (summary) summary.textContent = history.length ? `Position history (${history.length})` : 'Position history';
      if (!history.length) {
        setTableRows(hrEmployeePositionHistoryTbody, '', 'No position changes.');
        return;
      }
      const rows = history
        .slice()
        .reverse()
        .map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.changed_at || '')}</td>
            <td>${escapeHtml(getDepartmentNameById(item.from_department_id) || '')} / ${escapeHtml(getPositionNameById(item.from_position_id) || '')}</td>
            <td>${escapeHtml(getDepartmentNameById(item.to_department_id) || '')} / ${escapeHtml(getPositionNameById(item.to_position_id) || '')}</td>
            <td>${escapeHtml(item.changed_by_name || item.changed_by_admin_user_id || '')}</td>
          </tr>
        `).join('');
      setTableRows(hrEmployeePositionHistoryTbody, rows, 'No position changes.');
    }

    function _hasLinkedAccount(detail) {
      const v = detail && detail.admin_user_id;
      return v != null && String(v) !== '' && String(v) !== 'None';
    }

    function renderAdminAccountSummary(detail, adminUser) {
      const linked = _hasLinkedAccount(detail);
      const canManage = hasRole('root', 'hr_admin');
      if (hrLinkAccountBtn) hrLinkAccountBtn.style.display = (!linked && canManage) ? '' : 'none';
      if (hrUnlinkAccountBtn) hrUnlinkAccountBtn.style.display = (linked && canManage) ? '' : 'none';
      if (!hrAdminAccountSummary) return;
      if (!linked) {
        hrAdminAccountSummary.textContent = 'No linked admin account.';
        return;
      }
      const parts = [];
      if (adminUser) {
        parts.push(`${adminUser.full_name || adminUser.username || 'account'} (#${adminUser.id})`);
        if (adminUser.roles && adminUser.roles.length) parts.push(`Roles: ${adminUser.roles.join(', ')}`);
        parts.push(adminUser.is_active === false ? 'Locked' : 'Active');
      } else {
        // Name comes straight from the employee payload now (admin_user_name),
        // so non-root HR admins see the person without the extra user fetch.
        const label = detail.admin_user_name || detail.admin_user_username || `Admin ID ${detail.admin_user_id}`;
        parts.push(`${label} (#${detail.admin_user_id})`);
        if (detail.admin_user_active !== undefined && detail.admin_user_active !== null) {
          parts.push(detail.admin_user_active ? 'Active' : 'Locked');
        }
      }
      hrAdminAccountSummary.textContent = parts.join(' • ');
    }

    function renderLinkableUsers(items) {
      if (!hrLinkAccountResults) return;
      hrLinkAccountResults.innerHTML = '';
      if (!Array.isArray(items) || !items.length) {
        hrLinkAccountResults.innerHTML = '<div style="color:var(--sub); font-size:12px;">No unassigned admin accounts match.</div>';
        return;
      }
      items.forEach((u) => {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex; justify-content:space-between; align-items:center; gap:8px; padding:6px 4px; border-bottom:1px solid var(--border,#eee);';
        const label = document.createElement('div');
        label.style.cssText = 'font-size:13px; overflow:hidden; text-overflow:ellipsis;';
        label.textContent = `${u.full_name || u.username || 'account'} · ${u.email || ''} (#${u.id})`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn primary small';
        btn.textContent = 'Link';
        btn.addEventListener('click', () => linkEmployeeAccount(u.id));
        row.appendChild(label);
        row.appendChild(btn);
        hrLinkAccountResults.appendChild(row);
      });
    }

    async function searchLinkableUsers(term) {
      if (!hrLinkAccountResults) return;
      hrLinkAccountResults.innerHTML = '<div style="color:var(--sub); font-size:12px;">Searching…</div>';
      try {
        const data = await adminAPI.fetch(API.adminHrLinkableUsers(term || ''), { method: 'GET' });
        renderLinkableUsers(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        hrLinkAccountResults.innerHTML = `<div style="color:var(--err); font-size:12px;">${escapeHtml((err && err.message) || 'Search failed')}</div>`;
      }
    }

    function openHrLinkAccountDialog() {
      if (!hrLinkAccountDlg || !hrEmployeeDetail) return;
      if (hrLinkAccountSearch) hrLinkAccountSearch.value = '';
      searchLinkableUsers('');
      openDialog(hrLinkAccountDlg);
      if (hrLinkAccountSearch) hrLinkAccountSearch.focus();
    }

    async function linkEmployeeAccount(adminUserId) {
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
      try {
        await adminAPI.fetch(API.adminHrEmployeeLinkAccount(hrEmployeeDetail.id), {
          method: 'POST',
          body: JSON.stringify({ admin_user_id: adminUserId }),
        });
        showFlash('Admin account linked');
        try { hrLinkAccountDlg.close(); } catch { if (hrLinkAccountDlg) hrLinkAccountDlg.open = false; }
        await loadEmployeeDetail(hrEmployeeDetail.id);
        loadHrPeople().catch(() => {});
      } catch (err) {
        showFlash((err && err.message) || 'Failed to link account', 'error');
      }
    }

    async function unlinkEmployeeAccount() {
      if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
      if (!window.confirm('Unlink this employee from their admin account?')) return;
      try {
        await adminAPI.fetch(API.adminHrEmployeeUnlinkAccount(hrEmployeeDetail.id), { method: 'POST' });
        showFlash('Admin account unlinked');
        await loadEmployeeDetail(hrEmployeeDetail.id);
        loadHrPeople().catch(() => {});
      } catch (err) {
        showFlash((err && err.message) || 'Failed to unlink account', 'error');
      }
    }

    async function loadEmployeeStatusAudits(employeeId) {
      ensureEmployeeSectionState('status_audit');
      const state = hrEmployeeSectionState.status_audit;
      if (!hrEmployeeStatusAuditTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeStatusAuditTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrStatusAudits(employeeId), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const rows = items.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.created_at || '')}</td>
            <td>${escapeHtml(item.from_status || '—')} → ${escapeHtml(item.to_status || '')}</td>
            <td>${escapeHtml(item.changed_by_admin_user_id || '')}</td>
            <td>${escapeHtml(item.meta && item.meta.status_reason ? item.meta.status_reason : (item.meta && item.meta.reason) || '')}</td>
          </tr>
        `).join('');
        setTableRows(hrEmployeeStatusAuditTbody, rows, 'No status changes.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeStatusAuditTbody, '', err && err.message ? err.message : 'Failed to load status history.');
      } finally {
        state.loading = false;
      }
    }

    // Employee detail → Shifts: the standing shifts HR sets, and a Set shift
    // button that opens the same dialog as Attendance → Staff.
    const canSetStaffShifts = () => hasRole('root', 'hr_admin', 'hr_manager');

    async function loadEmployeeShiftAssignments(employeeId) {
      ensureEmployeeSectionState('shifts');
      const state = hrEmployeeSectionState.shifts;
      if (!hrEmployeeShiftsTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeShiftsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrEmployeeShiftSummary) hrEmployeeShiftSummary.textContent = 'Loading…';
      if (hrEmployeeSetShiftBtn) hrEmployeeSetShiftBtn.hidden = true;
      try {
        const detail = await adminAPI.fetch(API.adminHrStaffShiftDetail(employeeId), { method: 'GET', cache: 'no-store' });
        // The detail view may have moved on to another employee while this loaded.
        if (hrEmployeeDetail && String(hrEmployeeDetail.id) !== String(employeeId)) return;
        const defaultShift = detail.default_shift || null;
        if (hrEmployeeShiftSummary) {
          const parts = [];
          if (detail.source === 'assigned' && detail.effective_shift) {
            parts.push(`On ${staffShiftLabel(detail.effective_shift)} today.`);
          } else if (detail.effective_shift) {
            parts.push(`On the default shift today (${staffShiftLabel(detail.effective_shift)}).`);
          } else {
            parts.push('No shift today, and no default shift is set.');
          }
          if (detail.current && detail.current.shift_inactive) {
            parts.push(`“${detail.current.shift.name}” is inactive, so the default applies.`);
          }
          if (detail.selects_own_shift) {
            parts.push('Customer care agent: a shift they pick when signing on applies first for that day.');
          }
          hrEmployeeShiftSummary.textContent = parts.join(' ');
        }
        if (hrEmployeeSetShiftBtn) {
          const terminated = detail.employee_status === 'terminated';
          hrEmployeeSetShiftBtn.hidden = !canSetStaffShifts();
          hrEmployeeSetShiftBtn.disabled = terminated;
          hrEmployeeSetShiftBtn.title = terminated ? 'A terminated employee cannot be given a shift.' : '';
        }
        const day = detail.on_date || localIsoDate();
        const rows = (Array.isArray(detail.assignments) ? detail.assignments.slice() : [])
          .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from)))
          .map((row) => {
            const status = shiftRowStatus(row, day);
            const isTemp = (row.kind || 'standing') === 'temporary';
            const end = isTemp ? (row.effective_to || '—') : 'onward';
            const kindTag = isTemp ? ' <span class="badge">Temp</span>' : '';
            return `
          <tr${status === 'past' ? ' style="color:var(--sub);"' : ''}>
            <td>${escapeHtml(row.effective_from)}</td>
            <td>${escapeHtml(String(end))}</td>
            <td>${escapeHtml(staffTimelineShiftLabel(row, defaultShift))}${kindTag}</td>
            <td>${status === 'current' ? '<span class="badge">Active</span>' : `<span class="table-meta">${STAFF_STATUS_LABEL[status]}</span>`}</td>
            <td>${escapeHtml(row.note || '')}</td>
          </tr>`;
          }).join('');
        setTableRows(hrEmployeeShiftsTbody, rows, 'No shifts set. On the default shift.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeShiftsTbody, '', err && err.message ? err.message : 'Failed to load shifts.');
        if (hrEmployeeShiftSummary) hrEmployeeShiftSummary.textContent = '';
      } finally {
        state.loading = false;
      }
    }

    function openEmployeeShiftDialog() {
      const employeeId = hrEmployeeDetail && hrEmployeeDetail.id;
      if (!employeeId) return;
      const name = `${hrEmployeeDetail.first_name || ''} ${hrEmployeeDetail.last_name || ''}`.trim();
      openStaffShiftDialog(employeeId, {
        name,
        onChange: () => {
          if (!hrEmployeeDetail || String(hrEmployeeDetail.id) !== String(employeeId)) return;
          ensureEmployeeSectionState('shifts');
          hrEmployeeSectionState.shifts.loaded = false;
          loadEmployeeShiftAssignments(employeeId).catch(() => {});
        },
      }).catch(() => {});
    }

    // Compact per-stage approver chain shown under a leave request's status:
    // who is supposed to approve each stage and whether they have.
    function leaveChainInline(approvals) {
      if (!Array.isArray(approvals) || !approvals.length) return '';
      const line = approvals.map((a) => {
        const s = (a.status || 'pending').toLowerCase();
        const ok = s === 'approved';
        const bad = ['denied', 'rejected'].includes(s);
        const mark = ok ? '✓' : bad ? '✕' : '⋯';
        const color = ok ? 'var(--ok, #22c55e)' : bad ? 'var(--err)' : 'var(--sub)';
        const who = a.approver_name || a.expected_approver || '';
        return `<span><span style="color:${color}; font-weight:700;">${mark}</span> ${escapeHtml(a.stage_label || a.stage)}${who ? ' · ' + escapeHtml(who) : ''}</span>`;
      }).join('');
      return `<div style="margin-top:4px; font-size:11px; color:var(--sub); display:flex; flex-direction:column; gap:2px;">${line}</div>`;
    }

    async function openLeaveAnalysis(leaveId) {
      let data;
      try {
        data = await adminAPI.fetch(API.adminHrLeaveRequestAnalysis(leaveId), { method: 'GET' });
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to load analysis', 'error');
        return;
      }
      showLeaveAnalysisModal(data);
    }

    function showLeaveAnalysisModal(data) {
      const dept = data.department || {};
      const req = data.request || {};
      const roster = Array.isArray(data.roster) ? data.roster : [];
      const overlaps = Array.isArray(data.overlaps) ? data.overlaps : [];
      const cap = data.capacity_warning;

      const capBanner = cap
        ? `<div style="border:1px solid var(--err); background:color-mix(in srgb, var(--err) 12%, transparent); border-radius:8px; padding:8px 10px; font-size:12px; margin-bottom:10px;">
             <strong>Over department capacity</strong> — up to ${escapeHtml(String(Math.max(...cap.days.map((d) => d.count))))} people off on some days, cap is ${escapeHtml(String(cap.max_concurrent))}.
             <div style="color:var(--sub); margin-top:2px;">${cap.days.map((d) => `${escapeHtml(d.date)} (${d.count})`).join(' · ')}</div>
           </div>`
        : `<div style="color:var(--sub); font-size:12px; margin-bottom:10px;">Within department capacity${dept.max_concurrent != null ? ` (cap ${escapeHtml(String(dept.max_concurrent))})` : ''}.</div>`;

      const overlapBox = overlaps.length
        ? `<div style="font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--sub); margin:4px 0;">Overlapping this window (${overlaps.length})</div>
           ${overlaps.map((o) => `<div style="font-size:13px; padding:2px 0;">• ${escapeHtml(o.employee_name)} <span style="color:var(--sub);">${escapeHtml(o.start_date)} → ${escapeHtml(o.end_date)} · ${escapeHtml(o.status)}</span></div>`).join('')}`
        : `<div style="font-size:13px; color:var(--sub);">No teammates on leave over this window.</div>`;

      const rosterRows = roster.map((p) => {
        const win = (p.overlaps || []).map((w) => `${w.start_date}→${w.end_date}`).join(', ');
        const tag = p.is_requester
          ? '<span style="color:var(--accent, #2563eb); font-weight:600;"> (this request)</span>'
          : (p.has_overlap ? '<span style="color:var(--err); font-weight:600;"> ⚠ overlap</span>' : '');
        return `<tr>
          <td style="padding:4px 6px;">${escapeHtml(p.name)}${tag}</td>
          <td style="padding:4px 6px; color:var(--sub);">${escapeHtml(p.position || '—')}</td>
          <td style="padding:4px 6px; color:var(--sub);">${escapeHtml(win || '—')}</td></tr>`;
      }).join('');

      const dlg = document.createElement('dialog');
      dlg.style.cssText = 'border:0; border-radius:12px; padding:0; max-width:560px; width:94%; background:var(--panel); color:inherit;';
      dlg.innerHTML = `
        <div style="padding:16px 18px;">
          <div style="display:flex; justify-content:space-between; align-items:baseline; gap:10px;">
            <div style="font-weight:700; font-size:15px;">Department coverage — ${escapeHtml(dept.name || 'No department')}</div>
            <button class="btn ghost" data-analysis-close style="padding:2px 10px;">Close</button>
          </div>
          <div style="font-size:12px; color:var(--sub); margin:2px 0 12px;">${escapeHtml(req.employee_name || '')} · ${escapeHtml(req.start_date || '')} → ${escapeHtml(req.end_date || '')}</div>
          ${capBanner}
          ${overlapBox}
          <div style="font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--sub); margin:12px 0 4px;">Department roster (${roster.length})</div>
          <div style="max-height:260px; overflow:auto; border:1px solid var(--border-soft); border-radius:8px;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead><tr style="text-align:left; color:var(--sub);"><th style="padding:4px 6px;">Name</th><th style="padding:4px 6px;">Position</th><th style="padding:4px 6px;">Leave in window</th></tr></thead>
              <tbody>${rosterRows || '<tr><td colspan="3" style="padding:6px; color:var(--sub);">No teammates.</td></tr>'}</tbody>
            </table>
          </div>
        </div>`;
      document.body.appendChild(dlg);
      const close = () => { try { dlg.close(); } catch (e) { /* not open */ } dlg.remove(); };
      dlg.querySelector('[data-analysis-close]').addEventListener('click', close);
      dlg.addEventListener('cancel', (ev) => { ev.preventDefault(); close(); });
      try { dlg.showModal(); } catch (e) { /* no dialog support */ }
    }

    async function loadEmployeeLeave(employeeId) {
      ensureEmployeeSectionState('leave');
      const state = hrEmployeeSectionState.leave;
      if (!hrEmployeeLeaveTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeLeaveTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrLeaveRequests, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const filtered = items.filter((item) => String(item.employee_id || '') === String(employeeId));
        const rows = filtered.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.leave_type || '')}</td>
            <td>${formatDateRange(item.start_date, item.end_date)}</td>
            <td>${escapeHtml(item.status || '')}${leaveChainInline(item.approvals)}</td>
            <td>${escapeHtml(item.duration_days || '')}${item.half_day ? ' (half-day)' : ''}</td>
            <td>
              <div class="row" style="gap:6px; flex-wrap:wrap;">
                <button class="btn ghost" data-leave-act="approve" data-leave-id="${escapeHtml(item.id || '')}">Approve</button>
                <button class="btn ghost" data-leave-act="deny" data-leave-id="${escapeHtml(item.id || '')}">Deny</button>
                <button class="btn ghost" data-leave-analysis data-leave-id="${escapeHtml(item.id || '')}" title="Who else in the department is off over this period">Analysis</button>
              </div>
            </td>
          </tr>
        `).join('');
        setTableRows(hrEmployeeLeaveTbody, rows, 'No leave requests.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeLeaveTbody, '', err && err.message ? err.message : 'Failed to load leave.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeLeaveBalances(employeeId) {
      ensureEmployeeSectionState('leave_balances');
      const state = hrEmployeeSectionState.leave_balances;
      if (!hrEmployeeLeaveBalancesTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeLeaveBalancesTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrEmployee(employeeId) + '/leave_balances', { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const rows = items.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.year || '')}</td>
            <td>${escapeHtml(item.balance_days || '')}</td>
            <td>${escapeHtml(item.used_days || '')}</td>
            <td>${escapeHtml(item.carried_over_days || '')}</td>
            <td>${escapeHtml(item.locked_days_for_loan || '')}</td>
          </tr>
        `).join('');
        setTableRows(hrEmployeeLeaveBalancesTbody, rows, 'No leave balances.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeLeaveBalancesTbody, '', err && err.message ? err.message : 'Failed to load leave balances.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeAttendance(employeeId) {
      ensureEmployeeSectionState('attendance');
      const state = hrEmployeeSectionState.attendance;
      if (!hrEmployeeAttendanceTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeAttendanceTbody.innerHTML = `<tr><td colspan="2" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        // Rolling last-12-months summary: present/absent days + leave taken and
        // balance, computed server-side for this employee (no per-day fetch).
        const data = await adminAPI.fetch(`${API.adminHrEmployee(employeeId)}/attendance_summary`, { method: 'GET' });
        const present = Number(data?.present_days || 0);
        const absent = Number(data?.absent_days || 0);
        const leaveTaken = Number(data?.leave_taken_days || 0);
        const bal = data?.leave_balance || null;
        const remaining = bal ? Number(bal.remaining_days || 0) : null;
        const totalDays = present + absent;
        const rate = totalDays > 0 ? Math.round((present / totalDays) * 1000) / 10 : null;

        if (hrAttendanceOverview) {
          hrAttendanceOverview.textContent = totalDays > 0 || leaveTaken > 0
            ? `${present} present • ${absent} absent • ${leaveTaken} leave`
              + (remaining != null ? ` • ${remaining} balance` : '')
            : 'No attendance in the last 12 months';
        }

        const metricRow = (label, value) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(String(value))}</td></tr>`;
        const rows = [
          metricRow('Days present', present),
          metricRow('Days absent', absent),
          metricRow('Attendance rate', rate != null ? `${rate}%` : '—'),
          metricRow('Leave taken', `${leaveTaken} d`),
          metricRow(
            `Leave balance${bal ? ` (${bal.year})` : ''}`,
            remaining != null ? `${remaining} d` : '—',
          ),
        ].join('');
        setTableRows(hrEmployeeAttendanceTbody, rows, 'No attendance summary.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeAttendanceTbody, '', err && err.message ? err.message : 'Failed to load attendance.');
        if (hrAttendanceOverview) hrAttendanceOverview.textContent = 'Failed to load.';
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeePerformance(employeeId, departmentId) {
      ensureEmployeeSectionState('performance');
      const state = hrEmployeeSectionState.performance;
      if (state.loading || state.loaded) return;
      state.loading = true;
      if (hrEmployeeReviewsTbody) hrEmployeeReviewsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrEmployeeGoalsTbody) hrEmployeeGoalsTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const [reviewsData, goalsData] = await Promise.all([
          adminAPI.fetch(API.adminHrReviews, { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrGoals, { method: 'GET' }).catch(() => ({ items: [] })),
        ]);
        const reviews = Array.isArray(reviewsData?.items) ? reviewsData.items : [];
        const goals = Array.isArray(goalsData?.items) ? goalsData.items : [];
        hrDepartmentGoalsCache = goals;
        if (hrPerformanceOverview) {
          const myReviews = reviews.filter((item) => String(item.employee_id || '') === String(employeeId));
          const avgScore = myReviews.length
            ? (myReviews.reduce((sum, r) => sum + (Number(r.final_score) || 0), 0) / myReviews.length).toFixed(1)
            : null;
          const openGoals = goals.filter((g) => ['not_started', 'in_progress', 'at_risk'].includes((g.status || '').toLowerCase())).length;
          hrPerformanceOverview.textContent = `${myReviews.length} reviews${avgScore ? ` • Avg ${avgScore}` : ''} • ${openGoals} open goals`;
        }
        const reviewRows = reviews
          .filter((item) => String(item.employee_id || '') === String(employeeId))
          .map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.period || '')}</td>
              <td>${escapeHtml(item.status || '')}</td>
              <td>${item.final_score != null ? escapeHtml(item.final_score) : ''}</td>
              <td>${escapeHtml(getEmployeeNameById(item.reviewer_employee_id) || '')}</td>
            </tr>
          `).join('');
        setTableRows(hrEmployeeReviewsTbody, reviewRows, 'No reviews.');
        const now = new Date();
        const goalRows = goals
          .filter((item) => !departmentId || String(item.department_id || '') === String(departmentId))
          .map((item, idx) => {
            const allowCheckin = isGoalCheckinAllowed(item, now);
            const actionBtn = allowCheckin
              ? `<button class="btn ghost" data-goal-id="${escapeHtml(item.id || '')}" data-goal-act="checkin">Check-in</button>`
              : '';
            return `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.title || '')}</td>
              <td>${escapeHtml(item.period || '')}</td>
              <td>${escapeHtml(item.status || '')}</td>
              <td>${item.progress_pct != null ? `${item.progress_pct}%` : ''}</td>
              <td>${actionBtn}</td>
            </tr>
          `;
          }).join('');
        setTableRows(hrEmployeeGoalsTbody, goalRows, 'No goals.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeReviewsTbody, '', err && err.message ? err.message : 'Failed to load performance.');
        setTableRows(hrEmployeeGoalsTbody, '', err && err.message ? err.message : 'Failed to load performance.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeTalent(employeeId) {
      ensureEmployeeSectionState('talent');
      const state = hrEmployeeSectionState.talent;
      if (state.loading || state.loaded) return;
      state.loading = true;
      if (hrEmployeeOnboardingTbody) hrEmployeeOnboardingTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrEmployeeProbationTbody) hrEmployeeProbationTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const [onboardData, probationData, templateData] = await Promise.all([
          adminAPI.fetch(API.adminHrOnboarding, { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrProbation, { method: 'GET' }).catch(() => ({ items: [] })),
          hrOnboardingTemplates.length ? Promise.resolve({ items: hrOnboardingTemplates }) : adminAPI.fetch(API.adminHrOnboardingTemplates, { method: 'GET' }).catch(() => ({ items: [] })),
        ]);
        const onboard = Array.isArray(onboardData?.items) ? onboardData.items : [];
        const probation = Array.isArray(probationData?.items) ? probationData.items : [];
        const templates = Array.isArray(templateData?.items) ? templateData.items : [];
        if (templates.length) {
          hrOnboardingTemplates = templates;
          populateOnboardingTemplateOptions();
        }
        state.items = onboard;
        state.probationItems = probation;
        if (hrTalentOverview) {
          const myOnboard = onboard.filter((item) => String(item.employee_id || '') === String(employeeId));
          const myProbation = probation.filter((item) => String(item.employee_id || '') === String(employeeId));
          const onboardStatus = myOnboard[0]?.status || 'n/a';
          const probationStatus = myProbation[0]?.status || 'n/a';
          hrTalentOverview.textContent = `Onboarding: ${onboardStatus} • Probation: ${probationStatus}`;
        }
        const onboardRows = onboard
          .filter((item) => String(item.employee_id || '') === String(employeeId))
          .map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(getOnboardingTemplateNameById(item.template_id) || item.template_id || '')}</td>
              <td>${escapeHtml(item.status || '')}</td>
              <td>${item.completion_pct != null ? `${escapeHtml(item.completion_pct)}%` : ''}</td>
              <td>${escapeHtml(item.start_date || '')}</td>
              <td>
                <div class="row" style="gap:6px; flex-wrap:wrap;">
                  <button class="btn ghost small" data-onboarding-id="${escapeHtml(item.id || '')}" data-onboard-act="task">Add task</button>
                  <button class="btn ghost small" data-onboarding-id="${escapeHtml(item.id || '')}" data-onboard-act="complete">Complete</button>
                </div>
              </td>
            </tr>
          `).join('');
        const probationRows = probation
          .filter((item) => String(item.employee_id || '') === String(employeeId))
          .map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.end_date || '')}</td>
              <td>${escapeHtml(item.status || '')}</td>
              <td>${escapeHtml(item.decision || '')}</td>
              <td>${item.last_improvement_pct != null ? `${escapeHtml(item.last_improvement_pct)}%` : ''}</td>
              <td><button class="btn ghost small" data-probation-id="${escapeHtml(item.id || '')}" data-probation-act="checkin">Add check-in</button></td>
            </tr>
          `).join('');
        setTableRows(hrEmployeeOnboardingTbody, onboardRows, 'No onboarding data.');
        setTableRows(hrEmployeeProbationTbody, probationRows, 'No probation data.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeOnboardingTbody, '', err && err.message ? err.message : 'Failed to load talent.');
        setTableRows(hrEmployeeProbationTbody, '', err && err.message ? err.message : 'Failed to load talent.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeLearning(employeeId, positionId) {
      ensureEmployeeSectionState('learning');
      // Education/qualifications live in the Learning section but track their own
      // state, so load them regardless of the learning cache.
      _wireQualificationUI();
      _wireEmployeeCertDelete();
      loadEmployeeQualifications(employeeId).catch(() => {});
      const state = hrEmployeeSectionState.learning;
      if (state.loading || state.loaded) return;
      state.loading = true;
      if (hrEmployeeTrainingTbody) hrEmployeeTrainingTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrEmployeeCertsTbody) hrEmployeeCertsTbody.innerHTML = `<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrEmployeeSkillsTbody) hrEmployeeSkillsTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const [enrollData, certData, skillData, reqData, empSkillData] = await Promise.all([
          adminAPI.fetch(API.adminHrTrainingEnrollments, { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrEmployeeCertifications(employeeId), { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrSkills, { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrSkillRequirements, { method: 'GET' }).catch(() => ({ items: [] })),
          adminAPI.fetch(API.adminHrEmployeeSkills(employeeId), { method: 'GET' }).catch(() => ({ items: [] })),
        ]);
        const enrolls = Array.isArray(enrollData?.items) ? enrollData.items : [];
        const certs = Array.isArray(certData?.items) ? certData.items : [];
        const skills = Array.isArray(skillData?.items) ? skillData.items : [];
        hrSkillsList = skills;   // keep the catalogue fresh for name↔id lookups
        const requirements = Array.isArray(reqData?.items) ? reqData.items : [];
        const empSkills = Array.isArray(empSkillData?.items) ? empSkillData.items : [];
        // Merge role requirements (target) with the employee's own skills (current)
        // into one matrix so an assigned skill always shows, with its gap.
        const bySkill = new Map();
        requirements
          .filter((r) => !positionId || String(r.position_id || '') === String(positionId))
          .forEach((r) => {
            const key = String(r.skill_id);
            const row = bySkill.get(key) || { skill_id: key, name: getSkillNameById(r.skill_id) || '', target: null, current: null };
            if (r.target_level != null) row.target = Number(r.target_level);
            bySkill.set(key, row);
          });
        empSkills.forEach((s) => {
          const key = String(s.skill_id);
          const row = bySkill.get(key) || { skill_id: key, name: s.name || getSkillNameById(s.skill_id) || '', target: null, current: null };
          if (s.current_level != null) row.current = Number(s.current_level);
          if (!row.name) row.name = s.name || '';
          bySkill.set(key, row);
        });
        const skillMatrix = Array.from(bySkill.values())
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (hrLearningOverview) {
          const myEnroll = enrolls.filter((item) => String(item.employee_id || '') === String(employeeId));
          hrLearningOverview.textContent = `${myEnroll.length} enrollments • ${certs.length} certs • ${skillMatrix.length} skills`;
        }
        const trainingRows = enrolls
          .filter((item) => String(item.employee_id || '') === String(employeeId))
          .map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(hrTrainingCourseMap.get(String(hrTrainingSessionMap.get(String(item.training_session_id))?.course_id || ''))?.title || item.training_session_id || '')}</td>
              <td>${escapeHtml(item.status || '')}</td>
              <td>${escapeHtml(item.completion_score || '')}</td>
            </tr>
          `).join('');
        const certRows = certs
          .map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.name || '')}</td>
              <td>${escapeHtml(item.institution || '')}</td>
              <td>${escapeHtml(item.skills || '')}</td>
              <td>${escapeHtml(item.completion_date || '')}</td>
              <td>${escapeHtml(item.expiry_date || '')}</td>
              <td>${item.certificate_file_id
                ? `<a class="btn ghost" href="${API.adminHrDocumentDownload(item.certificate_file_id)}" target="_blank" rel="noopener">View</a>`
                : '<span style="color:var(--sub); font-size:12px;">—</span>'}</td>
              <td><button class="btn ghost" data-emp-cert-id="${escapeHtml(item.id || '')}">✕ Delete</button></td>
            </tr>
          `).join('');
        const skillRows = skillMatrix.map((row, idx) => {
          const gap = (row.target != null && row.current != null) ? row.target - row.current : null;
          const gapCell = gap == null ? '—'
            : (gap <= 0 ? '<span class="badge ok">met</span>' : `<span class="badge warn">-${gap}</span>`);
          return `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(row.name || row.skill_id)}</td>
              <td>${row.target != null ? row.target : '—'}</td>
              <td>${row.current != null ? row.current : '—'}</td>
              <td>${gapCell}</td>
            </tr>
          `;
        }).join('');
        setTableRows(hrEmployeeTrainingTbody, trainingRows, 'No training data.');
        setTableRows(hrEmployeeCertsTbody, certRows, 'No certifications.');
        setTableRows(hrEmployeeSkillsTbody, skillRows, 'No skills data.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeTrainingTbody, '', err && err.message ? err.message : 'Failed to load learning.');
        setTableRows(hrEmployeeCertsTbody, '', err && err.message ? err.message : 'Failed to load learning.');
        setTableRows(hrEmployeeSkillsTbody, '', err && err.message ? err.message : 'Failed to load learning.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeQualifications(employeeId) {
      ensureEmployeeSectionState('qualifications');
      const state = hrEmployeeSectionState.qualifications;
      const tbody = document.getElementById('hrEmployeeQualificationsTbody');
      if (!tbody || state.loading) return;
      state.loading = true;
      tbody.innerHTML = `<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>`;
      // Delete is delegated once on the persistent tbody.
      if (!tbody.dataset.wired) {
        tbody.dataset.wired = '1';
        tbody.addEventListener('click', async (e) => {
          const btn = e.target.closest('[data-qual-id]');
          if (!btn || !hrEmployeeDetail) return;
          if (!window.confirm('Delete this qualification?')) return;
          try {
            await adminAPI.fetch(API.adminHrEmployeeQualification(hrEmployeeDetail.id, btn.getAttribute('data-qual-id')), { method: 'DELETE' });
            state.loaded = false;
            loadEmployeeQualifications(hrEmployeeDetail.id);
          } catch (err) {
            showFlash(err && err.message ? err.message : 'Delete failed', 'error');
          }
        });
      }
      try {
        const data = await adminAPI.fetch(API.adminHrEmployeeQualifications(employeeId), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const rows = items.map((q, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(String(q.level || '').replace(/_/g, ' '))}</td>
            <td>${escapeHtml(q.institution || '')}</td>
            <td>${escapeHtml(q.field_of_study || '')}</td>
            <td>${escapeHtml(q.award_year || '')}</td>
            <td>${q.certificate_file_id
              ? `<a class="btn ghost" href="${API.adminHrDocumentDownload(q.certificate_file_id)}" target="_blank" rel="noopener">View</a>`
              : '<span style="color:var(--err); font-size:12px;">No certificate</span>'}</td>
            <td><button class="btn ghost" data-qual-id="${escapeHtml(q.id || '')}">✕ Delete</button></td>
          </tr>
        `).join('');
        setTableRows(tbody, rows, 'No qualifications.');
        state.loaded = true;
      } catch (err) {
        setTableRows(tbody, '', err && err.message ? err.message : 'Failed to load qualifications.');
      } finally {
        state.loading = false;
      }
    }

    // Delete is delegated once on the persistent employee-certs tbody.
    function _wireEmployeeCertDelete() {
      if (!hrEmployeeCertsTbody || hrEmployeeCertsTbody.dataset.wired) return;
      hrEmployeeCertsTbody.dataset.wired = '1';
      hrEmployeeCertsTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-emp-cert-id]');
        if (!btn || !hrEmployeeDetail) return;
        if (!window.confirm('Delete this certification?')) return;
        try {
          await adminAPI.fetch(API.adminHrEmployeeCertification(hrEmployeeDetail.id, btn.getAttribute('data-emp-cert-id')), { method: 'DELETE' });
          hrEmployeeSectionState.learning.loaded = false;
          loadEmployeeLearning(hrEmployeeDetail.id, hrEmployeeDetail.position_id || null);
        } catch (err) {
          showFlash(err && err.message ? err.message : 'Delete failed', 'error');
        }
      });
    }

    function _wireQualificationUI() {
      const addBtn = document.getElementById('hrEmployeeAddQualificationBtn');
      const form = document.getElementById('hrQualificationForm');
      if (addBtn && !addBtn.dataset.wired) {
        addBtn.dataset.wired = '1';
        addBtn.addEventListener('click', () => {
          if (!hrEmployeeDetail) { showFlash('Select an employee first.', 'error'); return; }
          if (form) form.reset();
          openDialog(document.getElementById('hrQualificationDlg'));
        });
      }
      if (form && !form.dataset.wired) {
        form.dataset.wired = '1';
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (!hrEmployeeDetail) return;
          const empId = hrEmployeeDetail.id;
          const level = document.getElementById('hrQualificationLevel').value;
          const institution = document.getElementById('hrQualificationInstitution').value.trim();
          const field = document.getElementById('hrQualificationField').value.trim();
          const yearRaw = document.getElementById('hrQualificationYear').value;
          const fileInput = document.getElementById('hrQualificationCertFile');
          const submitBtn = document.getElementById('hrQualificationSubmit');
          if (submitBtn) submitBtn.disabled = true;
          try {
            let certId = null;
            const file = fileInput && fileInput.files ? fileInput.files[0] : null;
            if (file) {
              // Upload the certificate first (validated PDF/image), then link it.
              const fd = new FormData();
              fd.append('file', file);
              fd.append('category', 'qualifications');
              fd.append('owner_type', 'employee');
              fd.append('owner_id', empId);
              const up = await adminAPI.fetch(API.adminHrDocumentUpload, { method: 'POST', body: fd });
              certId = up && up.id ? up.id : null;
            }
            const payload = {
              level,
              institution: institution || null,
              field_of_study: field || null,
              award_year: yearRaw ? Number(yearRaw) : null,
              certificate_file_id: certId,
            };
            await adminAPI.fetch(API.adminHrEmployeeQualifications(empId), { method: 'POST', body: JSON.stringify(payload) });
            closeDialog(document.getElementById('hrQualificationDlg'));
            showFlash('Degree added');
            hrEmployeeSectionState.qualifications.loaded = false;
            loadEmployeeQualifications(empId);
          } catch (err) {
            showFlash(err && err.message ? err.message : 'Failed to add degree', 'error');
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        });
      }
    }

    async function loadEmployeeFinance(employeeId) {
      ensureEmployeeSectionState('finance');
      const state = hrEmployeeSectionState.finance;
      if (state.loading || state.loaded) return;
      state.loading = true;
      if (hrEmployeeLoansTbody) hrEmployeeLoansTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrLoans, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const filtered = items.filter((item) => String(item.employee_id || '') === String(employeeId));
        filtered.forEach((item) => _loanApprovalsById.set(String(item.id), item.approvals || []));
        if (hrFinanceOverview) {
          const active = filtered.filter((i) => (i.status || '').toLowerCase() === 'active').length;
          const collateralLocked = filtered.reduce((sum, loan) => sum + (Number(loan.collateral_leave_days) || 0), 0);
          hrFinanceOverview.textContent = `${filtered.length} loans • ${active} active • Collateral ${collateralLocked} days`;
        }
        const rows = filtered.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.status || '')}</td>
            <td>${escapeHtml(item.principal_amount || '')}</td>
            <td>${escapeHtml(item.balance != null ? item.balance : '')}</td>
            <td>${escapeHtml(item.collateral_leave_days || '')}</td>
            <td>${escapeHtml(item.term_months || '')}</td>
            <td>
              <div class="row" style="gap:6px; flex-wrap:wrap;">
                ${String(item.status || '').toLowerCase() === 'pending'
                  ? `<button class="btn ghost" data-loan-id="${escapeHtml(item.id || '')}" data-loan-act="approve">Approve</button>
                     <button class="btn ghost" data-loan-id="${escapeHtml(item.id || '')}" data-loan-act="deny">Reject</button>`
                  : ''}
                ${String(item.status || '').toLowerCase() === 'approved' && Number(item.balance || 0) > 0
                  ? `<button class="btn ghost" data-loan-id="${escapeHtml(item.id || '')}" data-loan-act="repay">Repay</button>`
                  : ''}
              </div>
            </td>
          </tr>
        `).join('');
        setTableRows(hrEmployeeLoansTbody, rows, 'No loans.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeLoansTbody, '', err && err.message ? err.message : 'Failed to load loans.');
        if (hrFinanceOverview) hrFinanceOverview.textContent = 'Failed to load.';
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeDocuments(employeeId) {
      ensureEmployeeSectionState('documents');
      const state = hrEmployeeSectionState.documents;
      if (!hrEmployeeDocsTbody || state.loading || state.loaded) return;
      state.loading = true;
      hrEmployeeDocsTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrDocuments, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const filtered = items.filter((item) => String(item.owner_id || '') === String(employeeId));
        const rows = filtered.map((item, idx) => {
          const catLabel = item.category ? (HR_COMPLIANCE_FACTOR_LABELS[item.category] || item.category) : '—';
          return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.filename || '')}</td>
            <td>${escapeHtml(catLabel)}</td>
            <td>
              <div class="row" style="gap:6px;">
                <a class="btn ghost" href="${API.adminHrDocumentDownload(item.id || '')}" target="_blank" rel="noopener">Download</a>
                <button class="btn ghost" data-doc-id="${escapeHtml(item.id || '')}">✕ Delete</button>
              </div>
            </td>
          </tr>`;
        }).join('');
        setTableRows(hrEmployeeDocsTbody, rows, 'No documents.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeDocsTbody, '', err && err.message ? err.message : 'Failed to load documents.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeEss(employeeId) {
      ensureEmployeeSectionState('ess');
      const state = hrEmployeeSectionState.ess;
      if (state.loading || state.loaded) return;
      state.loading = true;
      if (hrEmployeeProfileChangesTbody) hrEmployeeProfileChangesTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      try {
        const data = await adminAPI.fetch(API.adminHrProfileChanges, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const filtered = items.filter((item) => String(item.employee_id || '') === String(employeeId));
        const rows = filtered.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.field || '')}</td>
            <td>${escapeHtml(item.old_value || '')} → ${escapeHtml(item.new_value || '')}</td>
            <td>${escapeHtml(item.status || '')}</td>
          </tr>
        `).join('');
        setTableRows(hrEmployeeProfileChangesTbody, rows, 'No profile changes.');
        state.loaded = true;
      } catch (err) {
        setTableRows(hrEmployeeProfileChangesTbody, '', err && err.message ? err.message : 'Failed to load ESS data.');
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeCompliance(employeeId) {
      ensureEmployeeSectionState('compliance');
      const state = hrEmployeeSectionState.compliance || { loaded: false, loading: false };
      hrEmployeeSectionState.compliance = state;
      if (state.loading || state.loaded) return;
      state.loading = true;
      try {
        const data = await adminAPI.fetch(`${API.adminHrEmployee(employeeId)}/compliance`, { method: 'GET' });
        const empStatus = data?.employee_status || null;
        if (hrEmployeeComplianceSummary) {
          const snap = data?.compliance_snapshot || null;
          const pct = empStatus && empStatus.completeness_pct != null ? `${empStatus.completeness_pct}%` : '—';
          const deptLine = snap
            ? ` • Dept completeness ${snap.completeness_score ?? '—'} • Training ${snap.training_score ?? '—'} • Recruitment ${snap.recruitment_score ?? '—'}`
            : '';
          hrEmployeeComplianceSummary.textContent = `This employee: ${pct} complete${deptLine}`;
        }
        // Required-documents checklist: each global/position-required category with a
        // satisfied flag and, when missing, an Upload action pre-tagged to that category.
        if (hrEmployeeRequiredDocsTbody) {
          const reqDocs = Array.isArray(empStatus?.required_documents) ? empStatus.required_documents : [];
          const docRows = reqDocs.map((doc, idx) => {
            const label = HR_COMPLIANCE_FACTOR_LABELS[doc.category] || doc.category;
            const badge = doc.satisfied
              ? '<span style="color:var(--ok,#2e7d32); font-weight:600;">✓ On file</span>'
              : '<span style="color:var(--danger,#c0392b); font-weight:600;">✗ Missing</span>';
            const action = doc.satisfied
              ? `<button class="btn ghost small" data-doc-view="${escapeHtml(doc.file_id || '')}">👁 View</button>`
                + ` <button class="btn ghost small" data-req-doc-upload="${escapeHtml(doc.category)}">⬆ Replace</button>`
                + ` <button class="btn ghost small" data-doc-delete="${escapeHtml(doc.file_id || '')}" data-doc-label="${escapeHtml(label)}">✕ Delete</button>`
              : `<button class="btn ghost small" data-req-doc-upload="${escapeHtml(doc.category)}">⬆ Upload</button>`;
            return `<tr><td>${idx + 1}</td><td>${escapeHtml(label)}</td><td>${badge}</td><td>${action}</td></tr>`;
          }).join('');
          setTableRows(hrEmployeeRequiredDocsTbody, docRows, 'No required documents.');
        }
        if (hrEmployeePoliciesTbody) {
          const policies = Array.isArray(data?.policies) ? data.policies : [];
          const rows = policies.map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.title || '')}</td>
              <td>${escapeHtml([_deptScopeLabel(item.department_scope), item.location_scope].filter(Boolean).join(' / '))}</td>
              <td>${escapeHtml(item.is_active)}</td>
            </tr>
          `).join('');
          setTableRows(hrEmployeePoliciesTbody, rows, 'No policies.');
        }
        if (hrEmployeeAnnouncementsTbody) {
          const anns = Array.isArray(data?.announcements) ? data.announcements : [];
          const rows = anns.map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(item.title || '')}</td>
              <td>${escapeHtml([_deptScopeLabel(item.department_scope), item.location_scope].filter(Boolean).join(' / '))}</td>
              <td>${escapeHtml(item.expires_at || '')}</td>
            </tr>
          `).join('');
          setTableRows(hrEmployeeAnnouncementsTbody, rows, 'No announcements.');
        }
        state.loaded = true;
      } catch (err) {
        if (hrEmployeeComplianceSummary) hrEmployeeComplianceSummary.textContent = err && err.message ? err.message : 'Failed to load compliance.';
      } finally {
        state.loading = false;
      }
    }

    async function loadEmployeeSections(employeeId) {
      const deptId = hrEmployeeDetail?.department_id || null;
      const positionId = hrEmployeeDetail?.position_id || null;
      await Promise.all([
        loadEmployeeShiftAssignments(employeeId),
        loadEmployeeLeave(employeeId),
        loadEmployeeLeaveBalances(employeeId),
        loadEmployeeStatusAudits(employeeId),
        loadEmployeeAttendance(employeeId),
        loadEmployeePerformance(employeeId, deptId),
        loadEmployeeTalent(employeeId),
        loadEmployeeLearning(employeeId, positionId),
        loadEmployeeFinance(employeeId),
        loadEmployeeEss(employeeId),
        loadEmployeeDocuments(employeeId),
        loadEmployeeCompliance(employeeId),
      ]);
    }

    async function loadHrPerformance() {
      if (hrGoalsTbody) hrGoalsTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrReviewsTbody) hrReviewsTbody.innerHTML = `<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.people) {
        try { await loadHrPeople(); } catch {}
      }
      if (!hrDataState.departments) {
        try { await loadHrDepartments(); } catch {}
      }
      populatePerformanceDeptOptions();
      const [goalsData, reviewsData] = await Promise.all([
        adminAPI.fetch(API.adminHrGoals, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrReviews, { method: 'GET' }).catch(() => ({ items: [] })),
      ]);
      hrGoalsList = Array.isArray(goalsData?.items) ? goalsData.items : [];
      hrReviewsList = Array.isArray(reviewsData?.items) ? reviewsData.items : [];
      populatePerformanceYearOptions();
      renderHrPerformanceTables();
      // Load the AI verification scores in parallel with the rest of the
      // panel. Failures fall back to an empty table; the AI section is
      // additive and shouldn't block the goals/reviews from rendering.
      loadHrAiScores().catch(() => {});
      hrDataState.performance = true;
    }

    // ---- AI Verification Scores (Phase 2 enhancement) -----------------
    // See documentation/HR/phase 2 ai verification scoring.md. Renders
    // one cohort at a time; cohort tabs swap the dataset and re-render.
    // Year+month filter reuses the year selector at the top of the panel
    // (and the current month within that year by default).

    function _formatAiPct(value, places = 0) {
      if (value === null || value === undefined || value === '') return '—';
      const n = Number(value);
      if (Number.isNaN(n)) return '—';
      return `${(n * 100).toFixed(places)}%`;
    }

    function _formatAiScore(value) {
      if (value === null || value === undefined || value === '') return '—';
      const n = Number(value);
      if (Number.isNaN(n)) return '—';
      return n.toFixed(1);
    }

    function _aiScoreClass(score) {
      const n = Number(score);
      if (Number.isNaN(n)) return '';
      if (n >= 85) return 'ok';
      if (n >= 70) return 'warn';
      return 'err';
    }

    function _resolveUserLabel(adminUserId, displayName) {
      // The API now resolves a name server-side (linked HR employee, else
      // admin username); prefer it. Fall back to the loaded people roster,
      // then the raw id.
      if (displayName) return displayName;
      if (!Array.isArray(hrPeopleList)) return `#${adminUserId}`;
      const match = hrPeopleList.find((p) => Number(p?.admin_user_id) === Number(adminUserId));
      if (!match) return `#${adminUserId}`;
      const name = [match.first_name, match.last_name].filter(Boolean).join(' ');
      return name || `#${adminUserId}`;
    }

    function _populateHrAiScoresYearOptions() {
      if (!hrAiScoresYear) return;
      // Populate once; defaults to current calendar year.
      if (hrAiScoresYear.options.length > 0) return;
      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const options = [];
      for (let y = currentYear; y >= currentYear - 4; y -= 1) {
        options.push(`<option value="${y}">${y}</option>`);
      }
      hrAiScoresYear.innerHTML = options.join('');
      hrAiScoresYear.value = String(currentYear);
      if (hrAiScoresMonth) {
        hrAiScoresMonth.value = String(now.getUTCMonth() + 1);
      }
    }

    function _readHrAiScoresPeriod() {
      const now = new Date();
      const year = hrAiScoresYear && hrAiScoresYear.value
        ? Number(hrAiScoresYear.value)
        : now.getUTCFullYear();
      const month = hrAiScoresMonth && hrAiScoresMonth.value
        ? Number(hrAiScoresMonth.value)
        : now.getUTCMonth() + 1;
      return { year, month };
    }

    async function loadHrAiScoresYtd() {
      if (!hrAiScoresYtdTbody) { _hrWarnMissing('#hrAiScoresYtdTbody'); return; }
      const cohort = hrAiScoresCohort || 'field_tech';
      const { year } = _readHrAiScoresPeriod();
      hrAiScoresYtdTbody.innerHTML = `<tr><td colspan="12" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrAiScoresYtdMeta) hrAiScoresYtdMeta.textContent = `${year} • loading…`;
      const params = new URLSearchParams({ cohort, year: String(year) });
      try {
        const data = await adminAPI.fetch(`${API.adminHrAiScoreYearSummary}?${params.toString()}`, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        renderHrAiScoresYtd(items, cohort, year);
      } catch (err) {
        hrAiScoresYtdTbody.innerHTML = `<tr><td colspan="12" style="color:var(--err);">Failed to load year-to-date</td></tr>`;
        if (hrAiScoresYtdMeta) hrAiScoresYtdMeta.textContent = `${year} • error`;
        // eslint-disable-next-line no-console
        console.error('loadHrAiScoresYtd failed for cohort', cohort, err);
      }
    }

    function renderHrAiScoresYtd(items, cohort, year) {
      if (!hrAiScoresYtdTbody) { _hrWarnMissing('#hrAiScoresYtdTbody'); return; }
      const isTechnician = cohort === 'field_tech';
      if (hrAiScoresYtdFlagCol) {
        hrAiScoresYtdFlagCol.style.display = isTechnician ? '' : 'none';
      }
      if (!items.length) {
        hrAiScoresYtdTbody.innerHTML = `<tr><td colspan="12" style="color:var(--sub);">No year-to-date data for ${year}. Click Recompute to run the rollup for this year.</td></tr>`;
        if (hrAiScoresYtdMeta) hrAiScoresYtdMeta.textContent = `${year} • 0 staff`;
        return;
      }
      if (hrAiScoresYtdMeta) hrAiScoresYtdMeta.textContent = `${year} • ${items.length} ${isTechnician ? 'technicians' : 'agents'}`;
      const flagDisplay = isTechnician ? '' : 'none';
      const rows = items.map((item, idx) => {
        const label = _resolveUserLabel(item.admin_user_id, item.display_name);
        const ytd = _formatAiScore(item.ytd_score_average);
        const ytdCls = _aiScoreClass(item.ytd_score_average);
        const latest = item.latest_score ? _formatAiScore(item.latest_score) : '—';
        const latestMonth = item.latest_month ? item.latest_month.slice(0, 7) : '';
        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(label)}</td>
            <td><span class="badge ${ytdCls}">${escapeHtml(ytd)}</span></td>
            <td>${escapeHtml(String(item.months_with_data ?? 0))}</td>
            <td>${escapeHtml(String(item.ytd_total_events ?? 0))}</td>
            <td>${escapeHtml(latest)}${latestMonth ? ` <span style="color:var(--sub);font-size:11px;">${escapeHtml(latestMonth)}</span>` : ''}</td>
            <td>${escapeHtml(_formatAiScore(item.ytd_quality_avg))}</td>
            <td>${escapeHtml(_formatAiPct(item.ytd_rejection_rate, 1))}</td>
            <td>${escapeHtml(_formatAiPct(item.ytd_override_rate, 1))}</td>
            <td style="display:${flagDisplay};">${escapeHtml(_formatAiPct(item.ytd_flag_rate, 1))}</td>
            <td>${escapeHtml(_formatAiPct(item.ytd_photo_pass_pct, 0))}</td>
            <td>${escapeHtml(_formatAiScore(item.ytd_customer_feedback))}</td>
          </tr>
        `;
      }).join('');
      hrAiScoresYtdTbody.innerHTML = rows;
    }

    async function loadHrAiScores() {
      if (!hrAiScoresTbody) { _hrWarnMissing('#hrAiScoresTbody'); return; }
      _populateHrAiScoresYearOptions();
      const cohort = hrAiScoresCohort || 'field_tech';
      const { year, month } = _readHrAiScoresPeriod();
      // YTD section reloads alongside the monthly view; runs in parallel.
      loadHrAiScoresYtd().catch(() => {});
      hrAiScoresTbody.innerHTML = `<tr><td colspan="10" style="color:var(--sub);">Loading…</td></tr>`;
      const params = new URLSearchParams({
        cohort,
        year: String(year),
        month: String(month),
        limit: '200',
      });
      const url = `${API.adminHrAiScores}?${params.toString()}`;
      let data;
      try {
        data = await adminAPI.fetch(url, { method: 'GET' });
      } catch {
        hrAiScoresTbody.innerHTML = `<tr><td colspan="10" style="color:var(--err);">Failed to load AI verification scores</td></tr>`;
        // eslint-disable-next-line no-console
        console.error('loadHrAiScores fetch failed for cohort', cohort);
        return;
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      renderHrAiScores(items, cohort);
    }

    function renderHrAiScores(items, cohort) {
      if (!hrAiScoresTbody) { _hrWarnMissing('#hrAiScoresTbody'); return; }
      const isTechnician = cohort === 'field_tech';
      if (hrAiScoresFlagCol) {
        hrAiScoresFlagCol.style.display = isTechnician ? '' : 'none';
      }
      if (!items.length) {
        hrAiScoresTbody.innerHTML = `<tr><td colspan="10" style="color:var(--sub);">No data for this period.</td></tr>`;
        if (hrAiScoresKpiAvg) hrAiScoresKpiAvg.textContent = '—';
        if (hrAiScoresKpiCoverage) hrAiScoresKpiCoverage.textContent = '0';
        if (hrAiScoresKpiLowCount) hrAiScoresKpiLowCount.textContent = '0';
        return;
      }
      // KPIs.
      const valid = items.filter((i) => i.score_average !== null && i.score_average !== '');
      const avg = valid.length
        ? valid.reduce((a, b) => a + Number(b.score_average), 0) / valid.length
        : null;
      const lowCount = valid.filter((i) => Number(i.score_average) < 70).length;
      if (hrAiScoresKpiAvg) hrAiScoresKpiAvg.textContent = avg === null ? '—' : avg.toFixed(1);
      if (hrAiScoresKpiCoverage) hrAiScoresKpiCoverage.textContent = String(items.length);
      if (hrAiScoresKpiLowCount) hrAiScoresKpiLowCount.textContent = String(lowCount);
      // Rows.
      const flagDisplay = isTechnician ? '' : 'none';
      const rows = items.map((item, idx) => {
        const label = _resolveUserLabel(item.admin_user_id, item.display_name);
        const score = _formatAiScore(item.score_average);
        const scoreCls = _aiScoreClass(item.score_average);
        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(label)}</td>
            <td><span class="badge ${scoreCls}">${escapeHtml(score)}</span></td>
            <td>${escapeHtml(String(item.score_count ?? 0))}</td>
            <td>${escapeHtml(_formatAiScore(item.quality_avg))}</td>
            <td>${escapeHtml(_formatAiPct(item.rejection_rate, 1))}</td>
            <td>${escapeHtml(_formatAiPct(item.override_rate, 1))}</td>
            <td style="display:${flagDisplay};">${escapeHtml(_formatAiPct(item.flag_rate, 1))}</td>
            <td>${escapeHtml(_formatAiPct(item.photo_pass_pct, 0))}</td>
            <td>${escapeHtml(_formatAiScore(item.customer_feedback_score))}</td>
          </tr>
        `;
      }).join('');
      hrAiScoresTbody.innerHTML = rows;
    }

    function bindHrAiScoresCohortTabs() {
      if (!hrAiScoresCohortTabs || hrAiScoresCohortTabs.dataset.bound === '1') return;
      hrAiScoresCohortTabs.dataset.bound = '1';
      hrAiScoresCohortTabs.addEventListener('click', (event) => {
        const btn = event.target.closest('button[data-ai-cohort]');
        if (!btn) return;
        const cohort = btn.dataset.aiCohort;
        if (!cohort || cohort === hrAiScoresCohort) return;
        hrAiScoresCohort = cohort;
        hrAiScoresCohortTabs.querySelectorAll('button[data-ai-cohort]').forEach((b) => {
          const active = b.dataset.aiCohort === cohort;
          b.classList.toggle('active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        loadHrAiScores().catch(() => {});
      });
    }

    // Recompute now: runs the same aggregator the nightly job runs, but
    // for the current cohort × current month, and reloads the table.
    // The recompute endpoint requires system_admin; non-admin users will
    // see a clear 403 message. The nightly job is still the routine
    // refresh; this button is the manual escape hatch when a supervisor
    // wants to see updated scores between cron runs.
    // Polling cadence for the queued recompute job. 1500ms feels live
    // without hammering the API. We cap total polling time defensively
    // so a stuck job can't keep the button disabled forever.
    const HR_AI_RECOMPUTE_POLL_INTERVAL_MS = 1500;
    const HR_AI_RECOMPUTE_POLL_MAX_MS = 10 * 60 * 1000; // 10 minutes

    function _isTerminalJobStatus(status) {
      return status === 'completed' || status === 'failed';
    }

    async function _pollHrAiScoresRecomputeJob(jobId, originalLabel, cohortLabel, year) {
      // Returns the final job payload (or null if polling timed out).
      const startedAt = Date.now();
      // Tiny initial delay so the runner has a chance to mark itself running.
      await new Promise((r) => setTimeout(r, 200));
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let job;
        try {
          job = await adminAPI.fetch(API.adminHrAiScoreRecomputeYearJob(jobId), { method: 'GET' });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Recompute poll failed:', err);
          return null;
        }
        const months = Array.isArray(job?.months_run) ? job.months_run : [];
        const startMonth = Number(job?.start_month) || null;
        const endMonth = Number(job?.end_month) || null;
        const totalMonths = (startMonth && endMonth) ? Math.max(0, endMonth - startMonth + 1) : null;
        if (hrAiScoresRecompute && !_isTerminalJobStatus(job?.status)) {
          const progress = totalMonths !== null && months.length <= totalMonths
            ? `${months.length}/${totalMonths}`
            : String(months.length);
          hrAiScoresRecompute.textContent = `Recomputing… ${progress}`;
        }
        if (_isTerminalJobStatus(job?.status)) {
          return job;
        }
        if (Date.now() - startedAt > HR_AI_RECOMPUTE_POLL_MAX_MS) {
          showFlash(
            `Recompute is taking longer than expected; check back later (${cohortLabel} ${year}).`,
            'error',
          );
          return null;
        }
        await new Promise((r) => setTimeout(r, HR_AI_RECOMPUTE_POLL_INTERVAL_MS));
      }
    }

    async function recomputeHrAiScores() {
      if (!hrAiScoresRecompute || hrAiScoresRecomputing) return;
      hrAiScoresRecomputing = true;
      const originalLabel = hrAiScoresRecompute.textContent;
      hrAiScoresRecompute.disabled = true;
      hrAiScoresRecompute.textContent = 'Recomputing…';
      try {
        const { year } = _readHrAiScoresPeriod();
        const cohort = hrAiScoresCohort || 'field_tech';
        const cohortLabel = cohort === 'field_tech' ? 'technicians' : 'agents';
        const payload = { cohort, year };
        // 1) Enqueue the job. Returns 202 with the pending job row.
        const enqueueResp = await adminAPI.fetch(API.adminHrAiScoreRecomputeYear, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const jobId = enqueueResp?.id;
        if (!jobId) {
          showFlash('Failed to enqueue recompute (no job id returned).', 'error');
          return;
        }
        // 2) Poll until status is completed or failed.
        const final = await _pollHrAiScoresRecomputeJob(jobId, originalLabel, cohortLabel, year);
        if (!final) {
          // Polling timed out — leave any earlier flash in place.
          return;
        }
        const months = Array.isArray(final.months_run) ? final.months_run : [];
        const totalRows = Number(final.total_rows_upserted || 0);
        const startMonth = final.start_month;
        const endMonth = final.end_month;
        if (final.status === 'failed') {
          const reason = final.error_message
            ? String(final.error_message).split('\n')[0]
            : 'see server logs';
          showFlash(`Recompute failed for ${cohortLabel} ${year}: ${reason}`, 'error');
        } else if (months.length === 0) {
          // Future-year guard or no months in range — both terminate as
          // completed with empty months_run. Make the reason explicit.
          showFlash(`No months to recompute for ${year}.`, 'error');
        } else {
          const monthsWithData = months.filter((m) => Number(m.rows_upserted) > 0).length;
          showFlash(
            `Recomputed ${cohortLabel} for ${year}:`
              + ` months ${startMonth}–${endMonth} (${months.length} run, ${monthsWithData} with data, ${totalRows} rows upserted).`,
            totalRows > 0 ? 'ok' : 'error',
          );
        }
        await loadHrAiScores();
      } catch (err) {
        const status = err && err.status;
        if (status === 403) {
          showFlash('Recompute requires admin role.', 'error');
        } else {
          showFlash(
            `Failed to recompute: ${err && err.message ? err.message : 'unknown error'}`,
            'error',
          );
        }
        // eslint-disable-next-line no-console
        console.error('recomputeHrAiScores failed:', err);
      } finally {
        hrAiScoresRecomputing = false;
        if (hrAiScoresRecompute) {
          hrAiScoresRecompute.disabled = false;
          hrAiScoresRecompute.textContent = originalLabel;
        }
      }
    }

    function bindHrAiScoresRecompute() {
      if (!hrAiScoresRecompute || hrAiScoresRecompute.dataset.bound === '1') return;
      hrAiScoresRecompute.dataset.bound = '1';
      hrAiScoresRecompute.addEventListener('click', () => {
        recomputeHrAiScores().catch(() => {});
      });
    }

    function bindHrAiScoresPeriodPickers() {
      if (hrAiScoresYear && hrAiScoresYear.dataset.bound !== '1') {
        hrAiScoresYear.dataset.bound = '1';
        hrAiScoresYear.addEventListener('change', () => {
          loadHrAiScores().catch(() => {});
        });
      }
      if (hrAiScoresMonth && hrAiScoresMonth.dataset.bound !== '1') {
        hrAiScoresMonth.dataset.bound = '1';
        hrAiScoresMonth.addEventListener('change', () => {
          loadHrAiScores().catch(() => {});
        });
      }
    }

    async function loadHrTalent() {
      if (hrRecruitmentTbody) hrRecruitmentTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrApplicantsTbody) hrApplicantsTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrVacanciesTbody) hrVacanciesTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrOnboardingTbody) hrOnboardingTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrProbationTbody) hrProbationTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.people) {
        try { await loadHrPeople(); } catch {}
      }
      const [appsData, onboardData, probationData, templateData, applicantsData, vacanciesData] = await Promise.all([
        adminAPI.fetch(API.adminHrApplications, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrOnboarding, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrProbation, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrOnboardingTemplates, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrApplicants, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrVacancies, { method: 'GET' }).catch(() => ({ items: [] })),
      ]);
      hrRecruitmentList = Array.isArray(appsData?.items) ? appsData.items : [];
      hrOnboardingList = Array.isArray(onboardData?.items) ? onboardData.items : [];
      hrProbationList = Array.isArray(probationData?.items) ? probationData.items : [];
      hrApplicantList = Array.isArray(applicantsData?.items) ? applicantsData.items : [];
      hrVacancyList = Array.isArray(vacanciesData?.items) ? vacanciesData.items : [];
      hrOnboardingTemplates = Array.isArray(templateData?.items) ? templateData.items : [];
      if (!hrSelectedOnboardingTemplateId && hrOnboardingTemplates.length) {
        hrSelectedOnboardingTemplateId = String(hrOnboardingTemplates[0].id || '');
      }
      populateOnboardingTemplateOptions();
      populateApplicantOptions();
      populateVacancyOptions();
      hrOnboardingTasksCache.clear();
      if (hrOnboardingTemplates.length) {
        const itemResponses = await Promise.all(
          hrOnboardingTemplates.map((t) =>
            adminAPI.fetch(API.adminHrOnboardingTemplateItems(t.id), { method: 'GET' }).catch(() => ({ items: [] }))
          )
        );
        hrOnboardingTemplateItems = itemResponses
          .map((res, idx) => {
            const tplId = hrOnboardingTemplates[idx]?.id;
            return (Array.isArray(res?.items) ? res.items : []).map((item) => ({ ...item, template_id: item.template_id || tplId }));
          })
          .flat();
      } else {
        hrOnboardingTemplateItems = [];
      }
      renderHrTalentTables();
      renderOnboardingTemplates();
      hrDataState.talent = true;
    }

    function populatePerformanceYearOptions() {
      if (!hrPerformanceYear) return;
      const currentYear = new Date().getFullYear();
      const years = new Set([currentYear]);
      hrGoalsList.forEach((g) => {
        const y = getItemYear(g);
        if (y) years.add(y);
      });
      hrReviewsList.forEach((r) => {
        const y = getItemYear(r);
        if (y) years.add(y);
      });
      const sortedYears = Array.from(years).sort((a, b) => b - a).slice(0, 5);
      const options = [`<option value="${currentYear}" selected>${currentYear} (current)</option>`];
      sortedYears.forEach((y) => {
        if (y === currentYear) return;
        options.push(`<option value="${y}">${y}</option>`);
      });
      options.push('<option value="">All years</option>');
      hrPerformanceYear.innerHTML = options.join('');
    }

    function renderHrPerformanceTables() {
      if (!hrGoalsTbody || !hrReviewsTbody) return;
      const currentYear = new Date().getFullYear();
      const selectedYear = hrPerformanceYear ? (hrPerformanceYear.value || String(currentYear)) : String(currentYear);
      const targetYear = selectedYear === '' ? null : Number(selectedYear);
      const periodFilter = hrPerformancePeriod ? (hrPerformancePeriod.value || '').toLowerCase() : '';
      const deptFilter = hrPerformanceDept ? hrPerformanceDept.value : '';
      const goalFiltered = hrGoalsList.filter((item) => {
        const yr = getItemYear(item);
        if (targetYear && yr && yr !== targetYear) return false;
        if (targetYear && !yr && targetYear !== currentYear) return false;
        if (periodFilter && String(item.period || '').toLowerCase() !== periodFilter) return false;
        if (deptFilter && String(item.department_id || '') !== deptFilter) return false;
        return true;
      });
      const reviewFiltered = hrReviewsList.filter((item) => {
        const yr = getItemYear(item);
        if (targetYear && yr && yr !== targetYear) return false;
        if (targetYear && !yr && targetYear !== currentYear) return false;
        if (periodFilter && String(item.period || '').toLowerCase() !== periodFilter) return false;
        if (deptFilter && String(item.department_id || '') !== deptFilter) return false;
        return true;
      });
      const goalRows = goalFiltered.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(getDepartmentNameById(item.department_id) || '')}</td>
          <td>${escapeHtml(item.period || '')}</td>
          <td>${escapeHtml(item.title || '')}</td>
          <td>${escapeHtml(item.status || '')}</td>
          <td>${item.progress_pct != null ? `${item.progress_pct}%` : ''}</td>
        </tr>
      `).join('');
      const reviewRows = reviewFiltered.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(getEmployeeNameById(item.employee_id) || '')}</td>
          <td>${escapeHtml(item.period || '')}</td>
          <td>${escapeHtml(item.status || '')}</td>
          <td>${item.final_score != null ? escapeHtml(item.final_score) : ''}</td>
          <td>${escapeHtml(getEmployeeNameById(item.reviewer_employee_id) || '')}</td>
          <td><button class="btn ghost small" data-review-id="${escapeHtml(item.id || '')}" data-review-act="view">View</button></td>
        </tr>
      `).join('');
      setTableRows(hrGoalsTbody, goalRows, 'No goals yet.');
      setTableRows(hrReviewsTbody, reviewRows, 'No reviews yet.');
      renderPerformanceSummary(goalFiltered, reviewFiltered, targetYear);
    }

    function renderPerformanceSummary(goalFiltered, reviewFiltered, targetYear) {
      const totalEmployees = hrPeopleList.length;
      const goalDeptSet = new Set(goalFiltered.map((g) => String(g.department_id || '')));
      const staffWithoutGoals = hrPeopleList.filter((p) => !goalDeptSet.has(String(p.department_id || ''))).length;
      const reviewEmployeeSet = new Set(reviewFiltered.map((r) => String(r.employee_id || '')));
      const staffWithoutReviews = hrPeopleList.filter((p) => !reviewEmployeeSet.has(String(p.id || ''))).length;
      const totalGoals = goalFiltered.length;
      const achievedGoals = goalFiltered.filter((g) => {
        const status = (g.status || '').toLowerCase();
        const progress = Number(g.progress_pct);
        return status === 'done' || status === 'completed' || status === 'met' || Number.isFinite(progress) && progress >= 100;
      }).length;
      const goalsPct = totalGoals ? Math.round((achievedGoals / totalGoals) * 100) : 0;
      if (hrPerformanceGapGoals) hrPerformanceGapGoals.textContent = totalEmployees ? `${staffWithoutGoals} staff` : '—';
      if (hrPerformanceGapReviews) hrPerformanceGapReviews.textContent = totalEmployees ? `${staffWithoutReviews} staff` : '—';
      if (hrPerformanceGoalsMet) hrPerformanceGoalsMet.textContent = totalGoals ? `${goalsPct}% met` : '—';
      if (hrPerformanceOverview) {
        hrPerformanceOverview.textContent = `${goalFiltered.length} goals • ${reviewFiltered.length} reviews${targetYear ? ` • ${targetYear}` : ''}`;
      }
    }

    function openReviewDetail(reviewId) {
      if (!reviewId || !hrReviewDetailDlg) return;
      const review = hrReviewsList.find((r) => String(r.id) === String(reviewId));
      if (!review) {
        showFlash('Review not found', 'error');
        return;
      }
      const employeeName = getEmployeeNameById(review.employee_id) || review.employee_id || 'Employee';
      const reviewerName = getEmployeeNameById(review.reviewer_employee_id) || review.reviewer_employee_id || '—';
      if (hrReviewDetailTitle) hrReviewDetailTitle.textContent = `${employeeName} — ${review.period || ''}`;
      if (hrReviewDetailMeta) hrReviewDetailMeta.textContent = `Reviewer: ${reviewerName} • Status: ${review.status || ''}`;
      if (hrReviewDetailScore) hrReviewDetailScore.textContent = review.final_score != null ? `${review.final_score} / 10` : '—';
      if (hrReviewDetailStrengths) hrReviewDetailStrengths.textContent = review.strengths || '—';
      if (hrReviewDetailImprovements) hrReviewDetailImprovements.textContent = review.improvements || '—';
      openDialog(hrReviewDetailDlg);
    }

    function renderOnboardingTemplates() {
      if (hrOnboardingTemplates.length && !hrOnboardingTemplates.find((t) => String(t.id) === String(hrSelectedOnboardingTemplateId))) {
        hrSelectedOnboardingTemplateId = String(hrOnboardingTemplates[0].id || '');
      }
      if (hrOnboardingTemplatesTbody) {
        const rows = hrOnboardingTemplates.map((tpl, idx) => {
          const isSelected = String(tpl.id || '') === String(hrSelectedOnboardingTemplateId);
          const statusLabel = tpl.is_active === false ? 'Inactive' : 'Active';
          return `
            <tr data-template-id="${escapeHtml(tpl.id || '')}" ${isSelected ? 'class="selected"' : ''}>
              <td>${idx + 1}</td>
              <td>${escapeHtml(tpl.name || '')}</td>
              <td>${escapeHtml(tpl.role_type || '')}</td>
              <td>${escapeHtml(tpl.onboarding_type || '')}</td>
              <td>${tpl.default_due_days != null ? escapeHtml(tpl.default_due_days) : ''}</td>
              <td>${escapeHtml(statusLabel)}</td>
              <td>
                <div class="row" style="gap:6px;">
                  <button class="btn ghost small" data-template-id="${escapeHtml(tpl.id || '')}" data-template-act="edit">Edit</button>
                  <button class="btn ghost small" data-template-id="${escapeHtml(tpl.id || '')}" data-template-act="delete">Delete</button>
                </div>
              </td>
            </tr>
          `;
        }).join('');
        setTableRows(hrOnboardingTemplatesTbody, rows, 'No templates.');
      }
      const selectedId = hrSelectedOnboardingTemplateId || '';
      const items = hrOnboardingTemplateItems.filter((i) => String(i.template_id || '') === String(selectedId));
      const title = selectedId ? `Template tasks — ${getOnboardingTemplateNameById(selectedId)}` : 'Template tasks';
      if (hrOnboardingTemplateItemsTitle) hrOnboardingTemplateItemsTitle.textContent = title;
      if (hrOnboardingTemplateItemsTbody) {
        const rows = items.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(item.name || '')}</td>
            <td>${escapeHtml(item.owner_role || '')}</td>
            <td>${escapeHtml(item.sequence != null ? item.sequence : '')}</td>
            <td>${escapeHtml(item.due_offset_days != null ? item.due_offset_days : '')}</td>
            <td>${item.requires_evidence ? 'Yes' : ''}</td>
            <td>
              <div class="row" style="gap:6px;">
                <button class="btn ghost small" data-template-item-id="${escapeHtml(item.id || '')}" data-template-item-act="edit">Edit</button>
                <button class="btn ghost small" data-template-item-id="${escapeHtml(item.id || '')}" data-template-item-act="delete">Delete</button>
              </div>
            </td>
          </tr>
        `).join('');
        setTableRows(hrOnboardingTemplateItemsTbody, rows, selectedId ? 'No tasks for this template.' : 'Select a template.');
      }
      if (hrAddOnboardingTemplateItemBtn) hrAddOnboardingTemplateItemBtn.disabled = !hrSelectedOnboardingTemplateId;
    }

    function renderHrTalentTables() {
      if (hrRecruitmentTbody) {
        const stage = hrRecruitmentStageFilter ? hrRecruitmentStageFilter.value : '';
        const apps = hrRecruitmentList.filter((item) => !stage || String(item.stage || '') === stage);
        const appRows = apps.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(getApplicantNameById(item.applicant_id) || item.applicant_id || '')}</td>
            <td>${escapeHtml(getVacancyTitleById(item.vacancy_id) || item.vacancy_id || '')}</td>
            <td>${escapeHtml(item.stage || '')}</td>
            <td>${escapeHtml(item.decision_notes || '')}</td>
            <td><button class="btn ghost small" type="button" data-application-id="${escapeHtml(item.id || '')}" data-app-act="update">Edit</button></td>
          </tr>
        `).join('');
        setTableRows(hrRecruitmentTbody, appRows, 'No applications yet.');
      }
      if (hrApplicantsTbody) {
        const term = hrApplicantSearch ? (hrApplicantSearch.value || '').toLowerCase() : '';
        const filtered = hrApplicantList
          .slice()
          .filter((a) => {
            if (!term) return true;
            return [
              a.full_name,
              a.email,
              a.phone,
              a.expertise,
              a.highest_degree,
            ].some((v) => (v || '').toLowerCase().includes(term));
          })
          .sort((a, b) => {
            const aDate = new Date(a.created_at || 0);
            const bDate = new Date(b.created_at || 0);
            const aTime = Number.isNaN(aDate.getTime()) ? 0 : aDate.getTime();
            const bTime = Number.isNaN(bDate.getTime()) ? 0 : bDate.getTime();
            return bTime - aTime;
          });
        const applicantRows = filtered.map((a, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(a.full_name || '')}</td>
            <td>${escapeHtml(a.highest_degree || '')}</td>
            <td>${escapeHtml(a.expertise || '')}</td>
            <td>${a.applications_count != null ? escapeHtml(a.applications_count) : 0}</td>
            <td>${escapeHtml(a.last_application_at || '')}</td>
            <td><button class="btn ghost small" data-applicant-id="${escapeHtml(a.id || '')}" data-applicant-act="view">View</button></td>
          </tr>
        `).join('');
        setTableRows(hrApplicantsTbody, applicantRows, 'No applicants.');
      }
      if (hrVacanciesTbody) {
        const rows = hrVacancyList.map((v, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(v.title || '')}</td>
            <td>${escapeHtml(getPositionNameById(v.position_id) || '')}</td>
            <td>${escapeHtml(v.status || '')}</td>
            <td>
              <button class="btn ghost small" data-vacancy-id="${escapeHtml(v.id || '')}" data-vacancy-act="edit">Edit</button>
            </td>
          </tr>
        `).join('');
        setTableRows(hrVacanciesTbody, rows, 'No vacancies.');
      }
      if (hrOnboardingTbody) {
        const status = hrOnboardingStatusFilter ? hrOnboardingStatusFilter.value : '';
        const onboard = hrOnboardingList.filter((item) => !status || String(item.status || '') === status);
        const onboardRows = onboard.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(getEmployeeNameById(item.employee_id) || item.employee_id || '')}</td>
            <td>${escapeHtml(getOnboardingTemplateNameById(item.template_id) || '')}</td>
            <td>${escapeHtml(item.status || '')}</td>
            <td>${item.completion_pct != null ? `${escapeHtml(item.completion_pct)}%` : ''}</td>
            <td>
              <div class="row" style="gap:6px; flex-wrap:wrap;">
                <button class="btn ghost small" type="button" data-onboarding-id="${escapeHtml(item.id || '')}" data-onboard-act="tasks">Tasks</button>
                <button class="btn ghost small" type="button" data-onboarding-id="${escapeHtml(item.id || '')}" data-onboard-act="add_task">Add task</button>
              </div>
            </td>
          </tr>
        `).join('');
        setTableRows(hrOnboardingTbody, onboardRows, 'No onboarding records.');
      }
      if (hrProbationTbody) {
        const status = hrProbationStatusFilter ? hrProbationStatusFilter.value : '';
        const probation = hrProbationList.filter((item) => !status || String(item.status || '') === status);
        const probationRows = probation.map((item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(getEmployeeNameById(item.employee_id) || item.employee_id || '')}</td>
            <td>${escapeHtml(item.end_date || '')}</td>
            <td>${escapeHtml(item.status || '')}</td>
            <td>${escapeHtml(item.extended_until || item.start_date || '')}</td>
            <td>${item.last_improvement_pct != null ? `${escapeHtml(item.last_improvement_pct)}%` : ''}</td>
            <td><button class="btn ghost small" type="button" data-probation-id="${escapeHtml(item.id || '')}" data-employee-id="${escapeHtml(item.employee_id || '')}" data-probation-act="checkin">Check-in</button></td>
          </tr>
        `).join('');
        setTableRows(hrProbationTbody, probationRows, 'No probation records.');
      }
    }

    async function loadHrLearning() {
      if (hrTrainingTbody) hrTrainingTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrCertsTbody) hrCertsTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrSkillsTbody) hrSkillsTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.people) {
        try { await loadHrPeople(); } catch {}
      }
      const [courseData, sessData, enrollData, certData, skillData, skillReqData] = await Promise.all([
        adminAPI.fetch(API.adminHrTrainingCourses, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrTrainingSessions, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrTrainingEnrollments, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrCertifications, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrSkills, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrSkillRequirements, { method: 'GET' }).catch(() => ({ items: [] })),
      ]);
      hrTrainingCourses = Array.isArray(courseData?.items) ? courseData.items : [];
      hrTrainingCourseMap.clear();
      hrTrainingCourses.forEach((c) => hrTrainingCourseMap.set(String(c.id), c));
      hrTrainingSessionMap.clear();
      const sessions = Array.isArray(sessData?.items) ? sessData.items : [];
      sessions.forEach((s) => hrTrainingSessionMap.set(String(s.id), s));
      populateTrainingCourseOptions();
      populateTrainingAttendeesSelect();
      const enrolls = Array.isArray(enrollData?.items) ? enrollData.items : [];
      const certs = Array.isArray(certData?.items) ? certData.items : [];
      hrSkillsList = Array.isArray(skillData?.items) ? skillData.items : [];
      hrSkillRequirements = Array.isArray(skillReqData?.items) ? skillReqData.items : [];
      populateSkillOptions();
      const sessionRows = sessions.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(hrTrainingCourseMap.get(String(item.course_id || ''))?.title || item.course_id || '')}</td>
          <td>${item.start_date || item.end_date ? escapeHtml([item.start_date, item.end_date].filter(Boolean).join(' → ')) : '<span class="badge">Date TBD</span>'}</td>
          <td>${escapeHtml(item.location || '')}</td>
          <td>${escapeHtml(item.status || '')}</td>
          <td>${escapeHtml(item.max_attendees || '')}</td>
        </tr>
      `).join('');
      const certRows = certs.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(getEmployeeNameById(item.employee_id) || item.employee_id || '')}</td>
          <td>${escapeHtml(item.name || '')}</td>
          <td>${escapeHtml(item.institution || '')}</td>
          <td>${escapeHtml(item.completion_date || '')}</td>
          <td>${escapeHtml(item.expiry_date || '')}</td>
        </tr>
      `).join('');
      const skillRows = hrSkillsList.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.name || '')}</td>
          <td>${escapeHtml(item.category || '')}</td>
          <td>${escapeHtml(item.level_scale || '')}</td>
          <td>
            <div class="row" style="gap:6px;">
              <button class="btn ghost small" data-skill-id="${escapeHtml(item.id || '')}" data-skill-act="edit">Edit</button>
              <button class="btn ghost small" data-skill-id="${escapeHtml(item.id || '')}" data-skill-act="delete">Delete</button>
            </div>
          </td>
        </tr>
      `).join('');
      const skillReqRows = hrSkillRequirements.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(getPositionNameById(item.position_id) || item.position_id || '')}</td>
          <td>${escapeHtml(getSkillNameById(item.skill_id) || item.skill_id || '')}</td>
          <td>${escapeHtml(formatLevelWithLabel(item.target_level))}</td>
          <td>
            <div class="row" style="gap:6px;">
              <button class="btn ghost small" data-skill-req-id="${escapeHtml(item.id || '')}" data-skill-req-act="edit">Edit</button>
              <button class="btn ghost small" data-skill-req-id="${escapeHtml(item.id || '')}" data-skill-req-act="delete">Delete</button>
            </div>
          </td>
        </tr>
      `).join('');
      const enrollmentRows = enrolls.map((item, idx) => {
        const courseTitle = hrTrainingCourseMap.get(String(hrTrainingSessionMap.get(String(item.training_session_id))?.course_id || ''))?.title
          || hrTrainingSessionMap.get(String(item.training_session_id))?.course_id
          || '';
        const employeeName = getEmployeeNameById(item.employee_id) || item.employee_id || '';
        return `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(employeeName)}</td>
          <td>${escapeHtml(courseTitle)}</td>
          <td>${escapeHtml(item.status || '')}</td>
          <td>${escapeHtml(item.completion_score || '')}</td>
        </tr>
      `;
      }).join('');
      setTableRows(hrTrainingTbody, sessionRows, 'No sessions scheduled.');
      setTableRows(hrCertsTbody, certRows, 'No certifications recorded.');
      setTableRows(hrSkillsTbody, skillRows, 'No skills defined.');
      const skillReqTbody = document.getElementById('hrSkillReqTbody');
      setTableRows(skillReqTbody, skillReqRows, 'No requirements.');
      if (document.getElementById('hrEnrollmentsTbody')) {
        setTableRows(document.getElementById('hrEnrollmentsTbody'), enrollmentRows, 'No enrollments.');
      }
      hrDataState.learning = true;
    }

    // loanId → its approval-stage progress, refreshed whenever loans render, so the
    // approve/reject actions can target the loan's *current* pending stage.
    const _loanApprovalsById = new Map();
    function _currentPendingStage(loanId) {
      const stages = _loanApprovalsById.get(String(loanId)) || [];
      const pending = stages.find((s) => s.status === 'pending');
      return pending ? pending.stage : 'hr';
    }

    function _loanApprovalBadges(approvals) {
      if (!approvals || !approvals.length) return '<span class="muted" style="font-size:11px;">—</span>';
      const icon = (s) => (s === 'approved' ? '✓' : s === 'denied' ? '✗' : '·');
      const color = (s) => (s === 'approved' ? '#15803d' : s === 'denied' ? '#b91c1c' : '#6b7280');
      return approvals.map((a) =>
        `<span class="badge" style="font-size:10px; margin:0 2px 2px 0; color:${color(a.status)};" title="${escapeHtml(a.status)}">${escapeHtml(a.label || a.stage)} ${icon(a.status)}</span>`
      ).join('');
    }

    function renderHrFinanceTable() {
      if (!hrLoansTbody) { _hrWarnMissing('#hrLoansTbody'); return; }
      const filter = hrLoanStatusFilter ? (hrLoanStatusFilter.value || 'open').toLowerCase() : 'open';
      const filtered = hrLoansList.filter((item) => {
        const status = (item.status || '').toLowerCase();
        switch (filter) {
          case 'pending': return status === 'pending';
          case 'approved': return status === 'approved';
          case 'paid': return ['paid', 'paid_up', 'settled', 'completed'].includes(status);
          case 'rejected': return ['rejected', 'denied'].includes(status);
          case 'all': return true;
          case 'open':
          default:
            return status === 'pending' || status === 'approved';
        }
      });
      filtered.forEach((item) => _loanApprovalsById.set(String(item.id), item.approvals || []));
      const rows = filtered.map((item, idx) => {
        const employeeName = getEmployeeNameById(item.employee_id) || item.employee_id || '—';
        const currency = item.currency || 'GMD';
        const paid = item.total_paid != null ? item.total_paid : 0;
        const repayLabel = `${paid} / ${item.principal_amount || 0} ${currency}`;
        return `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(employeeName)}</td>
          <td>${escapeHtml(item.status || '')}</td>
          <td>${escapeHtml(item.principal_amount || '')} ${escapeHtml(currency)}</td>
          <td>${escapeHtml(item.collateral_leave_days || '')}</td>
          <td>${escapeHtml(repayLabel)}</td>
          <td>${_loanApprovalBadges(item.approvals)}</td>
          <td><button class="btn ghost small" data-loan-id="${escapeHtml(item.id || '')}" data-loan-act="view">View</button></td>
        </tr>
      `;
      }).join('');
      setTableRows(hrLoansTbody, rows, 'No loans recorded.');
      renderFinanceSummary(filtered);
      renderLoanStaffChart(filtered);
    }

    function renderFinanceSummary(loans) {
      const pending = loans.filter((l) => (l.status || '').toLowerCase() === 'pending').length;
      const open = loans.filter((l) => {
        const status = (l.status || '').toLowerCase();
        return status === 'pending' || status === 'approved';
      }).length;
      const perCurrency = new Map();
      let baseTotal = 0;
      let baseCurrency = 'USD';
      loans.forEach((l) => {
        const cur = (l.currency || 'GMD').toUpperCase();
        perCurrency.set(cur, (perCurrency.get(cur) || 0) + (Number(l.balance) || 0));
        baseCurrency = l.base_currency || baseCurrency;
        baseTotal += Number(l.base_balance != null ? l.base_balance : l.balance || 0) || 0;
      });
      if (hrLoanPendingCount) hrLoanPendingCount.textContent = pending;
      if (hrLoanOpenCount) hrLoanOpenCount.textContent = open;
      if (hrLoanOutstanding) {
        const native = Array.from(perCurrency.entries()).map(([cur, val]) => `${cur} ${val.toFixed(2)}`).join(' • ');
        hrLoanOutstanding.textContent = native ? `${native} (base ${baseCurrency} ${baseTotal.toFixed(2)})` : '—';
      }
    }

    function renderLoanStaffChart(loans) {
      if (!hrLoanStaffChart) return;
      const totals = new Map();
      loans.forEach((loan) => {
        const key = `${getEmployeeNameById(loan.employee_id) || loan.employee_id || 'Unknown'} (${loan.currency || 'GMD'})`;
        const principal = Number(loan.principal_amount) || 0;
        totals.set(key, (totals.get(key) || 0) + principal);
      });
      const entries = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
      if (!entries.length) {
        hrLoanStaffChart.innerHTML = `<div style="color:var(--sub);">No data.</div>`;
        return;
      }
      const maxVal = Math.max(...entries.map(([, val]) => val), 1);
      hrLoanStaffChart.innerHTML = entries.map(([name, val]) => {
        const pct = Math.max(6, Math.round((val / maxVal) * 100));
        return `
          <div class="row" style="align-items:center; gap:8px;">
            <div style="min-width:140px;">${escapeHtml(name)}</div>
            <div style="flex:1; background:var(--border); border-radius:6px; overflow:hidden;">
              <div style="width:${pct}%; background:var(--accent); color:#fff; padding:4px 6px; font-size:12px;">${val.toFixed(2)}</div>
            </div>
          </div>
        `;
      }).join('');
    }

    async function loadHrFinance() {
      if (hrLoansTbody) hrLoansTbody.innerHTML = `<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>`;
      if (!hrDataState.people) {
        try { await loadHrPeople(); } catch {}
      }
      const data = await adminAPI.fetch(API.adminHrLoans, { method: 'GET' });
      hrLoansList = Array.isArray(data?.items) ? data.items : [];
      renderHrFinanceTable();
      loadHrLoanPolicyEditor().catch(() => {});
      hrDataState.finance = true;
    }

    // ---- Loan approval flow editor (HR → Finance) --------------------------
    let hrLoanPolicy = null;          // the policy being edited (null until loaded)
    let hrLoanPolicyWired = false;

    function _loanPolicyStageRow(stage) {
      const s = stage || {};
      const roles = (s.roles || []).join(', ');
      const users = (s.user_ids || []).join(', ');
      const div = document.createElement('div');
      div.className = 'card';
      div.style.cssText = 'padding:10px; display:grid; gap:6px;';
      div.dataset.stageRow = '1';
      div.innerHTML = `
        <div class="row" style="gap:8px; align-items:flex-end; flex-wrap:wrap;">
          <span style="cursor:grab; user-select:none; font-size:16px;" title="Order">⋮⋮</span>
          <label class="field" style="min-width:160px;"><span>Stage label</span><input type="text" data-stage-label value="${escapeHtml(s.label || '')}" placeholder="e.g. Finance sign-off" /></label>
          <label class="field" style="min-width:200px;"><span>Approver roles (comma-separated)</span><input type="text" data-stage-roles value="${escapeHtml(roles)}" placeholder="hr_admin, finance_director" /></label>
          <label class="field" style="min-width:160px;"><span>Approver user IDs (optional)</span><input type="text" data-stage-users value="${escapeHtml(users)}" placeholder="12, 34" /></label>
          <button class="btn ghost small" type="button" data-stage-up title="Move up">↑</button>
          <button class="btn ghost small" type="button" data-stage-down title="Move down">↓</button>
          <button class="btn ghost small" type="button" data-stage-remove title="Remove stage">✕</button>
        </div>`;
      return div;
    }

    function renderLoanPolicyStages() {
      const wrap = document.getElementById('hrLoanPolicyStages');
      if (!wrap) return;
      wrap.innerHTML = '';
      const stages = (hrLoanPolicy && hrLoanPolicy.approver_sequence) || [];
      if (!stages.length) {
        const hint = document.createElement('div');
        hint.className = 'muted';
        hint.style.fontSize = '12px';
        hint.textContent = 'No stages yet — add at least one. With none, requests fall back to a single HR stage.';
        wrap.appendChild(hint);
      }
      stages.forEach((s) => wrap.appendChild(_loanPolicyStageRow(s)));
    }

    function _collectLoanPolicyStages() {
      const wrap = document.getElementById('hrLoanPolicyStages');
      if (!wrap) return [];
      const splitList = (v) => (v || '').split(',').map((x) => x.trim()).filter(Boolean);
      return Array.from(wrap.querySelectorAll('[data-stage-row]')).map((row) => ({
        label: (row.querySelector('[data-stage-label]')?.value || '').trim(),
        roles: splitList(row.querySelector('[data-stage-roles]')?.value),
        user_ids: splitList(row.querySelector('[data-stage-users]')?.value)
          .map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n)),
      }));
    }

    async function loadHrLoanPolicyEditor() {
      _wireLoanPolicyEditor();
      let items = [];
      try {
        const data = await adminAPI.fetch(API.adminHrLoanPolicies, { method: 'GET' });
        items = Array.isArray(data?.items) ? data.items : [];
      } catch { /* editor stays empty */ }
      hrLoanPolicy = items.find((p) => p.is_active) || items[0] || {
        name: 'Standard staff loans', max_term_months: 6, max_deduction_pct: 40,
        approver_sequence: [], is_active: true,
      };
      const term = document.getElementById('hrLoanPolicyMaxTerm');
      const ded = document.getElementById('hrLoanPolicyMaxDeduction');
      if (term) term.value = hrLoanPolicy.max_term_months ?? '';
      if (ded) ded.value = hrLoanPolicy.max_deduction_pct ?? '';
      renderLoanPolicyStages();
    }

    function _wireLoanPolicyEditor() {
      if (hrLoanPolicyWired) return;
      hrLoanPolicyWired = true;
      const addBtn = document.getElementById('hrLoanPolicyAddStage');
      const saveBtn = document.getElementById('hrLoanPolicySave');
      const wrap = document.getElementById('hrLoanPolicyStages');
      if (addBtn) addBtn.addEventListener('click', () => {
        if (!hrLoanPolicy) hrLoanPolicy = { approver_sequence: [] };
        hrLoanPolicy.approver_sequence = _collectLoanPolicyStages();
        hrLoanPolicy.approver_sequence.push({ label: '', roles: [], user_ids: [] });
        renderLoanPolicyStages();
      });
      if (wrap) wrap.addEventListener('click', (e) => {
        const row = e.target.closest('[data-stage-row]');
        if (!row) return;
        const rows = Array.from(wrap.querySelectorAll('[data-stage-row]'));
        const idx = rows.indexOf(row);
        const stages = _collectLoanPolicyStages();
        if (e.target.closest('[data-stage-remove]')) {
          stages.splice(idx, 1);
        } else if (e.target.closest('[data-stage-up]') && idx > 0) {
          [stages[idx - 1], stages[idx]] = [stages[idx], stages[idx - 1]];
        } else if (e.target.closest('[data-stage-down]') && idx < stages.length - 1) {
          [stages[idx + 1], stages[idx]] = [stages[idx], stages[idx + 1]];
        } else {
          return;
        }
        hrLoanPolicy.approver_sequence = stages;
        renderLoanPolicyStages();
      });
      if (saveBtn) saveBtn.addEventListener('click', _saveLoanPolicy);
    }

    async function _saveLoanPolicy() {
      const msg = document.getElementById('hrLoanPolicyMsg');
      const setMsg = (t, ok) => { if (msg) { msg.textContent = t; msg.style.color = ok ? 'var(--ok, #15803d)' : 'var(--err)'; } };
      // Single-flow model: the policy is auto-managed, so the name is a fixed
      // internal label rather than something HR has to choose.
      const nameV = (hrLoanPolicy && hrLoanPolicy.name) || 'Standard staff loans';
      const termV = document.getElementById('hrLoanPolicyMaxTerm')?.value;
      const dedV = document.getElementById('hrLoanPolicyMaxDeduction')?.value;
      const payload = {
        name: nameV,
        max_term_months: termV === '' ? null : Number(termV),
        max_deduction_pct: dedV === '' ? null : Number(dedV),
        approver_sequence: _collectLoanPolicyStages(),
        is_active: true,
      };
      const saveBtn = document.getElementById('hrLoanPolicySave');
      if (saveBtn) saveBtn.disabled = true;
      try {
        const hasId = hrLoanPolicy && hrLoanPolicy.id;
        const res = hasId
          ? await adminAPI.fetch(API.adminHrLoanPolicy(hrLoanPolicy.id), { method: 'PATCH', body: JSON.stringify(payload) })
          : await adminAPI.fetch(API.adminHrLoanPolicies, { method: 'POST', body: JSON.stringify(payload) });
        hrLoanPolicy = res;
        renderLoanPolicyStages();
        setMsg('Approval flow saved.', true);
        showFlash('Loan approval flow saved.', 'success');
      } catch (err) {
        setMsg(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Save failed', false);
      } finally {
        if (saveBtn) saveBtn.disabled = false;
      }
    }

    const HR_COMPLIANCE_FACTOR_LABELS = {
      national_id: 'National ID', contract: 'Contract', tin_ssn: 'TIN / SSN',
      background_check: 'Background check', medical_cert: 'Medical cert',
      qualifications: 'Qualifications', certification: 'Certification',
      review: 'Recent review', training: 'Training freshness', recruitment: 'Recruitment',
      // Org data-quality gaps.
      missing_position: 'Missing position', missing_department: 'Missing department',
      missing_manager: 'Missing manager', manager_inactive: 'Inactive manager',
      position_department_mismatch: 'Position / department mismatch',
    };

    function _hrScore(v) { return (v === null || v === undefined) ? '—' : `${Math.round(Number(v))}%`; }
    function _hrScoreColor(v) {
      if (v === null || v === undefined) return '';
      const n = Number(v);
      return n >= 90 ? '#15803d' : (n >= 70 ? '#b45309' : '#b91c1c');
    }

    async function loadHrCompliance() {
      _hrWireComplianceUI();
      if (!hrDepartmentsList.length) { try { await loadHrDepartments(); } catch {} }
      if (hrComplianceTbody) hrComplianceTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      // Config windows.
      try {
        const cfg = await adminAPI.fetch(API.adminHrComplianceConfig, { method: 'GET' });
        const t = document.getElementById('hrComplianceTrainingMonths');
        const r = document.getElementById('hrComplianceReviewMonths');
        if (t) t.value = cfg.training_freshness_months ?? 6;
        if (r) r.value = cfg.review_recency_months ?? 12;
      } catch {}
      // Snapshots.
      const data = await adminAPI.fetch(API.adminHrCompliance, { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      const org = items.find((i) => !i.department_id) || items[0] || null;
      // Null score = nothing defined to measure → show "N/A" (only once computed).
      const naScore = (v) => (v === null || v === undefined) ? 'N/A' : _hrScore(v);
      const setTile = (el, v, naTitle) => {
        if (!el) return;
        el.textContent = org ? naScore(v) : '—';
        el.style.color = _hrScoreColor(v);
        el.title = (org && (v === null || v === undefined)) ? naTitle : '';
      };
      setTile(hrComplianceRecords, org?.completeness_score, 'No active employees to score');
      setTile(hrComplianceTraining, org?.training_score, 'No training requirements defined for any position');
      setTile(hrComplianceRecruitment, org?.recruitment_score, 'No onboarding records to score');
      const meta = document.getElementById('hrComplianceMeta');
      if (meta) meta.textContent = org ? `Latest: ${escapeHtml(org.period_month || '')}` : 'Not computed yet.';
      _hrRenderComplianceChips(org && org.meta);
      // Per-department table (skip the org-wide row).
      const deptRows = items.filter((i) => i.department_id).map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(getDepartmentNameById(item.department_id) || item.department_id || '')}</td>
          <td>${escapeHtml(item.period_month || '')}</td>
          <td>${_hrScore(item.completeness_score)}</td>
          <td>${_hrScore(item.training_score)}</td>
          <td>${_hrScore(item.recruitment_score)}</td>
        </tr>`).join('');
      setTableRows(hrComplianceTbody, deptRows, 'No compliance snapshots — click Recompute now.');
      _hrLoadComplianceWaivers();
      _hrLoadCompliancePositions();
      hrDataState.compliance = true;
    }

    function _hrRenderComplianceChips(meta) {
      const box = document.getElementById('hrComplianceFactorChips');
      if (!box) return;
      const counts = {};
      const records = (meta && meta.records) || {};
      Object.keys(records).forEach((k) => { counts[k] = (records[k] || []).length; });
      // Org data-quality gaps (missing position/manager/department, etc.).
      const dataGaps = (meta && meta.data_gaps) || {};
      Object.keys(dataGaps).forEach((k) => { counts[k] = (dataGaps[k] || []).length; });
      if (meta && meta.training && (meta.training.below_minimum || []).length) counts.training = meta.training.below_minimum.length;
      if (meta && meta.recruitment && (meta.recruitment.incomplete || []).length) counts.recruitment = meta.recruitment.incomplete.length;
      const factors = Object.keys(counts).filter((k) => counts[k] > 0);
      if (!factors.length) { box.innerHTML = '<span class="muted" style="font-size:12px;">No gaps 🎉</span>'; return; }
      box.innerHTML = factors.map((f) => `
        <button type="button" class="btn ghost" data-compliance-factor="${escapeHtml(f)}" style="font-size:12px;">
          ${escapeHtml(HR_COMPLIANCE_FACTOR_LABELS[f] || f)} <strong style="color:#b91c1c;">${counts[f]}</strong>
        </button>`).join('');
    }

    async function openHrComplianceGapDrill(factor) {
      const box = document.getElementById('hrComplianceGapDrill');
      if (!box) return;
      box.innerHTML = '<div class="muted" style="font-size:12px; padding:8px 0;">Loading…</div>';
      try {
        const data = await adminAPI.fetch(API.adminHrComplianceGaps(factor), { method: 'GET', cache: 'no-store' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const rows = items.map((g, i) => `
          <tr>
            <td>${i + 1}</td>
            <td><a href="#" data-compliance-emp="${escapeHtml(g.employee_id || '')}" title="Open profile to fix">${escapeHtml(g.name || g.employee_id || '')}</a></td>
            <td>${escapeHtml(g.detail || '')}</td>
          </tr>`).join('');
        box.innerHTML = `
          <div style="display:flex; align-items:center; gap:10px; margin:8px 0 6px; flex-wrap:wrap; border-top:1px solid var(--border,#eee); padding-top:10px;">
            <strong style="font-size:13px;">${escapeHtml(HR_COMPLIANCE_FACTOR_LABELS[factor] || factor)} — ${data.count}</strong>
            <span class="spacer" style="flex:1;"></span>
            <button type="button" class="btn ghost" id="hrComplianceGapCsv" style="font-size:11px; padding:2px 8px;">⬇ CSV</button>
            <button type="button" class="btn ghost" data-compliance-gap-close="1" style="font-size:11px; padding:2px 8px;">✕ Close</button>
          </div>
          <div class="table-wrap" style="max-height:320px; overflow:auto;">
            <table style="font-size:12px; width:100%;"><thead><tr><th style="width:44px;">#</th><th>Employee</th><th>Detail</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="3" class="muted">No one — all clear.</td></tr>'}</tbody></table>
          </div>`;
        const csv = document.getElementById('hrComplianceGapCsv');
        if (csv) csv.addEventListener('click', () => {
          const a = document.createElement('a');
          a.href = API.adminHrComplianceGaps(factor, 'csv');
          a.download = `compliance_gaps_${factor}.csv`;
          document.body.appendChild(a); a.click(); a.remove();
        });
        // Deep link: open the employee's profile so HR can fix the gap in one click.
        box.querySelectorAll('a[data-compliance-emp]').forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const empId = String(link.dataset.complianceEmp || '');
            if (!empId) return;
            try { setHrTab('people'); } catch {}
            setHrPeopleTab('employees');
            loadEmployeeDetail(empId).catch((err) => showFlash(err && err.message ? err.message : 'Failed to open employee', 'error'));
          });
        });
      } catch (err) {
        box.innerHTML = `<div class="error">${escapeHtml((err && err.message) || 'Failed to load gaps')}</div>`;
      }
    }

    async function _hrLoadComplianceWaivers() {
      const tbody = document.getElementById('hrComplianceWaiversTbody');
      if (!tbody) return;
      try {
        const data = await adminAPI.fetch(API.adminHrComplianceWaivers, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const rows = items.map((w, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${escapeHtml(w.employee_name || w.employee_id || '')}</td>
            <td>${escapeHtml(HR_COMPLIANCE_FACTOR_LABELS[w.category] || w.category || '')}</td>
            <td>${escapeHtml(w.justification || '')}</td>
          </tr>`).join('');
        setTableRows(tbody, rows, 'No active waivers.');
      } catch {
        setTableRows(tbody, '', 'Failed to load waivers.');
      }
    }

    async function _hrLoadCompliancePositions() {
      const sel = document.getElementById('hrCompliancePositionSelect');
      if (!sel || sel.dataset.loaded === '1') return;
      try {
        const data = await adminAPI.fetch(API.adminHrPositions, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        sel.innerHTML = '<option value="">Select a position…</option>'
          + items.map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name || p.id)}</option>`).join('');
        sel.dataset.loaded = '1';
      } catch {}
    }

    async function _hrLoadPositionReqs(positionId) {
      const box = document.getElementById('hrCompliancePositionReqs');
      if (!box) return;
      if (!positionId) { box.innerHTML = ''; return; }
      box.innerHTML = '<span class="muted" style="font-size:12px;">Loading…</span>';
      try {
        const data = await adminAPI.fetch(API.adminHrPositionDocRequirements(positionId), { method: 'GET' });
        const available = data.available || [];
        const active = new Set(data.categories || []);
        box.innerHTML = available.map((cat) => `
          <label style="font-size:12px; display:inline-flex; align-items:center; gap:4px;">
            <input type="checkbox" data-position-req="${escapeHtml(cat)}" ${active.has(cat) ? 'checked' : ''} />
            ${escapeHtml(HR_COMPLIANCE_FACTOR_LABELS[cat] || cat)}
          </label>`).join('') || '<span class="muted" style="font-size:12px;">No conditional documents.</span>';
      } catch (err) {
        box.innerHTML = `<span class="error">${escapeHtml((err && err.message) || 'Failed to load')}</span>`;
      }
    }

    async function _hrSavePositionReqs(positionId) {
      const box = document.getElementById('hrCompliancePositionReqs');
      if (!box || !positionId) return;
      const categories = Array.from(box.querySelectorAll('[data-position-req]'))
        .filter((cb) => cb.checked).map((cb) => cb.getAttribute('data-position-req'));
      try {
        await adminAPI.fetch(API.adminHrPositionDocRequirements(positionId), { method: 'PUT', body: JSON.stringify({ categories }) });
        showFlash('Requirements saved');
      } catch (err) {
        showFlash((err && err.message) || 'Save failed', 'error');
        _hrLoadPositionReqs(positionId);
      }
    }

    function _hrWireComplianceUI() {
      const cPanel = document.getElementById('hrCompliancePanel');
      if (cPanel && !cPanel.dataset.taskWired) {
        cPanel.dataset.taskWired = '1';
        cPanel.addEventListener('click', async (e) => {
          const btn = e.target.closest('[data-hr-task]');
          if (!btn) return;
          const task = btn.getAttribute('data-hr-task');
          if (btn.getAttribute('data-hr-task-email') === '1' && !window.confirm('This sends emails to staff. Continue?')) return;
          const orig = btn.textContent;
          btn.disabled = true; btn.textContent = 'Running…';
          try {
            const res = await adminAPI.fetch(API.adminHrTaskRun(task), { method: 'POST' });
            const r = (res && res.result) || {};
            const detail = Object.entries(r).map(([k, v]) => `${k}=${v}`).join(' ');
            showFlash(`${(res && res.label) || task} ✓ ${detail}`.trim());
            if (task === 'compliance_snapshot') { hrDataState.compliance = false; loadHrCompliance(); }
          } catch (err) {
            showFlash((err && err.message) || 'Task failed', 'error');
          } finally {
            btn.disabled = false; btn.textContent = orig;
          }
        });
      }
      const recompute = document.getElementById('hrComplianceRecomputeBtn');
      if (recompute && !recompute.dataset.wired) {
        recompute.dataset.wired = '1';
        recompute.addEventListener('click', async () => {
          recompute.disabled = true; recompute.textContent = 'Recomputing…';
          try {
            const res = await adminAPI.fetch(API.adminHrComplianceRecompute, { method: 'POST' });
            showFlash(`Computed ${res.count} snapshot(s)`);
            hrDataState.compliance = false;
            await loadHrCompliance();
          } catch (err) {
            showFlash((err && err.message) || 'Recompute failed', 'error');
          } finally {
            recompute.disabled = false; recompute.textContent = '↻ Recompute now';
          }
        });
      }
      const saveCfg = document.getElementById('hrComplianceConfigSaveBtn');
      if (saveCfg && !saveCfg.dataset.wired) {
        saveCfg.dataset.wired = '1';
        saveCfg.addEventListener('click', async () => {
          const t = document.getElementById('hrComplianceTrainingMonths');
          const r = document.getElementById('hrComplianceReviewMonths');
          const payload = {};
          if (t && t.value) payload.training_freshness_months = Number(t.value);
          if (r && r.value) payload.review_recency_months = Number(r.value);
          try {
            await adminAPI.fetch(API.adminHrComplianceConfig, { method: 'PUT', body: JSON.stringify(payload) });
            showFlash('Policy saved — recompute to apply');
          } catch (err) {
            showFlash((err && err.message) || 'Save failed', 'error');
          }
        });
      }
      const chips = document.getElementById('hrComplianceFactorChips');
      if (chips && !chips.dataset.wired) {
        chips.dataset.wired = '1';
        chips.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-compliance-factor]');
          if (btn) openHrComplianceGapDrill(btn.getAttribute('data-compliance-factor'));
        });
      }
      const drill = document.getElementById('hrComplianceGapDrill');
      if (drill && !drill.dataset.wired) {
        drill.dataset.wired = '1';
        drill.addEventListener('click', (e) => {
          if (e.target.closest('[data-compliance-gap-close]')) drill.innerHTML = '';
        });
      }
      const posSel = document.getElementById('hrCompliancePositionSelect');
      if (posSel && !posSel.dataset.wired) {
        posSel.dataset.wired = '1';
        posSel.addEventListener('change', () => _hrLoadPositionReqs(posSel.value));
      }
      const posReqs = document.getElementById('hrCompliancePositionReqs');
      if (posReqs && !posReqs.dataset.wired) {
        posReqs.dataset.wired = '1';
        posReqs.addEventListener('change', (e) => {
          if (e.target.matches('[data-position-req]')) {
            const sel = document.getElementById('hrCompliancePositionSelect');
            _hrSavePositionReqs(sel && sel.value);
          }
        });
      }
    }

    async function loadHrEss() {
      if (hrPoliciesTbody) hrPoliciesTbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrAnnouncementsTbody) hrAnnouncementsTbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
      if (hrProfileChangesTbody) hrProfileChangesTbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
      const [policiesData, announcementsData, changesData] = await Promise.all([
        adminAPI.fetch(API.adminHrPolicies, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrAnnouncements, { method: 'GET' }).catch(() => ({ items: [] })),
        adminAPI.fetch(API.adminHrProfileChanges, { method: 'GET' }).catch(() => ({ items: [] })),
      ]);
      _hrPolicies = Array.isArray(policiesData?.items) ? policiesData.items : [];
      const announcements = Array.isArray(announcementsData?.items) ? announcementsData.items : [];
      const changes = Array.isArray(changesData?.items) ? changesData.items : [];
      _wireHrPolicyUi();
      _renderPolicyRows();
      const announcementRows = announcements.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.title || '')}</td>
          <td>${escapeHtml([_deptScopeLabel(item.department_scope), item.location_scope].filter(Boolean).join(' / '))}</td>
          <td>${escapeHtml(item.expires_at || '')}</td>
        </tr>
      `).join('');
      const changeRows = changes.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.employee_id || '')}</td>
          <td>${escapeHtml(item.field || '')}</td>
          <td>${escapeHtml(item.old_value || '')} → ${escapeHtml(item.new_value || '')}</td>
          <td>${escapeHtml(item.status || '')}</td>
        </tr>
      `).join('');
      setTableRows(hrAnnouncementsTbody, announcementRows, 'No announcements.');
      setTableRows(hrProfileChangesTbody, changeRows, 'No profile change requests.');
      loadHolidays();
      loadGeneralQuizzes().catch(() => {});
      loadQuizCompletion().catch(() => {});
      loadQuizCoverage().catch(() => {});
      hrDataState.ess = true;
    }

    let _quizCoverageWired = false;

    async function loadQuizCoverage() {
      const msg = document.getElementById('hrQuizCoverageMsg');
      const btn = document.getElementById('hrGenerateMissingQuizzesBtn');
      if (!msg || !btn) return;
      if (!_quizCoverageWired) {
        _quizCoverageWired = true;
        btn.addEventListener('click', () => generateMissingQuizzes());
      }
      try {
        const data = await adminAPI.fetch(API.adminHrQuizCoverage, { method: 'GET', cache: 'no-store' });
        const s = data.summary || {};
        const missing = s.missing || 0;
        if (missing > 0) {
          const noBody = (data.missing || []).filter((p) => !p.has_body).length;
          msg.innerHTML = `⚠ <strong style="color:#b91c1c;">${missing}</strong> of ${s.active || 0} active `
            + `${(s.active === 1) ? 'policy has' : 'policies have'} no quiz`
            + (noBody ? ` <span class="muted">(${noBody} without body text — add text before generating)</span>` : '');
          btn.style.display = '';
          btn.disabled = false;
        } else {
          msg.innerHTML = `✅ All ${s.active || 0} active ${(s.active === 1) ? 'policy has' : 'policies have'} a quiz `
            + `<span class="muted">(${s.covered || 0}/${s.policies || 0} policies covered)</span>`;
          btn.style.display = 'none';
        }
      } catch (err) {
        msg.textContent = `Coverage unavailable: ${_errDetail(err) || 'failed to load'}`;
      }
    }

    async function generateMissingQuizzes() {
      const btn = document.getElementById('hrGenerateMissingQuizzesBtn');
      const msg = document.getElementById('hrQuizCoverageMsg');
      if (!btn || btn.disabled) return;
      if (!window.confirm('AI-draft a quiz for every active policy that has none? '
        + 'Each draft is saved inactive for you to review and Launch — staff are not emailed yet.')) return;
      btn.disabled = true;
      let totalGenerated = 0;
      const allFailed = [];
      try {
        // Batched so a large estate never blocks on one giant request; repeat
        // while the server still reports uncovered policies AND we made progress.
        for (let i = 0; i < 50; i += 1) {
          if (msg) msg.textContent = `Generating quizzes… ${totalGenerated} drafted so far`;
          const res = await adminAPI.fetch(API.adminHrQuizBackfill, {
            // No count → server picks an adaptive 5–10 per policy from its body size.
            method: 'POST', body: JSON.stringify({ limit: 10 }),
          });
          totalGenerated += (res.generated || []).length;
          (res.failed || []).forEach((f) => allFailed.push(f));
          // Stop when nothing left, or when a batch made no progress (remaining
          // are all failures — e.g. policies with no body / AI down).
          if (!res.remaining || !(res.generated || []).length) break;
        }
        const failNote = allFailed.length ? ` · ${allFailed.length} could not be generated (missing body text or AI unavailable)` : '';
        showFlash(`Drafted ${totalGenerated} quiz${totalGenerated === 1 ? '' : 'zes'}${failNote}. Review and Launch each from its policy row.`,
          allFailed.length ? 'warn' : 'success');
      } catch (err) {
        showFlash(_errDetail(err) || 'Quiz generation failed', 'error');
      } finally {
        btn.disabled = false;
        _refreshPolicies();          // re-render rows (clears ⚠ flags) + coverage
      }
    }

    function _completionBar(pct) {
      if (pct == null) return '<span class="muted">—</span>';
      const color = pct >= 90 ? '#22c55e' : (pct >= 50 ? '#f59e0b' : '#ef4444');
      return `<div style="background:rgba(120,120,120,0.2); border-radius:3px; height:14px; position:relative; min-width:80px;">
        <div style="background:${color}; width:${Math.max(0, Math.min(100, pct))}%; height:100%; border-radius:3px;"></div>
        <span style="position:absolute; inset:0; font-size:10px; text-align:center; line-height:14px;">${pct}%</span>
      </div>`;
    }

    async function loadQuizCompletion() {
      const tbody = document.getElementById('hrQuizCompletionTbody');
      const summary = document.getElementById('hrQuizCompletionSummary');
      const quarterInput = document.getElementById('hrQuizQuarterInput');
      if (!tbody) return;
      tbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
      try {
        const q = (quarterInput && quarterInput.value.trim()) || '';
        const url = q ? `${API.adminHrQuizCompletion}?quarter=${encodeURIComponent(q)}` : API.adminHrQuizCompletion;
        const data = await adminAPI.fetch(url, { method: 'GET', cache: 'no-store' });
        if (quarterInput && !quarterInput.value) quarterInput.value = data.quarter || '';
        const rows = (data.quizzes || []).map((qz) => `
          <tr>
            <td>${escapeHtml(qz.title || '')}</td>
            <td style="text-align:right;">${qz.applicable}</td>
            <td style="text-align:right;">${qz.completed}</td>
            <td style="text-align:right;">${qz.passed}</td>
            <td style="text-align:right; ${qz.overdue ? 'color:#ef4444; font-weight:600;' : ''}">${qz.overdue}</td>
            <td>${_completionBar(qz.completion_pct)}</td>
          </tr>`).join('');
        setTableRows(tbody, rows, 'No active quizzes.');
        if (summary) {
          const t = data.totals || {};
          summary.textContent = `${escapeHtml(data.quarter || '')} · ${t.quizzes || 0} quiz(zes) · ${t.completed || 0}/${t.applicable || 0} completed`
            + (t.completion_pct != null ? ` (${t.completion_pct}%)` : '')
            + ` · ${t.overdue || 0} overdue`;
        }
      } catch (err) {
        tbody.innerHTML = `<tr><td colspan="6" class="error">${escapeHtml(_errDetail(err) || 'Failed to load')}</td></tr>`;
      }
      // Keep the per-staff chart in step when its panel is open.
      const staffCard = document.getElementById('hrQuizStaffCard');
      if (staffCard && staffCard.open) loadQuizStaffScores().catch(() => {});
    }

    function _quizQuarterValue() {
      const el = document.getElementById('hrQuizQuarterInput');
      return (el && el.value.trim()) || '';
    }

    function _staffScoreRow(r) {
      const label = r.department ? `${r.name} · ${r.department}` : r.name;
      const name = `<div style="min-width:170px; max-width:170px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${escapeHtml(label)}">${escapeHtml(r.name)}</div>`;
      if (r.status === 'overdue') {
        return `<div class="row" style="align-items:center; gap:8px; font-size:12px;">${name}
          <div style="flex:1; background:rgba(120,120,120,0.15); border-radius:4px; height:13px;"></div>
          <div style="min-width:60px; text-align:right; color:var(--sub);">Overdue</div></div>`;
      }
      const score = r.score == null ? 0 : Math.max(0, Math.min(100, r.score));
      const color = r.passed ? '#22c55e' : '#ef4444';
      return `<div class="row" style="align-items:center; gap:8px; font-size:12px;">${name}
        <div style="flex:1; background:rgba(120,120,120,0.15); border-radius:4px; height:13px; position:relative;">
          <div style="width:${score}%; background:${color}; height:100%; border-radius:4px;"></div>
        </div>
        <div style="min-width:60px; text-align:right;">${r.score == null ? '—' : `${r.score}%`} ${r.passed ? '✓' : '✗'}</div></div>`;
    }

    async function loadQuizStaffScores() {
      const box = document.getElementById('hrQuizStaffChart');
      const summary = document.getElementById('hrQuizStaffSummary');
      if (!box) return;
      box.innerHTML = '<div style="color:var(--sub); font-size:12px;">Loading…</div>';
      try {
        const data = await adminAPI.fetch(API.adminHrQuizStaffScores(_quizQuarterValue()), { method: 'GET', cache: 'no-store' });
        const rows = data.results || [];
        if (!rows.length) {
          box.innerHTML = '<div style="color:var(--sub); font-size:12px;">No applicable staff this quarter.</div>';
          if (summary) summary.textContent = '';
          return;
        }
        box.innerHTML = rows.map(_staffScoreRow).join('');
        if (summary) {
          const done = rows.filter((r) => r.completed).length;
          const passed = rows.filter((r) => r.passed).length;
          summary.textContent = `— ${done}/${rows.length} completed · ${passed} passed`;
        }
      } catch (err) {
        box.innerHTML = `<div class="error" style="font-size:12px;">${escapeHtml(_errDetail(err) || 'Failed to load')}</div>`;
      }
    }

    function _downloadQuizReport(format) {
      const url = API.adminHrQuizStaffReport(format, _quizQuarterValue());
      if (format === 'html') { window.open(url, '_blank', 'noopener'); return; }
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    // ---- Policy authoring + graded quiz builder --------------------------
    let _hrPolicyEditId = null;
    let _hrQuizPolicyId = null;
    let _hrQuizEditId = null;
    let _hrPolicyUiWired = false;
    let _hrPolicies = [];
    let _hrPolicySearch = '';
    let _hrPolicySearchTimer = null;
    const _errDetail = (err) => (adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : (err && err.message)) || '';

    // Render the policies table from `_hrPolicies`, filtered by the title search.
    // The table body scrolls within a capped window (see `.hr-scroll-list`) so a
    // long estate never takes over the screen. Row actions are event-delegated on
    // the tbody, so re-rendering here is safe.
    function _renderPolicyRows() {
      if (!hrPoliciesTbody) return;
      const _truthy = (v) => v === true || String(v).toLowerCase() === 'true';
      const term = _hrPolicySearch.trim().toLowerCase();
      const filtered = term
        ? _hrPolicies.filter((p) => String(p.title || '').toLowerCase().includes(term))
        : _hrPolicies;
      const rows = filtered.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.title || '')}</td>
          <td>${escapeHtml([_deptScopeLabel(item.department_scope), item.location_scope].filter(Boolean).join(' / '))}</td>
          <td>${_truthy(item.is_active) ? '✓' : '—'}</td>
          <td><button type="button" class="btn ${Number(item.quiz_count) ? 'ghost' : ''}" data-policy-quizzes="${escapeHtml(item.id)}" data-policy-title="${escapeHtml(item.title || '')}" title="Open quizzes for this policy" style="font-size:11px; padding:2px 8px;${Number(item.quiz_count) ? '' : ' background:#fee2e2; color:#991b1b;'}">${Number(item.quiz_count) ? `${escapeHtml(String(item.quiz_count))} quiz${Number(item.quiz_count) === 1 ? '' : 'zes'}` : '⚠ no quiz'}</button></td>
          <td>
            <button type="button" class="btn" data-policy-edit="${escapeHtml(item.id)}" style="font-size:11px; padding:2px 8px;">✎ Edit</button>
            <button type="button" class="btn" data-policy-quizzes="${escapeHtml(item.id)}" data-policy-title="${escapeHtml(item.title || '')}" style="font-size:11px; padding:2px 8px;">📝 Quizzes</button>
            <button type="button" class="btn ghost" data-policy-del="${escapeHtml(item.id)}" style="font-size:11px; padding:2px 8px;">🗑</button>
          </td>
        </tr>
      `).join('');
      const empty = term ? `No policies match “${escapeHtml(_hrPolicySearch)}”.` : 'No policies.';
      setTableRows(hrPoliciesTbody, rows, empty);
      const countEl = document.getElementById('hrPolicyListCount');
      if (countEl) {
        countEl.textContent = term
          ? `${filtered.length} of ${_hrPolicies.length} policies`
          : `${_hrPolicies.length} polic${_hrPolicies.length === 1 ? 'y' : 'ies'}`;
      }
    }

    // ---- General quiz (Phase 6: what employees actually take) -------------
    let _genQuizEditId = null;
    let _genQuizItems = [];
    let _genQuizQuestionsId = null;
    let _genQuizBank = [];      // bank candidates for the open picker
    let _genQuizTarget = null;  // questions-per-attempt target for the open picker

    async function loadGeneralQuizzes() {
      const tbody = document.getElementById('hrGenQuizTbody');
      if (!tbody) return;
      try {
        const data = await adminAPI.fetch(API.adminHrGeneralQuizzes, { method: 'GET', cache: 'no-store' });
        const items = data.items || [];
        _genQuizItems = items;
        const rows = items.map((q) => {
          const scope = [q.department_scope ? 'dept-scoped' : '', q.location_scope || ''].filter(Boolean).join(' / ') || 'All staff';
          const nOwn = Number(q.question_count) || 0;
          const target = q.questions_per_attempt == null ? null : Number(q.questions_per_attempt);
          const short = target != null && nOwn < target;   // fewer selected than a full attempt
          const qLabel = nOwn ? `📝 Bank Qs (${nOwn}${target != null ? `/${target}` : ''})` : '📝 Add from bank';
          const qTitle = short
            ? `Only ${nOwn} of ${target} questions selected — add more from the bank so every attempt is full.`
            : (nOwn ? 'Edit which bank questions are on this quiz' : 'Pick existing bank questions to put on this quiz');
          const qPrimary = (nOwn === 0 || short) ? ' primary' : '';
          const launchBtn = q.is_active
            ? `<span class="muted" title="Already launched — deactivate (Edit → uncheck Active) to launch again" style="font-size:11px; padding:2px 8px;">✅ Launched</span>`
            : `<button type="button" class="btn" data-genquiz-launch="${escapeHtml(q.id)}" data-genquiz-title="${escapeHtml(q.title || '')}" title="Activate & email in-scope staff" style="font-size:11px; padding:2px 8px;">🚀 Launch</button>`;
          const deadlineCell = q.deadline
            ? `${escapeHtml(q.deadline)}${q.deadline < _todayISO() ? ' <span title="Deadline passed" style="color:#ef4444;">⚠</span>' : ''}`
            : '<span class="muted">quarter end</span>';
          // Overdue-notice: only for launched quizzes, and gated until the explicit
          // deadline has passed (a violation notice before the due date is premature).
          // With no explicit deadline the fallback is quarter-end — the server gates
          // that case and the click handler surfaces the 409.
          let notifyBtn = '';
          if (q.is_active) {
            const gated = q.deadline && q.deadline >= _todayISO();
            notifyBtn = gated
              ? `<button type="button" class="btn" disabled title="Overdue notice can only be sent after the deadline (${escapeHtml(q.deadline)})" style="font-size:11px; padding:2px 8px; opacity:0.5;">⚠ Notify overdue</button>`
              : `<button type="button" class="btn" data-genquiz-notify="${escapeHtml(q.id)}" data-genquiz-title="${escapeHtml(q.title || '')}" title="Email a formal overdue notice to everyone who hasn’t completed it (states non-completion is a violation of terms of employment)" style="font-size:11px; padding:2px 8px;">⚠ Notify overdue</button>`;
          }
          return `<tr>
            <td>${escapeHtml(q.title || '')}</td>
            <td style="text-align:right;">${q.questions_per_attempt == null ? 'all' : escapeHtml(String(q.questions_per_attempt))}</td>
            <td style="text-align:right;">${escapeHtml(String(q.time_limit_minutes))}m</td>
            <td style="text-align:right;">${escapeHtml(String(q.pass_mark))}%</td>
            <td>${escapeHtml(scope)}</td>
            <td style="font-size:12px;">${deadlineCell}</td>
            <td>${q.is_active ? '✓' : '—'}</td>
            <td>
              <button type="button" class="btn${qPrimary}" data-genquiz-questions="${escapeHtml(q.id)}" title="${escapeHtml(qTitle)}" style="font-size:11px; padding:2px 8px;">${qLabel}</button>
              ${launchBtn}
              ${notifyBtn}
              <button type="button" class="btn" data-genquiz-edit="${escapeHtml(q.id)}" style="font-size:11px; padding:2px 8px;">✎ Edit</button>
              <button type="button" class="btn ghost" data-genquiz-del="${escapeHtml(q.id)}" style="font-size:11px; padding:2px 8px;">🗑</button>
            </td>
          </tr>`;
        }).join('');
        setTableRows(tbody, rows, 'No general quizzes yet.');
      } catch (err) {
        tbody.innerHTML = `<tr><td colspan="8" class="error">${escapeHtml(_errDetail(err) || 'Failed to load')}</td></tr>`;
      }
    }

    function openGenQuizDialog(quiz) {
      _genQuizEditId = quiz ? quiz.id : null;
      const val = (id, v) => { const el = document.getElementById(id); if (el) el.value = v; };
      document.getElementById('hrGenQuizDlgTitle').textContent = quiz ? 'Edit general quiz' : 'Add general quiz';
      val('hrGenQuizTitle', quiz ? (quiz.title || '') : '');
      val('hrGenQuizN', quiz && quiz.questions_per_attempt != null ? quiz.questions_per_attempt : '');
      val('hrGenQuizTime', quiz ? (quiz.time_limit_minutes ?? 30) : 30);
      val('hrGenQuizPass', quiz ? (quiz.pass_mark ?? 70) : 70);
      val('hrGenQuizDept', quiz ? (quiz.department_scope || '') : '');
      val('hrGenQuizLoc', quiz ? (quiz.location_scope || '') : '');
      val('hrGenQuizDeadline', quiz ? (quiz.deadline || '') : '');
      const active = document.getElementById('hrGenQuizActive');
      if (active) active.checked = quiz ? !!quiz.is_active : true;
      openDialog(document.getElementById('hrGenQuizDlg'));
    }

    // Bank-question picker: HR selects EXISTING questions from the authored policy
    // bank to put on the general quiz (checked = on the quiz). No authoring here.
    async function openGenQuizQuestions(quiz) {
      _genQuizQuestionsId = quiz.id;
      _genQuizBank = [];
      _genQuizTarget = quiz.questions_per_attempt ?? null;
      const titleEl = document.getElementById('hrGenQuizQTitle');
      if (titleEl) titleEl.textContent = `Bank questions — ${quiz.title || 'quiz'}`;
      const box = document.getElementById('hrGenQuizQuestions');
      if (box) box.innerHTML = '<div class="muted" style="font-size:12px;">Loading bank…</div>';
      openDialog(document.getElementById('hrGenQuizQuestionsDlg'));
      try {
        const data = await adminAPI.fetch(API.adminHrGeneralQuizBank(quiz.id), { method: 'GET', cache: 'no-store' });
        _genQuizBank = data.candidates || [];
        _genQuizTarget = data.target ?? null;   // questions-per-attempt (null = all)
        _renderGenQuizBank();
      } catch (err) {
        if (box) box.innerHTML = `<div class="error" style="font-size:12px;">${escapeHtml(_errDetail(err) || 'Failed to load bank')}</div>`;
      }
    }

    function _genQuizCheckedIdx() {
      return Array.from(document.querySelectorAll('#hrGenQuizQuestions [data-bankq]:checked'))
        .map((cb) => Number(cb.getAttribute('data-bankq')));
    }

    // Recommended bank size for real per-staff variety: 50% more than the number
    // each employee is served per attempt.
    function _genQuizRecommended() {
      return _genQuizTarget ? Math.ceil(_genQuizTarget * 1.5) : null;
    }

    function _updateGenQuizQSummary() {
      const el = document.getElementById('hrGenQuizQSummary');
      if (!el) return;
      const n = _genQuizCheckedIdx().length;
      const perAttempt = _genQuizTarget;          // questions each staff member sees
      const bank = _genQuizBank.length;
      let html;
      if (!perAttempt) {
        // No per-attempt cap → every employee is served the whole selection (no variety).
        html = `<span class="muted">Selected <strong>${n}</strong> · bank has ${bank}. This quiz serves <strong>all ${n}</strong> selected question(s) to everyone — same set for all staff. To vary questions per staff, set a “questions per attempt” (via ✎ Edit) smaller than your selection.</span>`;
      } else {
        const rec = _genQuizRecommended();
        const lead = `Each staff member is served a random <strong>${perAttempt}</strong> of the <strong>${n}</strong> you select.`;
        if (n <= perAttempt) {
          html = `<span style="color:var(--err,#c62828);">${lead} That means everyone would see the same ${Math.min(perAttempt, n)} — select <strong>more than ${perAttempt}</strong> (aim for ${rec}+) so staff get different questions.</span>`;
        } else if (n < rec) {
          html = `<span style="color:var(--warn,#b8860b);">${lead} Staff get varied draws — for stronger variety aim for <strong>${rec}+</strong> (50% over per-attempt).</span>`;
        } else {
          html = `<span class="muted">${lead} ✓ Good variety — each attempt is a different random draw.</span>`;
        }
      }
      el.innerHTML = html;
      // Offer auto-select while a per-attempt is set, we're below the recommended
      // bank size, and there are still unselected questions to add.
      const af = document.getElementById('hrGenQuizQAutofillBtn');
      const rec = _genQuizRecommended();
      if (af) af.style.display = (rec && n < rec && bank > n) ? '' : 'none';
    }

    function _renderGenQuizBank() {
      const box = document.getElementById('hrGenQuizQuestions');
      if (!box) return;
      if (!_genQuizBank.length) {
        box.innerHTML = '<div class="muted" style="font-size:12px;">The question bank is empty. Add questions to a policy quiz first (Policies & Quizzes → 📝 Quizzes), then pick them here.</div>';
        _updateGenQuizQSummary();
        return;
      }
      box.innerHTML = _genQuizBank.map((q, i) => {
        const opts = (q.options || []).map((o, oi) =>
          `<li${oi === q.correct_index ? ' style="font-weight:600;"' : ''}>${escapeHtml(o)}${oi === q.correct_index ? ' ✓' : ''}</li>`).join('');
        return `<label class="hr-bankq" style="display:block; border:1px solid var(--border,#e5e7eb); border-radius:8px; padding:8px; cursor:pointer;">
          <div style="display:flex; gap:8px; align-items:flex-start;">
            <input type="checkbox" data-bankq="${i}" ${q.already ? 'checked' : ''} style="margin-top:3px;" />
            <div style="flex:1;">
              <div style="font-size:13px; font-weight:600;">${escapeHtml(q.text)}</div>
              <div class="muted" style="font-size:11px;">from ${escapeHtml(q.policy_title || 'policy')}</div>
              <ul style="margin:4px 0 0; padding-left:18px; font-size:12px;">${opts}</ul>
            </div>
          </div>
        </label>`;
      }).join('');
      box.querySelectorAll('[data-bankq]').forEach((cb) => cb.addEventListener('change', _updateGenQuizQSummary));
      _updateGenQuizQSummary();
    }

    function _autoFillGenQuizToTarget() {
      // Aim for 50% more than the per-attempt count so each staff member gets a
      // genuinely different random draw (capped at what the bank actually holds).
      const goal = _genQuizRecommended();
      if (!goal) return;
      const boxes = Array.from(document.querySelectorAll('#hrGenQuizQuestions [data-bankq]'));
      let n = boxes.filter((b) => b.checked).length;
      for (const b of boxes) {
        if (n >= goal) break;
        if (!b.checked) { b.checked = true; n += 1; }
      }
      _updateGenQuizQSummary();
    }

    async function saveGenQuizQuestions() {
      if (!_genQuizQuestionsId) return;
      const questions = _genQuizCheckedIdx().map((i) => {
        const q = _genQuizBank[i];
        return { text: q.text, options: q.options, correct_index: q.correct_index };
      });
      // Empty is allowed — it clears the selection and reverts the quiz to the pooled
      // policy draw. Send [] explicitly (not null) so the server stores it.
      try {
        await adminAPI.fetch(API.adminHrGeneralQuiz(_genQuizQuestionsId), { method: 'PATCH', body: JSON.stringify({ questions }) });
        closeDialog(document.getElementById('hrGenQuizQuestionsDlg'));
        showFlash(questions.length
          ? `Quiz now has ${questions.length} question(s) from the bank.`
          : 'Selection cleared — quiz reverts to the pooled policy draw.', 'success');
        loadGeneralQuizzes().catch(() => {});
      } catch (err) {
        showFlash(_errDetail(err) || 'Save failed', 'error');
      }
    }

    async function saveGenQuiz() {
      const num = (id) => { const v = (document.getElementById(id)?.value || '').trim(); return v === '' ? null : Number(v); };
      const title = (document.getElementById('hrGenQuizTitle')?.value || '').trim();
      if (!title) { showFlash('Title is required.', 'error'); return; }
      const payload = {
        title,
        questions_per_attempt: num('hrGenQuizN'),
        time_limit_minutes: num('hrGenQuizTime') ?? 30,
        pass_mark: num('hrGenQuizPass') ?? 70,
        department_scope: (document.getElementById('hrGenQuizDept')?.value || '').trim() || null,
        location_scope: (document.getElementById('hrGenQuizLoc')?.value || '').trim() || null,
        deadline: (document.getElementById('hrGenQuizDeadline')?.value || '').trim() || null,
        is_active: !!document.getElementById('hrGenQuizActive')?.checked,
      };
      try {
        if (_genQuizEditId) await adminAPI.fetch(API.adminHrGeneralQuiz(_genQuizEditId), { method: 'PATCH', body: JSON.stringify(payload) });
        else await adminAPI.fetch(API.adminHrGeneralQuizzes, { method: 'POST', body: JSON.stringify(payload) });
        closeDialog(document.getElementById('hrGenQuizDlg'));
        showFlash('General quiz saved.', 'success');
        loadGeneralQuizzes().catch(() => {});
      } catch (err) {
        showFlash(_errDetail(err) || 'Save failed', 'error');
      }
    }

    async function editGenQuiz(id) {
      try {
        const data = await adminAPI.fetch(API.adminHrGeneralQuizzes, { method: 'GET', cache: 'no-store' });
        const quiz = (data.items || []).find((q) => q.id === id);
        if (quiz) openGenQuizDialog(quiz);
      } catch (err) { showFlash(_errDetail(err) || 'Failed to load quiz', 'error'); }
    }

    async function deleteGenQuiz(id) {
      if (!window.confirm('Delete this general quiz? Employees will no longer be assigned it.')) return;
      try {
        await adminAPI.fetch(API.adminHrGeneralQuiz(id), { method: 'DELETE' });
        showFlash('General quiz deleted.', 'success');
        loadGeneralQuizzes().catch(() => {});
      } catch (err) { showFlash(_errDetail(err) || 'Delete failed', 'error'); }
    }

    async function launchGenQuiz(id, title) {
      let audience = null;
      try { audience = await adminAPI.fetch(API.adminHrGeneralQuizAudience(id), { method: 'GET', cache: 'no-store' }); }
      catch { /* generic prompt */ }
      const who = audience ? `${audience.applicable} in-scope employee(s) (${audience.with_email} with email)` : 'all in-scope employees';
      if (!window.confirm(`Launch "${title || 'this quiz'}"?\n\nThis activates it and emails ${who} that it's due this quarter.`)) return;
      try {
        const res = await adminAPI.fetch(API.adminHrGeneralQuizLaunch(id), { method: 'POST' });
        showFlash(`Launched to ${res.applicable} employee(s); emailing ${res.with_email} with an address.`
          + (res.activated ? ' Quiz activated.' : ''), 'success');
        loadGeneralQuizzes().catch(() => {});
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (err && err.status === 409 && d === 'already_launched') {
          showFlash('This quiz is already launched. Deactivate it (Edit → uncheck Active) to launch again.', 'error');
          loadGeneralQuizzes().catch(() => {});
        } else { showFlash(_errDetail(err) || 'Launch failed', 'error'); }
      }
    }

    function _todayISO() { return new Date().toISOString().slice(0, 10); }

    async function notifyOverdueGenQuiz(id, title) {
      if (!window.confirm(
        `Send a FORMAL OVERDUE NOTICE for "${title || 'this quiz'}"?\n\n`
        + 'Everyone in scope who has NOT completed it this quarter will be emailed that '
        + 'failing to finish by the deadline is a violation of the terms of their employment.'
      )) return;
      try {
        const res = await adminAPI.fetch(API.adminHrGeneralQuizNotifyOverdue(id), { method: 'POST' });
        if (res.overdue === 0) {
          showFlash('Nobody is overdue — everyone in scope has completed it. 🎉', 'success');
        } else {
          showFlash(`Overdue notice sent: ${res.notified} of ${res.overdue} overdue staff emailed`
            + (res.notified < res.overdue ? ` (${res.overdue - res.notified} have no email).` : '.'), 'success');
        }
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (err && err.status === 409 && d && typeof d === 'object' && d.error === 'deadline_not_passed') {
          showFlash(`Overdue notices can only be sent after the deadline (${d.deadline}).`, 'error');
        } else { showFlash(_errDetail(err) || 'Failed to send notices', 'error'); }
      }
    }

    function _wireHrPolicyUi() {
      if (_hrPolicyUiWired) return;
      const addBtn = document.getElementById('hrAddPolicyBtn');
      if (!addBtn) return;   // section not in DOM yet
      _hrPolicyUiWired = true;
      addBtn.addEventListener('click', () => openPolicyDialog(null));
      document.getElementById('hrGenQuizAddBtn')?.addEventListener('click', () => openGenQuizDialog(null));
      document.getElementById('hrGenQuizSaveBtn')?.addEventListener('click', saveGenQuiz);
      document.getElementById('hrGenQuizQSaveBtn')?.addEventListener('click', saveGenQuizQuestions);
      document.getElementById('hrGenQuizQAutofillBtn')?.addEventListener('click', _autoFillGenQuizToTarget);
      const genTbody = document.getElementById('hrGenQuizTbody');
      if (genTbody) genTbody.addEventListener('click', (e) => {
        const qb = e.target.closest('[data-genquiz-questions]');
        if (qb) {
          const quiz = (_genQuizItems || []).find((x) => x.id === qb.getAttribute('data-genquiz-questions'));
          if (quiz) openGenQuizQuestions(quiz);
          return;
        }
        const ln = e.target.closest('[data-genquiz-launch]');
        if (ln) return void launchGenQuiz(ln.getAttribute('data-genquiz-launch'), ln.getAttribute('data-genquiz-title'));
        const nf = e.target.closest('[data-genquiz-notify]');
        if (nf) return void notifyOverdueGenQuiz(nf.getAttribute('data-genquiz-notify'), nf.getAttribute('data-genquiz-title'));
        const ed = e.target.closest('[data-genquiz-edit]');
        if (ed) return void editGenQuiz(ed.getAttribute('data-genquiz-edit'));
        const del = e.target.closest('[data-genquiz-del]');
        if (del) return void deleteGenQuiz(del.getAttribute('data-genquiz-del'));
      });
      const policySearch = document.getElementById('hrPolicySearch');
      const policySearchClear = document.getElementById('hrPolicySearchClear');
      function applyPolicySearch() {
        const v = (policySearch && policySearch.value || '').trim();
        if (v === _hrPolicySearch) return;
        _hrPolicySearch = v;
        if (policySearchClear) policySearchClear.style.display = v ? '' : 'none';
        _renderPolicyRows();
      }
      if (policySearch) {
        policySearch.addEventListener('input', () => {
          if (policySearchClear) policySearchClear.style.display = policySearch.value ? '' : 'none';
          clearTimeout(_hrPolicySearchTimer);
          _hrPolicySearchTimer = setTimeout(applyPolicySearch, 150);
        });
        policySearch.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { clearTimeout(_hrPolicySearchTimer); applyPolicySearch(); }
        });
      }
      if (policySearchClear) policySearchClear.addEventListener('click', () => {
        if (policySearch) policySearch.value = '';
        clearTimeout(_hrPolicySearchTimer);
        applyPolicySearch();
      });
      document.getElementById('hrPolicyTemplateBtn')?.addEventListener('click', async () => {
        // Fetch as a blob so HTTP errors surface as a flash rather than an opaque
        // browser "site can't be reached" from a raw <a> navigation.
        try {
          const res = await fetch(API.adminHrPolicyImportTemplate, { credentials: 'same-origin', cache: 'no-store' });
          if (!res.ok) {
            const msg = res.status === 503
              ? 'Excel export is not available on this server (missing openpyxl). Please contact support.'
              : `Could not download the template (HTTP ${res.status}).`;
            showFlash(msg, 'error');
            return;
          }
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = 'policy_import_template.xlsx';
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        } catch (err) {
          showFlash('Could not download the template. Please try again.', 'error');
        }
      });
      const importFile = document.getElementById('hrPolicyImportFile');
      document.getElementById('hrPolicyImportBtn')?.addEventListener('click', () => importFile?.click());
      importFile?.addEventListener('change', () => {
        const f = importFile.files && importFile.files[0];
        if (f) importPolicies(f);
        importFile.value = '';
      });
      document.getElementById('hrHolidayAddBtn')?.addEventListener('click', addHoliday);
      const holTbody = document.getElementById('hrHolidaysTbody');
      if (holTbody) holTbody.addEventListener('click', (e) => {
        const del = e.target.closest('[data-holiday-del]');
        if (del) deleteHoliday(del.getAttribute('data-holiday-del'));
      });
      const tbody = document.getElementById('hrPoliciesTbody');
      if (tbody) tbody.addEventListener('click', (e) => {
        const ed = e.target.closest('[data-policy-edit]');
        if (ed) return void openPolicyDialog(ed.getAttribute('data-policy-edit'));
        const qz = e.target.closest('[data-policy-quizzes]');
        if (qz) return void openQuizManager(qz.getAttribute('data-policy-quizzes'), qz.getAttribute('data-policy-title'));
        const del = e.target.closest('[data-policy-del]');
        if (del) return void deletePolicy(del.getAttribute('data-policy-del'));
      });
      document.getElementById('hrPolicySaveBtn')?.addEventListener('click', savePolicy);
      document.getElementById('hrQuizNewBtn')?.addEventListener('click', () => startQuizEditor(null));
      document.getElementById('hrQuizAddQuestionBtn')?.addEventListener('click', () => addQuestionRow());
      document.getElementById('hrQuizAiBtn')?.addEventListener('click', aiGenerateQuestions);
      document.getElementById('hrQuizSaveBtn')?.addEventListener('click', saveQuiz);
      const qlist = document.getElementById('hrQuizList');
      if (qlist) qlist.addEventListener('click', (e) => {
        const ln = e.target.closest('[data-quiz-launch]');
        if (ln) return void launchQuiz(ln.getAttribute('data-quiz-launch'), ln.getAttribute('data-quiz-title'));
        const ed = e.target.closest('[data-quiz-edit]');
        if (ed) return void editQuiz(ed.getAttribute('data-quiz-edit'));
        const del = e.target.closest('[data-quiz-del]');
        if (del) return void deleteQuiz(del.getAttribute('data-quiz-del'));
      });
    }

    async function launchQuiz(id, title) {
      let audience = null;
      try { audience = await adminAPI.fetch(API.adminHrQuizAudience(id), { method: 'GET', cache: 'no-store' }); }
      catch { /* fall back to a generic prompt */ }
      const who = audience
        ? `${audience.applicable} in-scope employee(s) (${audience.with_email} with email)`
        : 'all in-scope employees';
      if (!window.confirm(`Launch "${title || 'this quiz'}"?\n\nThis activates the quiz and emails ${who} that it's due this quarter.`)) return;
      try {
        const res = await adminAPI.fetch(API.adminHrQuizLaunch(id), { method: 'POST' });
        showFlash(`Launched to ${res.applicable} in-scope employee(s); emailing ${res.with_email} with an address.`
          + (res.activated ? ' Quiz activated.' : ''), 'success');
        await loadQuizzesInto();
        _refreshPolicies();
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (d === 'quiz_has_no_questions') showFlash('Add questions before launching this quiz.', 'error');
        else showFlash(_errDetail(err) || 'Launch failed', 'error');
      }
    }

    function _refreshPolicies() { hrDataState.ess = false; loadHrEss().catch(() => {}); }

    async function openPolicyDialog(id) {
      const dlg = document.getElementById('hrPolicyDlg');
      if (!dlg) return;
      _hrPolicyEditId = id || null;
      document.getElementById('hrPolicyDlgTitle').textContent = id ? 'Edit policy' : 'Add policy';
      const set = (elId, v) => { const el = document.getElementById(elId); if (el) el.value = v || ''; };
      set('hrPolicyTitle', ''); set('hrPolicyBody', ''); set('hrPolicyLoc', '');
      document.getElementById('hrPolicyActive').checked = true;
      if (!hrDepartmentsList.length) { try { await loadHrDepartments(); } catch {} }
      _renderPolicyDeptScope([]);
      if (id) {
        try {
          const p = await adminAPI.fetch(API.adminHrPolicy(id), { method: 'GET' });
          set('hrPolicyTitle', p.title); set('hrPolicyBody', p.body); set('hrPolicyLoc', p.location_scope);
          _renderPolicyDeptScope(_scopeToIds(p.department_scope));
          document.getElementById('hrPolicyActive').checked = p.is_active === true || String(p.is_active).toLowerCase() === 'true';
        } catch (err) { showFlash(_errDetail(err) || 'Failed to load policy', 'error'); return; }
      }
      openDialog(dlg);
    }

    // Department scope is a comma-separated list of department ids (blank = all).
    function _scopeToIds(scope) {
      return (scope || '').split(',').map((s) => s.trim()).filter(Boolean);
    }

    function _deptScopeLabel(scope) {
      const ids = _scopeToIds(scope);
      if (!ids.length) return 'All';
      return ids.map((id) => (hrDepartmentsMap.get(String(id)) || {}).name || id).join(', ');
    }

    function _renderPolicyDeptScope(selectedIds) {
      const box = document.getElementById('hrPolicyDeptScope');
      if (!box) return;
      const selected = new Set((selectedIds || []).map(String));
      if (!hrDepartmentsList.length) { box.innerHTML = '<span class="muted" style="font-size:12px;">No departments defined.</span>'; return; }
      box.innerHTML = hrDepartmentsList.map((d) => `
        <label style="display:inline-flex; align-items:center; gap:4px; font-size:12px;">
          <input type="checkbox" data-policy-dept="${escapeHtml(d.id)}" ${selected.has(String(d.id)) ? 'checked' : ''} />
          ${escapeHtml(d.name || d.id)}
        </label>`).join('');
    }

    async function savePolicy() {
      const val = (elId) => (document.getElementById(elId)?.value || '').trim();
      const title = val('hrPolicyTitle');
      if (!title) { showFlash('Title is required', 'error'); return; }
      const deptIds = Array.from(document.querySelectorAll('#hrPolicyDeptScope [data-policy-dept]'))
        .filter((cb) => cb.checked).map((cb) => cb.getAttribute('data-policy-dept'));
      const payload = {
        title, body: val('hrPolicyBody') || null,
        department_scope: deptIds.length ? deptIds.join(',') : null,  // none = all
        location_scope: val('hrPolicyLoc') || null,
        is_active: !!document.getElementById('hrPolicyActive')?.checked,
      };
      try {
        if (_hrPolicyEditId) await adminAPI.fetch(API.adminHrPolicy(_hrPolicyEditId), { method: 'PUT', body: JSON.stringify(payload) });
        else await adminAPI.fetch(API.adminHrPolicies, { method: 'POST', body: JSON.stringify(payload) });
        showFlash('Policy saved');
        closeDialog(document.getElementById('hrPolicyDlg'));
        _refreshPolicies();
      } catch (err) { showFlash(_errDetail(err) || 'Save failed', 'error'); }
    }

    async function loadHolidays() {
      const tb = document.getElementById('hrHolidaysTbody');
      if (!tb) return;
      try {
        const data = await adminAPI.fetch(API.adminHrPublicHolidays, { method: 'GET' });
        const rows = (data.items || []).map((h) => {
          const rec = h.recurring === true || String(h.recurring).toLowerCase() === 'true';
          return `<tr>
            <td>${escapeHtml(h.holiday_date)}</td>
            <td>${escapeHtml(h.name)}</td>
            <td>${escapeHtml(h.location_scope || 'All')}</td>
            <td>${rec ? '✓' : '—'}</td>
            <td><button type="button" class="btn ghost" data-holiday-del="${escapeHtml(h.id)}" style="font-size:11px; padding:2px 6px;">🗑</button></td>
          </tr>`;
        }).join('');
        setTableRows(tb, rows, 'No holidays.');
      } catch (err) {
        setTableRows(tb, '', 'Failed to load holidays.');
      }
    }

    async function addHoliday() {
      const date = document.getElementById('hrHolidayDate')?.value;
      const name = (document.getElementById('hrHolidayName')?.value || '').trim();
      const recurring = !!document.getElementById('hrHolidayRecurring')?.checked;
      if (!date || !name) { showFlash('Date and name are required', 'error'); return; }
      try {
        await adminAPI.fetch(API.adminHrPublicHolidays, { method: 'POST', body: JSON.stringify({ holiday_date: date, name, recurring }) });
        showFlash('Holiday added');
        const nameEl = document.getElementById('hrHolidayName');
        if (nameEl) nameEl.value = '';
        loadHolidays();
      } catch (err) { showFlash(_errDetail(err) || 'Failed to add holiday', 'error'); }
    }

    async function deleteHoliday(id) {
      if (!window.confirm('Remove this public holiday?')) return;
      try {
        await adminAPI.fetch(API.adminHrPublicHoliday(id), { method: 'DELETE' });
        showFlash('Holiday removed');
        loadHolidays();
      } catch (err) { showFlash(_errDetail(err) || 'Failed to remove holiday', 'error'); }
    }

    async function importPolicies(file) {
      const fd = new FormData();
      fd.append('file', file);
      showFlash('Importing…');
      try {
        const res = await adminAPI.fetch(API.adminHrPolicyImport, { method: 'POST', body: fd });
        const errs = res.error_count || 0;
        showFlash(`Imported ${res.created} policies${errs ? ` · ${errs} row(s) skipped` : ''}`, errs ? 'error' : 'success');
        if (errs && Array.isArray(res.errors)) {
          console.warn('Policy import row errors:', res.errors);
        }
        _refreshPolicies();
      } catch (err) {
        showFlash(_errDetail(err) || 'Import failed', 'error');
      }
    }

    async function deletePolicy(id) {
      if (!window.confirm('Delete this policy and its quizzes? This cannot be undone.')) return;
      try {
        await adminAPI.fetch(API.adminHrPolicy(id), { method: 'DELETE' });
        showFlash('Policy deleted');
        _refreshPolicies();
      } catch (err) { showFlash(_errDetail(err) || 'Delete failed', 'error'); }
    }

    async function openQuizManager(policyId, title) {
      const dlg = document.getElementById('hrQuizDlg');
      if (!dlg) return;
      _hrQuizPolicyId = policyId; _hrQuizEditId = null;
      document.getElementById('hrQuizDlgTitle').textContent = `Quizzes — ${title || 'policy'}`;
      document.getElementById('hrQuizEditor').style.display = 'none';
      document.getElementById('hrQuizSaveBtn').style.display = 'none';
      await loadQuizzesInto();
      openDialog(dlg);
    }

    async function loadQuizzesInto() {
      const box = document.getElementById('hrQuizList');
      if (!box) return;
      box.innerHTML = '<div class="muted" style="font-size:12px;">Loading…</div>';
      try {
        const data = await adminAPI.fetch(API.adminHrPolicyQuizzes(_hrQuizPolicyId), { method: 'GET' });
        const items = data.items || [];
        box.innerHTML = items.length
          ? items.map((q) => `<div style="display:flex; align-items:center; gap:8px; padding:4px 0; border-bottom:1px dashed var(--border,#eee);">
              <span style="flex:1;">${escapeHtml(q.title || '(untitled quiz)')} <span class="muted" style="font-size:11px;">· ${q.question_count} Q · pass ${q.pass_mark}%${q.is_active ? '' : ' · inactive'}</span></span>
              <button type="button" class="btn" data-quiz-launch="${escapeHtml(q.id)}" data-quiz-title="${escapeHtml(q.title || '')}" title="Activate & email all in-scope employees" style="font-size:11px; padding:2px 8px;">🚀 Launch</button>
              <button type="button" class="btn ghost" data-quiz-edit="${escapeHtml(q.id)}" style="font-size:11px; padding:2px 8px;">Edit</button>
              <button type="button" class="btn ghost" data-quiz-del="${escapeHtml(q.id)}" style="font-size:11px; padding:2px 8px;">🗑</button>
            </div>`).join('')
          : '<div class="muted" style="font-size:12px;">No quizzes yet.</div>';
      } catch (err) { box.innerHTML = `<div class="error" style="font-size:12px;">${escapeHtml(_errDetail(err))}</div>`; }
    }

    function startQuizEditor(quiz) {
      _hrQuizEditId = quiz ? quiz.id : null;
      document.getElementById('hrQuizEditor').style.display = '';
      document.getElementById('hrQuizSaveBtn').style.display = '';
      document.getElementById('hrQuizTitle').value = quiz ? (quiz.title || '') : '';
      document.getElementById('hrQuizPassMark').value = quiz ? (quiz.pass_mark ?? 70) : 70;
      const qbox = document.getElementById('hrQuizQuestions');
      qbox.innerHTML = '';
      const qs = (quiz && quiz.questions) || [];
      if (qs.length) qs.forEach((q) => addQuestionRow(q));
      else addQuestionRow();
    }

    async function editQuiz(id) {
      try { startQuizEditor(await adminAPI.fetch(API.adminHrQuiz(id), { method: 'GET' })); }
      catch (err) { showFlash(_errDetail(err) || 'Failed to load quiz', 'error'); }
    }

    async function deleteQuiz(id) {
      if (!window.confirm('Delete this quiz?')) return;
      try { await adminAPI.fetch(API.adminHrQuiz(id), { method: 'DELETE' }); showFlash('Quiz deleted'); await loadQuizzesInto(); _refreshPolicies(); }
      catch (err) { showFlash(_errDetail(err) || 'Delete failed', 'error'); }
    }

    function addQuestionRow(q, boxId = 'hrQuizQuestions') {
      const qbox = document.getElementById(boxId);
      if (!qbox) return;
      const idx = qbox.children.length;
      const options = (q && q.options && q.options.length) ? q.options : ['', ''];
      const correct = (q && Number.isInteger(q.correct_index)) ? q.correct_index : 0;
      const radioName = `hrq_${boxId}_${idx}`;
      const wrap = document.createElement('div');
      wrap.className = 'hr-quiz-q';
      wrap.style.cssText = 'border:1px solid var(--border,#e5e7eb); border-radius:8px; padding:8px;';
      wrap.innerHTML = `
        <div style="display:flex; gap:8px; align-items:center;">
          <strong style="font-size:12px;">Q${idx + 1}</strong>
          <input class="hr-q-text" type="text" placeholder="Question text" value="${escapeHtml(q?.text || '')}" style="flex:1;" />
          <button type="button" class="btn ghost hr-q-remove" style="font-size:11px; padding:2px 8px;">✕</button>
        </div>
        <div class="hr-q-options" style="display:grid; gap:4px; margin-top:6px;"></div>
        <button type="button" class="btn ghost hr-q-addopt" style="font-size:11px; padding:2px 8px; margin-top:4px;">➕ Option</button>
        <div class="muted" style="font-size:11px; margin-top:2px;">Select the radio for the correct answer.</div>`;
      const optBox = wrap.querySelector('.hr-q-options');
      const addOpt = (text) => {
        const oi = optBox.children.length;
        const row = document.createElement('div');
        row.style.cssText = 'display:flex; gap:6px; align-items:center;';
        row.innerHTML = `<input type="radio" name="${radioName}" ${oi === correct ? 'checked' : ''} class="hr-q-correct" />
          <input class="hr-q-opt" type="text" placeholder="Option ${oi + 1}" value="${escapeHtml(text || '')}" style="flex:1;" />
          <button type="button" class="btn ghost hr-q-delopt" style="font-size:11px; padding:1px 6px;">✕</button>`;
        row.querySelector('.hr-q-delopt').addEventListener('click', () => { if (optBox.children.length > 2) row.remove(); });
        optBox.appendChild(row);
      };
      options.forEach(addOpt);
      wrap.querySelector('.hr-q-addopt').addEventListener('click', () => addOpt(''));
      wrap.querySelector('.hr-q-remove').addEventListener('click', () => wrap.remove());
      qbox.appendChild(wrap);
    }

    function _collectQuestions(boxId) {
      const questions = [];
      document.querySelectorAll(`#${boxId} .hr-quiz-q`).forEach((wrap) => {
        const text = (wrap.querySelector('.hr-q-text')?.value || '').trim();
        const rawOpts = Array.from(wrap.querySelectorAll('.hr-q-opt')).map((i) => i.value.trim());
        const radios = Array.from(wrap.querySelectorAll('.hr-q-correct'));
        let rawCorrect = radios.findIndex((r) => r.checked);
        if (rawCorrect < 0) rawCorrect = 0;
        const options = [];
        let correct_index = 0;
        rawOpts.forEach((o, i) => { if (o) { if (i === rawCorrect) correct_index = options.length; options.push(o); } });
        if (text && options.length >= 2) questions.push({ text, options, correct_index });
      });
      return questions;
    }

    function _collectQuiz() {
      const questions = _collectQuestions('hrQuizQuestions');
      return {
        title: (document.getElementById('hrQuizTitle')?.value || '').trim() || null,
        pass_mark: Math.max(0, Math.min(100, parseInt(document.getElementById('hrQuizPassMark')?.value, 10) || 70)),
        is_active: true,
        questions,
      };
    }

    async function saveQuiz() {
      const payload = _collectQuiz();
      if (!payload.questions.length) { showFlash('Add at least one question (with ≥2 options)', 'error'); return; }
      try {
        if (_hrQuizEditId) await adminAPI.fetch(API.adminHrQuiz(_hrQuizEditId), { method: 'PUT', body: JSON.stringify(payload) });
        else await adminAPI.fetch(API.adminHrPolicyQuizzes(_hrQuizPolicyId), { method: 'POST', body: JSON.stringify(payload) });
        showFlash('Quiz saved');
        document.getElementById('hrQuizEditor').style.display = 'none';
        document.getElementById('hrQuizSaveBtn').style.display = 'none';
        await loadQuizzesInto();
        _refreshPolicies();
      } catch (err) { showFlash(_errDetail(err) || 'Save failed', 'error'); }
    }

    async function aiGenerateQuestions() {
      if (!_hrQuizPolicyId) return;
      const btn = document.getElementById('hrQuizAiBtn');
      if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }
      try {
        // No count → server picks an adaptive 5–10 from the policy body size.
        const data = await adminAPI.fetch(API.adminHrPolicyQuizGenerate(_hrQuizPolicyId), { method: 'POST', body: JSON.stringify({}) });
        const qs = data.questions || [];
        if (!qs.length) { showFlash('No questions generated — add a policy body first', 'error'); return; }
        if (document.getElementById('hrQuizEditor').style.display === 'none') startQuizEditor(null);
        const qbox = document.getElementById('hrQuizQuestions');
        // Drop any blank starter question before appending the generated set.
        Array.from(qbox.querySelectorAll('.hr-quiz-q')).forEach((w) => {
          const t = (w.querySelector('.hr-q-text')?.value || '').trim();
          const anyOpt = Array.from(w.querySelectorAll('.hr-q-opt')).some((i) => i.value.trim());
          if (!t && !anyOpt) w.remove();
        });
        qs.forEach((q) => addQuestionRow(q));
        showFlash(`Generated ${qs.length} questions — review and save`);
      } catch (err) {
        const d = err && err.body && err.body.detail;
        if (d === 'no_policy_body') {
          showFlash('This policy has no body text — add the policy text, then generate questions from it.', 'error');
        } else if (d === 'ai_unavailable') {
          showFlash('AI generation is unavailable on this server (OPENAI_API_KEY not configured, or the AI service errored). Contact support.', 'error');
        } else {
          showFlash(_errDetail(err) || 'AI generation failed', 'error');
        }
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = '✨ AI generate questions'; }
      }
    }

    function loadHrSectionData(section) {
      switch (section) {
        case 'people':
          if (hrPeopleTab === 'departments') {
            if (!hrDataState.departments) loadHrDepartments().catch(() => {});
          } else if (hrPeopleTab === 'positions') {
            if (!hrDataState.positions) loadHrPositions().catch(() => {});
          } else if (!hrDataState.people) {
            loadHrPeople().catch(() => {});
          }
          break;
        case 'performance':
          if (!hrDataState.performance) loadHrPerformance().catch(() => {});
          break;
        case 'talent':
          if (!hrDataState.talent) loadHrTalent().catch(() => {});
          break;
        case 'learning':
          if (!hrDataState.learning) loadHrLearning().catch(() => {});
          break;
        case 'finance':
          if (!hrDataState.finance) loadHrFinance().catch(() => {});
          break;
        case 'compliance':
          if (!hrDataState.compliance) loadHrCompliance().catch(() => {});
          break;
        case 'ess':
          if (!hrDataState.ess) loadHrEss().catch(() => {});
          break;
        default:
          break;
      }
    }


    function openShiftTemplateDialog(template = null) {
      if (!shiftTemplateDlg || !shiftTemplateForm) return;
      shiftTemplateForm.reset();
      shiftTemplateForm.dataset.pending = '0';
      if (shiftTemplateSubmit) shiftTemplateSubmit.disabled = false;

      const defaultDays = [0, 1, 2, 3, 4];
      const timezoneDefault = detectDefaultTimezone();

      shiftTemplateIdInput.value = template?.id ? String(template.id) : '';
      shiftTemplateNameInput.value = template?.name || '';
      shiftTemplateGroupInput.value = template?.staff_group || 'customer_care';
      shiftTemplateTimezoneInput.value = template?.timezone || timezoneDefault;
      shiftTemplateStartInput.value = normalizeTimeInput(template?.start_time, '09:00');
      shiftTemplateEndInput.value = normalizeTimeInput(template?.end_time, '17:00');

      const selectedDays = Array.isArray(template?.days_of_week) && template.days_of_week.length
        ? template.days_of_week.map((day) => Number(day))
        : defaultDays;
      setShiftTemplateDaySelections(selectedDays);

      shiftTemplateEffectiveFromInput.value = normalizeDateValue(template?.effective_from);
      shiftTemplateEffectiveToInput.value = normalizeDateValue(template?.effective_to);
      shiftTemplateStatusInput.value = template?.is_active === false ? 'inactive' : 'active';
      if (shiftTemplateGraceInput) {
        shiftTemplateGraceInput.value = String(template?.grace_minutes ?? 10);
      }
      if (shiftTemplateIsDefaultInput) {
        shiftTemplateIsDefaultInput.checked = !!template?.is_default;
      }

      shiftTemplateDlgTitle.textContent = template ? 'Edit staff shift' : 'Add staff shift';
      openDialog(shiftTemplateDlg);
      setTimeout(() => {
        if (shiftTemplateNameInput) {
          try { shiftTemplateNameInput.focus(); } catch {}
        }
      }, 40);
    }

    function initShiftTemplatesView() {
      if (!hrView) return;
      if (!shiftTemplatesState.initialized) {
        shiftTemplatesState.initialized = true;
        if (hrAddEmployeeBtn) hrAddEmployeeBtn.addEventListener('click', () => openHrEmployeeDialog());
        if (hrAddDepartmentBtn) hrAddDepartmentBtn.addEventListener('click', () => openHrDepartmentDialog());
        if (hrAddPositionBtn) hrAddPositionBtn.addEventListener('click', () => openHrPositionDialog());
        if (hrPositionSearch) hrPositionSearch.addEventListener('input', () => renderHrPositionRows());
        if (hrPositionDeptFilter) hrPositionDeptFilter.addEventListener('change', () => renderHrPositionRows());
        if (hrPositionVacancyFilter) hrPositionVacancyFilter.addEventListener('change', () => renderHrPositionRows());
        if (hrPositionsTbody) {
          hrPositionsTbody.addEventListener('click', (event) => {
            const menuBtn = event.target.closest('button[data-position-menu]');
            if (menuBtn) {
              event.preventDefault();
              const id = String(menuBtn.dataset.positionMenu || '');
              const menu = hrPositionsTbody.querySelector(`.hr-menu[data-position-menu-for="${CSS.escape(id)}"]`);
              const wasOpen = menu && !menu.hidden;
              closeHrPositionMenus();
              if (menu && !wasOpen) { menu.hidden = false; menuBtn.setAttribute('aria-expanded', 'true'); }
              return;
            }
            const action = event.target.closest('button[data-position-action]');
            if (action) {
              event.preventDefault();
              const id = String(action.dataset.positionTarget || '');
              const pos = hrPositionsList.find((p) => String(p.id) === id);
              closeHrPositionMenus();
              if (!pos) return;
              const kind = action.dataset.positionAction;
              if (kind === 'edit') openHrPositionDialog(pos);
              else if (kind === 'toggle-status') void toggleHrPositionStatus(pos);
              else if (kind === 'adjust') void adjustHrPositionEstablished(pos);
              else if (kind === 'holders') viewHrPositionHolders(pos);
            }
          });
          document.addEventListener('click', (event) => {
            if (event.target.closest('.hr-menu-wrap')) return;
            closeHrPositionMenus();
          });
          document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeHrPositionMenus(); });
        }
        if (hrAddGradeBtn) hrAddGradeBtn.addEventListener('click', () => openHrGradeDialog());
        if (hrGradeForm) hrGradeForm.addEventListener('submit', handleHrGradeSubmit);
        if (hrGradesTbody) {
          hrGradesTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-grade-edit]');
            if (!btn) return;
            event.preventDefault();
            const grade = hrGradesList.find((g) => String(g.id) === String(btn.dataset.gradeEdit));
            if (grade) openHrGradeDialog(grade);
          });
        }
        if (hrAddTrainingSessionBtn) hrAddTrainingSessionBtn.addEventListener('click', () => openHrTrainingSessionDialog());
        if (hrPeopleTabBar) {
          hrPeopleTabBar.addEventListener('click', (event) => {
            const btn = event.target.closest('[data-hr-people-tab]');
            if (!btn) return;
            const nextTab = btn.dataset.hrPeopleTab || 'employees';
            if (nextTab === hrPeopleTab) return;
            setHrPeopleTab(nextTab);
          });
        }
        if (hrEmployeeForm) hrEmployeeForm.addEventListener('submit', handleHrEmployeeSubmit);
        if (hrDepartmentForm) hrDepartmentForm.addEventListener('submit', handleHrDepartmentSubmit);
        if (hrDepartmentsTbody) {
          hrDepartmentsTbody.addEventListener('click', (event) => {
            // Staff count → the employee list, filtered to that department.
            const staffLink = event.target.closest('a[data-dept-staff]');
            if (staffLink) {
              event.preventDefault();
              const deptId = String(staffLink.dataset.deptStaff || '');
              hrDeptTreeSelection = '';
              if (hrPeopleDeptFilter) hrPeopleDeptFilter.value = deptId;
              hrPeoplePage = 1;
              setHrPeopleTab('employees');
              Promise.resolve(ensureImportEmployees()).then(renderHrPeopleTable).catch(() => {});
              return;
            }
            // Row overflow menu: open one at a time.
            const menuBtn = event.target.closest('button[data-dept-menu]');
            if (menuBtn) {
              event.preventDefault();
              const id = String(menuBtn.dataset.deptMenu || '');
              const menu = hrDepartmentsTbody.querySelector(`.hr-menu[data-dept-menu-for="${CSS.escape(id)}"]`);
              const wasOpen = menu && !menu.hidden;
              closeHrDeptMenus();
              if (menu && !wasOpen) {
                menu.hidden = false;
                menuBtn.setAttribute('aria-expanded', 'true');
              }
              return;
            }
            const actionBtn = event.target.closest('button[data-dept-action]');
            if (actionBtn) {
              event.preventDefault();
              const id = String(actionBtn.dataset.deptTarget || '');
              const dept = hrDepartmentsList.find((d) => String(d.id) === id);
              closeHrDeptMenus();
              if (!dept) return;
              const action = actionBtn.dataset.deptAction;
              if (action === 'edit') openHrDepartmentDialog(dept);
              else if (action === 'add-child') openHrDepartmentDialog(null, { parentId: id });
              else if (action === 'toggle-status') void toggleHrDepartmentStatus(dept);
              return;
            }
            // Expand/collapse a parent (state persists per browser).
            const toggle = event.target.closest('button[data-dept-toggle]');
            if (toggle) {
              event.preventDefault();
              const id = String(toggle.dataset.deptToggle || '');
              const collapsed = getDeptCollapsed();
              if (collapsed.has(id)) collapsed.delete(id); else collapsed.add(id);
              saveDeptCollapsed();
              renderHrDepartmentRows();
              return;
            }
            const btn = event.target.closest('button[data-dept-id]');
            if (!btn) return;
            event.preventDefault();
            const dept = hrDepartmentsList.find((d) => String(d.id) === String(btn.dataset.deptId));
            openHrDepartmentDialog(dept || null);
          });
        }
        // Sortable Name / Staff headers — sorts within each hierarchy level.
        const hrDeptTable = document.getElementById('hrDepartmentsTable');
        if (hrDeptTable) {
          hrDeptTable.querySelectorAll('th[data-dept-sort]').forEach((th) => {
            th.addEventListener('click', () => {
              const key = th.dataset.deptSort;
              if (hrDeptSort.key === key) {
                hrDeptSort.dir = hrDeptSort.dir === 'asc' ? 'desc' : 'asc';
              } else {
                hrDeptSort = { key, dir: key === 'staff' ? 'desc' : 'asc' };
              }
              renderHrDepartmentRows();
            });
          });
        }
        // Dismiss any open row menu on an outside click or Escape.
        document.addEventListener('click', (event) => {
          if (!hrDepartmentsTbody) return;
          if (event.target.closest('.hr-menu-wrap')) return;
          closeHrDeptMenus();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') closeHrDeptMenus();
        });
        // Department search + status filter (client-side; same input→re-render
        // pattern the people list uses).
        if (hrDepartmentSearch) {
          hrDepartmentSearch.addEventListener('input', () => renderHrDepartmentRows());
        }
        if (hrDepartmentStatusFilter) {
          hrDepartmentStatusFilter.addEventListener('change', () => renderHrDepartmentRows());
        }
        if (hrPositionForm) hrPositionForm.addEventListener('submit', handleHrPositionSubmit);
        if (hrPeopleSearch) {
          hrPeopleSearch.addEventListener('input', () => {
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrPeopleDeptFilter) {
          hrPeopleDeptFilter.addEventListener('change', () => {
            hrDeptTreeSelection = '';
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrPeoplePositionFilter) {
          hrPeoplePositionFilter.addEventListener('change', () => {
            hrDeptTreeSelection = '';
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrPeopleStatusFilter) {
          hrPeopleStatusFilter.addEventListener('change', () => {
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrPeopleEmploymentFilter) {
          hrPeopleEmploymentFilter.addEventListener('change', () => {
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrPeopleAccountFilter) {
          hrPeopleAccountFilter.addEventListener('change', () => {
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        // Click-sortable Name / Department / Position headers.
        const peopleTable = document.getElementById('hrPeopleTable');
        if (peopleTable) {
          peopleTable.querySelectorAll('th[data-people-sort]').forEach((th) => {
            th.addEventListener('click', () => {
              const key = th.dataset.peopleSort;
              if (hrPeopleSortState.key === key) hrPeopleSortState.dir = hrPeopleSortState.dir === 'asc' ? 'desc' : 'asc';
              else hrPeopleSortState = { key, dir: 'asc' };
              hrPeoplePage = 1;
              renderHrPeopleTable();
            });
          });
        }
        // Row → employee profile; keyboard-activatable; manager link independent.
        if (hrPeopleTbody) {
          const openRow = (id) => {
            if (!id) return;
            loadEmployeeDetail(id).catch((err) => showFlash(err && err.message ? err.message : 'Failed to load employee', 'error'));
          };
          hrPeopleTbody.addEventListener('click', (event) => {
            const mgr = event.target.closest('a[data-people-manager]');
            if (mgr) { event.preventDefault(); event.stopPropagation(); openRow(String(mgr.dataset.peopleManager || '')); return; }
            const row = event.target.closest('tr[data-employee-id]');
            if (row) openRow(String(row.dataset.employeeId || ''));
          });
          hrPeopleTbody.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            const row = event.target.closest('tr[data-employee-id]');
            if (row) { event.preventDefault(); openRow(String(row.dataset.employeeId || '')); }
          });
        }
        // "Filters" disclosure popover (position / employment / account).
        if (hrPeopleFiltersBtn && hrPeopleFiltersPanel) {
          const closePanel = () => { hrPeopleFiltersPanel.hidden = true; hrPeopleFiltersBtn.setAttribute('aria-expanded', 'false'); };
          hrPeopleFiltersBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = !hrPeopleFiltersPanel.hidden;
            if (open) closePanel();
            else { hrPeopleFiltersPanel.hidden = false; hrPeopleFiltersBtn.setAttribute('aria-expanded', 'true'); }
          });
          hrPeopleFiltersPanel.addEventListener('click', (e) => e.stopPropagation());
          document.addEventListener('click', (e) => { if (!e.target.closest('.hr-menu-wrap')) closePanel(); });
          document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePanel(); });
        }
        if (hrPeopleFiltersClear) {
          hrPeopleFiltersClear.addEventListener('click', () => {
            [hrPeoplePositionFilter, hrPeopleEmploymentFilter, hrPeopleAccountFilter].forEach((el) => { if (el) el.value = ''; });
            hrPeoplePage = 1;
            renderHrPeopleTable();
          });
        }
        if (hrLoanStatusFilter) {
          hrLoanStatusFilter.addEventListener('change', () => {
            renderHrFinanceTable();
          });
        }
        if (hrPerformanceRefresh) {
          hrPerformanceRefresh.addEventListener('click', () => {
            hrDataState.performance = false;
            loadHrPerformance().catch(() => {});
          });
        }
        bindHrAiScoresCohortTabs();
        bindHrAiScoresRecompute();
        bindHrAiScoresPeriodPickers();
        if (hrPerformanceYear) {
          hrPerformanceYear.addEventListener('change', renderHrPerformanceTables);
        }
        if (hrPerformancePeriod) {
          hrPerformancePeriod.addEventListener('change', renderHrPerformanceTables);
        }
        if (hrPerformanceDept) {
          hrPerformanceDept.addEventListener('change', renderHrPerformanceTables);
        }
        if (hrReviewsTbody) {
          hrReviewsTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-review-id][data-review-act="view"]');
            if (!btn) return;
            const reviewId = btn.dataset.reviewId;
            openReviewDetail(reviewId);
          });
        }
        if (hrRecruitmentStageFilter) hrRecruitmentStageFilter.addEventListener('change', renderHrTalentTables);
        if (hrRecruitmentAddBtn) hrRecruitmentAddBtn.addEventListener('click', () => openRecruitmentDialog());
        if (hrRecruitmentEditBtn) hrRecruitmentEditBtn.addEventListener('click', () => openRecruitmentUpdateDialog());
        if (hrRecruitmentAddApplicantBtn) {
          hrRecruitmentAddApplicantBtn.addEventListener('click', () => {
            if (!hrApplicantDlg || !hrApplicantForm) { _hrWarnMissing('#hrApplicantDlg'); return; }
            hrApplicantForm.reset();
            hrApplicantForm.dataset.pending = '0';
            openDialog(hrApplicantDlg);
          });
        }
        if (hrAddVacancyBtn) {
          hrAddVacancyBtn.addEventListener('click', () => {
            if (!hrVacancyDlg || !hrVacancyForm) { _hrWarnMissing('#hrVacancyDlg'); return; }
            hrVacancyForm.reset();
            hrVacancyForm.dataset.editId = '';
            hrVacancyForm.dataset.pending = '0';
            if (hrVacancyStatusInput) hrVacancyStatusInput.value = 'open';
            updateVacancyTitleFromPosition();
            openDialog(hrVacancyDlg);
          });
        }
        if (hrOnboardingStatusFilter) hrOnboardingStatusFilter.addEventListener('change', renderHrTalentTables);
        if (hrOnboardingAddBtn) hrOnboardingAddBtn.addEventListener('click', () => openOnboardingCreateDialog());
        if (hrOnboardingCompleteBtn) hrOnboardingCompleteBtn.addEventListener('click', () => openHrOnboardingDialog());
        if (hrProbationStatusFilter) hrProbationStatusFilter.addEventListener('change', renderHrTalentTables);
        if (hrAddOnboardingTemplateBtn) {
          hrAddOnboardingTemplateBtn.addEventListener('click', () => {
            if (!hrOnboardingTemplateDlg || !hrOnboardingTemplateForm) { _hrWarnMissing('#hrOnboardingTemplateDlg'); return; }
            hrOnboardingTemplateForm.reset();
            hrOnboardingTemplateForm.dataset.editId = '';
            if (hrOnboardingTemplateStatusInput) hrOnboardingTemplateStatusInput.value = 'active';
            openDialog(hrOnboardingTemplateDlg);
          });
        }
        if (hrAddOnboardingTemplateItemBtn) {
          hrAddOnboardingTemplateItemBtn.addEventListener('click', () => {
            if (!hrOnboardingTemplateItemDlg || !hrOnboardingTemplateItemForm) { _hrWarnMissing('#hrOnboardingTemplateItemDlg'); return; }
            hrOnboardingTemplateItemForm.reset();
            hrOnboardingTemplateItemForm.dataset.editId = '';
            populateOnboardingTemplateOptions();
            if (hrSelectedOnboardingTemplateId && hrOnboardingTemplateItemTemplateInput) {
              hrOnboardingTemplateItemTemplateInput.value = getOnboardingTemplateNameById(hrSelectedOnboardingTemplateId) || '';
            }
            openDialog(hrOnboardingTemplateItemDlg);
          });
        }
        if (hrProbationAddBtn) hrProbationAddBtn.addEventListener('click', () => openProbationCreateDialog());
        if (hrPeoplePrev) {
          hrPeoplePrev.addEventListener('click', () => {
            if (hrPeoplePage > 1) {
              hrPeoplePage -= 1;
              renderHrPeopleTable();
            }
          });
        }
        if (hrPeopleNext) {
          hrPeopleNext.addEventListener('click', () => {
            hrPeoplePage += 1;
            renderHrPeopleTable();
          });
        }
        if (hrRecruitmentTbody) {
          hrRecruitmentTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-application-id][data-app-act="update"]');
            if (!btn) return;
            openRecruitmentUpdateDialog(btn.dataset.applicationId || '');
          });
        }
        if (hrVacancyForm) {
          hrVacancyForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrVacancyForm.dataset.pending === '1') return;
            updateVacancyTitleFromPosition();
            const title = hrVacancyTitleInput ? hrVacancyTitleInput.value.trim() : '';
            if (!title) { showFlash('Select a position to set the vacancy title', 'error'); return; }
            const positionId = resolvePositionId(normalizeOptionalInput(hrVacancyPositionInput));
            const deptId = positionId ? (hrPositionsList.find((p) => String(p.id) === String(positionId))?.department_id || null) : null;
            if (hrVacancyDeptInput) hrVacancyDeptInput.value = getDepartmentNameById(deptId) || '';
            const payload = {
              title,
              department_id: deptId,
              position_id: positionId,
              openings: hrVacancyOpeningsInput && hrVacancyOpeningsInput.value !== '' ? Number(hrVacancyOpeningsInput.value) : 1,
              status: hrVacancyStatusInput ? hrVacancyStatusInput.value : 'open',
              description: normalizeOptionalInput(hrVacancyDescriptionInput),
            };
            const editId = hrVacancyForm.dataset.editId || '';
            hrVacancyForm.dataset.pending = '1';
            if (hrVacancySubmitBtn) { hrVacancySubmitBtn.disabled = true; hrVacancySubmitBtn.textContent = editId ? 'Updating…' : 'Saving…'; }
            try {
              const endpoint = editId ? `/api/hr/admin/hr/recruitment/vacancies/${encodeURIComponent(editId)}` : '/api/hr/admin/hr/recruitment/vacancies';
              const method = editId ? 'PATCH' : 'POST';
              await adminAPI.fetch(endpoint, { method, body: JSON.stringify(payload) });
              showFlash(editId ? 'Vacancy updated' : 'Vacancy added');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrVacancyDlg) { try { hrVacancyDlg.close(); } catch { hrVacancyDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to save vacancy', 'error');
            } finally {
              hrVacancyForm.dataset.pending = '0';
              if (hrVacancySubmitBtn) { hrVacancySubmitBtn.disabled = false; hrVacancySubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrOnboardingTbody) {
          hrOnboardingTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-onboarding-id]');
            if (!btn) return;
            const onboardingId = btn.dataset.onboardingId;
            const act = btn.dataset.onboardAct;
            if (act === 'add_task') {
              openOnboardingTaskDialog(onboardingId);
            } else {
              loadOnboardingTasks(onboardingId).then((tasks) => renderOnboardingTasksDialog(onboardingId, tasks));
            }
          });
        }
        if (hrProbationTbody) {
          hrProbationTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-probation-id][data-probation-act]');
            if (!btn) return;
            openHrProbationDialog(btn.dataset.probationId || '', btn.dataset.employeeId || '');
          });
        }
        const performAdminToggle = async (isActive) => {
          if (!hrEmployeeAdminUser || !hrEmployeeAdminUser.id) return;
          try {
            const updated = await adminAPI.fetch(API.user(hrEmployeeAdminUser.id), {
              method: 'PUT',
              body: JSON.stringify({ is_active: isActive }),
            });
            hrEmployeeAdminUser = Object.assign({}, hrEmployeeAdminUser, updated);
            renderAdminAccountSummary(hrEmployeeDetail, hrEmployeeAdminUser);
            showFlash(isActive ? 'Admin unlocked' : 'Admin locked');
          } catch (err) {
            showFlash(err && err.message ? err.message : 'Failed to update admin account', 'error');
          }
        };
        if (hrAdminLockBtn) {
          hrAdminLockBtn.addEventListener('click', () => performAdminToggle(false));
        }
        if (hrAdminUnlockBtn) {
          hrAdminUnlockBtn.addEventListener('click', () => performAdminToggle(true));
        }
        if (hrLinkAccountBtn) {
          hrLinkAccountBtn.addEventListener('click', () => openHrLinkAccountDialog());
        }
        if (hrUnlinkAccountBtn) {
          hrUnlinkAccountBtn.addEventListener('click', () => unlinkEmployeeAccount());
        }
        if (hrLinkAccountSearch) {
          hrLinkAccountSearch.addEventListener('input', () => {
            if (hrLinkAccountSearchTimer) clearTimeout(hrLinkAccountSearchTimer);
            hrLinkAccountSearchTimer = setTimeout(() => searchLinkableUsers(hrLinkAccountSearch.value.trim()), 250);
          });
        }
        if (hrAdminResetBtn) {
          hrAdminResetBtn.addEventListener('click', async () => {
            if (!hrEmployeeAdminUser || !hrEmployeeAdminUser.id) return;
            const newPass = window.prompt('Enter new password for admin account:', '');
            if (!newPass || newPass.length < 6) {
              showFlash('Password must be at least 6 characters.', 'error');
              return;
            }
            try {
              const updated = await adminAPI.fetch(API.user(hrEmployeeAdminUser.id), {
                method: 'PUT',
                body: JSON.stringify({ password: newPass }),
              });
              hrEmployeeAdminUser = Object.assign({}, hrEmployeeAdminUser, updated);
              showFlash('Password reset');
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to reset password', 'error');
            }
          });
        }
        // (Row navigation is wired above via the tr[data-employee-id] handler.)
        if (hrEmployeeBackBtn) {
          hrEmployeeBackBtn.addEventListener('click', () => {
            showEmployeeList();
          });
        }
        const handleStatusChange = async (targetStatus) => {
          if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
          const reason = window.prompt(`Reason for ${targetStatus}:`, '');
          if (!reason) {
            showFlash('Reason is required.', 'error');
            return;
          }
          let terminationDateVal = normalizeOptionalInput(hrDetailTerminationDateInput);
          const todayStr = new Date().toISOString().slice(0, 10);
          if (targetStatus === 'inactive' || targetStatus === 'terminated') {
            if (!terminationDateVal) terminationDateVal = todayStr;
          }
          try {
            const updated = await adminAPI.fetch(API.adminHrEmployee(hrEmployeeDetail.id), {
              method: 'PATCH',
              body: JSON.stringify({
                status: targetStatus,
                status_reason: reason,
                termination_date: terminationDateVal,
              }),
            });
            hrEmployeeDetail = Object.assign({}, hrEmployeeDetail, updated);
            applyEmployeeDetailToUI(hrEmployeeDetail);
            let accountMsg = '';
            if (updated && Object.prototype.hasOwnProperty.call(updated, 'admin_user_id') && updated.admin_user_id) {
              const activeFlag = Object.prototype.hasOwnProperty.call(updated, 'admin_user_active') ? updated.admin_user_active : null;
              if (activeFlag === true) accountMsg = ' (linked admin account enabled)';
              else if (activeFlag === false) accountMsg = ' (linked admin account disabled)';
            }
            if (hrEmployeeSectionState.status_audit) hrEmployeeSectionState.status_audit.loaded = false;
            loadEmployeeStatusAudits(hrEmployeeDetail.id).catch(() => {});
            showFlash(`Employee ${targetStatus}${accountMsg}`);
            hrDataState.people = false;
            loadHrPeople().catch(() => {});
          } catch (err) {
            showFlash(err && err.message ? err.message : `Failed to ${targetStatus}`, 'error');
          }
        };
        if (hrEmployeeDeactivateBtn) {
          hrEmployeeDeactivateBtn.addEventListener('click', () => handleStatusChange('inactive'));
        }
        if (hrEmployeeTerminateBtn) {
          hrEmployeeTerminateBtn.addEventListener('click', () => handleStatusChange('terminated'));
        }
        if (hrEmployeeReactivateBtn) {
          hrEmployeeReactivateBtn.addEventListener('click', () => handleStatusChange('active'));
        }
        if (hrEmployeeJobForm) {
          hrEmployeeJobForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
            if (hrEmployeeJobSubmit) {
              hrEmployeeJobSubmit.disabled = true;
              hrEmployeeJobSubmit.textContent = 'Saving…';
            }
            const statusVal = hrDetailStatusInput ? hrDetailStatusInput.value : undefined;
            const statusReason = normalizeOptionalInput(hrDetailStatusReasonInput);
            let terminationDateVal = normalizeOptionalInput(hrDetailTerminationDateInput);
            const todayStr = new Date().toISOString().slice(0, 10);
            if (statusVal === 'inactive' || statusVal === 'terminated') {
              if (!statusReason) {
                showFlash('Reason is required for inactive/terminated status.', 'error');
                if (hrEmployeeJobSubmit) {
                  hrEmployeeJobSubmit.disabled = false;
                  hrEmployeeJobSubmit.textContent = 'Save employment';
                }
                return;
              }
              if (!terminationDateVal) terminationDateVal = todayStr;
            }
            const payload = {
              first_name: normalizeOptionalInput(hrDetailFirstNameInput) || undefined,
              last_name: normalizeOptionalInput(hrDetailLastNameInput) || undefined,
              department_id: resolveDepartmentId(normalizeOptionalInput(hrDetailDepartmentInput)),
              position_id: resolvePositionId(normalizeOptionalInput(hrDetailPositionInput)),
              status: statusVal,
              employment_type: hrDetailEmploymentTypeInput ? hrDetailEmploymentTypeInput.value : undefined,
              hire_date: normalizeOptionalInput(hrDetailHireDateInput),
              termination_date: terminationDateVal,
              status_reason: statusReason,
              employment_status_id: normalizeOptionalInput(hrDetailEmploymentStatusIdInput),
              location_id: normalizeOptionalInput(hrDetailLocationInput),
              monthly_pay: hrDetailMonthlyPayInput && hrDetailMonthlyPayInput.value !== '' ? Number(hrDetailMonthlyPayInput.value) : null,
            };
            try {
              const updated = await adminAPI.fetch(API.adminHrEmployee(hrEmployeeDetail.id), {
                method: 'PATCH',
                body: JSON.stringify(payload),
              });
              hrEmployeeDetail = Object.assign({}, hrEmployeeDetail, updated);
              applyEmployeeDetailToUI(hrEmployeeDetail);
              showFlash('Employment updated');
              if (payload.status && hrEmployeeSectionState.status_audit) {
                hrEmployeeSectionState.status_audit.loaded = false;
                loadEmployeeStatusAudits(hrEmployeeDetail.id).catch(() => {});
              }
              hrDataState.people = false;
              loadHrPeople().catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update employment', 'error');
            } finally {
              if (hrEmployeeJobSubmit) {
                hrEmployeeJobSubmit.disabled = false;
                hrEmployeeJobSubmit.textContent = 'Save employment';
              }
            }
          });
        }
        if (hrEmployeeContactForm) {
          hrEmployeeContactForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
            if (hrEmployeeContactSubmit) {
              hrEmployeeContactSubmit.disabled = true;
              hrEmployeeContactSubmit.textContent = 'Saving…';
            }
            const payload = {
              email: normalizeOptionalInput(hrDetailEmailInput),
              phone: normalizeOptionalInput(hrDetailPhoneInput),
            };
            try {
              const updated = await adminAPI.fetch(API.adminHrEmployee(hrEmployeeDetail.id), {
                method: 'PATCH',
                body: JSON.stringify(payload),
              });
              hrEmployeeDetail = Object.assign({}, hrEmployeeDetail, updated);
              applyEmployeeDetailToUI(hrEmployeeDetail);
              showFlash('Contact updated');
              hrDataState.people = false;
              loadHrPeople().catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update contact', 'error');
            } finally {
              if (hrEmployeeContactSubmit) {
                hrEmployeeContactSubmit.disabled = false;
                hrEmployeeContactSubmit.textContent = 'Save contact';
              }
            }
          });
        }
        if (hrEmployeeLeaveRefreshBtn) {
          hrEmployeeLeaveRefreshBtn.addEventListener('click', () => {
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
            hrEmployeeSectionState.leave.loaded = false;
            hrEmployeeSectionState.leave_balances.loaded = false;
            loadEmployeeLeave(hrEmployeeDetail.id).catch(() => {});
            loadEmployeeLeaveBalances(hrEmployeeDetail.id).catch(() => {});
          });
        }
        if (hrEmployeeLeaveAddBtn) {
          hrEmployeeLeaveAddBtn.addEventListener('click', openHrLeaveDialog);
        }
        if (hrLeaveStartInput) {
          hrLeaveStartInput.addEventListener('change', () => {
            hrLeaveDayOverrides = [];
            renderLeaveDays();
          });
        }
        if (hrLeaveEndInput) {
          hrLeaveEndInput.addEventListener('change', () => {
            hrLeaveDayOverrides = [];
            renderLeaveDays();
          });
        }
        if (hrLeaveDaysTbody) {
          hrLeaveDaysTbody.addEventListener('change', handleLeaveDayToggle);
        }
        if (hrLeaveForm) {
          hrLeaveForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            if (hrLeaveForm.dataset.pending === '1') return;
            const leaveType = hrLeaveTypeInput ? hrLeaveTypeInput.value : 'annual';
            const start = hrLeaveStartInput ? hrLeaveStartInput.value : '';
            const end = hrLeaveEndInput ? hrLeaveEndInput.value : '';
            const reason = normalizeOptionalInput(hrLeaveReasonInput);
            if (!start || !end) {
              showFlash('Start and end dates are required.', 'error');
              return;
            }
            const { chargeable, halfCount } = summarizeLeaveDays();
            hrLeaveForm.dataset.pending = '1';
            if (hrLeaveSubmitBtn) {
              hrLeaveSubmitBtn.disabled = true;
              hrLeaveSubmitBtn.textContent = 'Creating…';
            }
            try {
              await createEmployeeLeave(hrEmployeeDetail.id, {
                leave_type: leaveType,
                start_date: start,
                end_date: end,
                reason,
                half_day: halfCount > 0,
                duration_days: chargeable || undefined,
              });
              showFlash('Leave request created');
              closeHrLeaveDialog();
              hrEmployeeSectionState.leave.loaded = false;
              hrEmployeeSectionState.leave_balances.loaded = false;
              loadEmployeeLeave(hrEmployeeDetail.id).catch(() => {});
              loadEmployeeLeaveBalances(hrEmployeeDetail.id).catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create leave', 'error');
            } finally {
              hrLeaveForm.dataset.pending = '0';
              if (hrLeaveSubmitBtn) {
                hrLeaveSubmitBtn.disabled = false;
                hrLeaveSubmitBtn.textContent = 'Create';
              }
            }
          });
        }
        if (hrEmployeeAddGoalBtn) {
          hrEmployeeAddGoalBtn.addEventListener('click', openHrGoalDialog);
        }
        if (hrGoalCheckinForm) {
          hrGoalCheckinForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrGoalCheckinForm || hrGoalCheckinForm.dataset.pending === '1') return;
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            const goalId = hrGoalCheckinSelect ? hrGoalCheckinSelect.value : '';
            if (!goalId) {
              showFlash('Select a goal to update', 'error');
              return;
            }
            const progress = hrGoalCheckinProgress ? Number(hrGoalCheckinProgress.value) : 0;
            if (!Number.isFinite(progress)) {
              showFlash('Enter a valid progress percent', 'error');
              return;
            }
            const notes = normalizeOptionalInput(hrGoalCheckinNotes);
            const goal = hrDepartmentGoalsCache.find((g) => String(g.id) === String(goalId));
            if (goal && !isGoalCheckinAllowed(goal)) {
              showFlash('Goal check-ins are closed for this goal.', 'error');
              return;
            }
            hrGoalCheckinForm.dataset.pending = '1';
            if (hrGoalCheckinSubmit) {
              hrGoalCheckinSubmit.disabled = true;
              hrGoalCheckinSubmit.textContent = 'Updating…';
            }
            try {
              await adminAPI.fetch(API.adminHrGoalCheckin(goalId), {
                method: 'POST',
                body: JSON.stringify({ progress_pct: progress, notes: notes || null }),
              });
              showFlash('Goal updated');
              hrEmployeeSectionState.performance.loaded = false;
              loadEmployeePerformance(hrEmployeeDetail.id, hrEmployeeDetail.department_id || null).catch(() => {});
              if (hrGoalCheckinDlg) {
                try { hrGoalCheckinDlg.close(); } catch { hrGoalCheckinDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update goal', 'error');
            } finally {
              hrGoalCheckinForm.dataset.pending = '0';
              if (hrGoalCheckinSubmit) {
                hrGoalCheckinSubmit.disabled = false;
                hrGoalCheckinSubmit.textContent = 'Update goal';
              }
            }
          });
        }
        if (hrReviewForm) {
          hrReviewForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            if (hrReviewForm.dataset.pending === '1') return;
            const period = hrReviewPeriodInput ? hrReviewPeriodInput.value : 'annual';
            const reviewerRaw = normalizeOptionalInput(hrReviewReviewerInput);
            const reviewerId = reviewerRaw ? resolveReviewerEmployeeId(reviewerRaw) : null;
            if (reviewerRaw && !reviewerId) {
              showFlash('Select a reviewer from the manager list.', 'error');
              return;
            }
            const finalScore = hrReviewScoreInput && hrReviewScoreInput.value !== '' ? Number(hrReviewScoreInput.value) : null;
            if (!Number.isFinite(finalScore)) {
              showFlash('Score is required (0–10)', 'error');
              return;
            }
            const strengths = normalizeOptionalInput(hrReviewStrengthsInput);
            const improvements = normalizeOptionalInput(hrReviewImprovementsInput);
            hrReviewForm.dataset.pending = '1';
            if (hrReviewSubmitBtn) {
              hrReviewSubmitBtn.disabled = true;
              hrReviewSubmitBtn.textContent = 'Creating…';
            }
            try {
              await createEmployeeReview(hrEmployeeDetail.id, {
                period,
                reviewer_employee_id: reviewerId,
                final_score: finalScore,
                strengths,
                improvements,
              });
              showFlash('Review created');
              hrEmployeeSectionState.performance.loaded = false;
              loadEmployeePerformance(hrEmployeeDetail.id, hrEmployeeDetail.department_id || null).catch(() => {});
              if (hrReviewDlg) {
                try { hrReviewDlg.close(); } catch { hrReviewDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create review', 'error');
            } finally {
              hrReviewForm.dataset.pending = '0';
              if (hrReviewSubmitBtn) {
                hrReviewSubmitBtn.disabled = false;
                hrReviewSubmitBtn.textContent = 'Create review';
              }
            }
          });
        }
        if (hrGoalForm) {
          hrGoalForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.department_id) {
              showFlash('Department required to add a goal', 'error');
              return;
            }
            if (hrGoalForm.dataset.pending === '1') return;
            const mode = hrGoalModeSelect ? hrGoalModeSelect.value : 'dept';
            const title = hrGoalTitleInput ? hrGoalTitleInput.value.trim() : '';
            if (!title) {
              showFlash('Title is required', 'error');
              return;
            }
            const yearStart = hrGoalStartInput ? hrGoalStartInput.value : null;
            const yearEnd = hrGoalEndInput ? hrGoalEndInput.value : null;
            let descriptionVal = normalizeOptionalInput(hrGoalDescriptionInput);
            if (mode === 'dept' && hrGoalExistingSelect && hrGoalExistingSelect.value) {
              const option = hrGoalExistingSelect.selectedOptions[0];
              if (option && !descriptionVal) descriptionVal = option.dataset.description || null;
            }
            const payload = {
              department_id: hrEmployeeDetail.department_id,
              period: 'year',
              title,
              description: descriptionVal,
              period_start: yearStart,
              period_end: yearEnd,
            };
            hrGoalForm.dataset.pending = '1';
            if (hrGoalSubmitBtn) {
              hrGoalSubmitBtn.disabled = true;
              hrGoalSubmitBtn.textContent = 'Creating…';
            }
            try {
              await adminAPI.fetch(API.adminHrGoalCreate, { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Goal created');
              if (hrGoalDlg) {
                try { hrGoalDlg.close(); } catch { hrGoalDlg.open = false; }
              }
              hrEmployeeSectionState.performance.loaded = false;
              loadEmployeePerformance(hrEmployeeDetail.id, hrEmployeeDetail.department_id || null).catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create goal', 'error');
            } finally {
              hrGoalForm.dataset.pending = '0';
              if (hrGoalSubmitBtn) {
                hrGoalSubmitBtn.disabled = false;
                hrGoalSubmitBtn.textContent = 'Create goal';
              }
            }
          });
        }
        if (hrOnboardingCreateForm) {
          hrOnboardingCreateForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrOnboardingCreateForm.dataset.pending === '1') return;
            const employeeId = resolveEmployeeId(normalizeOptionalInput(hrOnboardingEmployeeInput));
            if (!employeeId) { showFlash('Valid employee required', 'error'); return; }
            const tplVal = normalizeOptionalInput(hrOnboardingTemplateInput);
            const templateId = (() => {
              if (!tplVal) return null;
              const opt = hrOnboardingTemplateOptions ? hrOnboardingTemplateOptions.querySelector(`option[value="${CSS.escape(tplVal)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const found = hrOnboardingTemplates.find((t) => (t.name || '').toLowerCase() === tplVal.toLowerCase());
              return found ? found.id : null;
            })();
            const payload = {
              employee_id: employeeId,
              template_id: templateId,
              start_date: normalizeOptionalInput(hrOnboardingStartInput),
              status: hrOnboardingStatusInput ? hrOnboardingStatusInput.value : null,
            };
            hrOnboardingCreateForm.dataset.pending = '1';
            if (hrOnboardingCreateSubmitBtn) { hrOnboardingCreateSubmitBtn.disabled = true; hrOnboardingCreateSubmitBtn.textContent = 'Saving…'; }
            try {
              await adminAPI.fetch(API.adminHrOnboardingCreate, { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Onboarding created');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrOnboardingCreateDlg) { try { hrOnboardingCreateDlg.close(); } catch { hrOnboardingCreateDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create onboarding', 'error');
            } finally {
              hrOnboardingCreateForm.dataset.pending = '0';
              if (hrOnboardingCreateSubmitBtn) { hrOnboardingCreateSubmitBtn.disabled = false; hrOnboardingCreateSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrOnboardingTaskForm) {
          hrOnboardingTaskForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrOnboardingTaskForm.dataset.pending === '1') return;
            const onboardingId = normalizeOptionalInput(hrOnboardingTaskOnboardingInput);
            if (!onboardingId) { showFlash('Onboarding ID is required', 'error'); return; }
            const payload = {
              employee_onboarding_id: onboardingId,
              template_item_id: normalizeOptionalInput(hrOnboardingTaskTemplateItemInput),
              assignee_employee_id: resolveEmployeeId(normalizeOptionalInput(hrOnboardingTaskAssigneeInput)),
              due_date: normalizeOptionalInput(hrOnboardingTaskDueInput),
              status: hrOnboardingTaskStatusInput ? hrOnboardingTaskStatusInput.value : null,
              notes: normalizeOptionalInput(hrOnboardingTaskNotesInput),
            };
            hrOnboardingTaskForm.dataset.pending = '1';
            if (hrOnboardingTaskSubmitBtn) { hrOnboardingTaskSubmitBtn.disabled = true; hrOnboardingTaskSubmitBtn.textContent = 'Saving…'; }
            try {
              await adminAPI.fetch(API.adminHrOnboardingTaskCreate, { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Onboarding task added');
              hrEmployeeSectionState.talent.loaded = false;
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrEmployeeDetail && hrEmployeeDetail.id) loadEmployeeTalent(hrEmployeeDetail.id).catch(() => {});
              if (hrOnboardingTaskDlg) { try { hrOnboardingTaskDlg.close(); } catch { hrOnboardingTaskDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add task', 'error');
            } finally {
              hrOnboardingTaskForm.dataset.pending = '0';
              if (hrOnboardingTaskSubmitBtn) { hrOnboardingTaskSubmitBtn.disabled = false; hrOnboardingTaskSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrProbationCreateForm) {
          hrProbationCreateForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrProbationCreateForm.dataset.pending === '1') return;
            const employeeId = resolveEmployeeId(normalizeOptionalInput(hrProbationEmployeeInput));
            if (!employeeId) { showFlash('Valid employee required', 'error'); return; }
            const payload = {
              employee_id: employeeId,
              start_date: normalizeOptionalInput(hrProbationStartInput),
              end_date: normalizeOptionalInput(hrProbationEndInput),
              status: hrProbationStatusInput ? hrProbationStatusInput.value : null,
              checkin_frequency: hrProbationFrequencyInput ? hrProbationFrequencyInput.value : null,
            };
            if (!payload.start_date || !payload.end_date) { showFlash('Start and end dates are required', 'error'); return; }
            hrProbationCreateForm.dataset.pending = '1';
            if (hrProbationCreateSubmitBtn) { hrProbationCreateSubmitBtn.disabled = true; hrProbationCreateSubmitBtn.textContent = 'Saving…'; }
            try {
              await adminAPI.fetch(API.adminHrProbationCreate, { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Probation added');
              hrDataState.talent = false;
              hrEmployeeSectionState.talent.loaded = false;
              loadHrTalent().catch(() => {});
              if (hrEmployeeDetail && hrEmployeeDetail.id) loadEmployeeTalent(hrEmployeeDetail.id).catch(() => {});
              if (hrProbationCreateDlg) { try { hrProbationCreateDlg.close(); } catch { hrProbationCreateDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add probation', 'error');
            } finally {
              hrProbationCreateForm.dataset.pending = '0';
              if (hrProbationCreateSubmitBtn) { hrProbationCreateSubmitBtn.disabled = false; hrProbationCreateSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrRecruitmentForm) {
          hrRecruitmentForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrRecruitmentForm.dataset.pending === '1') return;
            const applicantId = (() => {
              const val = normalizeOptionalInput(hrRecruitmentApplicantInput);
              if (!val) return null;
              const opt = hrApplicantOptions ? hrApplicantOptions.querySelector(`option[value="${CSS.escape(val)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const lower = val.toLowerCase();
              const byName = hrApplicantList.find((a) => (a.full_name || '').toLowerCase() === lower);
              if (byName) return byName.id;
              const byId = hrApplicantList.find((a) => String(a.id) === val);
              return byId ? byId.id : null;
            })();
            const vacancyId = (() => {
              const val = normalizeOptionalInput(hrRecruitmentVacancyInput);
              if (!val) return null;
              const opt = hrVacancyOptions ? hrVacancyOptions.querySelector(`option[value="${CSS.escape(val)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const lower = val.toLowerCase();
              const byTitle = hrVacancyList.find((v) => (v.title || '').toLowerCase() === lower);
              if (byTitle) return byTitle.id;
              const byId = hrVacancyList.find((v) => String(v.id) === val);
              return byId ? byId.id : null;
            })();
            const stage = hrRecruitmentStageInput ? hrRecruitmentStageInput.value : 'applied';
            const notes = normalizeOptionalInput(hrRecruitmentNotesInput);
            if (!applicantId || !vacancyId) {
              showFlash('Applicant and open vacancy are required; please pick from the lists (create a vacancy if none are shown).', 'error');
              return;
            }
            hrRecruitmentForm.dataset.pending = '1';
            if (hrRecruitmentSubmitBtn) {
              hrRecruitmentSubmitBtn.disabled = true;
              hrRecruitmentSubmitBtn.textContent = 'Saving…';
            }
            try {
              await adminAPI.fetch(API.adminHrApplicationCreate, { method: 'POST', body: JSON.stringify({ applicant_id: applicantId, vacancy_id: vacancyId, stage, decision_notes: notes }) });
              showFlash('Application created');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrRecruitmentDlg) { try { hrRecruitmentDlg.close(); } catch { hrRecruitmentDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create application', 'error');
            } finally {
              hrRecruitmentForm.dataset.pending = '0';
              if (hrRecruitmentSubmitBtn) { hrRecruitmentSubmitBtn.disabled = false; hrRecruitmentSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrRecruitmentUpdateForm) {
          hrRecruitmentUpdateForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrRecruitmentUpdateForm.dataset.pending === '1') return;
            const appId = normalizeOptionalInput(hrRecruitmentUpdateIdInput);
            if (!appId) { showFlash('Application ID required', 'error'); return; }
            const stage = hrRecruitmentUpdateStageInput ? hrRecruitmentUpdateStageInput.value : null;
            const notes = normalizeOptionalInput(hrRecruitmentUpdateNotesInput);
            hrRecruitmentUpdateForm.dataset.pending = '1';
            if (hrRecruitmentUpdateSubmitBtn) { hrRecruitmentUpdateSubmitBtn.disabled = true; hrRecruitmentUpdateSubmitBtn.textContent = 'Updating…'; }
            try {
              await adminAPI.fetch(API.adminHrApplication(appId), { method: 'PATCH', body: JSON.stringify({ stage, decision_notes: notes }) });
              showFlash('Application updated');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrRecruitmentUpdateDlg) { try { hrRecruitmentUpdateDlg.close(); } catch { hrRecruitmentUpdateDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update application', 'error');
            } finally {
              hrRecruitmentUpdateForm.dataset.pending = '0';
              if (hrRecruitmentUpdateSubmitBtn) { hrRecruitmentUpdateSubmitBtn.disabled = false; hrRecruitmentUpdateSubmitBtn.textContent = 'Update'; }
            }
          });
        }
        if (hrApplicantForm) {
          hrApplicantForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrApplicantForm.dataset.pending === '1') return;
            const name = hrApplicantNameInput ? hrApplicantNameInput.value.trim() : '';
            if (!name) { showFlash('Name is required', 'error'); return; }
            const payload = {
              full_name: name,
              email: normalizeOptionalInput(hrApplicantEmailInput),
              phone: normalizeOptionalInput(hrApplicantPhoneInput),
              source: normalizeOptionalInput(hrApplicantSourceInput),
              highest_degree: normalizeOptionalInput(hrApplicantDegreeInput),
              expertise: normalizeOptionalInput(hrApplicantExpertiseInput),
              skills: normalizeOptionalInput(hrApplicantSkillsInput),
              competencies: normalizeOptionalInput(hrApplicantCompetenciesInput),
              work_experience: normalizeOptionalInput(hrApplicantWorkExpInput),
              biography: normalizeOptionalInput(hrApplicantBioInput),
              date_of_birth: normalizeOptionalInput(hrApplicantDobInput),
            };
            hrApplicantForm.dataset.pending = '1';
            if (hrApplicantSubmitBtn) { hrApplicantSubmitBtn.disabled = true; hrApplicantSubmitBtn.textContent = 'Saving…'; }
            try {
              await adminAPI.fetch(API.adminHrApplicantCreate, { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Applicant added');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrApplicantDlg) { try { hrApplicantDlg.close(); } catch { hrApplicantDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add applicant', 'error');
            } finally {
              hrApplicantForm.dataset.pending = '0';
              if (hrApplicantSubmitBtn) { hrApplicantSubmitBtn.disabled = false; hrApplicantSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrApplicantSearch) {
          hrApplicantSearch.addEventListener('input', () => renderHrTalentTables());
        }
        if (hrApplicantUploadBtn) {
          hrApplicantUploadBtn.addEventListener('click', () => {
            if (!hrApplicantDocDlg || !hrApplicantDocForm) { _hrWarnMissing('#hrApplicantDocDlg'); return; }
            hrApplicantDocForm.reset();
            hrApplicantDocForm.dataset.pending = '0';
            populateApplicantOptions();
            openDialog(hrApplicantDocDlg);
          });
        }
        if (hrApplicantsTbody) {
          hrApplicantsTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-applicant-id][data-applicant-act]');
            if (!btn) return;
            const appId = btn.dataset.applicantId;
            const act = btn.dataset.applicantAct;
            if (act === 'view') {
              openApplicantDetailDialog(appId);
            }
          });
        }
        if (hrApplicantDocForm) {
          hrApplicantDocForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrApplicantDocForm.dataset.pending === '1') return;
            const applicantVal = normalizeOptionalInput(hrApplicantDocApplicantInput);
            const applicantId = (() => {
              if (!applicantVal) return null;
              const opt = hrApplicantOptions ? hrApplicantOptions.querySelector(`option[value="${CSS.escape(applicantVal)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const found = hrApplicantList.find((a) => (a.full_name || '').toLowerCase() === applicantVal.toLowerCase());
              return found ? found.id : null;
            })();
            if (!applicantId || !hrApplicantDocFileInput || !hrApplicantDocFileInput.files || !hrApplicantDocFileInput.files.length) {
              showFlash('Applicant and file are required', 'error');
              return;
            }
            const file = hrApplicantDocFileInput.files[0];
            const formData = new FormData();
            formData.append('file', file);
            formData.append('owner_type', 'applicant');
            formData.append('owner_id', applicantId);
            hrApplicantDocForm.dataset.pending = '1';
            if (hrApplicantDocSubmitBtn) { hrApplicantDocSubmitBtn.disabled = true; hrApplicantDocSubmitBtn.textContent = 'Uploading…'; }
            try {
              await adminAPI.fetch(API.adminHrDocumentUpload, { method: 'POST', body: formData });
              showFlash('Applicant document uploaded');
              if (hrApplicantDocDlg) { try { hrApplicantDocDlg.close(); } catch { hrApplicantDocDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to upload document', 'error');
            } finally {
              hrApplicantDocForm.dataset.pending = '0';
              if (hrApplicantDocSubmitBtn) { hrApplicantDocSubmitBtn.disabled = false; hrApplicantDocSubmitBtn.textContent = 'Upload'; }
            }
          });
        }
        if (hrEmployeeCertForm) {
          hrEmployeeCertForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrEmployeeCertForm.dataset.pending === '1') return;
            // Employee is fixed when opened from a profile (dataset.employeeId), else
            // resolved from the picker.
            const preset = hrEmployeeCertForm.dataset.employeeId || '';
            const employeeId = preset || resolveEmployeeId(normalizeOptionalInput(hrEmployeeCertEmployeeInput));
            const name = (document.getElementById('hrEmployeeCertNameInput')?.value || '').trim();
            if (!employeeId) { showFlash('Employee is required', 'error'); return; }
            if (!name) { showFlash('Certification name is required', 'error'); return; }
            hrEmployeeCertForm.dataset.pending = '1';
            if (hrEmployeeCertSubmitBtn) { hrEmployeeCertSubmitBtn.disabled = true; hrEmployeeCertSubmitBtn.textContent = 'Saving…'; }
            try {
              // Upload the certificate first (validated PDF/image), then link it.
              let certFileId = null;
              const fileInput = document.getElementById('hrEmployeeCertFileInput');
              const file = fileInput && fileInput.files ? fileInput.files[0] : null;
              if (file) {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('category', 'certifications');
                fd.append('owner_type', 'employee');
                fd.append('owner_id', employeeId);
                const up = await adminAPI.fetch(API.adminHrDocumentUpload, { method: 'POST', body: fd });
                certFileId = up && up.id ? up.id : null;
              }
              const payload = {
                name,
                institution: normalizeOptionalInput(document.getElementById('hrEmployeeCertInstitutionInput')),
                skills: normalizeOptionalInput(document.getElementById('hrEmployeeCertSkillsInput')),
                completion_date: normalizeOptionalInput(document.getElementById('hrEmployeeCertCompletionInput')),
                expiry_date: normalizeOptionalInput(document.getElementById('hrEmployeeCertExpiryInput')),
                certificate_file_id: certFileId,
              };
              await adminAPI.fetch(API.adminHrEmployeeCertifications(employeeId), { method: 'POST', body: JSON.stringify(payload) });
              showFlash('Certification added');
              if (hrEmployeeDetail && String(hrEmployeeDetail.id) === String(employeeId)) {
                hrEmployeeSectionState.learning.loaded = false;
                loadEmployeeLearning(employeeId, hrEmployeeDetail.position_id || null).catch(() => {});
              }
              hrDataState.learning = false;
              loadHrLearning().catch(() => {});
              try { hrEmployeeCertDlg.close(); } catch { hrEmployeeCertDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add certification', 'error');
            } finally {
              hrEmployeeCertForm.dataset.pending = '0';
              if (hrEmployeeCertSubmitBtn) { hrEmployeeCertSubmitBtn.disabled = false; hrEmployeeCertSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrEmployeeAddReviewBtn) {
          hrEmployeeAddReviewBtn.addEventListener('click', openHrReviewDialog);
        }
        if (hrEmployeeGoalCheckinBtn) {
          hrEmployeeGoalCheckinBtn.addEventListener('click', openHrGoalCheckinDialog);
        }
        if (hrEmployeeEnrollTrainingBtn) {
          hrEmployeeEnrollTrainingBtn.addEventListener('click', () => {
            openHrTrainingSessionDialog();
            if (hrTrainingAttendeesSelect && hrEmployeeDetail && hrEmployeeDetail.id) {
              Array.from(hrTrainingAttendeesSelect.options).forEach((opt) => {
                opt.selected = String(opt.value) === String(hrEmployeeDetail.id);
              });
            }
          });
        }
        if (hrEmployeeAddOnboardingTaskBtn) {
          hrEmployeeAddOnboardingTaskBtn.addEventListener('click', () => openOnboardingTaskDialog('', hrEmployeeDetail && hrEmployeeDetail.id));
        }
        if (hrEmployeeAddProbationBtn) {
          hrEmployeeAddProbationBtn.addEventListener('click', () => openProbationCreateDialog(hrEmployeeDetail && hrEmployeeDetail.id));
        }
        if (hrEmployeeGoalsTbody) {
          hrEmployeeGoalsTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-goal-id][data-goal-act="checkin"]');
            if (!btn) return;
            const goalId = btn.dataset.goalId;
            openHrGoalCheckinDialogWithGoal(goalId);
          });
        }
        if (hrEmployeeOnboardingTbody) {
          hrEmployeeOnboardingTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-onboarding-id]');
            if (!btn) return;
            const onboardingId = btn.dataset.onboardingId;
            const act = btn.dataset.onboardAct || '';
            if (act === 'task') {
              openOnboardingTaskDialog(onboardingId, hrEmployeeDetail && hrEmployeeDetail.id);
            } else if (act === 'complete') {
              openHrOnboardingDialog(onboardingId);
            }
          });
        }
        if (hrEmployeeProbationTbody) {
          hrEmployeeProbationTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-probation-id][data-probation-act]');
            if (!btn) return;
            const probationId = btn.dataset.probationId;
            if (!probationId) return;
            openHrProbationDialog(probationId, hrEmployeeDetail && hrEmployeeDetail.id);
          });
        }
        if (hrEmployeeEnrollTrainingBtn) {
          hrEmployeeEnrollTrainingBtn.addEventListener('click', openHrTrainingDialog);
        }
        if (hrEmployeeAddCertBtn) {
          hrEmployeeAddCertBtn.addEventListener('click', () => openEmployeeCertDialog(hrEmployeeDetail && hrEmployeeDetail.id));
        }
        if (hrEmployeeUpdateSkillBtn) {
          hrEmployeeUpdateSkillBtn.addEventListener('click', () => {
            if (!hrEmployeeDetail) { showFlash('Select an employee first', 'error'); return; }
            openEmployeeSkillDialog(hrEmployeeDetail.id).catch(() => {});
          });
        }
        if (hrOnboardingForm) {
          hrOnboardingForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
            if (hrOnboardingForm.dataset.pending === '1') return;
            const onboardingId = hrOnboardingSelect ? hrOnboardingSelect.value : '';
            if (!onboardingId) {
              showFlash('Select an onboarding task', 'error');
              return;
            }
            const notes = normalizeOptionalInput(hrOnboardingNotes);
            let evidenceId = null;
            if (hrOnboardingEvidenceFile && hrOnboardingEvidenceFile.files && hrOnboardingEvidenceFile.files.length) {
              const formData = new FormData();
              formData.append('file', hrOnboardingEvidenceFile.files[0]);
              formData.append('owner_type', 'onboarding_task');
              formData.append('owner_id', onboardingId);
              try {
                const uploaded = await adminAPI.fetch(API.adminHrDocumentUpload, { method: 'POST', body: formData });
                evidenceId = uploaded && uploaded.id ? uploaded.id : null;
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to upload evidence', 'error');
                return;
              }
            }
            hrOnboardingForm.dataset.pending = '1';
            if (hrOnboardingSubmit) {
              hrOnboardingSubmit.disabled = true;
              hrOnboardingSubmit.textContent = 'Completing…';
            }
            try {
              await adminAPI.fetch(API.adminHrOnboardingTaskComplete(onboardingId), {
                method: 'POST',
                body: JSON.stringify({ status: 'completed', notes, evidence_file_id: evidenceId }),
              });
              showFlash('Onboarding task completed');
              hrEmployeeSectionState.talent.loaded = false;
              loadEmployeeTalent(hrEmployeeDetail.id).catch(() => {});
              if (hrOnboardingDlg) {
                try { hrOnboardingDlg.close(); } catch { hrOnboardingDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to complete onboarding task', 'error');
            } finally {
              hrOnboardingForm.dataset.pending = '0';
              if (hrOnboardingSubmit) {
                hrOnboardingSubmit.disabled = false;
                hrOnboardingSubmit.textContent = 'Complete task';
              }
            }
          });
        }
        if (hrProbationForm) {
          hrProbationForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrProbationForm.dataset.pending === '1') return;
            const probationId = hrProbationSelect ? hrProbationSelect.value : '';
            if (!probationId) {
              showFlash('Select a probation period', 'error');
              return;
            }
            const checkinDate = normalizeOptionalInput(hrProbationDateInput);
            if (!checkinDate) {
              showFlash('Check-in date is required', 'error');
              return;
            }
            const notes = normalizeOptionalInput(hrProbationNotesInput);
            const improvement = hrProbationImprovementInput && hrProbationImprovementInput.value !== '' ? Number(hrProbationImprovementInput.value) : null;
            hrProbationForm.dataset.pending = '1';
            if (hrProbationSubmit) {
              hrProbationSubmit.disabled = true;
              hrProbationSubmit.textContent = 'Saving…';
            }
            try {
              await adminAPI.fetch(API.adminHrProbationCheckins, {
                method: 'POST',
                body: JSON.stringify({
                  probation_period_id: probationId,
                  checkin_date: checkinDate,
                  by_role: 'manager',
                  improvement_pct: improvement,
                  notes,
                }),
              });
              showFlash('Probation check-in added');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              if (hrProbationDlg) {
                try { hrProbationDlg.close(); } catch { hrProbationDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add check-in', 'error');
            } finally {
              hrProbationForm.dataset.pending = '0';
              if (hrProbationSubmit) {
                hrProbationSubmit.disabled = false;
                hrProbationSubmit.textContent = 'Add check-in';
              }
            }
          });
        }
        if (hrTrainingForm) {
          hrTrainingForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            if (hrTrainingForm.dataset.pending === '1') return;
            const sessionId = normalizeOptionalInput(hrTrainingSessionInput);
            if (!sessionId) {
              showFlash('Training session ID is required', 'error');
              return;
            }
            const statusVal = normalizeOptionalInput(hrTrainingStatusInput);
            hrTrainingForm.dataset.pending = '1';
            if (hrTrainingSubmitBtn) {
              hrTrainingSubmitBtn.disabled = true;
              hrTrainingSubmitBtn.textContent = 'Enrolling…';
            }
            try {
              await createTrainingEnrollment(sessionId, hrEmployeeDetail.id, statusVal || undefined);
              showFlash('Enrollment created');
              hrEmployeeSectionState.learning.loaded = false;
              loadEmployeeLearning(hrEmployeeDetail.id, hrEmployeeDetail.position_id || null).catch(() => {});
              if (hrTrainingDlg) {
                try { hrTrainingDlg.close(); } catch { hrTrainingDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to enroll training', 'error');
            } finally {
              hrTrainingForm.dataset.pending = '0';
              if (hrTrainingSubmitBtn) {
                hrTrainingSubmitBtn.disabled = false;
                hrTrainingSubmitBtn.textContent = 'Enroll';
              }
            }
          });
        }
        if (hrTrainingCourseNewToggle && hrTrainingCourseExtra) {
          hrTrainingCourseNewToggle.addEventListener('change', (event) => {
            hrTrainingCourseExtra.style.display = event.target.checked ? 'grid' : 'none';
          });
        }
        if (hrTrainingSessionForm) {
          hrTrainingSessionForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrTrainingSessionForm.dataset.pending === '1') return;
            const courseVal = normalizeOptionalInput(hrTrainingCourseInput);
            if (!courseVal) { showFlash('Course is required', 'error'); return; }
            hrTrainingSessionForm.dataset.pending = '1';
            if (hrTrainingSessionSubmitBtn) { hrTrainingSessionSubmitBtn.disabled = true; hrTrainingSessionSubmitBtn.textContent = 'Creating…'; }
            try {
              let courseId = resolveCourseId(courseVal);
              if ((hrTrainingCourseNewToggle && hrTrainingCourseNewToggle.checked) || !courseId) {
                const payload = {
                  title: courseVal,
                  provider: normalizeOptionalInput(hrTrainingCourseProviderInput),
                  category: normalizeOptionalInput(hrTrainingCourseCategoryInput),
                  internal: hrTrainingCourseInternalInput ? hrTrainingCourseInternalInput.checked : false,
                };
                const created = await adminAPI.fetch(API.adminHrTrainingCourseCreate, { method: 'POST', body: JSON.stringify(payload) });
                courseId = created?.id;
                if (created) {
                  hrTrainingCourses.push(created);
                  hrTrainingCourseMap.set(String(created.id), created);
                  populateTrainingCourseOptions();
                }
              }
              if (!courseId) throw new Error('Unable to resolve course');
              const sessionPayload = {
                course_id: courseId,
                start_date: normalizeOptionalInput(hrTrainingSessionStartInput),
                end_date: normalizeOptionalInput(hrTrainingSessionEndInput),
                location: normalizeOptionalInput(hrTrainingSessionLocationInput),
                status: hrTrainingSessionStatusInput ? hrTrainingSessionStatusInput.value : null,
                max_attendees: hrTrainingSessionCapacityInput && hrTrainingSessionCapacityInput.value !== '' ? Number(hrTrainingSessionCapacityInput.value) : null,
              };
              const session = await adminAPI.fetch(API.adminHrTrainingSessionCreate, { method: 'POST', body: JSON.stringify(sessionPayload) });
              const attendeeIds = Array.from(hrTrainingAttendeesSelect ? hrTrainingAttendeesSelect.selectedOptions : []).map((opt) => opt.value).filter(Boolean);
              if (attendeeIds.length) {
                await adminAPI.fetch(API.adminHrTrainingBulkEnroll(session.id), { method: 'POST', body: JSON.stringify({ employee_ids: attendeeIds }) });
              }
              if (hrTrainingSkillInput && hrTrainingSkillInput.value.trim()) {
                const skillPayload = {
                  name: hrTrainingSkillInput.value.trim(),
                  category: normalizeOptionalInput(hrTrainingSkillCategoryInput),
                };
                try { await adminAPI.fetch(API.adminHrSkillCreate, { method: 'POST', body: JSON.stringify(skillPayload) }); } catch (err) { console.warn('skill_create_failed', err); }
              }
              showFlash('Training session created');
              hrDataState.learning = false;
              loadHrLearning().catch(() => {});
              if (hrTrainingSessionDlg) { try { hrTrainingSessionDlg.close(); } catch { hrTrainingSessionDlg.open = false; } }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create session', 'error');
            } finally {
              hrTrainingSessionForm.dataset.pending = '0';
              if (hrTrainingSessionSubmitBtn) { hrTrainingSessionSubmitBtn.disabled = false; hrTrainingSessionSubmitBtn.textContent = 'Create session'; }
            }
          });
        }
        if (hrLoanForm) {
          hrLoanForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) return;
            if (hrLoanForm.dataset.pending === '1') return;
            const principal = Number(normalizeOptionalInput(hrLoanCreatePrincipalInput) || 0);
            const term = Number(normalizeOptionalInput(hrLoanCreateTermInput) || 0);
            const collateral = Number(normalizeOptionalInput(hrLoanCreateCollateralInput) || 0);
            if (!Number.isFinite(principal) || principal <= 0) {
              showFlash('Enter a valid principal amount', 'error');
              return;
            }
            if (!Number.isFinite(term) || term <= 0) {
              showFlash('Enter a valid term', 'error');
              return;
            }
            hrLoanForm.dataset.pending = '1';
            if (hrLoanSubmitBtn) {
              hrLoanSubmitBtn.disabled = true;
              hrLoanSubmitBtn.textContent = 'Creating…';
            }
            try {
              await adminAPI.fetch(API.adminHrLoansCreate, {
                method: 'POST',
                body: JSON.stringify({
                  employee_id: hrEmployeeDetail.id,
                  principal_amount: principal,
                  term_months: term,
                  collateral_leave_days: collateral,
                  reason: hrLoanCreateReasonInput && hrLoanCreateReasonInput.value.trim() ? hrLoanCreateReasonInput.value.trim() : null,
                  currency: hrLoanCreateCurrencySelect ? hrLoanCreateCurrencySelect.value : 'GMD',
                  fx_rate: hrLoanCreateFxInput && hrLoanCreateFxInput.value !== '' ? Number(hrLoanCreateFxInput.value) : null,
                  base_currency: hrLoanCreateBaseCurrencySelect ? hrLoanCreateBaseCurrencySelect.value : 'USD',
                }),
              });
              showFlash('Loan created');
              hrEmployeeSectionState.finance.loaded = false;
              loadEmployeeFinance(hrEmployeeDetail.id).catch(() => {});
              if (hrLoanDlg) {
                try { hrLoanDlg.close(); } catch { hrLoanDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to create loan', 'error');
            } finally {
              hrLoanForm.dataset.pending = '0';
              if (hrLoanSubmitBtn) {
                hrLoanSubmitBtn.disabled = false;
                hrLoanSubmitBtn.textContent = 'Create loan';
              }
            }
          });
        }
        if (hrProfileChangeForm) {
          hrProfileChangeForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            if (hrProfileChangeForm.dataset.pending === '1') return;
            const fieldVal = normalizeOptionalInput(hrProfileFieldInput);
            const newVal = normalizeOptionalInput(hrProfileNewValueInput);
            const oldVal = normalizeOptionalInput(hrProfileOldValueInput);
            if (!fieldVal || !newVal) {
              showFlash('Field and new value are required', 'error');
              return;
            }
            hrProfileChangeForm.dataset.pending = '1';
            if (hrProfileChangeSubmitBtn) {
              hrProfileChangeSubmitBtn.disabled = true;
              hrProfileChangeSubmitBtn.textContent = 'Submitting…';
            }
            try {
              await adminAPI.fetch(API.adminHrProfileChangeCreate, {
                method: 'POST',
                body: JSON.stringify({
                  employee_id: hrEmployeeDetail.id,
                  field: fieldVal,
                  old_value: oldVal,
                  new_value: newVal,
                }),
              });
              showFlash('Profile change submitted');
              hrEmployeeSectionState.ess.loaded = false;
              loadEmployeeEss(hrEmployeeDetail.id).catch(() => {});
              if (hrProfileChangeDlg) {
                try { hrProfileChangeDlg.close(); } catch { hrProfileChangeDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to submit profile change', 'error');
            } finally {
              hrProfileChangeForm.dataset.pending = '0';
              if (hrProfileChangeSubmitBtn) {
                hrProfileChangeSubmitBtn.disabled = false;
                hrProfileChangeSubmitBtn.textContent = 'Submit';
              }
            }
          });
        }
        if (hrDocUploadForm) {
          hrDocUploadForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!hrEmployeeDetail || !hrEmployeeDetail.id) {
              showFlash('Select an employee first.', 'error');
              return;
            }
            if (hrDocUploadForm.dataset.pending === '1') return;
            const file = hrDocFileInput && hrDocFileInput.files ? hrDocFileInput.files[0] : null;
            if (!file) {
              showFlash('Choose a file to upload', 'error');
              return;
            }
            const category = hrDocCategoryInput ? (hrDocCategoryInput.value || '') : '';
            const formData = new FormData();
            formData.append('file', file);
            formData.append('owner_type', 'employee');
            formData.append('owner_id', hrEmployeeDetail.id);
            if (category) formData.append('category', category);
            hrDocUploadForm.dataset.pending = '1';
            if (hrDocUploadSubmit) {
              hrDocUploadSubmit.disabled = true;
              hrDocUploadSubmit.textContent = 'Uploading…';
            }
            try {
              await adminAPI.fetch(API.adminHrDocumentUpload, { method: 'POST', body: formData });
              showFlash('Document uploaded');
              hrEmployeeSectionState.documents.loaded = false;
              loadEmployeeDocuments(hrEmployeeDetail.id).catch(() => {});
              // A compliance document may have just satisfied a requirement — refresh the checklist.
              if (category && hrEmployeeSectionState.compliance) {
                hrEmployeeSectionState.compliance.loaded = false;
                loadEmployeeCompliance(hrEmployeeDetail.id).catch(() => {});
              }
              if (hrDocUploadDlg) {
                try { hrDocUploadDlg.close(); } catch { hrDocUploadDlg.open = false; }
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to upload document', 'error');
            } finally {
              hrDocUploadForm.dataset.pending = '0';
              if (hrDocUploadSubmit) {
                hrDocUploadSubmit.disabled = false;
                hrDocUploadSubmit.textContent = 'Upload';
              }
            }
          });
        }
        if (hrEmployeeAddLoanBtn) {
          hrEmployeeAddLoanBtn.addEventListener('click', openHrLoanDialog);
        }
        if (hrEmployeeLoansTbody) {
          hrEmployeeLoansTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('button[data-loan-id][data-loan-act]');
            if (!btn) return;
            const loanId = btn.dataset.loanId;
            const act = btn.dataset.loanAct;
            if (!loanId || !act) return;
            if (act === 'repay') {
              const amtRaw = window.prompt('Repayment amount:', '0');
              const amt = Number(amtRaw);
              if (!Number.isFinite(amt) || amt <= 0) {
                showFlash('Enter a valid amount', 'error');
                return;
              }
              try {
                await adminAPI.fetch(API.adminHrLoanRepay(loanId), {
                  method: 'POST',
                  body: JSON.stringify({ amount: amt }),
                });
                showFlash('Repayment recorded');
                hrEmployeeSectionState.finance.loaded = false;
                loadEmployeeFinance(hrEmployeeDetail.id).catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to record repayment', 'error');
              }
            } else if (act === 'approve' || act === 'deny') {
              const statusVal = act === 'approve' ? 'approved' : 'denied';
              try {
                await adminAPI.fetch(API.adminHrLoanApprove(loanId), {
                  method: 'POST',
                  body: JSON.stringify({ stage: _currentPendingStage(loanId), status: statusVal }),
                });
                showFlash(`Loan ${statusVal}`);
                hrEmployeeSectionState.finance.loaded = false;
                loadEmployeeFinance(hrEmployeeDetail.id).catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : `Failed to ${statusVal} loan`, 'error');
              }
            }
          });
        }
        if (hrLoansTbody) {
          hrLoansTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-loan-id][data-loan-act]');
            if (!btn) return;
            const loanId = btn.dataset.loanId;
            const act = btn.dataset.loanAct;
            if (act === 'view') {
              openLoanDetailDialog(loanId);
            }
          });
        }
        if (hrLoanApproveBtn) {
          hrLoanApproveBtn.addEventListener('click', async () => {
            const loanId = hrLoanDetailDlg && hrLoanDetailDlg.dataset.loanId;
            if (!loanId) return;
          try {
            await adminAPI.fetch(API.adminHrLoanApprove(loanId), { method: 'POST', body: JSON.stringify({ stage: _currentPendingStage(loanId), status: 'approved' }) });
            showFlash('Loan approved');
            loadHrFinance().catch(() => {});
            try { hrLoanDetailDlg.close(); } catch { hrLoanDetailDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to approve loan', 'error');
            }
          });
        }
        if (hrLoanRejectBtn) {
          hrLoanRejectBtn.addEventListener('click', async () => {
            const loanId = hrLoanDetailDlg && hrLoanDetailDlg.dataset.loanId;
            if (!loanId) return;
            try {
              await adminAPI.fetch(API.adminHrLoanApprove(loanId), { method: 'POST', body: JSON.stringify({ stage: _currentPendingStage(loanId), status: 'denied' }) });
              showFlash('Loan rejected');
              loadHrFinance().catch(() => {});
              try { hrLoanDetailDlg.close(); } catch { hrLoanDetailDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to reject loan', 'error');
            }
          });
        }
        if (hrLoanSaveBtn) {
          hrLoanSaveBtn.addEventListener('click', async () => {
            const loanId = hrLoanDetailDlg && hrLoanDetailDlg.dataset.loanId;
            if (!loanId) return;
            const payload = {
              principal_amount: hrLoanPrincipalInput && hrLoanPrincipalInput.value !== '' ? Number(hrLoanPrincipalInput.value) : null,
              term_months: hrLoanTermInput && hrLoanTermInput.value !== '' ? Number(hrLoanTermInput.value) : null,
              collateral_leave_days: hrLoanCollateralInput && hrLoanCollateralInput.value !== '' ? Number(hrLoanCollateralInput.value) : null,
              interest_rate: hrLoanInterestInput && hrLoanInterestInput.value !== '' ? Number(hrLoanInterestInput.value) : null,
              issue_date: normalizeOptionalInput(hrLoanIssueInput),
              currency: hrLoanCurrencyInput ? hrLoanCurrencyInput.value : null,
              fx_rate: hrLoanFxInput && hrLoanFxInput.value !== '' ? Number(hrLoanFxInput.value) : null,
              base_currency: hrLoanBaseCurrencyInput ? hrLoanBaseCurrencyInput.value : null,
            };
            try {
              await adminAPI.fetch(API.adminHrLoan(loanId), { method: 'PATCH', body: JSON.stringify(payload) });
              showFlash('Loan updated');
              await loadHrFinance();
              try { hrLoanDetailDlg.close(); } catch { hrLoanDetailDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update loan', 'error');
            }
          });
        }
        if (hrLoanRepayBtn) {
          hrLoanRepayBtn.addEventListener('click', async () => {
            const loanId = hrLoanDetailDlg && hrLoanDetailDlg.dataset.loanId;
            if (!loanId) return;
            const amt = hrLoanRepayInput && hrLoanRepayInput.value !== '' ? Number(hrLoanRepayInput.value) : 0;
            if (!Number.isFinite(amt) || amt <= 0) {
              showFlash('Enter a valid repayment amount', 'error');
              return;
            }
            try {
              await adminAPI.fetch(API.adminHrLoanRepay(loanId), { method: 'POST', body: JSON.stringify({ amount: amt }) });
              showFlash('Repayment recorded');
              loadHrFinance().catch(() => {});
              try { hrLoanDetailDlg.close(); } catch { hrLoanDetailDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to record repayment', 'error');
            }
          });
        }
        if (hrEmployeeAddProfileChangeBtn) {
          hrEmployeeAddProfileChangeBtn.addEventListener('click', openHrProfileChangeDialog);
        }
        if (hrEmployeeApproveProfileChangeBtn) {
          hrEmployeeApproveProfileChangeBtn.addEventListener('click', async () => {
            const changeId = window.prompt('Profile change ID to approve:', '');
            if (!changeId) return;
            try {
              await adminAPI.fetch(API.adminHrProfileChangeApprove(changeId), { method: 'POST', body: JSON.stringify({ status: 'approved' }) });
              showFlash('Profile change approved');
              hrEmployeeSectionState.ess.loaded = false;
              loadEmployeeEss(hrEmployeeDetail.id).catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to approve profile change', 'error');
            }
          });
        }
        if (hrEmployeeAddDocBtn) {
          hrEmployeeAddDocBtn.addEventListener('click', () => openHrDocUploadDialog());
        }
        if (hrEmployeeRequiredDocsTbody) {
          hrEmployeeRequiredDocsTbody.addEventListener('click', async (event) => {
            const uploadBtn = event.target.closest('button[data-req-doc-upload]');
            if (uploadBtn) { openHrDocUploadDialog(uploadBtn.dataset.reqDocUpload || ''); return; }

            const viewBtn = event.target.closest('button[data-doc-view]');
            if (viewBtn && viewBtn.dataset.docView) {
              // Same-origin → the admin_token cookie rides along; inline=1 previews it.
              window.open(`${API.adminHrDocumentDownload(viewBtn.dataset.docView)}?inline=1`, '_blank', 'noopener');
              return;
            }

            const delBtn = event.target.closest('button[data-doc-delete]');
            if (delBtn && delBtn.dataset.docDelete) {
              const label = delBtn.dataset.docLabel || 'this document';
              if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
              try {
                await adminAPI.fetch(API.adminHrDocument(delBtn.dataset.docDelete), { method: 'DELETE' });
                showFlash('Document deleted');
                hrEmployeeSectionState.compliance.loaded = false;
                if (hrEmployeeSectionState.documents) hrEmployeeSectionState.documents.loaded = false;
                loadEmployeeCompliance(hrEmployeeDetail.id).catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to delete document', 'error');
              }
            }
          });
        }
        if (hrEmployeeDocsTbody) {
          hrEmployeeDocsTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('button[data-doc-id]');
            if (!btn) return;
            const docId = btn.dataset.docId;
            try {
              await adminAPI.fetch(API.adminHrDocument(docId), { method: 'DELETE' });
              showFlash('Document deleted');
              hrEmployeeSectionState.documents.loaded = false;
              loadEmployeeDocuments(hrEmployeeDetail.id).catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to delete document', 'error');
            }
          });
        }
        if (shiftTemplateAddBtn) shiftTemplateAddBtn.addEventListener('click', () => openShiftTemplateDialog());
        if (shiftRefreshBtn) {
          shiftRefreshBtn.addEventListener('click', () => {
            if (hrActiveTab === 'active') {
              loadActiveShifts().catch(() => {});
            } else {
              loadShiftTemplates().catch(() => {});
            }
          });
        }
        if (hrTabBar) {
          hrTabBar.addEventListener('click', (event) => {
            const btn = event.target.closest('[data-hr-tab]');
            if (!btn) return;
            const tab = btn.dataset.hrTab || 'templates';
            if (tab === hrActiveTab) return;
            setHrTab(tab);
          });
        }
        if (hrImportPreviewBtn) hrImportPreviewBtn.addEventListener('click', () => { previewAttendanceImport().catch(() => {}); });
        if (hrImportResetBtn) hrImportResetBtn.addEventListener('click', () => resetAttendanceImport());
        if (hrImportCommitBtn) hrImportCommitBtn.addEventListener('click', () => { commitAttendanceImport().catch(() => {}); });
        if (hrImportFile) hrImportFile.addEventListener('change', () => {
          // Choosing a new file invalidates the prior preview.
          if (hrImportPreviewWrap) hrImportPreviewWrap.style.display = 'none';
          if (hrImportSummary) hrImportSummary.style.display = 'none';
          hrImportState.preview = null;
        });
        if (hrGridView) hrGridView.addEventListener('change', () => { syncGridViewFields(); loadAttendanceGrid().catch(() => {}); });
        if (hrGridMonth) hrGridMonth.addEventListener('change', () => loadAttendanceGrid().catch(() => {}));
        if (hrGridWeek) hrGridWeek.addEventListener('change', () => loadAttendanceGrid().catch(() => {}));
        if (hrGridRefreshBtn) hrGridRefreshBtn.addEventListener('click', () => loadAttendanceGrid().catch(() => {}));
        if (hrGridRecomputeBtn) hrGridRecomputeBtn.addEventListener('click', () => recomputeAttendanceGrid().catch(() => {}));
        if (hrGridPrintBtn) hrGridPrintBtn.addEventListener('click', () => { try { window.print(); } catch {} });
        if (hrGridBody) hrGridBody.addEventListener('click', onGridCellClick);
        if (hrRosterRefreshBtn) hrRosterRefreshBtn.addEventListener('click', () => loadLeaveRoster().catch(() => {}));
        if (hrStaffRefreshBtn) hrStaffRefreshBtn.addEventListener('click', () => loadStaffShifts().catch(() => {}));
        if (hrStaffOn) hrStaffOn.addEventListener('change', () => loadStaffShifts().catch(() => {}));
        if (hrStaffSearch) hrStaffSearch.addEventListener('input', () => renderStaffShifts());
        if (hrStaffFilter) hrStaffFilter.addEventListener('change', () => renderStaffShifts());
        if (hrStaffTbody) hrStaffTbody.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-staff-shift-edit]');
          if (!btn) return;
          const row = (hrStaffState.data?.items || []).find((i) => i.employee_id === btn.dataset.staffShiftEdit);
          openStaffShiftDialog(btn.dataset.staffShiftEdit, {
            onDate: (hrStaffOn && hrStaffOn.value) || localIsoDate(),
            name: row ? row.name : '',
          }).catch(() => {});
        });
        if (hrStaffShiftForm) hrStaffShiftForm.addEventListener('submit', (event) => { submitStaffShift(event).catch(() => {}); });
        if (hrEmployeeSetShiftBtn) hrEmployeeSetShiftBtn.addEventListener('click', () => openEmployeeShiftDialog());
        if (hrStaffShiftHistoryTbody) hrStaffShiftHistoryTbody.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-staff-shift-delete]');
          if (btn) deleteStaffShiftChange(btn.dataset.staffShiftDelete).catch(() => {});
        });
        if (hrRosterDept) hrRosterDept.addEventListener('change', () => loadLeaveRoster().catch(() => {}));
        if (hrRosterYear) hrRosterYear.addEventListener('change', () => loadLeaveRoster().catch(() => {}));
        const quizCompletionBtn = document.getElementById('hrQuizCompletionRefreshBtn');
        if (quizCompletionBtn) quizCompletionBtn.addEventListener('click', () => loadQuizCompletion().catch(() => {}));
        // Lazy-load the per-staff chart the first time its panel is expanded.
        const quizStaffCard = document.getElementById('hrQuizStaffCard');
        if (quizStaffCard) quizStaffCard.addEventListener('toggle', () => { if (quizStaffCard.open) loadQuizStaffScores().catch(() => {}); });
        const quizPrintBtn = document.getElementById('hrQuizReportPrintBtn');
        if (quizPrintBtn) quizPrintBtn.addEventListener('click', () => _downloadQuizReport('html'));
        const quizCsvBtn = document.getElementById('hrQuizReportCsvBtn');
        if (quizCsvBtn) quizCsvBtn.addEventListener('click', () => _downloadQuizReport('csv'));
        if (shiftTemplatesTbody) {
          shiftTemplatesTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('[data-shift-template-action]');
            if (!btn) return;
            const id = btn.dataset.id;
            if (!id) return;
            const template = (shiftTemplatesState.items || []).find((item) => String(item.id) === String(id));
            const action = btn.dataset.shiftTemplateAction;
            if (action === 'edit') {
              openShiftTemplateDialog(template || null);
              return;
            }
            if (action === 'remove') {
              const name = template?.name || 'this staff shift';
              if (!window.confirm(`Delete ${name}?`)) return;
              try {
                await adminAPI.fetch(API.adminShiftTemplate(id), { method: 'DELETE' });
                showFlash('Staff shift deleted.');
                await loadShiftTemplates({ silent: true });
              } catch (err) {
                const message = err && err.message ? err.message : 'Failed to delete staff shift.';
                showFlash(message, 'error');
              }
            }
          });
        }
        if (shiftTemplateForm) {
          shiftTemplateForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (shiftTemplateForm.dataset.pending === '1') return;
            const id = shiftTemplateIdInput ? shiftTemplateIdInput.value.trim() : '';
            const name = shiftTemplateNameInput ? shiftTemplateNameInput.value.trim() : '';
            const groupValue = shiftTemplateGroupInput ? shiftTemplateGroupInput.value : 'customer_care';
            const timezone = shiftTemplateTimezoneInput ? shiftTemplateTimezoneInput.value.trim() : '';
            const startValue = shiftTemplateStartInput ? normalizeTimeInput(shiftTemplateStartInput.value, '') : '';
            const endValue = shiftTemplateEndInput ? normalizeTimeInput(shiftTemplateEndInput.value, '') : '';
            const daysOfWeek = collectShiftTemplateDaySelections().sort((a, b) => a - b);
            const effectiveFrom = shiftTemplateEffectiveFromInput ? shiftTemplateEffectiveFromInput.value : '';
            const effectiveTo = shiftTemplateEffectiveToInput ? shiftTemplateEffectiveToInput.value : '';
            const statusValue = shiftTemplateStatusInput ? shiftTemplateStatusInput.value : 'active';

            if (!name) {
              showFlash('Enter a name for the staff shift.', 'error');
              if (shiftTemplateNameInput) shiftTemplateNameInput.focus();
              return;
            }
            if (!timezone) {
              showFlash('Enter a timezone for this shift.', 'error');
              if (shiftTemplateTimezoneInput) shiftTemplateTimezoneInput.focus();
              return;
            }
            if (!startValue || !endValue) {
              showFlash('Provide both start and end times.', 'error');
              return;
            }
            if (!daysOfWeek.length) {
              showFlash('Select at least one day for the shift.', 'error');
              return;
            }

            const graceRaw = shiftTemplateGraceInput ? parseInt(shiftTemplateGraceInput.value, 10) : 0;
            const graceMinutes = Number.isFinite(graceRaw) && graceRaw >= 0 ? graceRaw : 0;
            const isDefault = shiftTemplateIsDefaultInput ? !!shiftTemplateIsDefaultInput.checked : false;

            const payload = {
              name,
              staff_group: groupValue,
              timezone,
              start_time: startValue,
              end_time: endValue,
              days_of_week: daysOfWeek,
              is_active: statusValue !== 'inactive',
              grace_minutes: graceMinutes,
              is_default: isDefault,
            };
            if (effectiveFrom) payload.effective_from = effectiveFrom;
            if (effectiveTo) payload.effective_to = effectiveTo;

            shiftTemplateForm.dataset.pending = '1';
            if (shiftTemplateSubmit) shiftTemplateSubmit.disabled = true;
            try {
              if (id) {
                await adminAPI.fetch(API.adminShiftTemplate(id), {
                  method: 'PATCH',
                  body: JSON.stringify(payload),
                });
                showFlash('Staff shift updated.');
              } else {
                await adminAPI.fetch(API.adminShiftTemplates, {
                  method: 'POST',
                  body: JSON.stringify(payload),
                });
                showFlash('Staff shift created.');
              }
              try { shiftTemplateDlg.close(); }
              catch { shiftTemplateDlg.open = false; }
              await loadShiftTemplates({ silent: true });
            } catch (err) {
              const message = err && err.message ? err.message : 'Failed to save staff shift.';
              showFlash(message, 'error');
            } finally {
              shiftTemplateForm.dataset.pending = '0';
              if (shiftTemplateSubmit) shiftTemplateSubmit.disabled = false;
            }
          });
        }
        setHrTab(hrActiveTab || 'templates');
      }
      loadShiftTemplates().catch(() => {});
    }


    function renderLoanDetail(loan) {
      if (!loan || !hrLoanDetailDlg) return;
      const employeeName = getEmployeeNameById(loan.employee_id) || loan.employee_id || 'Employee';
      if (hrLoanDetailTitle) hrLoanDetailTitle.textContent = `${employeeName} — ${loan.status || 'status'} (${loan.currency || 'GMD'})`;
      if (hrLoanDetailMeta) {
        const bits = [];
        if (loan.term_months != null) bits.push(`Term: ${loan.term_months} mo`);
        if (loan.balance != null) bits.push(`Balance: ${loan.balance}`);
        if (loan.created_at) bits.push(`Created: ${loan.created_at}`);
        hrLoanDetailMeta.textContent = bits.join(' • ');
      }
      if (hrLoanDetailFields) {
        const rows = [
          ['Principal', loan.principal_amount],
          ['Collateral days', loan.collateral_leave_days],
          ['Collateral value', loan.collateral_value_snapshot],
          ['Balance', loan.balance],
          ['Reason', loan.reason],
        ].map(([label, value]) => `
          <div class="field read-only">
            <span>${escapeHtml(label)}</span>
            <div>${value != null && value !== '' ? escapeHtml(String(value)) : '—'}</div>
          </div>
        `).join('');
        hrLoanDetailFields.innerHTML = rows;
      }
      if (hrLoanRepayInput) {
        const defaultVal = loan.balance != null ? loan.balance : loan.principal_amount;
        hrLoanRepayInput.value = defaultVal != null ? defaultVal : '';
      }
      if (hrLoanPrincipalInput) hrLoanPrincipalInput.value = loan.principal_amount != null ? loan.principal_amount : '';
      if (hrLoanTermInput) hrLoanTermInput.value = loan.term_months != null ? loan.term_months : '';
      if (hrLoanCollateralInput) hrLoanCollateralInput.value = loan.collateral_leave_days != null ? loan.collateral_leave_days : '';
      if (hrLoanInterestInput) hrLoanInterestInput.value = loan.interest_rate != null ? loan.interest_rate : '';
      if (hrLoanIssueInput) hrLoanIssueInput.value = loan.issue_date || '';
      if (hrLoanCurrencyInput) hrLoanCurrencyInput.value = loan.currency || 'GMD';
      if (hrLoanFxInput) hrLoanFxInput.value = loan.fx_rate != null ? loan.fx_rate : '';
      if (hrLoanBaseCurrencyInput) hrLoanBaseCurrencyInput.value = loan.base_currency || 'USD';
      const pending = (loan.status || '').toLowerCase() === 'pending';
      [hrLoanPrincipalInput, hrLoanTermInput, hrLoanCollateralInput, hrLoanInterestInput, hrLoanIssueInput, hrLoanCurrencyInput, hrLoanFxInput, hrLoanBaseCurrencyInput, hrLoanSaveBtn].forEach((el) => {
        if (el) el.disabled = !pending;
      });
      hrLoanDetailDlg.dataset.loanId = loan.id || '';
    }

    function openLoanDetailDialog(loanId) {
      const loan = hrLoansList.find((l) => String(l.id) === String(loanId));
      if (!loan) {
        showFlash('Loan not found', 'error');
        return;
      }
      renderLoanDetail(loan);
      openDialog(hrLoanDetailDlg);
      loadLoanInsight(loanId);
    }

    function _loanMoney(cur, v) {
      if (v == null) return '—';
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${cur || 'GMD'} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    async function loadLoanInsight(loanId) {
      const box = document.getElementById('hrLoanInsight');
      const printBtn = document.getElementById('hrLoanPrintCertBtn');
      if (box) box.innerHTML = '<div style="color:var(--sub); font-size:13px;">Loading approver insight…</div>';
      if (printBtn) { printBtn.hidden = true; }
      try {
        const ins = await adminAPI.fetch(API.adminHrLoanInsight(loanId));
        if (hrLoanDetailDlg) hrLoanDetailDlg._insight = ins;
        renderLoanInsight(ins);
        if (printBtn) {
          printBtn.hidden = String((ins.loan && ins.loan.status) || '').toLowerCase() !== 'approved';
          printBtn.onclick = () => printLoanCertificate(ins);
        }
      } catch (err) {
        if (box) box.innerHTML = '<div style="color:var(--sub); font-size:13px;">Approver insight unavailable.</div>';
      }
    }

    function renderLoanInsight(ins) {
      const box = document.getElementById('hrLoanInsight');
      if (!box || !ins) return;
      const cur = ins.loan && ins.loan.currency;
      const emp = ins.employee || {};
      const af = ins.affordability || {};
      const perf = ins.performance;
      const priorActive = (ins.prior_loans || []).filter((p) => Number(p.outstanding) > 0).length;

      const stat = (lab, val, note) => `<div style="border:1px solid var(--border-soft); border-radius:10px; padding:10px 12px; background:var(--panel);">
        <div style="font-size:11px; letter-spacing:.04em; text-transform:uppercase; color:var(--sub);">${escapeHtml(lab)}</div>
        <div style="font-size:18px; font-weight:700;">${val}</div>${note ? `<div style="font-size:11px; color:var(--sub);">${escapeHtml(note)}</div>` : ''}</div>`;

      const stats = `<div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:8px;">
        ${stat('Service', emp.tenure || '—', emp.hire_date ? 'Hired ' + emp.hire_date : '')}
        ${stat('Take-home', _loanMoney(cur, emp.monthly_pay), 'Gross monthly')}
        ${stat('Performance', perf ? `${perf.score_average} <span style="font-size:12px; color:var(--sub);">/5</span>` : '—', perf ? `${perf.months}-mo avg` : 'No scores')}
        ${stat('Prior loans', `${(ins.prior_loans || []).length} <span style="font-size:12px; color:var(--sub);">· ${priorActive} active</span>`, ins.outstanding_total != null ? _loanMoney(cur, ins.outstanding_total) + ' total exposure' : '')}
      </div>`;

      const pct = af.combined_pct != null ? af.combined_pct : 0;
      const barColor = af.over_cap ? 'var(--err)' : 'var(--ok, #22c55e)';
      const afford = `<div style="border:1px solid var(--border-soft); border-radius:10px; padding:10px 12px;">
        <div style="display:flex; justify-content:space-between; font-size:13px;"><span><strong>Affordability</strong> — new + existing deductions vs pay</span>
          <span>${_loanMoney(cur, af.combined_monthly)}/mo${af.combined_pct != null ? ` · <strong style="color:${af.over_cap ? 'var(--err)' : 'inherit'}">${af.combined_pct}%</strong>` : ''}</span></div>
        <div style="height:8px; border-radius:999px; background:var(--border-soft); overflow:hidden; margin-top:8px;"><span style="display:block; height:100%; width:${Math.min(pct, 100)}%; background:${barColor};"></span></div>
        <div style="font-size:11px; color:var(--sub); margin-top:6px;">Policy cap ${af.cap_pct != null ? af.cap_pct + '%' : '—'}${af.cap_amount != null ? ' (' + _loanMoney(cur, af.cap_amount) + ')' : ''}${af.over_cap ? ' · <strong style="color:var(--err)">over cap</strong>' : ' · within cap'}.</div></div>`;

      const priorRows = (ins.prior_loans || []).map((p) => `<tr>
        <td style="padding:4px 6px;">${escapeHtml(p.reference)}</td>
        <td style="padding:4px 6px;">${escapeHtml(p.issue_date || '—')}</td>
        <td style="padding:4px 6px; text-align:right;">${_loanMoney(cur, p.principal)}</td>
        <td style="padding:4px 6px; text-align:right;">${_loanMoney(cur, p.outstanding)}</td>
        <td style="padding:4px 6px;">${escapeHtml(p.status || '')}</td></tr>`).join('');
      const prior = (ins.prior_loans || []).length ? `<div><div style="font-size:11px; letter-spacing:.04em; text-transform:uppercase; color:var(--sub); margin-bottom:4px;">Prior loans</div>
        <div style="overflow-x:auto;"><table style="width:100%; border-collapse:collapse; font-size:12px;">
        <thead><tr style="color:var(--sub); text-align:left;"><th style="padding:4px 6px;">Ref</th><th style="padding:4px 6px;">Issued</th><th style="padding:4px 6px; text-align:right;">Principal</th><th style="padding:4px 6px; text-align:right;">Outstanding</th><th style="padding:4px 6px;">Status</th></tr></thead>
        <tbody>${priorRows}</tbody></table></div></div>` : '';

      const chain = (ins.approvals || []).map((a) => {
        const ok = (a.status || '').toLowerCase() === 'approved';
        const bad = ['denied', 'rejected'].includes((a.status || '').toLowerCase());
        const mark = ok ? '✓' : bad ? '✕' : '⋯';
        const color = ok ? 'var(--ok, #22c55e)' : bad ? 'var(--err)' : 'var(--sub)';
        // Who acted (once decided) or who is expected to act (while pending).
        const expected = (a.expected_approvers || []).join(', ');
        const who = a.approver_name
          ? ' · ' + escapeHtml(a.approver_name)
          : (expected ? ` · <span style="color:var(--sub);">awaiting ${escapeHtml(expected)}</span>` : '');
        return `<div style="display:flex; gap:8px; align-items:baseline; padding:3px 0;">
          <span style="color:${color}; font-weight:700;">${mark}</span>
          <span style="flex:1;"><strong>${escapeHtml(a.label || a.stage)}</strong>${who}${a.comments ? `<span style="color:var(--sub); font-style:italic;"> — ${escapeHtml(a.comments)}</span>` : ''}</span>
          <span style="color:var(--sub); font-size:12px;">${a.decided_at ? escapeHtml(a.decided_at.slice(0, 10)) : ''}</span></div>`;
      }).join('');
      const chainBox = (ins.approvals || []).length ? `<div><div style="font-size:11px; letter-spacing:.04em; text-transform:uppercase; color:var(--sub); margin-bottom:4px;">Approval chain</div>${chain}</div>` : '';

      const reason = ins.loan && ins.loan.reason ? `<div style="font-size:13px;"><span style="color:var(--sub);">Reason:</span> ${escapeHtml(ins.loan.reason)}</div>` : '';

      box.innerHTML = stats + afford + prior + chainBox + reason;
    }

    function printLoanCertificate(ins) {
      const cur = ins.loan.currency || 'GMD';
      const money = (v) => _loanMoney(cur, v);
      const emp = ins.employee || {};
      const L = ins.loan;
      const approvedStage = (ins.approvals || []).slice().reverse().find((a) => (a.status || '').toLowerCase() === 'approved');
      const chainRows = (ins.approvals || []).map((a) => `<tr>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${escapeHtml(a.label || a.stage)}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${escapeHtml(a.approver_name || '—')}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb; text-transform:capitalize;">${escapeHtml(a.status || '')}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${a.decided_at ? escapeHtml(a.decided_at.slice(0, 10)) : '—'}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb; color:#555; font-style:italic;">${escapeHtml(a.comments || '')}</td></tr>`).join('');
      const priorRows = (ins.prior_loans || []).map((p) => `<tr>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${escapeHtml(p.reference)}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${escapeHtml(p.issue_date || '—')}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb; text-align:right;">${money(p.principal)}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb; text-align:right;">${money(p.outstanding)}</td>
        <td style="padding:6px 4px; border-bottom:1px solid #e5e7eb;">${escapeHtml(p.status || '')}</td></tr>`).join('') || '<tr><td colspan="5" style="padding:6px 4px; color:#777;">None on record.</td></tr>';
      const today = new Date().toISOString().slice(0, 10);
      const win = window.open('', '_blank', 'width=900,height=760');
      if (!win) { showFlash('Allow pop-ups to print the certificate', 'error'); return; }
      win.document.write(`<html><head><title>Loan Approval Certificate ${escapeHtml(L.reference)}</title></head>
        <body style="font-family:Georgia, 'Times New Roman', serif; color:#111; padding:28px; max-width:820px; margin:0 auto;">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:12px;">
            <div><div style="font-size:18px; font-weight:700;">Staff Loan Approval Certificate</div>
              <div style="font-size:12px; color:#555; font-family:Arial,sans-serif;">Human Resources · Staff Welfare &amp; Loans</div></div>
            <div style="text-align:right; font-family:Arial,sans-serif; font-size:12px; color:#555;">Ref ${escapeHtml(L.reference)}<br>Issued ${escapeHtml(today)}
              <div style="margin-top:6px; display:inline-block; border:2px solid #0d7a4f; color:#0d7a4f; font-weight:800; letter-spacing:.12em; padding:2px 10px; border-radius:6px;">APPROVED</div></div>
          </div>
          <div style="font-family:Arial,sans-serif; font-size:13px; margin-top:16px; display:grid; grid-template-columns:1fr 1fr; gap:4px 30px;">
            <div><b>Employee:</b> ${escapeHtml(emp.name || '—')}</div>
            <div><b>Department:</b> ${escapeHtml(emp.department || '—')}</div>
            <div><b>Position:</b> ${escapeHtml(emp.position || '—')}</div>
            <div><b>Date of hire:</b> ${escapeHtml(emp.hire_date || '—')}${emp.tenure ? ' · ' + escapeHtml(emp.tenure) : ''}</div>
            <div><b>Gross monthly pay:</b> ${money(emp.monthly_pay)}</div>
          </div>
          <div style="margin-top:18px; border:1px solid #0d7a4f; background:#f0f7f5; border-radius:8px; padding:12px 14px; font-family:Arial,sans-serif; display:flex; flex-wrap:wrap; gap:8px 28px;">
            <div><div style="font-size:11px; text-transform:uppercase; color:#555;">Principal</div><div style="font-size:17px; font-weight:700;">${money(L.principal)}</div></div>
            <div><div style="font-size:11px; text-transform:uppercase; color:#555;">Term</div><div style="font-size:17px; font-weight:700;">${escapeHtml(String(L.term_months || '—'))} mo</div></div>
            <div><div style="font-size:11px; text-transform:uppercase; color:#555;">Interest</div><div style="font-size:17px; font-weight:700;">${escapeHtml(String(L.interest_rate || 0))}%</div></div>
            <div><div style="font-size:11px; text-transform:uppercase; color:#555;">Monthly repayment</div><div style="font-size:17px; font-weight:700;">${money(L.monthly_repayment)}</div></div>
            <div><div style="font-size:11px; text-transform:uppercase; color:#555;">Total repayable</div><div style="font-size:17px; font-weight:700;">${money(L.total_repayable)}</div></div>
          </div>
          <div style="font-family:Arial,sans-serif; font-size:13px; margin-top:16px;"><b>Reason for request:</b><div style="border-left:3px solid #0d7a4f; padding-left:12px; margin-top:4px;">${escapeHtml(L.reason || '—')}</div></div>
          <div style="font-family:Arial,sans-serif; margin-top:18px;"><div style="font-weight:700; font-size:13px; border-bottom:1px solid #ccc; padding-bottom:4px;">Approval chain</div>
            <table style="width:100%; border-collapse:collapse; font-size:12px; margin-top:6px;"><thead><tr style="text-align:left; color:#555;"><th style="padding:6px 4px;">Stage</th><th style="padding:6px 4px;">Approver</th><th style="padding:6px 4px;">Decision</th><th style="padding:6px 4px;">Date</th><th style="padding:6px 4px;">Comment</th></tr></thead><tbody>${chainRows}</tbody></table></div>
          <div style="font-family:Arial,sans-serif; margin-top:18px;"><div style="font-weight:700; font-size:13px; border-bottom:1px solid #ccc; padding-bottom:4px;">Outstanding loans on record</div>
            <table style="width:100%; border-collapse:collapse; font-size:12px; margin-top:6px;"><thead><tr style="text-align:left; color:#555;"><th style="padding:6px 4px;">Ref</th><th style="padding:6px 4px;">Issued</th><th style="padding:6px 4px; text-align:right;">Principal</th><th style="padding:6px 4px; text-align:right;">Outstanding</th><th style="padding:6px 4px;">Status</th></tr></thead><tbody>${priorRows}</tbody></table>
            <div style="text-align:right; font-size:12px; margin-top:6px;"><b>Total exposure incl. this loan:</b> ${money(ins.outstanding_total)}</div></div>
          <div style="font-family:Arial,sans-serif; margin-top:40px; display:grid; grid-template-columns:1fr 1fr 1fr; gap:24px; font-size:12px;">
            <div style="border-top:1px solid #111; padding-top:6px; color:#555;">Requester — ${escapeHtml(emp.name || '')}</div>
            <div style="border-top:1px solid #111; padding-top:6px; color:#555;">Prepared by — HR</div>
            <div style="border-top:1px solid #111; padding-top:6px; color:#555;">Authorised by — ${escapeHtml(approvedStage && approvedStage.approver_name || '')}</div>
          </div>
          <div style="font-family:Arial,sans-serif; margin-top:22px; padding-top:10px; border-top:1px solid #ddd; font-size:11px; color:#777; display:flex; justify-content:space-between;"><span>HR-LOAN-CERT v1</span><span>Generated ${escapeHtml(today)} · Ref ${escapeHtml(L.reference)}</span></div>
          <script>window.print();</script>
        </body></html>`);
      win.document.close();
    }


  // ---- Fragment B function declarations (verbatim) ---------------------
    async function createEmployeeLeave(employeeId, payload) {
      const body = Object.assign({}, payload, { employee_id: employeeId });
      return adminAPI.fetch(API.adminHrLeaveRequests, { method: 'POST', body: JSON.stringify(body) });
    }
    async function decideEmployeeLeave(leaveId, decision, comments) {
      const endpoint = `${API.adminHrLeaveRequests}/${encodeURIComponent(leaveId)}/${decision}`;
      return adminAPI.fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ comments: comments || null }),
      });
    }
    async function createEmployeeGoal(departmentId, period, title) {
      const payload = { department_id: departmentId, period, title };
      return adminAPI.fetch(API.adminHrGoalCreate, { method: 'POST', body: JSON.stringify(payload) });
    }
    async function createEmployeeReview(employeeId, payload) {
      const body = Object.assign({ employee_id: employeeId }, payload || {});
      return adminAPI.fetch(API.adminHrReviewCreate, { method: 'POST', body: JSON.stringify(body) });
    }
    async function createTrainingEnrollment(sessionId, employeeId, status = null) {
      const payload = { training_session_id: sessionId, employee_id: employeeId };
      if (status) payload.status = status;
      return adminAPI.fetch(API.adminHrTrainingEnrollmentCreate, { method: 'POST', body: JSON.stringify(payload) });
    }
    async function updateEmployeeSkill(employeeId, skillId, level) {
      const payload = { employee_id: employeeId, skill_id: skillId, current_level: level };
      return adminAPI.fetch(API.adminHrEmployeeSkill, { method: 'POST', body: JSON.stringify(payload) });
    }

    // Add/update an employee's skill without hunting for a skill ID: type to search
    // the catalogue, or type a new name to define it inline (created on save). The
    // dialog is built lazily in JS so no shared markup is needed.
    let hrEmpSkillModal = null;

    async function openEmployeeSkillDialog(employeeId) {
      if (!employeeId) { showFlash('Select an employee first', 'error'); return; }
      let catalog = [];
      try {
        const data = await adminAPI.fetch(API.adminHrSkills, { method: 'GET' });
        catalog = Array.isArray(data?.items) ? data.items : [];
        hrSkillsList = catalog;   // keep the shared list fresh for name↔id resolution
      } catch { /* proceed with an empty catalogue — a new skill can still be defined */ }

      if (!hrEmpSkillModal) {
        hrEmpSkillModal = document.createElement('dialog');
        hrEmpSkillModal.id = 'hrEmployeeSkillModal';
        hrEmpSkillModal.innerHTML = `
          <form class="dlg-form" novalidate>
            <div class="dlg-head" style="display:flex; align-items:center; justify-content:space-between;">
              <strong>Add / update skill</strong>
              <button type="button" class="btn ghost" data-emp-skill-close aria-label="Close">✕</button>
            </div>
            <div class="dlg-body" style="display:grid; gap:10px; min-width:320px;">
              <label class="field">
                <span>Skill</span>
                <input type="text" data-emp-skill-name list="hrEmpSkillNameOptions" placeholder="Search a skill or type a new one" autocomplete="off" />
                <datalist id="hrEmpSkillNameOptions"></datalist>
                <div class="hint" data-emp-skill-hint style="display:none;">New skill — it will be added to the catalogue on save.</div>
              </label>
              <label class="field">
                <span>Current level</span>
                <input type="number" data-emp-skill-level min="0" step="1" value="1" />
              </label>
            </div>
            <div class="dlg-actions" style="display:flex; justify-content:flex-end; gap:8px;">
              <button type="button" class="btn" data-emp-skill-close>Cancel</button>
              <button type="submit" class="btn primary" data-emp-skill-save>Save</button>
            </div>
          </form>`;
        document.body.appendChild(hrEmpSkillModal);
        hrEmpSkillModal.querySelectorAll('[data-emp-skill-close]')
          .forEach((b) => b.addEventListener('click', () => closeDialog(hrEmpSkillModal)));
        const nameInput = hrEmpSkillModal.querySelector('[data-emp-skill-name]');
        const hint = hrEmpSkillModal.querySelector('[data-emp-skill-hint]');
        nameInput.addEventListener('input', () => {
          const v = nameInput.value.trim().toLowerCase();
          const exists = hrSkillsList.some((s) => (s.name || '').toLowerCase() === v);
          hint.style.display = (v && !exists) ? '' : 'none';
        });
        hrEmpSkillModal.querySelector('form')
          .addEventListener('submit', (ev) => { ev.preventDefault(); saveEmployeeSkillFromModal(); });
      }

      hrEmpSkillModal.querySelector('#hrEmpSkillNameOptions').innerHTML =
        catalog.map((s) => `<option value="${escapeHtml(s.name || '')}" data-id="${escapeHtml(s.id || '')}"></option>`).join('');
      hrEmpSkillModal.dataset.employeeId = employeeId;
      hrEmpSkillModal.querySelector('[data-emp-skill-name]').value = '';
      hrEmpSkillModal.querySelector('[data-emp-skill-level]').value = '1';
      hrEmpSkillModal.querySelector('[data-emp-skill-hint]').style.display = 'none';
      openDialog(hrEmpSkillModal);
      hrEmpSkillModal.querySelector('[data-emp-skill-name]').focus();
    }

    async function saveEmployeeSkillFromModal() {
      const dlg = hrEmpSkillModal;
      const employeeId = dlg.dataset.employeeId;
      const name = dlg.querySelector('[data-emp-skill-name]').value.trim();
      const level = Number(dlg.querySelector('[data-emp-skill-level]').value);
      if (!name) { showFlash('Enter a skill name', 'error'); return; }
      if (!Number.isFinite(level)) { showFlash('Enter a valid level', 'error'); return; }
      const saveBtn = dlg.querySelector('[data-emp-skill-save]');
      saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
      try {
        // Resolve the skill by name; define it in the catalogue when it's new.
        const lower = name.toLowerCase();
        let skill = hrSkillsList.find((s) => (s.name || '').toLowerCase() === lower);
        if (!skill) {
          skill = await adminAPI.fetch(API.adminHrSkillCreate, { method: 'POST', body: JSON.stringify({ name }) });
          if (skill && skill.id) hrSkillsList.push(skill);
        }
        if (!skill || !skill.id) throw new Error('Could not resolve the skill');
        await updateEmployeeSkill(employeeId, skill.id, level);
        showFlash('Skill saved');
        closeDialog(hrEmpSkillModal);
        if (hrEmployeeDetail && hrEmployeeSectionState.learning) {
          hrEmployeeSectionState.learning.loaded = false;
          loadEmployeeLearning(hrEmployeeDetail.id, hrEmployeeDetail.position_id || null).catch(() => {});
        }
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to save skill', 'error');
      } finally {
        saveBtn.disabled = false; saveBtn.textContent = 'Save';
      }
    }
    function populateReviewerOptions() {
      if (!hrReviewReviewerOptions) return;
      hrReviewReviewerOptions.innerHTML = '';
      if (!hrEmployeeDetail || !hrEmployeeDetail.department_id) return;
      const managers = getManagerCandidates(hrEmployeeDetail);
      managers.forEach((person) => {
        const opt = document.createElement('option');
        opt.value = `${person.first_name || ''} ${person.last_name || ''}`.trim();
        opt.dataset.id = person.id;
        opt.label = `${person.first_name || ''} ${person.last_name || ''} (${person.id})`;
        hrReviewReviewerOptions.appendChild(opt);
      });
    }
    function getManagerCandidates(employee) {
      const candidates = [];
      const deptChain = [];
      let current = employee ? employee.department_id : null;
      const visited = new Set();
      while (current && !visited.has(String(current))) {
        visited.add(String(current));
        deptChain.push(current);
        const dept = hrDepartmentsMap.get(String(current));
        current = dept && dept.parent_department_id ? dept.parent_department_id : null;
      }
      const ids = new Set();
      deptChain.forEach((deptId) => {
        const dept = hrDepartmentsMap.get(String(deptId));
        if (dept?.manager_employee_id) ids.add(String(dept.manager_employee_id));
        if (dept?.deputy_employee_id) ids.add(String(dept.deputy_employee_id));
      });
      hrPeopleList.forEach((p) => {
        if (ids.has(String(p.id))) candidates.push(p);
      });
      return candidates;
    }
    function updateVacancyTitleFromPosition() {
      if (!hrVacancyTitleInput || !hrVacancyPositionInput) return;
      const positionId = resolvePositionId(normalizeOptionalInput(hrVacancyPositionInput));
      const label = positionId ? (getPositionNameById(positionId) || positionId) : (hrVacancyPositionInput.value || 'Position');
      hrVacancyTitleInput.value = `Vacancy for ${label}`;
    }

  // ---- mount(): runs orphan top-level listeners that originally fired
  // at admin.js script-load time. Called from admin.js boot sequence.
  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();






        if (hrEmployeeLeaveTbody) {
          hrEmployeeLeaveTbody.addEventListener('click', async (event) => {
            const analysisBtn = event.target.closest('button[data-leave-analysis]');
            if (analysisBtn) {
              if (analysisBtn.dataset.leaveId) openLeaveAnalysis(analysisBtn.dataset.leaveId).catch(() => {});
              return;
            }
            const btn = event.target.closest('button[data-leave-act]');
            if (!btn) return;
            const leaveId = btn.dataset.leaveId;
            const act = btn.dataset.leaveAct;
            if (!leaveId || !act || !hrEmployeeDetail || !hrEmployeeDetail.id) return;
            const comments = window.prompt('Comments (optional):', '');
            try {
              await decideEmployeeLeave(leaveId, act, comments || '');
              showFlash(`Leave ${act}d`);
              hrEmployeeSectionState.leave.loaded = false;
              loadEmployeeLeave(hrEmployeeDetail.id).catch(() => {});
              hrEmployeeSectionState.leave_balances.loaded = false;
              loadEmployeeLeaveBalances(hrEmployeeDetail.id).catch(() => {});
            } catch (err) {
              showFlash(err && err.message ? err.message : `Failed to ${act} leave`, 'error');
            }
          });
        }
        if (hrGoalModeSelect) {
          hrGoalModeSelect.addEventListener('change', (event) => {
            setGoalMode(event.target.value || 'dept');
          });
        }
        if (hrGoalExistingSelect) {
          hrGoalExistingSelect.addEventListener('change', () => {
            if (!hrGoalExistingSelect.value) return;
            setGoalMode('dept');
          });
        }

        if (hrAddSkillBtn) {
          hrAddSkillBtn.addEventListener('click', () => {
            if (!hrSkillDlg || !hrSkillForm) { _hrWarnMissing('#hrSkillDlg'); return; }
            hrSkillForm.reset();
            hrSkillForm.dataset.pending = '0';
            hrSkillForm.dataset.editId = '';
            openDialog(hrSkillDlg);
          });
        }
        if (hrSkillForm) {
          hrSkillForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrSkillForm.dataset.pending === '1') return;
            const name = hrSkillNameInput ? hrSkillNameInput.value.trim() : '';
            if (!name) { showFlash('Skill name required', 'error'); return; }
            const editId = hrSkillForm.dataset.editId || '';
            const payload = {
              name,
              category: normalizeOptionalInput(hrSkillCategoryInput),
              level_scale: hrSkillScaleInput && hrSkillScaleInput.value !== '' ? Number(hrSkillScaleInput.value) : null,
            };
            hrSkillForm.dataset.pending = '1';
            if (hrSkillSubmitBtn) { hrSkillSubmitBtn.disabled = true; hrSkillSubmitBtn.textContent = editId ? 'Updating…' : 'Saving…'; }
            try {
              const endpoint = editId ? API.adminHrSkill(editId) : API.adminHrSkillCreate;
              const method = editId ? 'PATCH' : 'POST';
              await adminAPI.fetch(endpoint, { method, body: JSON.stringify(payload) });
              showFlash(editId ? 'Skill updated' : 'Skill added');
              hrDataState.learning = false;
              loadHrLearning().catch(() => {});
              try { hrSkillDlg.close(); } catch { hrSkillDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add skill', 'error');
            } finally {
              hrSkillForm.dataset.pending = '0';
              if (hrSkillSubmitBtn) { hrSkillSubmitBtn.disabled = false; hrSkillSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrSkillsTbody) {
          hrSkillsTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('button[data-skill-act]');
            if (!btn) return;
            const skillId = btn.dataset.skillId;
            const act = btn.dataset.skillAct;
            if (!skillId || !act) return;
            const skill = hrSkillsList.find((s) => String(s.id) === String(skillId));
            if (act === 'edit') {
              if (!hrSkillDlg || !hrSkillForm) { _hrWarnMissing('#hrSkillDlg'); return; }
              hrSkillForm.reset();
              hrSkillForm.dataset.pending = '0';
              hrSkillForm.dataset.editId = skillId;
              if (hrSkillNameInput) hrSkillNameInput.value = skill?.name || '';
              if (hrSkillCategoryInput) hrSkillCategoryInput.value = skill?.category || '';
              if (hrSkillScaleInput) hrSkillScaleInput.value = skill?.level_scale != null ? skill.level_scale : 10;
              openDialog(hrSkillDlg);
              return;
            }
            if (act === 'delete') {
              const confirmed = window.confirm('Delete this skill? This may affect requirements.');
              if (!confirmed) return;
              try {
                await adminAPI.fetch(API.adminHrSkill(skillId), { method: 'DELETE' });
                showFlash('Skill deleted');
                hrDataState.learning = false;
                loadHrLearning().catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to delete skill', 'error');
              }
            }
          });
        }
        if (hrAddCertBtn) {
          // Learning page: always pick the employee in the dialog.
          hrAddCertBtn.addEventListener('click', () => openEmployeeCertDialog(''));
        }
        if (hrAddSkillReqBtn) {
          hrAddSkillReqBtn.addEventListener('click', () => {
            if (!hrSkillReqDlg || !hrSkillReqForm) { _hrWarnMissing('#hrSkillReqDlg'); return; }
            hrSkillReqForm.reset();
            hrSkillReqForm.dataset.pending = '0';
            hrSkillReqForm.dataset.editId = '';
            populateHrPositionOptions();
            populateSkillOptions();
            openDialog(hrSkillReqDlg);
          });
        }
        if (hrSkillReqForm) {
          hrSkillReqForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrSkillReqForm.dataset.pending === '1') return;
            const positionId = resolvePositionId(normalizeOptionalInput(hrSkillReqPositionInput));
            const skillId = (() => {
              const val = normalizeOptionalInput(hrSkillReqSkillInput);
              if (!val) return null;
              const opt = hrSkillOptions ? hrSkillOptions.querySelector(`option[value="${CSS.escape(val)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const lower = val.toLowerCase();
              const found = hrSkillsList.find((s) => (s.name || '').toLowerCase() === lower);
              return found ? found.id : null;
            })();
            const targetLevel = hrSkillReqLevelInput && hrSkillReqLevelInput.value !== '' ? Number(hrSkillReqLevelInput.value) : null;
            if (!positionId || !skillId || !Number.isFinite(targetLevel)) {
              showFlash('Position, skill, and target level are required', 'error');
              return;
            }
            const payload = {
              position_id: positionId,
              skill_id: skillId,
              target_level: targetLevel,
              criticality: hrSkillReqCriticalityInput ? hrSkillReqCriticalityInput.value || null : null,
            };
            const editId = hrSkillReqForm.dataset.editId || '';
            hrSkillReqForm.dataset.pending = '1';
            if (hrSkillReqSubmitBtn) { hrSkillReqSubmitBtn.disabled = true; hrSkillReqSubmitBtn.textContent = editId ? 'Updating…' : 'Saving…'; }
            try {
              const endpoint = editId ? API.adminHrSkillRequirement(editId) : API.adminHrSkillRequirementCreate;
              const method = editId ? 'PATCH' : 'POST';
              await adminAPI.fetch(endpoint, { method, body: JSON.stringify(payload) });
              showFlash(editId ? 'Skill requirement updated' : 'Skill requirement added');
              hrDataState.learning = false;
              loadHrLearning().catch(() => {});
              try { hrSkillReqDlg.close(); } catch { hrSkillReqDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to add skill requirement', 'error');
            } finally {
              hrSkillReqForm.dataset.pending = '0';
              if (hrSkillReqSubmitBtn) { hrSkillReqSubmitBtn.disabled = false; hrSkillReqSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrOnboardingTemplateForm) {
          hrOnboardingTemplateForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrOnboardingTemplateForm.dataset.pending === '1') return;
            const name = hrOnboardingTemplateNameInput ? hrOnboardingTemplateNameInput.value.trim() : '';
            if (!name) { showFlash('Template name is required', 'error'); return; }
            const payload = {
              name,
              role_type: hrOnboardingTemplateRoleInput ? normalizeOptionalInput(hrOnboardingTemplateRoleInput) : null,
              is_active: !hrOnboardingTemplateStatusInput || hrOnboardingTemplateStatusInput.value !== 'inactive',
              onboarding_type: hrOnboardingTemplateTypeInput ? normalizeOptionalInput(hrOnboardingTemplateTypeInput) : null,
              location_scope: normalizeOptionalInput(hrOnboardingTemplateLocationInput),
              default_due_days: hrOnboardingTemplateDueInput && hrOnboardingTemplateDueInput.value !== '' ? Number(hrOnboardingTemplateDueInput.value) : null,
            };
            const editId = hrOnboardingTemplateForm.dataset.editId || '';
            hrOnboardingTemplateForm.dataset.pending = '1';
            if (hrOnboardingTemplateSubmitBtn) { hrOnboardingTemplateSubmitBtn.disabled = true; hrOnboardingTemplateSubmitBtn.textContent = editId ? 'Updating…' : 'Saving…'; }
            try {
              const endpoint = editId ? API.adminHrOnboardingTemplate(editId) : API.adminHrOnboardingTemplateCreate;
              const method = editId ? 'PATCH' : 'POST';
              await adminAPI.fetch(endpoint, { method, body: JSON.stringify(payload) });
              showFlash(editId ? 'Template updated' : 'Template added');
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              try { hrOnboardingTemplateDlg.close(); } catch { hrOnboardingTemplateDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to save template', 'error');
            } finally {
              hrOnboardingTemplateForm.dataset.pending = '0';
              if (hrOnboardingTemplateSubmitBtn) { hrOnboardingTemplateSubmitBtn.disabled = false; hrOnboardingTemplateSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrOnboardingTemplateItemForm) {
          hrOnboardingTemplateItemForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (hrOnboardingTemplateItemForm.dataset.pending === '1') return;
            const tplNameVal = normalizeOptionalInput(hrOnboardingTemplateItemTemplateInput);
            const templateId = (() => {
              if (!tplNameVal) return null;
              const opt = hrOnboardingTemplateOptions ? hrOnboardingTemplateOptions.querySelector(`option[value="${CSS.escape(tplNameVal)}"]`) : null;
              if (opt && opt.dataset && opt.dataset.id) return opt.dataset.id;
              const found = hrOnboardingTemplates.find((t) => (t.name || '').toLowerCase() === tplNameVal.toLowerCase());
              return found ? found.id : null;
            })();
            const name = hrOnboardingTemplateItemNameInput ? hrOnboardingTemplateItemNameInput.value.trim() : '';
            if (!templateId || !name) { showFlash('Template and task name are required', 'error'); return; }
            const payload = {
              name,
              template_id: templateId,
              description: normalizeOptionalInput(hrOnboardingTemplateItemDescInput),
              owner_role: normalizeOptionalInput(hrOnboardingTemplateItemOwnerInput) || 'hr',
              sequence: hrOnboardingTemplateItemSeqInput && hrOnboardingTemplateItemSeqInput.value !== '' ? Number(hrOnboardingTemplateItemSeqInput.value) : null,
              due_offset_days: hrOnboardingTemplateItemOffsetInput && hrOnboardingTemplateItemOffsetInput.value !== '' ? Number(hrOnboardingTemplateItemOffsetInput.value) : null,
              requires_evidence: hrOnboardingTemplateItemEvidenceInput ? Boolean(hrOnboardingTemplateItemEvidenceInput.checked) : null,
              reminder_cadence_days: hrOnboardingTemplateItemReminderInput && hrOnboardingTemplateItemReminderInput.value !== '' ? Number(hrOnboardingTemplateItemReminderInput.value) : null,
            };
            const editId = hrOnboardingTemplateItemForm.dataset.editId || '';
            hrOnboardingTemplateItemForm.dataset.pending = '1';
            if (hrOnboardingTemplateItemSubmitBtn) { hrOnboardingTemplateItemSubmitBtn.disabled = true; hrOnboardingTemplateItemSubmitBtn.textContent = editId ? 'Updating…' : 'Saving…'; }
            try {
              if (editId) {
                await adminAPI.fetch(API.adminHrOnboardingTemplateItem(editId), { method: 'PATCH', body: JSON.stringify(payload) });
              } else {
                await adminAPI.fetch(API.adminHrOnboardingTemplateItems(templateId), { method: 'POST', body: JSON.stringify(payload) });
              }
              showFlash(editId ? 'Task updated' : 'Task added');
              hrSelectedOnboardingTemplateId = templateId || hrSelectedOnboardingTemplateId;
              hrDataState.talent = false;
              loadHrTalent().catch(() => {});
              try { hrOnboardingTemplateItemDlg.close(); } catch { hrOnboardingTemplateItemDlg.open = false; }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to save task', 'error');
            } finally {
              hrOnboardingTemplateItemForm.dataset.pending = '0';
              if (hrOnboardingTemplateItemSubmitBtn) { hrOnboardingTemplateItemSubmitBtn.disabled = false; hrOnboardingTemplateItemSubmitBtn.textContent = 'Save'; }
            }
          });
        }
        if (hrOnboardingTemplatesTbody) {
          hrOnboardingTemplatesTbody.addEventListener('click', async (event) => {
            const row = event.target.closest('tr[data-template-id]');
            const btn = event.target.closest('button[data-template-act]');
            if (row && !btn) {
              hrSelectedOnboardingTemplateId = row.dataset.templateId || '';
              renderOnboardingTemplates();
              return;
            }
            if (!btn) return;
            const tplId = btn.dataset.templateId;
            const act = btn.dataset.templateAct;
            const template = hrOnboardingTemplates.find((t) => String(t.id) === String(tplId));
            if (act === 'edit') {
              if (!hrOnboardingTemplateDlg || !hrOnboardingTemplateForm) { _hrWarnMissing('#hrOnboardingTemplateDlg'); return; }
              hrOnboardingTemplateForm.reset();
              hrOnboardingTemplateForm.dataset.editId = tplId;
              if (hrOnboardingTemplateNameInput) hrOnboardingTemplateNameInput.value = template?.name || '';
              if (hrOnboardingTemplateRoleInput) hrOnboardingTemplateRoleInput.value = template?.role_type || '';
              if (hrOnboardingTemplateTypeInput) hrOnboardingTemplateTypeInput.value = template?.onboarding_type || '';
              if (hrOnboardingTemplateLocationInput) hrOnboardingTemplateLocationInput.value = template?.location_scope || '';
              if (hrOnboardingTemplateDueInput) hrOnboardingTemplateDueInput.value = template?.default_due_days != null ? template.default_due_days : '';
              if (hrOnboardingTemplateStatusInput) hrOnboardingTemplateStatusInput.value = template?.is_active === false ? 'inactive' : 'active';
              openDialog(hrOnboardingTemplateDlg);
              return;
            }
            if (act === 'delete') {
              const confirmed = window.confirm('Delete this template and its tasks?');
              if (!confirmed) return;
              try {
                await adminAPI.fetch(API.adminHrOnboardingTemplate(tplId), { method: 'DELETE' });
                showFlash('Template deleted');
                hrDataState.talent = false;
                if (String(hrSelectedOnboardingTemplateId) === String(tplId)) hrSelectedOnboardingTemplateId = '';
                loadHrTalent().catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to delete template', 'error');
              }
            }
          });
        }
        if (hrOnboardingTemplateItemsTbody) {
          hrOnboardingTemplateItemsTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('button[data-template-item-act]');
            if (!btn) return;
            const itemId = btn.dataset.templateItemId;
            const act = btn.dataset.templateItemAct;
            const item = hrOnboardingTemplateItems.find((i) => String(i.id) === String(itemId));
            if (act === 'edit') {
              if (!hrOnboardingTemplateItemDlg || !hrOnboardingTemplateItemForm) { _hrWarnMissing('#hrOnboardingTemplateItemDlg'); return; }
              hrOnboardingTemplateItemForm.reset();
              hrOnboardingTemplateItemForm.dataset.editId = itemId;
              populateOnboardingTemplateOptions();
              if (hrOnboardingTemplateItemTemplateInput) hrOnboardingTemplateItemTemplateInput.value = getOnboardingTemplateNameById(item?.template_id) || '';
              if (hrOnboardingTemplateItemNameInput) hrOnboardingTemplateItemNameInput.value = item?.name || '';
              if (hrOnboardingTemplateItemDescInput) hrOnboardingTemplateItemDescInput.value = item?.description || '';
              if (hrOnboardingTemplateItemOwnerInput) hrOnboardingTemplateItemOwnerInput.value = item?.owner_role || 'hr';
              if (hrOnboardingTemplateItemSeqInput) hrOnboardingTemplateItemSeqInput.value = item?.sequence != null ? item.sequence : '';
              if (hrOnboardingTemplateItemOffsetInput) hrOnboardingTemplateItemOffsetInput.value = item?.due_offset_days != null ? item.due_offset_days : '';
              if (hrOnboardingTemplateItemEvidenceInput) hrOnboardingTemplateItemEvidenceInput.checked = Boolean(item?.requires_evidence);
              if (hrOnboardingTemplateItemReminderInput) hrOnboardingTemplateItemReminderInput.value = item?.reminder_cadence_days != null ? item.reminder_cadence_days : '';
              openDialog(hrOnboardingTemplateItemDlg);
              return;
            }
            if (act === 'delete') {
              const confirmed = window.confirm('Delete this task?');
              if (!confirmed) return;
              try {
                await adminAPI.fetch(API.adminHrOnboardingTemplateItem(itemId), { method: 'DELETE' });
                showFlash('Task deleted');
                hrDataState.talent = false;
                loadHrTalent().catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to delete task', 'error');
              }
            }
          });
        }
        if (hrOnboardingTasksTbody) {
          hrOnboardingTasksTbody.addEventListener('change', async (event) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
            const taskId = target.dataset.onboardingTaskId;
            if (!taskId) return;
            const status = target.checked ? 'completed' : 'not_started';
            try {
              await adminAPI.fetch(API.adminHrOnboardingTaskComplete(taskId), { method: 'POST', body: JSON.stringify({ status }) });
              if (hrOnboardingTasksOnboardingId) {
                hrOnboardingTasksCache.delete(hrOnboardingTasksOnboardingId);
                loadOnboardingTasks(hrOnboardingTasksOnboardingId).then((tasks) => renderOnboardingTasksDialog(hrOnboardingTasksOnboardingId, tasks));
              }
            } catch (err) {
              showFlash(err && err.message ? err.message : 'Failed to update task', 'error');
              target.checked = !target.checked;
            }
          });
        }
        if (hrSkillReqTbody) {
          hrSkillReqTbody.addEventListener('click', async (event) => {
            const btn = event.target.closest('button[data-skill-req-act]');
            if (!btn) return;
            const reqId = btn.dataset.skillReqId;
            const act = btn.dataset.skillReqAct;
            if (!reqId || !act) return;
            const req = hrSkillRequirements.find((r) => String(r.id) === String(reqId));
            if (act === 'edit') {
              if (!hrSkillReqDlg || !hrSkillReqForm) { _hrWarnMissing('#hrSkillReqDlg'); return; }
              hrSkillReqForm.reset();
              hrSkillReqForm.dataset.pending = '0';
              hrSkillReqForm.dataset.editId = reqId;
              populateHrPositionOptions();
              populateSkillOptions();
              if (hrSkillReqPositionInput) hrSkillReqPositionInput.value = getPositionNameById(req?.position_id) || req?.position_id || '';
              if (hrSkillReqSkillInput) hrSkillReqSkillInput.value = getSkillNameById(req?.skill_id) || req?.skill_id || '';
              if (hrSkillReqLevelInput) hrSkillReqLevelInput.value = req?.target_level != null ? req.target_level : '';
              if (hrSkillReqCriticalityInput) hrSkillReqCriticalityInput.value = req?.criticality || 'medium';
              openDialog(hrSkillReqDlg);
              return;
            }
            if (act === 'delete') {
              const confirmed = window.confirm('Delete this skill requirement?');
              if (!confirmed) return;
              try {
                await adminAPI.fetch(API.adminHrSkillRequirement(reqId), { method: 'DELETE' });
                showFlash('Skill requirement deleted');
                hrDataState.learning = false;
                loadHrLearning().catch(() => {});
              } catch (err) {
                showFlash(err && err.message ? err.message : 'Failed to delete skill requirement', 'error');
              }
            }
          });
        }
        if (hrVacanciesTbody) {
          hrVacanciesTbody.addEventListener('click', (event) => {
            const btn = event.target.closest('button[data-vacancy-id][data-vacancy-act]');
            if (!btn) return;
            const vacId = btn.dataset.vacancyId;
            const vac = hrVacancyList.find((v) => String(v.id) === String(vacId));
          if (!vac || !hrVacancyDlg || !hrVacancyForm) return;
          hrVacancyForm.reset();
          hrVacancyForm.dataset.editId = vacId;
          hrVacancyForm.dataset.pending = '0';
          if (hrVacancyTitleInput) hrVacancyTitleInput.value = vac.title || '';
          if (hrVacancyPositionInput) hrVacancyPositionInput.value = getPositionNameById(vac.position_id) || '';
          if (hrVacancyDeptInput) hrVacancyDeptInput.value = getDepartmentNameById(vac.department_id) || '';
            if (hrVacancyOpeningsInput) hrVacancyOpeningsInput.value = vac.openings != null ? vac.openings : 1;
            if (hrVacancyStatusInput) hrVacancyStatusInput.value = vac.status || 'open';
            if (hrVacancyDescriptionInput) hrVacancyDescriptionInput.value = vac.description || '';
            updateVacancyTitleFromPosition();
            openDialog(hrVacancyDlg);
          });
        }
        if (hrVacancyPositionInput) {
          hrVacancyPositionInput.addEventListener('change', updateVacancyTitleFromPosition);
          hrVacancyPositionInput.addEventListener('blur', updateVacancyTitleFromPosition);
        }
  }

  function init() {
    resolveDom();
    initShiftTemplatesView();
    setHrSection(hrSection);
    setHrTab(hrActiveTab);
    loadShiftTemplates().catch(() => {});
  }

export default {
  mount,
  init,
  setSection: (s) => setHrSection(s),
  setTab: (t) => setHrTab(t),
  setPeopleTab: (t) => setHrPeopleTab(t),
  loadShiftTemplates: () => loadShiftTemplates(),
  loadActiveShifts: () => loadActiveShifts(),
  getSection: () => hrSection,
  getActiveTab: () => hrActiveTab,
  getPeopleTab: () => hrPeopleTab,
};
